import React, { useState, useEffect, createContext, useContext, useMemo, useRef, useCallback } from 'react';
import { keyframes } from '@emotion/react';
import {
  AppBar, Avatar, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Collapse, createTheme, CssBaseline, Dialog, DialogContent, DialogTitle, Divider, Drawer, Fab, FormControl, Grid, IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Popover, Stack, TextField, ThemeProvider, Toolbar, Tooltip, Typography, Alert, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme
} from "@mui/material";
import {
  Alert as AntAlert,
  Button as AntButton,
  Card as AntCard,
  Checkbox as AntCheckbox,
  Col as AntCol,
  Collapse as AntCollapse,
  Divider as AntDivider,
  Input as AntInput,
  List as AntList,
  Modal as AntModal,
  Radio as AntRadio,
  Row as AntRow,
  Segmented as AntSegmented,
  Select as AntSelect,
  Space as AntSpace,
  Spin as AntSpin,
  Table as AntTable,
  Tag as AntTag,
  Typography as AntTypography,
} from 'antd';
import {
  Button as AMButton,
  Card as AMCard,
  Collapse as AMCollapse,
  Grid as AMGrid,
  Input as AMInput,
  List as AMList,
  Popup as AMPopup,
  Selector as AMSelector,
  SafeArea as AMSafeArea,
  Space as AMSpace,
  SpinLoading as AMSpin,
  Tag as AMTag,
  TextArea as AMTextArea,
} from 'antd-mobile';
import { Bar as AntPlotBar, Column as AntPlotColumn, Line as AntPlotLine, Pie as AntPlotPie } from '@ant-design/plots';
import { CONFIG, getRequiredConfig } from './config';

// --- ICONS ---
import {
  AdminPanelSettings as AdminPanelSettingsIcon, Article as ArticleIcon, Bolt as BoltIcon, Build as BuildIcon,
  Business as BusinessIcon, Description as DescriptionIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon,
  HealthAndSafety as HealthAndSafetyIcon, Menu as MenuIcon, MyLocation as MyLocationIcon, OndemandVideo as OndemandVideoIcon, // prettier-ignore
  People as PeopleIcon, School as SchoolIcon, Search as SearchIcon, Umbrella as UmbrellaIcon, MenuBook as MenuBookIcon,
  Landscape as SandboxIcon, Calculate as CalculateIcon, Close as CloseIcon, RocketLaunch as RocketLaunchIcon,
  Shield as ShieldIcon, Check as CheckIcon, Upgrade as UpgradeIcon, RequestQuote as RequestQuoteIcon, Group as GroupIcon,
  Login as LoginIcon, FindInPage as FindInPageIcon, Store as StoreIcon, DocumentScanner as DocumentScannerIcon,
  Home as HomeIcon, FitnessCenter as FitnessCenterIcon, Map as MapIcon, AssuredWorkload as AssuredWorkloadIcon,
  WbSunny as WbSunnyIcon, Dialpad as DialpadIcon, Apps as AppsIcon, Whatshot as WhatshotIcon, Phone as PhoneIcon, Add as AddIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, LocalHospital as LocalHospitalIcon, RequestPage as RequestPageIcon, Headset as HeadsetIcon, UploadFile as UploadFileIcon, Storefront as StorefrontIcon, Download as DownloadIcon,
  Dashboard as DashboardIcon, BarChart as BarChartIcon, Link as LinkIcon,
  Announcement as AnnouncementIcon, ContentCopy as ContentCopyIcon, EmojiEvents as EmojiEventsIcon,
  TrendingUp as TrendingUpIcon, Info as InfoIcon, ErrorOutline as ErrorOutlineIcon, CompareArrows as CompareArrowsIcon,
  MilitaryTech as MilitaryTechIcon, WorkspacePremium as WorkspacePremiumIcon, Refresh as RefreshIcon, CalendarToday as CalendarTodayIcon
} from "@mui/icons-material";

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Select, FormControl as MuiFormControl, InputLabel, TableSortLabel, TablePagination, DialogActions } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  FormLabel
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import LifeQuoteTool from './LifeQuoteTool';

const AMCardBody = ({ children }) => <>{children}</>;
if (!AMCard.Body) {
  AMCard.Body = AMCardBody;
}

// --- API Service Object ---
const API_BASE_URL = getRequiredConfig(CONFIG.API_BASE_URL, 'REACT_APP_API_BASE_URL');
const SLACK_CLIENT_ID = (process.env.REACT_APP_SLACK_CLIENT_ID || '').trim();
const AHM_KPI_REPORT_URL = getRequiredConfig(CONFIG.AHM_KPI_REPORT_URL, 'REACT_APP_AHM_KPI_REPORT_URL');
const AHM_KPI_RUN_URL =
  CONFIG.AHM_KPI_RUN_URL || (API_BASE_URL ? `${API_BASE_URL}/ahm/agent-kpi-run` : '');
const KPI_PALETTE = ['#2563eb', '#0ea5e9', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#14b8a6', '#c084fc', '#22c55e'];
// Token for app-secure redirects only
const API_GATE_TOKEN = getRequiredConfig(CONFIG.API_GATE_TOKEN, 'REACT_APP_API_GATE_TOKEN');
const COMPLIANCEGUARD_PORTAL_PATH = getRequiredConfig(
  CONFIG.COMPLIANCEGUARD_PORTAL_PATH,
  'REACT_APP_COMPLIANCEGUARD_PORTAL_PATH'
);

const API_MATCH_URL = getRequiredConfig(CONFIG.API_MATCH_URL, 'REACT_APP_API_MATCH_URL');
const AGENT_DIRECTORY_ENDPOINT =
  CONFIG.AGENT_DIRECTORY_ENDPOINT || (API_BASE_URL ? `${API_BASE_URL}/agman/dialer-login` : '');
const POLARIS_APP_USER_ENDPOINT = getRequiredConfig(
  CONFIG.POLARIS_APP_USER_ENDPOINT,
  'REACT_APP_POLARIS_APP_USER_ENDPOINT'
);
const POLARIS_INTRO_STORAGE_VERSION = '2024-dashboard-update-v1';

const SABAL_HIERARCHY_ENDPOINT_URL = getRequiredConfig(
  CONFIG.SABAL_HIERARCHY_ENDPOINT_URL,
  'REACT_APP_SABAL_HIERARCHY_ENDPOINT_URL'
);
const SABAL_HIERARCHY_ROSTER_ENDPOINT_URL =
  CONFIG.SABAL_HIERARCHY_ROSTER_ENDPOINT_URL ||
  (API_BASE_URL ? `${API_BASE_URL}/agman/hierarchy/roster` : '');
const COMPLIANCE_SCORECARD_URL = getRequiredConfig(
  CONFIG.COMPLIANCE_SCORECARD_URL,
  'REACT_APP_COMPLIANCE_SCORECARD_URL'
);
const AI_NPC_AGENT_LIST_URL = getRequiredConfig(
  CONFIG.AI_NPC_AGENT_LIST_URL,
  'REACT_APP_AI_NPC_AGENT_LIST_URL'
);
const UPDATES_URL = getRequiredConfig(CONFIG.UPDATES_URL, 'REACT_APP_UPDATES_URL');
const POLARIS_ICON_URL = getRequiredConfig(CONFIG.POLARIS_ICON_URL, 'REACT_APP_POLARIS_ICON_URL');
const SLACK_OAUTH_AUTHORIZE_URL = getRequiredConfig(
  CONFIG.SLACK_OAUTH_AUTHORIZE_URL,
  'REACT_APP_SLACK_OAUTH_AUTHORIZE_URL'
);
const SABAL_HIERARCHY_TIMEOUT_MS = (() => {
  const parsed = Number(process.env.REACT_APP_SABAL_HIERARCHY_TIMEOUT_MS);
  if (!Number.isFinite(parsed) || parsed < 1000) {
    return 420000;
  }
  return parsed;
})();
const SABAL_HIERARCHY_CACHE_TTL_MS = (() => {
  const parsed = Number(process.env.REACT_APP_SABAL_HIERARCHY_CACHE_TTL_MS);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 5 * 60 * 1000;
  }
  return parsed;
})();
const sabalHierarchyCache = { data: null, expiresAt: 0, promise: null };
const sabalRosterHierarchyCache = { data: null, expiresAt: 0, promise: null };

const EXECUTIVE_DASHBOARD_CACHE_TTL_MS = (() => {
  const parsed = Number(process.env.REACT_APP_EXEC_DASH_CACHE_TTL_MS);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 5 * 60 * 1000;
  }
  return parsed;
})();
const executiveDashboardCache = new Map();

const fetchSabalHierarchyPayload = async () => {
  const now = Date.now();
  if (sabalHierarchyCache.data && sabalHierarchyCache.expiresAt > now) {
    return sabalHierarchyCache.data;
  }
  if (sabalHierarchyCache.promise) {
    return sabalHierarchyCache.promise;
  }
  sabalHierarchyCache.promise = (async () => {
    const controller =
      typeof AbortController === 'function' && SABAL_HIERARCHY_TIMEOUT_MS > 0
        ? new AbortController()
        : null;
    const timeoutId =
      controller && SABAL_HIERARCHY_TIMEOUT_MS > 0
        ? setTimeout(() => controller.abort(), SABAL_HIERARCHY_TIMEOUT_MS)
        : null;
    try {
      const response = await fetch(SABAL_HIERARCHY_ENDPOINT_URL, {
        method: 'GET',
        headers: { accept: 'application/json' },
        mode: 'cors',
        signal: controller?.signal,
      });
      const responseText = await response.text();
      if (!response.ok) {
        const snippet = typeof responseText === 'string' ? responseText.slice(0, 200) : '';
        throw new Error(
          `Sabal hierarchy request failed (${response.status})${snippet ? `: ${snippet}` : ''}`
        );
      }
      const trimmed = typeof responseText === 'string' ? responseText.trim() : '';
      if (trimmed.startsWith('<')) {
        const snippet = typeof responseText === 'string' ? responseText.slice(0, 200) : '';
        throw new Error(
          `Sabal hierarchy request returned HTML (expected JSON). Check endpoint/auth.${snippet ? `: ${snippet}` : ''}`
        );
      }
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch (error) {
        throw new Error(`Unable to parse Sabal hierarchy payload: ${error?.message || error}`);
      }
      if (!parsed || typeof parsed !== 'object' || parsed.status !== 'ok' || !Array.isArray(parsed.seniors)) {
        throw new Error('Sabal hierarchy payload missing expected fields.');
      }
      if (SABAL_HIERARCHY_CACHE_TTL_MS > 0) {
        sabalHierarchyCache.data = parsed;
        sabalHierarchyCache.expiresAt = Date.now() + SABAL_HIERARCHY_CACHE_TTL_MS;
      }
      return parsed;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('Sabal hierarchy request timed out.');
      }
      throw error;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      sabalHierarchyCache.promise = null;
    }
  })();
  return sabalHierarchyCache.promise;
};

const fetchSabalRosterHierarchyPayload = async () => {
  const now = Date.now();
  if (sabalRosterHierarchyCache.data && sabalRosterHierarchyCache.expiresAt > now) {
    return sabalRosterHierarchyCache.data;
  }
  if (sabalRosterHierarchyCache.promise) {
    return sabalRosterHierarchyCache.promise;
  }
  sabalRosterHierarchyCache.promise = (async () => {
    const controller =
      typeof AbortController === 'function' && SABAL_HIERARCHY_TIMEOUT_MS > 0
        ? new AbortController()
        : null;
    const timeoutId =
      controller && SABAL_HIERARCHY_TIMEOUT_MS > 0
        ? setTimeout(() => controller.abort(), SABAL_HIERARCHY_TIMEOUT_MS)
        : null;
    try {
      const response = await fetch(SABAL_HIERARCHY_ROSTER_ENDPOINT_URL, {
        method: 'GET',
        headers: { accept: 'application/json' },
        mode: 'cors',
        signal: controller?.signal,
      });
      const responseText = await response.text();
      if (!response.ok) {
        const snippet = typeof responseText === 'string' ? responseText.slice(0, 200) : '';
        throw new Error(
          `Sabal roster hierarchy request failed (${response.status})${snippet ? `: ${snippet}` : ''}`
        );
      }
      const trimmed = typeof responseText === 'string' ? responseText.trim() : '';
      if (trimmed.startsWith('<')) {
        const snippet = typeof responseText === 'string' ? responseText.slice(0, 200) : '';
        throw new Error(
          `Sabal roster hierarchy request returned HTML (expected JSON).${snippet ? `: ${snippet}` : ''}`
        );
      }
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch (error) {
        throw new Error(`Unable to parse Sabal roster hierarchy payload: ${error?.message || error}`);
      }
      if (!parsed || typeof parsed !== 'object' || parsed.status !== 'ok' || !Array.isArray(parsed.seniors)) {
        throw new Error('Sabal roster hierarchy payload missing expected fields.');
      }
      if (SABAL_HIERARCHY_CACHE_TTL_MS > 0) {
        sabalRosterHierarchyCache.data = parsed;
        sabalRosterHierarchyCache.expiresAt = Date.now() + SABAL_HIERARCHY_CACHE_TTL_MS;
      }
      return parsed;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('Sabal roster hierarchy request timed out.');
      }
      throw error;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      sabalRosterHierarchyCache.promise = null;
    }
  })();
  return sabalRosterHierarchyCache.promise;
};

const api = {
  // JWT is not used for API auth; only for deriving user identity
  _authToken: null,
  setAuthToken: function(token) {
    this._authToken = token;
  },

  get: async (endpoint, baseUrl = API_BASE_URL) => {
    const response = await fetch(`${baseUrl}${endpoint}`, { method: 'GET' });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return response.json();
    }
    return response.text();
  },

  post: async (endpoint, body, baseUrl = API_BASE_URL) => {
    const headers = { 'Content-Type': 'application/json' };
    const response = await fetch(`${baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return response.json();
    }
    return response.text();
  },
  
  // All other API methods remain unchanged as they internally use api.post
  getPermissions: (userEmail) => api.post('/permissions', { userEmail }),
  getSecureLink: (button_id, activeUserEmail) => api.post('/view-secure-link', { button_id, activeUserEmail }),
  viewResource: (button_id, activeUserEmail) => api.post('/view-resource', { button_id, activeUserEmail }),
  viewResourceUnsecure: (button_id, activeUserEmail) => api.post('/view-resource-unsecure', { button_id, activeUserEmail }),
  viewResourceMedia: (button_id, activeUserEmail) => api.post('/media-viewer', { button_id, activeUserEmail }),
  toggleForm: (button_id) => api.post('/toggle-form', { button_id }),
  // Generic secure redirect (no API key)
  secureRedirect: (targetUrl, userName, linkName) => api.post('/secure-portal', { targetUrl, userName, linkName }),
  // App-only secure redirect (includes API key)
  secureRedirectApp: (targetUrl, userName, linkName) => api.post('/secure-portal', { targetUrl, userName, linkName, api_key: API_GATE_TOKEN }),
  getCounties: (stateAbbreviation) => api.post('/county-by-state', { state: stateAbbreviation }, API_MATCH_URL),
  getPolicyMatch: (formInput, activeUserEmail) => api.post('/mapd-policy-matchv3', { ...formInput, activeUserEmail }, API_MATCH_URL),
  processRxFile: (fileContent) => api.post('/process-file', { fileContent }),
  sendRxToCrm: (jsonData) => api.post('/send-rx-to-crm', jsonData),
  sendRequest: (payload) => api.post('/request', payload),
  // FEMA SEP Data endpoints on sabal-agent-ops base URL
  femaDataDelete: (row) => api.post('/fema-data-delete', row),
  femaDataInsert: (rows) => api.post('/fema-data-insert', {
    projectId: 'pdf-extractor-412122',
    datasetId: 'sabal_resource_repo',
    tableId: 'fema_sep_data',
    rows,
  }),
  // AI NPC: agent level completion manifest (unique row per level with highest score)
  getNpcLevelCompletedByAgent: (agent_name) => api.post('/ai-npc/level-completed/agent', { agent_name })
};


// --- AUTHENTICATION CONTEXT ---
const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const SearchContext = createContext();
const useSearch = () => useContext(SearchContext);

const ModalContext = createContext();
const useModal = () => useContext(ModalContext);

const NavItemsContext = createContext();
const useNavItems = () => useContext(NavItemsContext);

// --- PAGES CONFIG CONTEXT ---
const PagesContext = createContext();
const usePages = () => useContext(PagesContext);

const ICON_REGISTRY = {
  DashboardIcon: <DashboardIcon />,
  ArticleIcon: <ArticleIcon />,
  DescriptionIcon: <DescriptionIcon />,
  HealthAndSafetyIcon: <HealthAndSafetyIcon />,
  RequestQuoteIcon: <RequestQuoteIcon />,
  GroupIcon: <GroupIcon />,
  ShieldIcon: <ShieldIcon />,
  UmbrellaIcon: <UmbrellaIcon />,
  CheckIcon: <CheckIcon />,
  SearchIcon: <SearchIcon />,
  FindInPageIcon: <FindInPageIcon />,
  BoltIcon: <BoltIcon />,
  LoginIcon: <LoginIcon />,
  DocumentScannerIcon: <DocumentScannerIcon />,
  MapIcon: <MapIcon />,
  MyLocationIcon: <MyLocationIcon />,
  LocalHospitalIcon: <LocalHospitalIcon />,
  AssuredWorkloadIcon: <AssuredWorkloadIcon />,
  CalculateIcon: <CalculateIcon />,
  MenuBookIcon: <MenuBookIcon />,
  StoreIcon: <StoreIcon />,
  HomeIcon: <HomeIcon />,
  StorefrontIcon: <StorefrontIcon />,
  RequestPageIcon: <RequestPageIcon />,
  HeadsetIcon: <HeadsetIcon />,
  UploadFileIcon: <UploadFileIcon />,
  DownloadIcon: <DownloadIcon />,
  OndemandVideoIcon: <OndemandVideoIcon />,
  SandboxIcon: <SandboxIcon />,
  BarChartIcon: <BarChartIcon />,
  AnnouncementIcon: <AnnouncementIcon />,
  ContentCopyIcon: <ContentCopyIcon />,
  EmojiEventsIcon: <EmojiEventsIcon />,
  TrendingUpIcon: <TrendingUpIcon />,
  InfoIcon: <InfoIcon />,
  ErrorOutlineIcon: <ErrorOutlineIcon />,
  WbSunnyIcon: <WbSunnyIcon />,
  DialpadIcon: <DialpadIcon />,
  AppsIcon: <AppsIcon />,
  WhatshotIcon: <WhatshotIcon />,
  PhoneIcon: <PhoneIcon />,
  AddIcon: <AddIcon />,
  CheckCircleIcon: <CheckCircleIcon />,
  CancelIcon: <CancelIcon />,
  FitnessCenterIcon: <FitnessCenterIcon />,
  PeopleIcon: <PeopleIcon />,
  SchoolIcon: <SchoolIcon />,
  AdminPanelSettingsIcon: <AdminPanelSettingsIcon />,
  RocketLaunchIcon: <RocketLaunchIcon />,
  UpgradeIcon: <UpgradeIcon />,
  BusinessIcon: <BusinessIcon />,
  CompareArrowsIcon: <CompareArrowsIcon />,
};

const polarisHopKeyframes = keyframes`
  0% {
    transform: translateY(0) scale(1);
  }
  30% {
    transform: translateY(-10px) scale(1.03);
  }
  60% {
    transform: translateY(0) scale(0.98);
  }
  100% {
    transform: translateY(0) scale(1);
  }
`;

const normalizeTeamLabel = (value) => (value || '').toString().trim().toLowerCase();
const normalizeAgentKey = (value) => (value || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();
const normalizeEmailKey = (value) => (value || '').toString().trim().toLowerCase();
const normalizeCompactKey = (value) => normalizeAgentKey(value).replace(/\s+/g, '');
const toTrimmedString = (value) => (value == null ? '' : String(value).trim());
const normalizeMeetingPayload = (input = {}) => {
  const toArray = (list) => (Array.isArray(list) ? list : []);
  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  return {
    scheduled_meetings: toNumber(input.scheduled_meetings ?? input.scheduledCount ?? 0),
    missed_meetings: toNumber(input.missed_meetings ?? input.missedCount ?? 0),
    scheduled_times: toArray(input.scheduled_times),
    missed_times: toArray(input.missed_times),
  };
};
const isOldAgentDescriptor = (record = {}) => {
  const isLegacyLabel = (value) => {
    if (!value) return false;
    const text = String(value).toLowerCase();
    if (!text) return false;
    if (text.includes('old agent')) return true;
    if (text.includes('old roster')) return true;
    if (text.includes('roster & member senior manager')) return true;
    if (text.includes('roster and member senior manager')) return true;
    return false;
  };
  if (typeof record === 'string') {
    return isLegacyLabel(record);
  }
  const fields = [
    record.team_name,
    record.teamName,
    record.membership,
    record.company,
    record.organization,
    record.org,
    record.full_name,
    record.agent_name,
    record.name,
  ];
  return fields.some(isLegacyLabel);
};
const normalizeCompanyKey = (value) => {
  const key = (value || '').toString().trim().toLowerCase();
  if (!key) return 'sabal';
  if (key.includes('ahm') || key.includes('alliance health')) return 'ahm';
  if (key.includes('sabal')) return 'sabal';
  return key.replace(/[^a-z0-9]/g, '') || 'sabal';
};

const getComplianceChipColor = (score) => {
  if (!Number.isFinite(score)) return 'default';
  if (score >= 75) return 'success';
  if (score >= 70) return 'warning';
  return 'error';
};

const buildUniqueAgentLists = (agents = []) => {
  const names = new Map();
  const emails = new Map();
  agents.forEach(({ name, email }) => {
    const trimmedName = (name || '').trim();
    if (trimmedName) {
      const nameKey = normalizeAgentKey(trimmedName);
      if (nameKey && !names.has(nameKey)) {
        names.set(nameKey, trimmedName);
      }
    }
    const trimmedEmail = (email || '').trim();
    if (trimmedEmail) {
      const emailKey = normalizeEmailKey(trimmedEmail);
      if (emailKey && !emails.has(emailKey)) {
        emails.set(emailKey, trimmedEmail);
      }
    }
  });
  return {
    names: Array.from(names.values()),
    emails: Array.from(emails.values()),
  };
};
const formatTeamDisplayName = (value) => {
  if (!value) return null;
  return String(value)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatChipValue = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (!text) return '';
  if (!/[_-]/.test(text)) return text;
  return text
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word) => {
      if (!word) return '';
      if (word.toUpperCase() === word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .filter(Boolean)
    .join(' ');
};

const TEAM_KPI_STANDARD_OPTIONS = {
  sales: {
    label: 'Closed Sales',
    isAbove: (metrics = {}) => {
      const value = Number(metrics.sales ?? 0);
      return Number.isFinite(value) ? value > 15 : false;
    },
    isBelow: (metrics = {}) => {
      const value = Number(metrics.sales ?? 0);
      return Number.isFinite(value) ? value <= 15 : false;
    },
  },
  conversion: {
    label: 'Call Conversion',
    isAbove: (metrics = {}) => !metrics.conversionLow,
    isBelow: (metrics = {}) => Boolean(metrics.conversionLow),
  },
  ipc: {
    label: 'IPC Sales',
    isAbove: (metrics = {}) => !metrics.ipcHigh,
    isBelow: (metrics = {}) => Boolean(metrics.ipcHigh),
  },
};

const ANCILLARY_KEY_CONFIG = [
  { key: 'ma_mapd', label: 'MA/MAPD' },
  { key: 'hip', label: 'HIP' },
  { key: 'pdp', label: 'PDP' },
  { key: 'cancer', label: 'Cancer' },
  { key: 'supp', label: 'SUPP' },
  { key: 'dvh', label: 'DVH' },
  { key: 'life', label: 'Life' },
  { key: 'total', label: 'Total' },
];

const normalizeAncillaryBreakdown = (breakdown = {}, totalSales = 0) => {
  const normalized = {};
  ANCILLARY_KEY_CONFIG.forEach(({ key }) => {
    if (key === 'total') return;
    const value = Number(breakdown[key]);
    normalized[key] = Number.isFinite(value) ? value : 0;
  });
  normalized.total = Number.isFinite(Number(totalSales))
    ? Number(totalSales)
    : Object.values(normalized).reduce((sum, value) => sum + value, 0);
  return normalized;
};

const formatAncillaryEntries = (ancillary = {}) =>
  ANCILLARY_KEY_CONFIG.map(({ key, label }) => ({
    key,
    label,
    value: Number.isFinite(Number(ancillary[key])) ? Number(ancillary[key]) : 0,
  }));

const mergeAncillaryTotals = (target = {}, source = {}) => {
  if (!source || typeof source !== 'object') return target;
  ANCILLARY_KEY_CONFIG.forEach(({ key }) => {
    const value = Number(source[key]);
    if (!Number.isFinite(value) || value === 0) return;
    target[key] = (target[key] || 0) + value;
  });
  return target;
};

const hasAncillaryCounts = (counts = {}) =>
  ANCILLARY_KEY_CONFIG.some(({ key }) => Number(counts[key]) > 0);

const buildAncillaryMetricCards = (counts = {}, { includeTotal = false } = {}) =>
  ANCILLARY_KEY_CONFIG
    .filter(({ key }) => includeTotal || key !== 'total')
    .map(({ key, label }) => ({ key, label, value: Number(counts[key]) || 0 }))
    .filter(({ value }) => value > 0)
    .map(({ key, label, value }) => ({
      label: key === 'total' ? label : `${label} Sales`,
      value,
      caption: key === 'total' ? 'All ancillary products' : 'Ancillary count',
      accent: key === 'total' ? 'secondary.main' : 'info.main',
    }));

const buildPublisherLiveRows = (report) => {
  if (!report || !report.publishers) return [];
  const rows = [];
  const overallCurrency =
    report.overall?.currency ||
    report.overall?.Currency ||
    'USD';
  Object.entries(report.publishers).forEach(([publisherKey, campaigns]) => {
    if (!campaigns) return;
    let totalCalls = 0;
    let salesCount = 0;
    let billedCalls = 0;
    let billedCost = 0;
    let currency = null;
    Object.values(campaigns).forEach((campaign) => {
      if (!campaign) return;
      const numericTotalCalls = Number(campaign.total_calls);
      if (Number.isFinite(numericTotalCalls) && numericTotalCalls > 0) {
        totalCalls += numericTotalCalls;
      }
      const numericSales =
        Number(
          campaign.sales_count != null
            ? campaign.sales_count
            : campaign.sales
        );
      if (Number.isFinite(numericSales) && numericSales > 0) {
        salesCount += numericSales;
      }
      const numericBilledCalls =
        Number(
          campaign.billed_calls_count != null
            ? campaign.billed_calls_count
            : campaign.billed_count != null
              ? campaign.billed_count
              : campaign.billed_calls
        );
      if (Number.isFinite(numericBilledCalls) && numericBilledCalls > 0) {
        billedCalls += numericBilledCalls;
      }
      const billedCostValue =
        campaign.total_cost_billed_calls != null
          ? campaign.total_cost_billed_calls
          : campaign.total_billed_cost != null
            ? campaign.total_billed_cost
            : campaign.total_cost_all_calls != null
              ? campaign.total_cost_all_calls
              : campaign.total_cost;
      const numericBilledCost = Number(billedCostValue);
      if (Number.isFinite(numericBilledCost) && numericBilledCost !== 0) {
        billedCost += numericBilledCost;
      }
      const campaignCurrency =
        campaign.currency ||
        campaign.raw?.Currency ||
        campaign.raw?.currency ||
        null;
      if (!currency && campaignCurrency) {
        currency = campaignCurrency;
      }
    });
    if (!totalCalls && !salesCount && !billedCalls && !billedCost) {
      return;
    }
    const cpaValue =
      salesCount > 0 && billedCost > 0 ? billedCost / salesCount : null;
    const totalConversionPct =
      totalCalls > 0 ? (salesCount / totalCalls) * 100 : null;
    const billedConversionPct =
      billedCalls > 0 ? (salesCount / billedCalls) * 100 : null;
    rows.push({
      id: publisherKey || `publisher-${rows.length}`,
      publisher: publisherKey || 'Unknown',
      totalCalls,
      salesCount,
      billedCalls,
      billedCost,
      cpaValue,
      totalConversionPct,
      billedConversionPct,
      currency: currency || overallCurrency,
    });
  });
  rows.sort((a, b) => {
    const aCpa = a.cpaValue;
    const bCpa = b.cpaValue;
    if (aCpa == null && bCpa == null) {
      return (b.salesCount || 0) - (a.salesCount || 0);
    }
    if (aCpa == null) return 1;
    if (bCpa == null) return -1;
    if (aCpa === bCpa) {
      return (b.salesCount || 0) - (a.salesCount || 0);
    }
    return aCpa - bCpa;
  });
  return rows;
};

const buildPersonRecord = (entry = {}, fallbackCompany = 'SABAL') => ({
  full_name: (entry?.full_name || '').trim(),
  email: (entry?.email || '').trim(),
  company: entry?.company || fallbackCompany || 'SABAL',
});

const normalizeSeniorRecord = (record) => {
  if (!record) return null;
  const senior = buildPersonRecord(record);
  const managers = Array.isArray(record?.managers)
    ? record.managers.map((manager) => ({
        ...buildPersonRecord(manager, senior.company),
        agents: (Array.isArray(manager?.agents) ? manager.agents : []).map((agent) =>
          buildPersonRecord(agent, manager?.company || senior.company)
        ),
      }))
    : [];
  const directAgents = Array.isArray(record?.direct_agents)
    ? record.direct_agents.map((agent) => buildPersonRecord(agent, senior.company))
    : Array.isArray(record?.agents)
      ? record.agents.map((agent) => buildPersonRecord(agent, senior.company))
      : [];
  return { ...senior, managers, directAgents };
};

const buildHierarchyStateFromSenior = (seniorRecord) => {
  if (!seniorRecord) return null;
  const baseId = normalizeAgentKey(seniorRecord.full_name || seniorRecord.email || 'senior');
  const managers = [];
  (seniorRecord.managers || []).forEach((manager, index) => {
    const managerName = (manager?.full_name || '').trim();
    const managerEmail = (manager?.email || '').trim();
    if (!managerName && !managerEmail) return;
    const agents = (Array.isArray(manager?.agents) ? manager.agents : [])
      .map((agent) => ({
        full_name: (agent?.full_name || '').trim(),
        email: (agent?.email || '').trim(),
        company: agent?.company || manager?.company || seniorRecord.company || 'SABAL',
      }))
      .filter((agent) => agent.full_name);
    managers.push({
      id:
        managerEmail ||
        (managerName ? `${baseId || 'manager'}-${normalizeAgentKey(managerName)}` : `manager-${index}`),
      full_name: managerName || managerEmail || `Manager ${index + 1}`,
      email: managerEmail,
      company: manager?.company || seniorRecord.company || 'SABAL',
      agents,
    });
  });
  const directAgents = (seniorRecord.directAgents || [])
    .map((agent) => ({
      full_name: (agent?.full_name || '').trim(),
      email: (agent?.email || '').trim(),
      company: agent?.company || seniorRecord.company || 'SABAL',
    }))
    .filter((agent) => agent.full_name);
  if (directAgents.length) {
    managers.push({
      id: `${baseId || 'senior'}-direct`,
      full_name: seniorRecord.full_name
        ? `${seniorRecord.full_name} (Direct Reports)`
        : 'Direct Reports',
      email: seniorRecord.email || '',
      company: seniorRecord.company || 'SABAL',
      isDirect: true,
      agents: directAgents,
    });
  }
  return { managers, senior: seniorRecord };
};

const flattenHierarchyAgents = (hierarchyState, { excludeOldAgents = false } = {}) => {
  const agents = [];
  if (!hierarchyState) return agents;
  (hierarchyState.managers || []).forEach((manager) => {
    (manager.agents || []).forEach((agent) => {
      if (excludeOldAgents && isOldAgentDescriptor(agent)) return;
      agents.push({ name: agent.full_name, email: agent.email });
    });
  });
  return agents;
};

const getComponentRegistry = () => ({
  Dashboard: <Dashboard />,
  FemaSepTool: <FemaSepTool />,
  PolicyMatchTool: <PolicyMatchTool />,
  DoctorSearchTool: <DoctorSearchTool />,
  MedicationRetrieverTool: <MedicationRetrieverTool />,
  MARxCheckTool: <MARxCheckTool />,
  MedicaidCheckTool: <MedicaidCheckTool />,
  ORCRequestTool: <ORCRequestTool />,
  LifeQuoteTool: <LifeQuoteTool />,
});

const extractJsExpression = (source) => {
  let cleaned = String(source || '').trim();
  if (!cleaned) return cleaned;
  cleaned = cleaned
    .replace(/^export\s+default\s+/, '')
    .replace(/^module\.exports\s*=\s*/, '')
    .replace(/^window\.[A-Za-z0-9_]+\s*=\s*/, '')
    .replace(/^const\s+[A-Za-z0-9_]+\s*=\s*/, '')
    .replace(/^let\s+[A-Za-z0-9_]+\s*=\s*/, '')
    .replace(/^var\s+[A-Za-z0-9_]+\s*=\s*/, '')
    .replace(/;\s*$/, '');

  if (!/^[{[]/.test(cleaned)) {
    const braceIndex = cleaned.indexOf('{');
    const bracketIndex = cleaned.indexOf('[');
    const candidates = [braceIndex, bracketIndex].filter((i) => i !== -1);
    if (candidates.length) {
      const start = Math.min(...candidates);
      const endBrace = cleaned.lastIndexOf('}');
      const endBracket = cleaned.lastIndexOf(']');
      const endCandidates = [endBrace, endBracket].filter((i) => i !== -1);
      const end = endCandidates.length ? Math.max(...endCandidates) : cleaned.length - 1;
      if (start !== -1 && end >= start) {
        cleaned = cleaned.slice(start, end + 1);
      }
    }
  }
  return cleaned;
};

const encodeJsxAttrs = (attrsRaw) => {
  const s = String(attrsRaw || '').trim();
  if (!s) return '';
  const pick = (name) => {
    const m = s.match(new RegExp(`${name}\\s*=\\s*['"]([^'"]+)['"]`));
    return m ? m[1] : '';
  };
  const letter = pick('letter');
  const bgColor = pick('bgColor');
  const color = pick('color');
  return [letter, bgColor, color].map((v) => (v || '').replace(/__/g, '_')).join('__');
};

const addJsxPlaceholders = (input) => {
  const jsxToPlaceholder = (prop) => new RegExp(`${prop}\\s*:\\s*<([A-Za-z0-9_]+)([^>]*)\\/>`, 'g');
  return String(input || '')
    .replace(jsxToPlaceholder('icon'), (_m, name, attrs) => `icon: "__JSX__icon__${name}__${encodeJsxAttrs(attrs)}__"`)
    .replace(jsxToPlaceholder('sidebarIcon'), (_m, name, attrs) => `sidebarIcon: "__JSX__sidebarIcon__${name}__${encodeJsxAttrs(attrs)}__"`)
    .replace(jsxToPlaceholder('component'), (_m, name) => `component: "__JSX__component__${name}____"`)
    .replace(/<([A-Za-z0-9_]+)([^>]*)\/>/g, (_m, name, attrs) => `"__JSX__auto__${name}__${encodeJsxAttrs(attrs)}__"`);
};

const rehydrateStructure = (value) => {
  if (Array.isArray(value)) return value.map(rehydrateStructure);
  if (value && typeof value === 'object') {
    const out = {};
    for (const key in value) out[key] = rehydrateStructure(value[key]);
    return out;
  }
  if (typeof value === 'string' && value.startsWith('__JSX__')) {
    const parts = value.split('__');
    const type = parts[2];
    const name = parts[3];
    const letter = parts[4] || '';
    const bgColor = parts[5] || '';
    const color = parts[6] || '';
    if (type === 'icon' || type === 'sidebarIcon' || type === 'auto') {
      if (name === 'CarrierIcon') {
        return <CarrierIcon letter={letter || undefined} bgColor={bgColor || undefined} color={color || undefined} />;
      }
      return ICON_REGISTRY[name] || null;
    }
    if (type === 'component') {
      const registry = getComponentRegistry();
      return registry[name] || null;
    }
  }
  return value;
};

const parseRemoteStructure = (source) => {
  if (source == null) return source;
  const cleaned = addJsxPlaceholders(extractJsExpression(source));
  if (!cleaned) return cleaned;
  let evaluated;
  try {
    // eslint-disable-next-line no-new-func
    evaluated = new Function(`return (${cleaned})`)();
  } catch (error) {
    throw new Error(`Failed to evaluate remote structure: ${error.message}`);
  }
  return rehydrateStructure(evaluated);
};

const NAV_ITEM_KEYS = ['topLevelItems', 'allToolItems', 'allAncillaryItems', 'carrierItems', 'trainingItems'];

const createEmptyNavItems = () => NAV_ITEM_KEYS.reduce((acc, key) => {
  acc[key] = [];
  return acc;
}, {});

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null); // New state to handle auth errors
  
  // --- STATE FOR DASHBOARD DATA ---
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [teamName, setTeamName] = useState(null);
  const [teamRosterStatus, setTeamRosterStatus] = useState('loading');
  const [teamDisplayName, setTeamDisplayName] = useState(null);
  const [teamRoster, setTeamRoster] = useState([]);
  const [polarisProfile, setPolarisProfile] = useState({
    userId: null,
    threadId: null,
    status: null,
    viciDialerLoginUrl: null,
  });
  const [shouldAnimatePolarisIcon, setShouldAnimatePolarisIcon] = useState(false);
  const [agentDirectoryProfile, setAgentDirectoryProfile] = useState(null);
  const agentDirectoryEmailRef = useRef(null);

  const safeStorageGet = useCallback((key) => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }, [SLACK_OAUTH_AUTHORIZE_URL, SLACK_CLIENT_ID]);

  const safeStorageSet = useCallback((key, value) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value);
    } catch (_) {
      // ignore private browsing / quota errors
    }
  }, []);

  // --- STATE FOR DERIVED PERMISSIONS ---
  const [derivedPermissions, setDerivedPermissions] = useState({
    needsOnboarding: false,
    hasAdminReports: false,
    hasRetentionAccess: false,
    hasAcaPermission: false,
    lifeInsuranceAccessLevel: 'none',
    getAdminReportButtons: [],
    restrictedPageIds: [],
    hasExecutiveDashboard: false,
  });

  // Normalize email + build variants for endpoints that accept domain-only identifiers
  const normalizeEmail = (email) => {
    try {
      return String(email || '').trim();
    } catch (_) {
      return email;
    }
  };

  const buildPermissionEmailList = (email) => {
    const primary = normalizeEmail(email);
    const emailCandidates = [];
    let domainCandidate = null;

    if (primary) {
      emailCandidates.push(primary);
      const lowerVariant = primary.toLowerCase();
      if (lowerVariant && lowerVariant !== primary) {
        emailCandidates.push(lowerVariant);
      }
      try {
        if (lowerVariant && !lowerVariant.includes('sabalgrp')) {
          const at = primary.indexOf('@');
          if (at !== -1) {
            const domainOnly = primary.slice(at).toLowerCase();
            if (domainOnly) {
              domainCandidate = domainOnly;
            }
          }
        }
      } catch (_) {}
    }

    return { emailCandidates, domainCandidate };
  };

  const fetchAgentDirectoryProfile = useCallback(async (rawEmail) => {
    const sourceEmail = rawEmail ?? safeStorageGet('activeUserEmail');
    const email = sourceEmail ? String(sourceEmail).trim().toLowerCase() : null;
    if (!email) return null;

    safeStorageSet('activeUserEmail', email);

    const query = new URLSearchParams({ email });
    const response = await fetch(`${AGENT_DIRECTORY_ENDPOINT}?${query.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload = await response.json();

    let profile = null;
    if (payload?.status === 'success') {
      const upstream = payload?.response;
      const extracted =
        upstream && typeof upstream === 'object'
          ? (upstream.response && typeof upstream.response === 'object'
              ? upstream.response
              : upstream)
          : null;

      if (extracted && typeof extracted === 'object') {
        profile = {
          ...extracted,
          _meta: {
            timestamp: payload.timestamp || upstream?.timestamp || null,
            requested_email: payload.requested_email || upstream?.requested_email || email,
          },
        };
      }
    }

    if (profile?.agent_name) {
      setUser((prev) => {
        if (!prev) return prev;
        const prevName = (prev.name || '').trim();
        const emailLocal = String(prev.email || '').split('@')[0] || '';
        const lowerEmailLocal = emailLocal.toLowerCase();
        if (!prevName || prevName.toLowerCase() === lowerEmailLocal) {
          if (prevName === profile.agent_name) return prev;
          return { ...prev, name: profile.agent_name };
        }
        return prev;
      });
    }

    agentDirectoryEmailRef.current = email;
    return profile;
  }, [safeStorageGet, safeStorageSet, setUser]);

  const fetchDashboardData = useCallback(async (email, { showLoader = true } = {}) => {
    if (!email) return;
    if (showLoader) setDashboardLoading(true);
    try {
      const data = await api.post('/adaptive-vega-log', { userEmail: normalizeEmail(email) });
      setDashboardData(data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardData(null);
    } finally {
      if (showLoader) setDashboardLoading(false);
    }
  }, []);
  const refreshDashboardData = useCallback(
    async ({ showLoader = false } = {}) => {
      if (!user?.email) return;
      await fetchDashboardData(user.email, { showLoader });
    },
    [fetchDashboardData, user?.email]
  );

  const toRosterKey = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[._-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const buildRosterCandidateKeys = (userName, userEmail) => {
    const keys = new Set();
    const push = (val) => {
      const key = toRosterKey(val);
      if (!key) return;
      keys.add(key);
      const compact = key.replace(/\s+/g, '');
      if (compact) keys.add(compact);
    };

    push(userName);
    try {
      const local = String(userEmail || '').split('@')[0];
      if (local) {
        push(local);
        push(local.replace(/[._-]+/g, ' '));
      }
    } catch (_) {}

    return keys;
  };

  const fetchTeamRoster = async (userName, userEmail) => {
    try {
      const rosterText = await api.get('/load-team-roster');
      const lines = rosterText.trim().split('\n');
      if (lines.length < 2) {
        setTeamName(null);
        setTeamDisplayName(null);
        setTeamRoster([]);
        setTeamRosterStatus('empty');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const roster = lines.slice(1).map(line => {
        const values = line.split(',');
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index] ? values[index].trim() : '';
          return obj;
        }, {});
      });

      setTeamRoster(roster);
      const candidates = buildRosterCandidateKeys(userName, userEmail);
      const userEntry = roster.find(member => {
        const key = toRosterKey(member.agent_name);
        if (!key) return false;
        if (candidates.has(key)) return true;
        const compact = key.replace(/\s+/g, '');
        return candidates.has(compact);
      });
      if (!userEntry) {
        setTeamName(null);
        setTeamDisplayName(null);
        setTeamRosterStatus('not-found');
        return;
      }

      const rawTeamName = userEntry.team_name || null;
      setTeamName(rawTeamName);
      setTeamDisplayName(formatTeamDisplayName(rawTeamName));
      setTeamRosterStatus(rawTeamName && rawTeamName.toLowerCase().includes('old agent team') ? 'legacy' : 'active');
    } catch (error) {
      console.error("Error fetching team roster:", error);
      setTeamName(null);
      setTeamDisplayName(null);
      setTeamRoster([]);
      setTeamRosterStatus('error');
    }
  };

  const fetchPolarisProfile = useCallback(async (userEmail, userName) => {
    if (!userEmail) {
      setPolarisProfile({
        userId: null,
        threadId: null,
        status: null,
        viciDialerLoginUrl: null,
      });
      setAgentDirectoryProfile(null);
      agentDirectoryEmailRef.current = null;
      return {
        userId: null,
        threadId: null,
        status: null,
        viciDialerLoginUrl: null,
      };
    }
    try {
      const response = await fetch(POLARIS_APP_USER_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, user_full_name: userName || undefined })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }
      const data = await response.json();
      const userId = data.user_id || data.userId || null;
      const status = data.user_status || data.userStatus || data.status || 'active';
      const threadId = data.thread_id
        || data.threadId
        || (Array.isArray(data.thread_ids) && data.thread_ids.length ? data.thread_ids[0] : null)
        || null;

      const pickFirstString = (...values) => {
        for (const value of values) {
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed) return trimmed;
          }
        }
        return null;
      };

      let viciDialerLoginUrl = pickFirstString(
        data?.vici_dialer_login_url,
        data?.vici_dialer_login_link,
        data?.viciDialerLoginUrl,
        data?.viciDialerLoginLink,
        data?.vici_dialer_url,
        data?.viciDialerUrl,
        data?.vici_url,
        data?.viciUrl,
        data?.vicidial_url,
        data?.vicidialUrl,
        data?.dialer_login_url,
        data?.dialerLoginUrl,
        data?.links?.viciDialerLoginUrl,
        data?.links?.viciDialerUrl,
        data?.links?.vici_dialer_login_url,
        data?.links?.vici_dialer_url,
        data?.dialer?.login_url,
        data?.dialer?.loginUrl
      );

      if (!viciDialerLoginUrl && data && typeof data === 'object') {
        const queue = [data];
        const visited = new Set();
        while (queue.length && !viciDialerLoginUrl) {
          const current = queue.shift();
          if (!current || typeof current !== 'object') continue;
          if (visited.has(current)) continue;
          visited.add(current);
          for (const [key, value] of Object.entries(current)) {
            if (typeof value === 'string') {
              const normalizedKey = key.toLowerCase();
              if ((normalizedKey.includes('vici') || normalizedKey.includes('dialer')) && normalizedKey.includes('url')) {
                const trimmed = value.trim();
                if (trimmed && /^https?:\/\//i.test(trimmed)) {
                  viciDialerLoginUrl = trimmed;
                  break;
                }
              }
            } else if (value && typeof value === 'object') {
              queue.push(value);
            }
          }
        }
      }

      const profile = { userId, threadId, status, viciDialerLoginUrl };
      setPolarisProfile(profile);
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('activeUserEmail', userEmail);
          if (userName) localStorage.setItem('activeUserName', userName);
          if (userId) localStorage.setItem('polarisUserId', userId);
          if (threadId) localStorage.setItem('polarisThreadId', threadId);
          if (status) localStorage.setItem('polarisUserStatus', status);
        } catch (_) {}
      }
      return profile;
    } catch (error) {
      console.error('Failed to resolve Polaris profile:', error);
      const fallbackProfile = {
        userId: null,
        threadId: null,
        status: null,
        viciDialerLoginUrl: null,
      };
      setPolarisProfile(fallbackProfile);
      return fallbackProfile;
    }
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Determine the Access base path and provide a fallback between root and /dashboard
        const inferAccessBase = () => {
          const p = window.location.pathname || '';
          return p.startsWith('/dashboard') ? '/dashboard' : '';
        };
        const ACCESS_BASE = (process.env.REACT_APP_ACCESS_BASE_PATH || inferAccessBase()).replace(/\/$/, '');

        const fetchAccess = async (endpoint) => {
          const primary = `${ACCESS_BASE}${endpoint}`;
          const altBase = ACCESS_BASE === '' ? '/dashboard' : '';
          const alt = `${altBase}${endpoint}`;
          let res;
          try {
            res = await fetch(primary, { credentials: 'include' });
          } catch (_) {}
          if (!res || !res.ok || res.status === 404) {
            try {
              const resAlt = await fetch(alt, { credentials: 'include' });
              if (resAlt) return resAlt;
            } catch (_) {}
          }
          return res;
        };

        // Fetch user identity from Cloudflare's endpoint
        let identity = null;
        try {
          const identityResponse = await fetchAccess('/cdn-cgi/access/get-identity');
          if (identityResponse && identityResponse.ok) {
            identity = await identityResponse.json();
          }
        } catch (e) {
          // non-fatal; we'll fall back to dev/local values below
        }

        // Prefer identity endpoint for normalized fields
        const rawEmail = identity?.email;
        let normalizedEmail = rawEmail ? String(rawEmail).trim() : null;
        let email = normalizedEmail ? normalizedEmail.toLowerCase() : null;

        // Dev/non-Access fallback: allow specifying email via URL or localStorage
        if (!normalizedEmail) {
          try {
            const params = new URLSearchParams(window.location.search || '');
            const fallbackCandidates = [
              params.get('email'),
              params.get('userEmail'),
              params.get('e'),
              params.get('u'),
              localStorage.getItem('devUserEmail'),
              localStorage.getItem('userEmail'),
              localStorage.getItem('activeUserEmail'),
              process.env.REACT_APP_DEV_USER_EMAIL
            ]
              .map(v => (v ? String(v).trim() : null))
              .filter(Boolean);
            normalizedEmail = fallbackCandidates.length ? fallbackCandidates[0] : null;
            email = normalizedEmail ? normalizedEmail.toLowerCase() : null;
            if (normalizedEmail) console.warn('Using fallback user email (no Cloudflare token found):', normalizedEmail);
          } catch (_) {}
        }

        let name = identity?.name;
        if (!name && (normalizedEmail || email)) {
          // Derive a display name from the email for stability in non-Access environments
          const sourceEmail = normalizedEmail || email;
          name = String(sourceEmail).split('@')[0];
        }
        const id = identity?.id || null;

        if (!normalizedEmail) {
          throw new Error('Authenticated, but could not determine user email from JWT.');
        }

        console.log("Authenticated via Cloudflare Access:", normalizedEmail);

        const cloudflareUser = { email, originalEmail: normalizedEmail, name, id };

        // Set the user and fetch their associated data
        setUser(cloudflareUser);

        try {
          const directoryProfile = await fetchAgentDirectoryProfile(cloudflareUser.email);
          setAgentDirectoryProfile(directoryProfile);
        } catch (error) {
          agentDirectoryEmailRef.current = cloudflareUser.email;
          console.error('Failed to load agent directory profile during authentication:', error);
          setAgentDirectoryProfile(null);
        }

        await checkUserAccess(cloudflareUser.originalEmail || cloudflareUser.email);
        await fetchDashboardData(cloudflareUser.email, { showLoader: true });
        await fetchTeamRoster(cloudflareUser.name, cloudflareUser.email);
        await fetchPolarisProfile(cloudflareUser.email, cloudflareUser.name);
        
      } catch (error) {
        // --- Failure: Cloudflare authentication failed ---
        console.error(`Authentication failed: ${error.message}`);
        // Set an error message to be displayed to the user
        setAuthError(error.message || "An unknown authentication error occurred.");
        // Stop all loading spinners
        setLoading(false);
        setDashboardLoading(false);
        setPolarisProfile({
          userId: null,
          threadId: null,
          status: null,
          viciDialerLoginUrl: null,
        });
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user?.email) {
      setShouldAnimatePolarisIcon(false);
      setAgentDirectoryProfile(null);
      return;
    }

    const storageKey = 'polarisLoginCounts';
    let record = {};
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          record = parsed;
        }
      }
    } catch (err) {
      console.warn('Failed to read Polaris login counts from storage', err);
    }

    const emailKey = String(user.email).toLowerCase();
    const previousCount = Number.isFinite(Number(record[emailKey])) ? Number(record[emailKey]) : 0;
    const nextCount = previousCount + 1;
    record[emailKey] = nextCount;

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(record));
    } catch (err) {
      console.warn('Failed to persist Polaris login counts to storage', err);
    }

    setShouldAnimatePolarisIcon(nextCount <= 10);
  }, [user?.email]);

  useEffect(() => {
    const targetEmail = user?.email ? String(user.email).trim().toLowerCase() : null;
    if (!targetEmail) {
      setAgentDirectoryProfile(null);
      agentDirectoryEmailRef.current = null;
      return;
    }

    if (agentDirectoryEmailRef.current === targetEmail) return;

    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchAgentDirectoryProfile(targetEmail);
        if (cancelled) return;
        setAgentDirectoryProfile(profile);
      } catch (error) {
        if (cancelled) return;
        agentDirectoryEmailRef.current = targetEmail;
        console.error('Failed to fetch agent directory profile:', error);
        setAgentDirectoryProfile(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email, fetchAgentDirectoryProfile]);

  useEffect(() => {
    if (loading || !permissions) return;

    // Onboarding disabled: do not compute or apply onboarding gating
    // const isOnboarded = permissions.some(p => p.div === 'onboard' && (p.reportButtons === 'true' || p.reportButtons === true));
    const needsOnboarding = false;

    const hasAdminReports = permissions.some(p => p.div === 'admin-reports');
    const hasRetentionAccess = permissions.some(p => p.div === 'retention-specific');
    const hasAcaPermission = permissions.some(p => String(p.div || '').toLowerCase() === 'aca');
    let lifeInsuranceAccessLevel = 'none';
    const lifePerm = permissions.find(p => p.div === 'life-insurance');
    if (lifePerm) {
      lifeInsuranceAccessLevel = lifePerm.reportButtons === 'restricted' ? 'restricted' : 'full';
    }

    const hasExecutiveDashboard = permissions.some((p) => {
      if (String(p.div || '').toLowerCase() !== 'dashboard') return false;
      const rawButtons =
        p.reportButtons ??
        p.reportbuttons ??
        p['report-button'] ??
        p.button_id ??
        p.buttonId ??
        '';
      const normalized = Array.isArray(rawButtons)
        ? rawButtons.join(',')
        : typeof rawButtons === 'string'
          ? rawButtons
          : String(rawButtons || '');
      if (!normalized) return false;
      return normalized
        .toLowerCase()
        .split(',')
        .map((token) => token.trim())
        .some((token) => token === 'executive' || token.includes('executive'));
    });
    
    const adminPerm = permissions.find(p => p.div === 'admin-reports');
    let adminButtons = [];
    if (adminPerm) {
      if (adminPerm.reportButtons === 'all') adminButtons = 'all';
      else if (typeof adminPerm.reportButtons === 'string') adminButtons = adminPerm.reportButtons.split(',').map(id => id.trim().toLowerCase());
    }
    // Collect restricted page_config ids from any 'restricted' permission rows
    const restrictedPageIds = permissions
      .filter(p => (p.div || '').toLowerCase() === 'restricted')
      .flatMap(p => {
        const ids = p.button_id || p.buttonId || p.reportButtons;
        if (!ids || typeof ids !== 'string') return [];
        return ids.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      });

    setDerivedPermissions({
      needsOnboarding,
      hasAdminReports,
      hasRetentionAccess,
      hasAcaPermission,
      lifeInsuranceAccessLevel,
      getAdminReportButtons: adminButtons,
      restrictedPageIds,
      hasExecutiveDashboard,
    });
  }, [permissions, loading]);

  const checkUserAccess = async (email) => {
    setLoading(true);
    try {
      const combinedPermissions = [];
      const seen = new Set();

      const appendPermissions = (list) => {
        if (!Array.isArray(list)) return;
        list.forEach((entry) => {
          try {
            const key = JSON.stringify(entry);
            if (seen.has(key)) return;
            seen.add(key);
            combinedPermissions.push(entry);
          } catch (_) {
            combinedPermissions.push(entry);
          }
        });
      };

      const { emailCandidates, domainCandidate } = buildPermissionEmailList(email);
      const triedCandidates = new Set();

      const tryCandidate = async (candidate) => {
        if (!candidate || triedCandidates.has(candidate)) return;
        triedCandidates.add(candidate);
        try {
          const permsData = await api.getPermissions(candidate);
          appendPermissions(permsData?.permissions);
        } catch (error) {
          console.error(`Error fetching user access data (${candidate}):`, error);
        }
      };

      for (const candidate of emailCandidates) {
        await tryCandidate(candidate);
        if (combinedPermissions.length > 0) break;
      }

      if (combinedPermissions.length === 0 && domainCandidate) {
        await tryCandidate(domainCandidate);
      }

      setPermissions(combinedPermissions);
    } catch (error) {
      console.error("Error resolving user access data:", error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
      // In a Cloudflare Access environment, logout is handled by redirecting
      // to the special logout path for your Access domain.
      // Example: https://<your-team-name>.cloudflareaccess.com/cdn-cgi/access/logout
      setUser(null);
      setPolarisProfile({
        userId: null,
        threadId: null,
        status: null,
        viciDialerLoginUrl: null,
      });
      setTeamName(null);
      setTeamDisplayName(null);
      api.setAuthToken(null);
      try {
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('polarisUserId');
            localStorage.removeItem('polarisThreadId');
            localStorage.removeItem('activeUserEmail');
            localStorage.removeItem('polarisUserStatus');
            localStorage.removeItem('activeUserName');
          } catch (_) {}
        }
        // Respect base path if only a subpath is protected
        const base = (process.env.REACT_APP_ACCESS_BASE_PATH || (window.location.pathname.startsWith('/dashboard') ? '/dashboard' : '')).replace(/\/$/, '');
        window.location.href = `${base}/cdn-cgi/access/logout`;
      } catch (e) {
        // no-op
      }
  };

  const openPolarisWindow = useCallback(async () => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL('/polaris', window.location.origin);
      const storage = window.localStorage;
      const safeGet = (key) => {
        try {
          return storage.getItem(key);
        } catch (_) {
          return null;
        }
      };
      const safeSet = (key, value) => {
        try {
          storage.setItem(key, value);
        } catch (_) {}
      };

      const userEmail = user?.email || safeGet('activeUserEmail');
      const userName = user?.name || safeGet('activeUserName');
      if (userEmail) {
        url.searchParams.set('userEmail', userEmail);
        safeSet('activeUserEmail', userEmail);
      }

      if (userName) {
        url.searchParams.set('userName', userName);
        safeSet('activeUserName', userName);
      }

      let profile = polarisProfile;
      if (userEmail && (!profile?.threadId || !profile?.userId || !profile?.status)) {
        profile = await fetchPolarisProfile(userEmail, userName);
      }

      const resolvedThreadId = profile?.threadId || safeGet('polarisThreadId');
      const resolvedUserId = profile?.userId || safeGet('polarisUserId');
      const resolvedStatus = profile?.status || safeGet('polarisUserStatus');

      if (resolvedThreadId) {
        url.searchParams.set('threadId', resolvedThreadId);
        safeSet('polarisThreadId', resolvedThreadId);
      }

      if (resolvedUserId) {
        url.searchParams.set('userId', resolvedUserId);
        safeSet('polarisUserId', resolvedUserId);
      }

      if (resolvedStatus) {
        url.searchParams.set('userStatus', resolvedStatus);
        safeSet('polarisUserStatus', resolvedStatus);
      }

      const newWindow = window.open(url.toString(), 'polaris-app');
      if (newWindow) {
        try {
          newWindow.focus();
        } catch (_) {}
      } else {
        console.warn('Unable to open Polaris window. A popup blocker may be active.');
      }
    } catch (error) {
      console.error('Failed to open Polaris window:', error);
    }
  }, [user?.email, user?.name, polarisProfile, fetchPolarisProfile]);

  const hasAccess = (key) => {
    if (!key) return true;
    // Hide any page explicitly included in restrictedPageIds
    if (derivedPermissions?.restrictedPageIds?.includes?.(String(key).toLowerCase())) return false;
    switch (key) {
      case 'admin-reports':
        return !!derivedPermissions.hasAdminReports;
      case 'retention-specific':
        return !!derivedPermissions.hasRetentionAccess;
      case 'life-insurance':
        // Allow if user has any life access (restricted or full)
        return derivedPermissions.lifeInsuranceAccessLevel !== 'none';
      default:
        // All other pages are accessible by default
        return true;
    }
  };
   
  const acknowledgePolarisIconAnimation = useCallback(() => {
    setShouldAnimatePolarisIcon(false);
  }, []);

  const value = useMemo(() => ({ 
      user, 
      loading, 
      logout, 
      hasAccess,
      dashboardData,
      dashboardLoading,
      refreshDashboardData,
      teamName,
      teamDisplayName,
      teamRoster,
      teamRosterStatus,
      polarisUser: polarisProfile,
      polarisThreadId: polarisProfile.threadId,
      openPolarisWindow,
      shouldAnimatePolarisIcon,
      acknowledgePolarisIconAnimation,
      agentDirectoryProfile,
      ...derivedPermissions
  }), [user, loading, derivedPermissions, dashboardData, dashboardLoading, refreshDashboardData, teamName, teamDisplayName, teamRoster, teamRosterStatus, polarisProfile, openPolarisWindow, shouldAnimatePolarisIcon, acknowledgePolarisIconAnimation, agentDirectoryProfile]);

  // If an authentication error occurred, render an error screen instead of the app
  if (authError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3, textAlign: 'center', backgroundColor: 'background.default' }}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom>
          Authentication Required
        </Typography>
        <Typography color="text.secondary">
          {authError}
        </Typography>
      </Box>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const SearchProvider = ({ children, searchableData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const value = useMemo(() => ({ searchTerm, setSearchTerm, searchableData }), [searchTerm, searchableData]);
  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

const ModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const openModal = async (modalTitle, actionValueOrComponent) => {
    setTitle(modalTitle);
    setIsOpen(true);
    setIsLoading(true);

    try {
      if (typeof actionValueOrComponent === "function" || React.isValidElement(actionValueOrComponent)) {
        // Already a component, render directly!
        setContent(actionValueOrComponent);
      } else {
        // Old logic: treat as actionValue for API
        const html = await api.toggleForm(actionValueOrComponent);
        setContent(html);
      }
    } catch (error) {
      setContent(<p style={{color:'red'}}>Error loading content.</p>);
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setTitle('');
    setContent('');
  };

  const value = useMemo(() => ({ isOpen, isLoading, title, content, openModal, closeModal }), [isOpen, isLoading, title, content]);
  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

const NavItemsProvider = ({ children }) => {
  const { agentDirectoryProfile, hasAcaPermission } = useAuth();
  const [navItems, setNavItems] = useState(() => createEmptyNavItems());
  const [loadingNavItems, setLoadingNavItems] = useState(true);
  const [navItemsError, setNavItemsError] = useState(null);
  const [acaItems, setAcaItems] = useState([]);
  const [loadingAcaItems, setLoadingAcaItems] = useState(false);
  const [acaItemsError, setAcaItemsError] = useState(null);
  const [acaItemsFetched, setAcaItemsFetched] = useState(false);
  const acaFetchInFlight = useRef(false);

  const loadNavItems = useCallback(async () => {
    setLoadingNavItems(true);
    setNavItemsError(null);
    try {
      const data = await api.get('/load-top-level-items');
      const parsedItems = createEmptyNavItems();

      if (data && typeof data === 'object') {
        for (const key of NAV_ITEM_KEYS) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            try {
              const structure = parseRemoteStructure(data[key]);
              parsedItems[key] = Array.isArray(structure) ? structure : (structure || []);
            } catch (parseError) {
              console.error(`[load-top-level-items] Failed to parse ${key}:`, parseError);
              parsedItems[key] = [];
            }
          }
        }
      }

      const hasZing = parsedItems.carrierItems.some((item) => String(item?.key || '').toLowerCase() === 'zing-health');
      if (!hasZing) {
        parsedItems.carrierItems = [
          ...parsedItems.carrierItems,
          { key: 'zing-health', label: 'Zing Health', icon: <CarrierIcon letter="Z" bgColor="#5fb83b" /> },
        ];
      }

      setNavItems(parsedItems);
    } catch (error) {
      console.error('Failed to load navigation items:', error);
      setNavItems(createEmptyNavItems());
      setNavItemsError(error?.message || 'Failed to load navigation items');
    } finally {
      setLoadingNavItems(false);
    }
  }, []);

  useEffect(() => {
    loadNavItems();
  }, [loadNavItems]);

  const wantsAcaMenu = useMemo(() => {
    const typeValue = agentDirectoryProfile?.agent_type;
    const typeMatches = typeof typeValue === 'string' && typeValue.toLowerCase().includes('aca');
    return typeMatches || !!hasAcaPermission;
  }, [agentDirectoryProfile?.agent_type, hasAcaPermission]);

  useEffect(() => {
    if (!wantsAcaMenu) {
      setAcaItems([]);
      setAcaItemsError(null);
      setAcaItemsFetched(false);
      setLoadingAcaItems(false);
    }
  }, [wantsAcaMenu]);

  useEffect(() => {
    if (!wantsAcaMenu || acaItemsFetched || acaFetchInFlight.current) {
      return;
    }

    let cancelled = false;
    acaFetchInFlight.current = true;
    setLoadingAcaItems(true);
    setAcaItemsError(null);

    const fetchAcaItems = async () => {
      try {
        const data = await api.get('/load-aca-items');
        const raw = typeof data === 'string' ? data : data?.acaItems;
        const parsed = parseRemoteStructure(raw);
        if (cancelled) return;
        setAcaItems(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load ACA navigation items:', error);
        setAcaItems([]);
        setAcaItemsError(error?.message || 'Failed to load ACA navigation items');
      } finally {
        acaFetchInFlight.current = false;
        if (cancelled) return;
        setLoadingAcaItems(false);
        setAcaItemsFetched(true);
      }
    };

    fetchAcaItems();

    return () => {
      cancelled = true;
    };
  }, [wantsAcaMenu, acaItemsFetched]);

  const reloadAcaItems = useCallback(() => {
    setAcaItems([]);
    setAcaItemsError(null);
    setAcaItemsFetched(false);
  }, []);

  const value = useMemo(() => ({
    navItems,
    loadingNavItems,
    navItemsError,
    reloadNavItems: loadNavItems,
    acaItems,
    loadingAcaItems,
    acaItemsError,
    reloadAcaItems,
  }), [navItems, loadingNavItems, navItemsError, loadNavItems, acaItems, loadingAcaItems, acaItemsError, reloadAcaItems]);

  return <NavItemsContext.Provider value={value}>{children}</NavItemsContext.Provider>;
};

// --- PAGES CONFIG PROVIDER ---
const PagesProvider = ({ children }) => {
  // Holds only the remotely loaded pages config
  const [pagesConfig, setPagesConfig] = useState({});
  const [loadingPages, setLoadingPages] = useState(true);
  const [error, setError] = useState(null);

  const loadPagesConfig = useCallback(async () => {
    setLoadingPages(true);
    setError(null);
    try {
      // Fetch JavaScript-wrapped config from server
      const jsText = await api.get('/load-resource-pages');
      const content = String(jsText || '').trim();
      const looksLikeHtml = /^\s*</.test(content) || /<html/i.test(content);
      if (looksLikeHtml) {
        throw new Error('Received HTML instead of pages config');
      }

      const parsed = parseRemoteStructure(content);

      if (parsed && typeof parsed === 'object') {
        setPagesConfig(parsed);
      }
    } catch (err) {
      console.error('Failed to load remote pages config:', err);
      setError(err?.message || 'Failed to load pages config');
      // Keep existing (local) pagesConfig as fallback
    } finally {
      setLoadingPages(false);
    }
  }, []);

  useEffect(() => {
    loadPagesConfig();
  }, [loadPagesConfig]);

  const value = useMemo(() => ({ pagesConfig, loadingPages, error, reloadPages: loadPagesConfig }), [pagesConfig, loadingPages, error, loadPagesConfig]);
  return <PagesContext.Provider value={value}>{children}</PagesContext.Provider>;
};


// --- THEME & STYLING (For Dashboard) ---
const CATCH_GRADIENT = 'linear-gradient(108deg, #2c47a1, #6b3acc 83%)';
const ORANGE_GRADIENT = 'linear-gradient(108deg, #ffb823, #f78f1e 83%)';
const GREEN_GRADIENT = 'linear-gradient(108deg, #1a8f35,rgb(82, 194, 89) 83%)';

const UIContext = createContext();
const useUI = () => useContext(UIContext);
const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

const getThemeOptions = (activeGradient) => ({
  palette: {
    primary: { main: '#6b3acc' },
    secondary: { main: '#111012' },
    background: { default: '#f4f3f7', paper: '#ffffff' },
    text: { primary: '#111012', secondary: '#5a6474' },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 700, fontSize: '1.5rem' },
    h6: { fontWeight: 600, fontSize: '1.25rem' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: '12px', fontWeight: 500, padding: '10px 20px', transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 12px rgba(0,0,0,0.1)' } },
        containedPrimary: { background: activeGradient, color: '#fff', '&:hover': { background: activeGradient, opacity: 0.9, boxShadow: '0 4px 12px 0 rgba(0,0,0,0.1)' } }
      }
    },
    MuiPaper: { styleOverrides: { root: { borderRadius: 16, boxShadow: '0 6px 20px 0 rgba(0,0,0,0.07)' } } },
    MuiAppBar: { styleOverrides: { root: { background: '#ffffff', color: '#111012', boxShadow: 'none', borderBottom: '1px solid #0000001a' } } },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          backgroundColor: '#0f1116',
          color: '#fff',
          '& .MuiListItemIcon-root': { color: '#e0e0e0' },
        },
      },
    },
    MuiCard: { styleOverrides: { root: { borderRadius: 16, boxShadow: '0 6px 20px 0 rgba(0,0,0,0.07)' } } },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          transition: 'background 0.2s ease-in-out, color 0.2s ease-in-out',
          '&:hover': {
            background: activeGradient,
            color: '#fff',
            '& .MuiListItemIcon-root, & .MuiListItemText-secondary': { color: '#fff' },
          },
          '&.Mui-selected': {
            background: activeGradient,
            color: '#fff',
            '& .MuiListItemIcon-root, & .MuiListItemText-secondary': { color: '#fff' },
            '&:hover': { opacity: 0.9 }
          }
        }
      }
    },
    MuiListItemIcon: { 
      styleOverrides: { 
        root: { color: '#111012', transition: 'color 0.2s ease-in-out' } 
      } 
    },
  }
});

const UIProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth(); // Depend on AuthContext
  const [activeGradient, setActiveGradient] = useState(CATCH_GRADIENT);
  const [logoUrl, setLogoUrl] = useState(null);
  const [isUILoading, setUILoading] = useState(true); 
  
  useEffect(() => {
    // Don't do anything until the initial authentication check is complete
    if (authLoading) {
      return; 
    }
    
    // If there's no user after the auth check, set defaults and finish loading
    if (!user) {
      setActiveGradient(CATCH_GRADIENT);
      setLogoUrl(null);
      setUILoading(false);
      return;
    }

    const fetchUIData = async () => {
      // Ensure UI is marked as loading when this fetch starts
      setUILoading(true);
      try {
        const response = await api.getSecureLink('react-logo1', user.email);
        const imageUrl = response.content;
        
        if (imageUrl && (imageUrl.startsWith('http') || imageUrl.startsWith('/'))) {
            setLogoUrl(imageUrl);
            if (imageUrl.includes('policypros')) {
                setActiveGradient(ORANGE_GRADIENT);
            } else if (imageUrl.includes('greenstone')) {
                setActiveGradient(GREEN_GRADIENT);
            } else {
                setActiveGradient(CATCH_GRADIENT);
            }
        } else {
            throw new Error('Invalid image URL received.');
        }
      } catch (error) {
        console.error("Error fetching UI data:", error);
        // Fallback to defaults on error
        setLogoUrl(null);
        setActiveGradient(CATCH_GRADIENT);
      } finally {
        // Mark UI loading as complete, regardless of success or failure
        setUILoading(false);
      }
    };

    fetchUIData();

  }, [user, authLoading]); // Re-run this logic only when the user or auth status changes

  const theme = useMemo(() => createTheme(getThemeOptions(activeGradient)), [activeGradient]);
  
  // Expose the logoUrl and loading state to the rest of the app
  const value = useMemo(() => ({ activeGradient, setActiveGradient, logoUrl, isUILoading }), [activeGradient, logoUrl, isUILoading]);

  return (
    <UIContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </UIContext.Provider>
  );
};

const Dashboard = ({ data, loading, onRefreshDashboard }) => {
  const { user, teamName, teamDisplayName, teamRoster, teamRosterStatus, agentDirectoryProfile, loading: authLoading, hasExecutiveDashboard } = useAuth();
  const [agentKpi, setAgentKpi] = useState(null);
  const [agentKpiLoading, setAgentKpiLoading] = useState(false);
  const [agentKpiError, setAgentKpiError] = useState(null);
  const [agentMeetingStats, setAgentMeetingStats] = useState({ loading: false, error: null, stats: null });
  const canViewExecutiveDashboard = Boolean(hasExecutiveDashboard);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartMinWidth = isMobile ? 520 : 0;
  const [publisherLiveState, setPublisherLiveState] = useState({
    loading: false,
    error: null,
    data: null,
    lastUpdated: null,
  });
  const [executiveDashboardState, setExecutiveDashboardState] = useState({ loading: true, error: null, seniors: [] });
  const [executiveRollupTotals, setExecutiveRollupTotals] = useState({
    loading: false,
    error: null,
    totalSales: null,
    ancillary: null,
  });
  const executiveStateByCompanyRef = useRef({
    sabal: { loading: true, error: null, seniors: [] },
    ahm: { loading: false, error: null, seniors: [] },
  });
  const executiveRollupByCompanyRef = useRef({
    sabal: { loading: false, error: null, totalSales: null, ancillary: null },
    ahm: { loading: false, error: null, totalSales: null, ancillary: null },
  });
  const [ahmExecutiveState, setAhmExecutiveState] = useState({
    loading: false,
    error: null,
    summary: null,
    teams: [],
    genesysAnalytics: null,
    lastUpdated: null,
  });
  const [ahmDateRange, setAhmDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [ahmAppliedDateRange, setAhmAppliedDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [ahmRangeSubmitNonce, setAhmRangeSubmitNonce] = useState(0);
  const [complianceScoreState, setComplianceScoreState] = useState({
    loading: false,
    error: null,
    averages: {},
  });
  const displayTeamName = teamDisplayName || teamName;
  const resolvedAgentName = (user?.name && user.name.trim())
    || (agentDirectoryProfile?.agent_name && agentDirectoryProfile.agent_name.trim())
    || (user?.email ? String(user.email).split('@')[0] : '');
  const rosterEntry = useMemo(() => {
    if (!Array.isArray(teamRoster) || teamRoster.length === 0) return null;
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const emailMatch = teamRoster.find(
      (member) => normalize(member.email) === normalize(user?.email)
    );
    if (emailMatch) return emailMatch;
    const candidateName = normalize(agentDirectoryProfile?.agent_name || resolvedAgentName);
    if (candidateName) {
      return (
        teamRoster.find(
          (member) => normalize(member.agent_name) === candidateName
        ) || null
      );
    }
    return null;
  }, [teamRoster, user?.email, agentDirectoryProfile?.agent_name, resolvedAgentName]);
  const executiveCompanyKey = useMemo(() => {
    const candidates = [
      agentDirectoryProfile?.agent_company,
      rosterEntry?.organization_name,
      rosterEntry?.company,
    ];
    const selected = candidates.find((value) => typeof value === 'string' && value.trim());
    return normalizeCompanyKey(selected);
  }, [agentDirectoryProfile?.agent_company, rosterEntry?.company, rosterEntry?.organization_name]);
  const executiveCompanyManualRef = useRef(false);
  const [activeExecutiveCompany, setActiveExecutiveCompany] = useState(executiveCompanyKey || 'sabal');
  useEffect(() => {
    if (executiveCompanyManualRef.current) return;
    if (executiveCompanyKey && executiveCompanyKey !== activeExecutiveCompany) {
      setActiveExecutiveCompany(executiveCompanyKey);
    }
  }, [activeExecutiveCompany, executiveCompanyKey]);
  const executiveCompanyLabel = activeExecutiveCompany === 'ahm' ? 'ACA' : 'Medicare';
  const syncExecutiveViewForCompany = useCallback((company) => {
    const state =
      executiveStateByCompanyRef.current[company] || { loading: true, error: null, seniors: [] };
    const rollup =
      executiveRollupByCompanyRef.current[company] || {
        loading: false,
        error: null,
        totalSales: null,
        ancillary: null,
      };
    setExecutiveDashboardState(state);
    setExecutiveRollupTotals(rollup);
  }, []);
  const updateExecutiveStateForCompany = useCallback(
    (company, nextStateOrUpdater) => {
      const current = executiveStateByCompanyRef.current[company] || {
        loading: company === 'sabal',
        error: null,
        seniors: [],
      };
      const next =
        typeof nextStateOrUpdater === 'function'
          ? nextStateOrUpdater(current)
          : nextStateOrUpdater || current;
      executiveStateByCompanyRef.current[company] = next;
      if (activeExecutiveCompany === company) {
        setExecutiveDashboardState(next);
      }
    },
    [activeExecutiveCompany]
  );
  const updateExecutiveRollupForCompany = useCallback(
    (company, nextRollupOrUpdater) => {
      const current = executiveRollupByCompanyRef.current[company] || {
        loading: false,
        error: null,
        totalSales: null,
        ancillary: null,
      };
      const next =
        typeof nextRollupOrUpdater === 'function'
          ? nextRollupOrUpdater(current)
          : nextRollupOrUpdater || current;
      executiveRollupByCompanyRef.current[company] = next;
      if (activeExecutiveCompany === company) {
        setExecutiveRollupTotals(next);
      }
    },
    [activeExecutiveCompany]
  );
  useEffect(() => {
    syncExecutiveViewForCompany(activeExecutiveCompany);
  }, [activeExecutiveCompany, syncExecutiveViewForCompany]);
  const createEmptyMeetingStats = useCallback(() => ({
    scheduled_meetings: 0,
    missed_meetings: 0,
    scheduled_times: [],
    missed_times: [],
  }), []);
  const normalizedManagerEmail = useMemo(() => (user?.email || '').toString().trim().toLowerCase(), [user?.email]);
  const isTeamManager = useMemo(() => {
    const isManagerLike = (value) => {
      const membership = normalizeTeamLabel(value);
      if (!membership) return false;
      if (membership.includes('team lead')) return true;
      if (membership.includes('lead')) return true;
      if (membership.includes('coach')) return true;
      if (membership.includes('director')) return true;
      // Some rosters label managers as "manager/member"; allow any role containing "manager"
      if (membership.includes('manager')) return true;
      return false;
    };
    return (
      isManagerLike(rosterEntry?.membership) ||
      isManagerLike(agentDirectoryProfile?.membership) ||
      isManagerLike(agentDirectoryProfile?.agent_role) ||
      isManagerLike(agentDirectoryProfile?.agent_title) ||
      isManagerLike(agentDirectoryProfile?.agent_level)
    );
  }, [
    agentDirectoryProfile?.agent_level,
    agentDirectoryProfile?.agent_role,
    agentDirectoryProfile?.agent_title,
    agentDirectoryProfile?.membership,
    rosterEntry?.membership,
  ]);
  const rosterIndicatedSenior = useMemo(() => {
    if (!Array.isArray(teamRoster) || teamRoster.length === 0) return false;
    const normalizedEmail = normalizeEmailKey(user?.email);
    const normalizedName = normalizeAgentKey(
      rosterEntry?.agent_name || agentDirectoryProfile?.agent_name || resolvedAgentName || ''
    );
    if (!normalizedEmail && !normalizedName) return false;
    return teamRoster.some((member) => {
      const seniorEmail = normalizeEmailKey(
        member?.senior_manager_email || member?.senior_manager || member?.senior_manager_email_address
      );
      const seniorName = normalizeAgentKey(member?.senior_manager_name || '');
      return (
        (normalizedEmail && seniorEmail && seniorEmail === normalizedEmail) ||
        (normalizedName && seniorName && seniorName === normalizedName)
      );
    });
  }, [
    agentDirectoryProfile?.agent_name,
    resolvedAgentName,
    rosterEntry?.agent_name,
    teamRoster,
    user?.email,
  ]);
  const isSeniorManager = useMemo(() => {
    const matchesSeniorLabel = (value) => {
      const normalized = normalizeTeamLabel(value).replace(/[_-]+/g, ' ');
      if (!normalized) return false;
      if (normalized.includes('senior manager')) return true;
      if (normalized.includes('senior management')) return true;
      if (normalized.includes('sr manager')) return true;
      if (normalized.includes('sr mgr')) return true;
      if (normalized.includes('senior') && normalized.includes('manager')) return true;
      if (normalized.includes('senior') && normalized.includes('management')) return true;
      return false;
    };
    const matchesByLabel = [
      rosterEntry?.membership,
      agentDirectoryProfile?.membership,
      agentDirectoryProfile?.agent_role,
      agentDirectoryProfile?.agent_title,
      agentDirectoryProfile?.agent_level,
    ].some((value) => matchesSeniorLabel(value));
    return matchesByLabel || rosterIndicatedSenior;
  }, [
    agentDirectoryProfile?.agent_level,
    agentDirectoryProfile?.agent_role,
    agentDirectoryProfile?.agent_title,
    agentDirectoryProfile?.membership,
    rosterIndicatedSenior,
    rosterEntry?.membership,
  ]);
  const isManagerView = isTeamManager || isSeniorManager;
  const managerTeamMembers = useMemo(() => {
    if (!isTeamManager || isSeniorManager || !Array.isArray(teamRoster) || !teamRoster.length) return [];

    const normalizedManagerName = normalizeAgentKey(
      rosterEntry?.agent_name || agentDirectoryProfile?.agent_name || resolvedAgentName || ''
    );
    const normalizedManagerEmail = normalizeEmailKey(
      rosterEntry?.email || agentDirectoryProfile?.email || user?.email || ''
    );

    const targetTeam = normalizeTeamLabel(
      rosterEntry?.team_name ||
      teamName ||
      agentDirectoryProfile?.team_name ||
      agentDirectoryProfile?.team ||
      agentDirectoryProfile?.teamName
    );

    const byTeam = targetTeam
      ? teamRoster.filter((member) => normalizeTeamLabel(member.team_name) === targetTeam)
      : [];

    const byManager = teamRoster.filter((member) => {
      const managerNameKey = normalizeAgentKey(member?.manager_name || member?.manager || '');
      const managerEmailKey = normalizeEmailKey(member?.manager_email || member?.managerEmail || '');
      return (
        (normalizedManagerName && managerNameKey && managerNameKey === normalizedManagerName) ||
        (normalizedManagerEmail && managerEmailKey && managerEmailKey === normalizedManagerEmail)
      );
    });

    const candidates = (byTeam.length ? byTeam : byManager).filter((member) => !isOldAgentDescriptor(member));
    return candidates;
  }, [
    agentDirectoryProfile?.agent_name,
    agentDirectoryProfile?.email,
    agentDirectoryProfile?.team,
    agentDirectoryProfile?.teamName,
    agentDirectoryProfile?.team_name,
    isSeniorManager,
    isTeamManager,
    teamName,
    teamRoster,
    rosterEntry?.agent_name,
    rosterEntry?.email,
    rosterEntry?.team_name,
    resolvedAgentName,
    user?.email,
  ]);
  const seniorTeamRosterGroups = useMemo(() => {
    if (!isSeniorManager || !Array.isArray(teamRoster) || !teamRoster.length) return [];
    const normalizedEmail = normalizeEmailKey(user?.email);
    const normalizedName = normalizeAgentKey(
      rosterEntry?.agent_name || agentDirectoryProfile?.agent_name || resolvedAgentName || ''
    );
    if (!normalizedEmail && !normalizedName) return [];
    const groups = new Map();
    teamRoster.forEach((member) => {
      if (isOldAgentDescriptor(member)) return;
      const seniorEmail = normalizeEmailKey(
        member?.senior_manager_email || member?.senior_manager || member?.senior_manager_email_address
      );
      const seniorName = normalizeAgentKey(member?.senior_manager_name || '');
      const matchesSenior =
        (normalizedEmail && seniorEmail && seniorEmail === normalizedEmail) ||
        (normalizedName && seniorName && seniorName === normalizedName);
      if (!matchesSenior) return;
      const teamKey = normalizeTeamLabel(member?.team_name);
      if (!teamKey) return;
      if (!groups.has(teamKey)) {
        groups.set(teamKey, {
          teamKey,
          teamName: member?.team_name || 'Team',
          members: [],
        });
      }
      groups.get(teamKey).members.push(member);
    });
    return Array.from(groups.values()).map((group, index) => ({
      ...group,
      id: `${group.teamKey || 'team'}-${index}`,
      displayName: formatTeamDisplayName(group.teamName) || group.teamName || `Team ${index + 1}`,
    }));
  }, [
    agentDirectoryProfile?.agent_name,
    isSeniorManager,
    resolvedAgentName,
    rosterEntry?.agent_name,
    teamRoster,
    user?.email,
  ]);
  const [teamManagerKpis, setTeamManagerKpis] = useState({ loading: false, error: null, agents: [], meetings: {} });
  const [seniorHierarchy, setSeniorHierarchy] = useState({ loading: false, error: null, managers: [], senior: null });
  const [seniorTeamKpis, setSeniorTeamKpis] = useState({ loading: false, error: null, agents: [], meetings: {} });
  const [teamKpiFilter, setTeamKpiFilter] = useState({ kpi: 'sales', direction: 'all' });
  const [meetingRowFilter, setMeetingRowFilter] = useState('all');
  const [managerSortOrder, setManagerSortOrder] = useState('desc'); // desc = highest production first
  const [executiveViewMode, setExecutiveViewMode] = useState('publisherLive');
  const fetchPublisherLive = useCallback(
    async ({ forceRefresh = false, showLoader = false } = {}) => {
      setPublisherLiveState((prev) => ({
        ...prev,
        loading: showLoader || !prev.data,
        error: null,
      }));
      try {
        const today = new Date();
        const todayIso = today.toISOString().slice(0, 10);
        const payload = {
          live_cpa_report: {
            start_date: todayIso,
            end_date: todayIso,
          },
        };
        if (forceRefresh) {
          payload.live_cpa_report.force_refresh = true;
        }
        const response = await fetch(AHM_KPI_REPORT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `Live CPA API responded with ${response.status}`);
        }
        const result = await response.json();
        const timestamp =
          result.generated_at || result.cached_at || new Date().toISOString();
        setPublisherLiveState({
          loading: false,
          error: null,
          data: result,
          lastUpdated: timestamp,
        });
      } catch (error) {
        setPublisherLiveState((prev) => ({
          ...prev,
          loading: false,
          error: error?.message || 'Failed to load publisher live CPA data.',
        }));
      }
    },
    []
  );
  const agentMatchesTeamFilters = useCallback(
    (row) => {
      if (!row) return false;
      const metrics = row.metrics || {};
      const { kpi, direction } = teamKpiFilter;
      // No threshold filtering when using highest/lowest; sorting is handled downstream
      if (meetingRowFilter === 'scheduled' && !(metrics.scheduledCount > 0)) {
        return false;
      }
      if (meetingRowFilter === 'missed' && !(metrics.missedCount > 0)) {
        return false;
      }
      return true;
    },
    [meetingRowFilter, teamKpiFilter.direction, teamKpiFilter.kpi]
  );
  const resetTeamKpiFilters = useCallback(() => {
    setTeamKpiFilter({ kpi: 'sales', direction: 'all' });
    setMeetingRowFilter('all');
  }, []);
  const seniorHierarchyEmailLookup = useMemo(() => {
    const map = new Map();
    (seniorHierarchy?.managers || []).forEach((manager) => {
      const managerName = (manager?.full_name || '').trim();
      const managerEmail = (manager?.email || '').trim();
      if (managerName && managerEmail) {
        map.set(normalizeAgentKey(managerName), managerEmail);
      }
      (manager?.agents || []).forEach((agent) => {
        const name = (agent?.full_name || '').trim();
        const email = (agent?.email || '').trim();
        if (name && email) {
          map.set(normalizeAgentKey(name), email);
        }
      });
    });
    return map;
  }, [seniorHierarchy?.managers]);
  const seniorEmailToNameMap = useMemo(() => {
    if (!isSeniorManager) return new Map();
    const map = new Map();
    const register = (name, email) => {
      if (!name || !email) return;
      const key = normalizeEmailKey(email);
      if (!key) return;
      const existing = map.get(key);
      if (existing) {
        existing.add(name);
      } else {
        map.set(key, new Set([name]));
      }
    };
    seniorTeamRosterGroups.forEach((group) => {
      (group.members || []).forEach((member) => {
        register(member?.agent_name, member?.email);
      });
    });
    (seniorHierarchy?.managers || []).forEach((manager) => {
      register(manager?.full_name, manager?.email);
      (manager?.agents || []).forEach((agent) => register(agent?.full_name, agent?.email));
    });
    return map;
  }, [isSeniorManager, seniorHierarchy?.managers, seniorTeamRosterGroups]);
  const seniorAgentAssignments = useMemo(() => {
    if (!isSeniorManager) return { names: [], emails: [] };
    const nameSet = new Set();
    const emailSet = new Set();
    const register = (name, email) => {
      if (typeof name === 'string' && name.trim()) {
        nameSet.add(name.trim());
      }
      if (typeof email === 'string' && email.trim()) {
        emailSet.add(email.trim());
      }
    };
    const resolveEmail = (name, email) => {
      if (email && email.trim()) return email;
      const lookup = seniorHierarchyEmailLookup.get(normalizeAgentKey(name || ''));
      return lookup || '';
    };
    if (seniorTeamRosterGroups.length) {
      seniorTeamRosterGroups.forEach((group) => {
        (group.members || []).forEach((member) =>
          register(member?.agent_name, resolveEmail(member?.agent_name, member?.email))
        );
      });
    }
    if (Array.isArray(seniorHierarchy?.managers)) {
      seniorHierarchy.managers.forEach((manager) => {
        register(manager?.full_name, manager?.email);
        (manager?.agents || []).forEach((agent) => register(agent?.full_name, agent?.email));
      });
    }
    return {
      names: Array.from(nameSet),
      emails: Array.from(emailSet),
    };
  }, [isSeniorManager, seniorHierarchy?.managers, seniorHierarchyEmailLookup, seniorTeamRosterGroups]);

  const sabalExecutiveRosterGroups = useMemo(() => {
    if (!canViewExecutiveDashboard || !Array.isArray(teamRoster) || teamRoster.length === 0) {
      return new Map();
    }
    const bySenior = new Map();
    teamRoster.forEach((member) => {
      if (!member || isOldAgentDescriptor(member)) return;
      const seniorName = (member?.senior_manager_name || '').trim();
      if (!seniorName) return;
      const seniorKey = normalizeAgentKey(seniorName);
      if (!seniorKey) return;
      if (!bySenior.has(seniorKey)) {
        bySenior.set(seniorKey, {
          seniorName,
          groups: new Map(),
        });
      }
      const entry = bySenior.get(seniorKey);
      const teamKey = normalizeTeamLabel(member?.team_name);
      if (!teamKey) return;
      if (!entry.groups.has(teamKey)) {
        entry.groups.set(teamKey, {
          teamKey,
          teamName: member?.team_name || 'Team',
          members: [],
        });
      }
      entry.groups.get(teamKey).members.push(member);
    });

    const result = new Map();
    bySenior.forEach((value, key) => {
      const rosterGroups = Array.from(value.groups.values()).map((group, idx) => ({
        ...group,
        id: `${key || 'senior'}-${idx}`,
        displayName: formatTeamDisplayName(group.teamName) || group.teamName || `Team ${idx + 1}`,
      }));
      result.set(key, {
        seniorName: value.seniorName,
        rosterGroups,
      });
    });
    return result;
  }, [canViewExecutiveDashboard, teamRoster]);

  const hasMedicareCostCenter = useMemo(() => {
    if (!agentDirectoryProfile) return false;
    const candidates = [
      agentDirectoryProfile.costcenter2_type,
      agentDirectoryProfile.costcenter_2_type,
      agentDirectoryProfile.costcenter2Type,
      agentDirectoryProfile.cost_center2_type,
      agentDirectoryProfile.costcenter2?.type,
      agentDirectoryProfile.costcenter2?.name,
      agentDirectoryProfile.costcenter2?.description,
    ];
    return candidates.some(
      (value) => typeof value === 'string' && value.toLowerCase().includes('medicare')
    );
  }, [agentDirectoryProfile]);

  const rosterOrgIsSabal = useMemo(() => {
    if (!rosterEntry?.organization_name) return false;
    return rosterEntry.organization_name.trim().toLowerCase().includes('sabal');
  }, [rosterEntry]);

  const shouldShowKpiSection = hasMedicareCostCenter || rosterOrgIsSabal;
  const scopedRosterRows = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const defaultOrg = rosterEntry?.organization_name || 'sabal';
    const register = (row) => {
      const agentName = toTrimmedString(row.agent_name);
      const team = toTrimmedString(row.team_name);
      if (!agentName || !team) return;
      const key = normalizeAgentKey(agentName);
      if (!key || seen.has(key)) return;
      seen.add(key);
      rows.push({
        agent_name: agentName,
        team_name: team,
        senior_manager_name: toTrimmedString(row.senior_manager_name),
        manager_name: toTrimmedString(row.manager_name),
        organization_name: toTrimmedString(row.organization_name) || defaultOrg,
      });
    };

    if (isSeniorManager && seniorTeamRosterGroups.length) {
      seniorTeamRosterGroups.forEach((group) => {
        const members = Array.isArray(group.members) ? group.members : [];
        const managerRow = members.find((member) => {
          const label = normalizeTeamLabel(member?.membership);
          return label.includes('lead') || label.includes('manager') || label.includes('mgr');
        });
        const managerName = managerRow?.agent_name || '';
        const seniorName =
          members[0]?.senior_manager_name ||
          rosterEntry?.agent_name ||
          resolvedAgentName ||
          '';
        members.forEach((member) => {
          register({
            agent_name: member?.agent_name,
            team_name: member?.team_name || group.teamName,
            manager_name: managerName,
            senior_manager_name: member?.senior_manager_name || seniorName,
            organization_name: member?.organization_name || defaultOrg,
          });
        });
      });
    } else if (isTeamManager && managerTeamMembers.length) {
      const managerName = (rosterEntry?.agent_name || resolvedAgentName || '').trim();
      const seniorName =
        managerTeamMembers[0]?.senior_manager_name || rosterEntry?.senior_manager_name || '';
      const managerTeamName =
        managerTeamMembers[0]?.team_name || rosterEntry?.team_name || teamName || '';
      const orgName =
        managerTeamMembers[0]?.organization_name || rosterEntry?.organization_name || defaultOrg;
      managerTeamMembers.forEach((member) => {
        register({
          agent_name: member?.agent_name,
          team_name: member?.team_name || managerTeamName,
          manager_name: managerName,
          senior_manager_name: member?.senior_manager_name || seniorName,
          organization_name: member?.organization_name || orgName,
        });
      });
      if (managerName && managerTeamName) {
        register({
          agent_name: managerName,
          team_name: managerTeamName,
          manager_name: managerName,
          senior_manager_name: seniorName,
          organization_name: orgName,
        });
      }
    }
    return rows;
  }, [
    isSeniorManager,
    seniorTeamRosterGroups,
    rosterEntry?.agent_name,
    rosterEntry?.organization_name,
    rosterEntry?.senior_manager_name,
    resolvedAgentName,
    isTeamManager,
    managerTeamMembers,
    teamName,
  ]);
  const sabalOrgPresent = useMemo(() => {
    if (rosterOrgIsSabal) return true;
    return scopedRosterRows.some((row) =>
      String(row?.organization_name || '').toLowerCase().includes('sabal')
    );
  }, [rosterOrgIsSabal, scopedRosterRows]);
  const availableTeams = useMemo(() => {
    if (isSeniorManager && seniorTeamRosterGroups.length) {
      return Array.from(
        new Set(
          seniorTeamRosterGroups
            .map((group) => toTrimmedString(group.teamName) || toTrimmedString(group.displayName))
            .filter(Boolean)
        )
      );
    }
    if (isTeamManager && managerTeamMembers.length) {
      return Array.from(
        new Set(managerTeamMembers.map((member) => toTrimmedString(member.team_name)).filter(Boolean))
      );
    }
    const fallback = toTrimmedString(rosterEntry?.team_name || teamName);
    return fallback ? [fallback] : [];
  }, [isSeniorManager, seniorTeamRosterGroups, isTeamManager, managerTeamMembers, rosterEntry?.team_name, teamName]);
  const [rosterEditorRows, setRosterEditorRows] = useState([]);
  const [rosterDraftRow, setRosterDraftRow] = useState({
    agent_name: '',
    team_name: '',
    senior_manager_name: '',
    manager_name: '',
    organization_name: 'sabal',
  });
  const [rosterEditorStatus, setRosterEditorStatus] = useState({
    saving: false,
    message: null,
    error: null,
  });
  const [showRosterPanel, setShowRosterPanel] = useState(false);
  useEffect(() => {
    if (!isManagerView || !sabalOrgPresent) {
      setShowRosterPanel(false);
    }
  }, [isManagerView, sabalOrgPresent]);
  const deriveManagerFromTeamName = useCallback((teamName) => {
    const raw = toTrimmedString(teamName);
    if (!raw) return '';
    const withoutUnderscores = raw.replace(/_/g, ' ');
    const stripped = withoutUnderscores.replace(/\bteam\b/gi, ' ').replace(/\s+/g, ' ').trim();
    return stripped;
  }, []);
  const dedupeRosterRows = useCallback((rows) => {
    const seen = new Set();
    const result = [];
    (rows || []).forEach((row) => {
      const agentName = toTrimmedString(row?.agent_name);
      const team = toTrimmedString(row?.team_name);
      if (!agentName || !team) return;
      const key = normalizeAgentKey(agentName);
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push({
        agent_name: agentName,
        team_name: team,
        senior_manager_name: toTrimmedString(row?.senior_manager_name),
        manager_name: toTrimmedString(row?.manager_name),
        organization_name: toTrimmedString(row?.organization_name) || 'sabal',
      });
    });
    return result;
  }, []);

  useEffect(() => {
    setRosterEditorRows(scopedRosterRows);
    const managerSeed =
      scopedRosterRows.find((row) => row.manager_name)?.manager_name ||
      rosterEntry?.agent_name ||
      resolvedAgentName ||
      '';
    const seniorSeed =
      scopedRosterRows.find((row) => row.senior_manager_name)?.senior_manager_name ||
      rosterEntry?.senior_manager_name ||
      '';
    const teamSeed =
      scopedRosterRows.find((row) => row.team_name)?.team_name ||
      rosterEntry?.team_name ||
      teamName ||
      '';
    const orgSeed =
      scopedRosterRows.find((row) => row.organization_name)?.organization_name ||
      rosterEntry?.organization_name ||
      'sabal';
    setRosterDraftRow((prev) => ({
      ...prev,
      manager_name: prev.manager_name || managerSeed,
      senior_manager_name: prev.senior_manager_name || seniorSeed,
      team_name: prev.team_name || teamSeed,
      organization_name: prev.organization_name || orgSeed,
    }));
    setRosterEditorStatus({ saving: false, message: null, error: null });
  }, [
    scopedRosterRows,
    rosterEntry?.agent_name,
    rosterEntry?.organization_name,
    rosterEntry?.senior_manager_name,
    resolvedAgentName,
    teamName,
  ]);
  const handleRemoveRosterRow = useCallback((index) => {
    setRosterEditorRows((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleAddDraftRow = useCallback(() => {
    const derivedManager = toTrimmedString(
      rosterDraftRow.manager_name || rosterEntry?.agent_name || resolvedAgentName
    );
    const derivedManagerFromTeam = deriveManagerFromTeamName(rosterDraftRow.team_name);
    const derivedSenior = toTrimmedString(
      rosterDraftRow.senior_manager_name ||
        rosterEntry?.senior_manager_name ||
        (isSeniorManager ? resolvedAgentName : '')
    );
    const derivedOrg =
      toTrimmedString(rosterDraftRow.organization_name) ||
      toTrimmedString(rosterEntry?.organization_name) ||
      'sabal';
    const base = {
      agent_name: toTrimmedString(rosterDraftRow.agent_name),
      team_name: toTrimmedString(rosterDraftRow.team_name),
      manager_name: derivedManagerFromTeam || derivedManager,
      senior_manager_name: derivedSenior,
      organization_name: derivedOrg,
    };
    if (!base.agent_name || !base.team_name) {
      setRosterEditorStatus({
        saving: false,
        message: null,
        error: 'Agent name and team are required before adding.',
      });
      return;
    }
    setRosterEditorRows((prev) => dedupeRosterRows([...prev, base]));
    setRosterDraftRow((prev) => ({ ...prev, agent_name: '' }));
    setRosterEditorStatus({ saving: false, message: null, error: null });
  }, [
    dedupeRosterRows,
    deriveManagerFromTeamName,
    isSeniorManager,
    resolvedAgentName,
    rosterDraftRow.agent_name,
    rosterDraftRow.manager_name,
    rosterDraftRow.organization_name,
    rosterDraftRow.senior_manager_name,
    rosterDraftRow.team_name,
    rosterEntry?.organization_name,
    rosterEntry?.senior_manager_name,
  ]);

  const handleRosterReset = useCallback(() => {
    setRosterEditorRows(scopedRosterRows);
    setRosterEditorStatus({ saving: false, message: null, error: null });
  }, [scopedRosterRows]);

  const handleRosterSave = useCallback(async () => {
    if (!isManagerView || !rosterOrgIsSabal) {
      setRosterEditorStatus({
        saving: false,
        message: null,
        error: 'Roster updates are limited to Sabal managers.',
      });
      return;
    }
    const payload = dedupeRosterRows(rosterEditorRows).map((row) => ({
      ...row,
      manager_name: deriveManagerFromTeamName(row.team_name) || toTrimmedString(row.manager_name),
    }));
    if (!payload.length) {
      setRosterEditorStatus({
        saving: false,
        message: null,
        error: 'Add at least one roster row before saving.',
      });
      return;
    }
    setRosterEditorStatus({ saving: true, message: null, error: null });
    try {
      await api.post('/sabal/roster-validations/replace', { rows: payload });
      setRosterEditorStatus({
        saving: false,
        message: `Saved ${payload.length} roster entr${payload.length === 1 ? 'y' : 'ies'}.`,
        error: null,
      });
    } catch (err) {
      setRosterEditorStatus({
        saving: false,
        message: null,
        error: err?.message || 'Failed to save roster updates.',
      });
    }
  }, [dedupeRosterRows, isManagerView, rosterEditorRows, rosterOrgIsSabal]);

  const weekWindow = useMemo(() => {
    const today = new Date();
    const day = today.getDay();
    const monday = new Date(today);
    const diffToMonday = (day + 6) % 7; // Monday → 0
    monday.setDate(today.getDate() - diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (date) => date.toISOString().slice(0, 10);
    const label = `${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${sunday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    return { startDate: fmt(monday), endDate: fmt(sunday), label };
  }, []);
  const clampDateToWeek = useCallback(
    (isoDate) => {
      const parseIsoDateUtc = (value) => {
        const parts = String(value || '').split('-').map((p) => Number(p));
        if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return null;
        const [y, m, d] = parts;
        const dt = new Date(Date.UTC(y, m - 1, d));
        return Number.isNaN(dt.getTime()) ? null : dt;
      };
      const candidate = parseIsoDateUtc(isoDate);
      if (!candidate) return '';
      const start = parseIsoDateUtc(weekWindow.startDate);
      const end = parseIsoDateUtc(weekWindow.endDate);
      if (!start || !end) return '';
      if (candidate < start || candidate > end) return '';
      // Return the original yyyy-mm-dd string to avoid TZ shifts
      return String(isoDate).slice(0, 10);
    },
    [weekWindow.endDate, weekWindow.startDate]
  );
  const [kpiSelectedDate, setKpiSelectedDate] = useState('');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const kpiDateRange = useMemo(() => {
    const selected = clampDateToWeek(kpiSelectedDate);
    if (selected) {
      const d = new Date(selected);
      const label = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      return { startDate: selected, endDate: selected, label };
    }
    return { ...weekWindow };
  }, [clampDateToWeek, kpiSelectedDate, weekWindow]);
  const kpiDisplayLabel = kpiSelectedDate ? kpiDateRange.startDate : weekWindow.label;
  const handleKpiDateChange = useCallback(
    (value) => {
      const clamped = clampDateToWeek(value);
      setKpiSelectedDate(clamped);
      setRefreshNonce((prev) => prev + 1);
    },
    [clampDateToWeek]
  );

  const [executiveDateRange, setExecutiveDateRange] = useState({
    startDate: weekWindow.startDate,
    endDate: weekWindow.endDate,
  });
  useEffect(() => {
    setExecutiveDateRange((prev) => {
      if (prev.startDate && prev.endDate) return prev;
      return { startDate: weekWindow.startDate, endDate: weekWindow.endDate };
    });
  }, [weekWindow.endDate, weekWindow.startDate]);
  const handleExecutiveDateChange = useCallback((field, value) => {
    const safeValue = value ? value.slice(0, 10) : '';
    setExecutiveDateRange((prev) => {
      const next = { ...prev, [field]: safeValue };
      if (field === 'startDate' && next.endDate && safeValue && safeValue > next.endDate) {
        next.endDate = safeValue;
      }
      if (field === 'endDate' && next.startDate && safeValue && safeValue < next.startDate) {
        next.startDate = safeValue;
      }
      return next;
    });
  }, []);
  const executiveRange = useMemo(() => {
    const start = executiveDateRange.startDate || weekWindow.startDate;
    const end = executiveDateRange.endDate || weekWindow.endDate;
    const label = start === end ? start : `${start} – ${end}`;
    return { startDate: start, endDate: end, label };
  }, [executiveDateRange.endDate, executiveDateRange.startDate, weekWindow.endDate, weekWindow.startDate]);

  useEffect(() => {
    setAhmDateRange((prev) => {
      if (prev.startDate && prev.endDate) return prev;
      return { startDate: weekWindow.startDate, endDate: weekWindow.endDate };
    });
    setAhmAppliedDateRange((prev) => {
      if (prev.startDate && prev.endDate) return prev;
      return { startDate: weekWindow.startDate, endDate: weekWindow.endDate };
    });
  }, [weekWindow.endDate, weekWindow.startDate]);
  const handleAhmDateChange = useCallback((field, value) => {
    const safeValue = value ? value.slice(0, 10) : '';
    setAhmDateRange((prev) => {
      const next = { ...prev, [field]: safeValue };
      if (field === 'startDate' && next.endDate && safeValue && safeValue > next.endDate) {
        next.endDate = safeValue;
      }
      if (field === 'endDate' && next.startDate && safeValue && safeValue < next.startDate) {
        next.startDate = safeValue;
      }
      return next;
    });
  }, []);
  const ahmSelectedRange = useMemo(() => {
    const start = ahmDateRange.startDate || weekWindow.startDate;
    const end = ahmDateRange.endDate || weekWindow.endDate;
    const label = start === end ? start : `${start} – ${end}`;
    return { startDate: start, endDate: end, label };
  }, [ahmDateRange.endDate, ahmDateRange.startDate, weekWindow.endDate, weekWindow.startDate]);
  const ahmActiveRange = useMemo(() => {
    const start = ahmAppliedDateRange.startDate || weekWindow.startDate;
    const end = ahmAppliedDateRange.endDate || weekWindow.endDate;
    const label = start === end ? start : `${start} – ${end}`;
    return { startDate: start, endDate: end, label };
  }, [ahmAppliedDateRange.endDate, ahmAppliedDateRange.startDate, weekWindow.endDate, weekWindow.startDate]);
  const handleAhmDateSubmit = useCallback(() => {
    if (!ahmSelectedRange.startDate || !ahmSelectedRange.endDate) return;
    setAhmAppliedDateRange({ startDate: ahmSelectedRange.startDate, endDate: ahmSelectedRange.endDate });
    setAhmRangeSubmitNonce((prev) => prev + 1);
  }, [ahmSelectedRange.endDate, ahmSelectedRange.startDate]);

  const shouldFetchComplianceScores = canViewExecutiveDashboard || isSeniorManager || isTeamManager;

  useEffect(() => {
    if (!shouldFetchComplianceScores) {
      setComplianceScoreState({ loading: false, error: null, averages: {} });
      return;
    }
    let cancelled = false;
    const fetchComplianceScores = async () => {
      setComplianceScoreState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        if (!COMPLIANCE_SCORECARD_URL) {
          throw new Error('Compliance scorecard URL is not configured.');
        }
        const url = new URL(COMPLIANCE_SCORECARD_URL);
        url.searchParams.set('organization_name', 'sabal');
        url.searchParams.set('start_date', weekWindow.startDate);
        url.searchParams.set('end_date', weekWindow.endDate);
        const response = await fetch(url.toString(), {
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `Compliance tracker request failed (${response.status})`);
        }
        const payload = await response.json();
        const aggregates = {};
        const toNumber = (value) => {
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        };
        (Array.isArray(payload) ? payload : []).forEach((row) => {
          const agentName = (row?.agentBeingAudited || row?.agentbeingaudited || '').trim();
          const score = toNumber(row?.total_score ?? row?.totalScore);
          if (!agentName || score == null) return;
          const key = normalizeAgentKey(agentName);
          if (!key) return;
          if (!aggregates[key]) {
            aggregates[key] = { sum: 0, count: 0 };
          }
          aggregates[key].sum += score;
          aggregates[key].count += 1;
        });
        if (!cancelled) {
          setComplianceScoreState({ loading: false, error: null, averages: aggregates });
        }
      } catch (error) {
        if (!cancelled) {
          setComplianceScoreState({
            loading: false,
            error: error?.message || 'Failed to load compliance scores.',
            averages: {},
          });
        }
      }
    };
    fetchComplianceScores();
    return () => {
      cancelled = true;
    };
  }, [shouldFetchComplianceScores, weekWindow.endDate, weekWindow.startDate]);

  const complianceScoreMap = useMemo(() => {
    const map = new Map();
    Object.entries(complianceScoreState.averages || {}).forEach(([key, stats]) => {
      if (!stats || !Number.isFinite(stats.sum) || !Number.isFinite(stats.count) || stats.count <= 0) return;
      map.set(key, {
        average: stats.sum / stats.count,
        count: stats.count,
      });
    });
    return map;
  }, [complianceScoreState.averages]);

  useEffect(() => {
    if (authLoading) return;
    if (!canViewExecutiveDashboard) return;
    if (publisherLiveState.data || publisherLiveState.loading) return;
    fetchPublisherLive({ forceRefresh: false, showLoader: false });
  }, [
    authLoading,
    canViewExecutiveDashboard,
    publisherLiveState.data,
    publisherLiveState.loading,
    fetchPublisherLive,
  ]);

  useEffect(() => {
    if (!canViewExecutiveDashboard) {
      const emptyState = { loading: false, error: null, seniors: [] };
      const emptyRollup = { loading: false, error: null, totalSales: null, ancillary: null };
      updateExecutiveStateForCompany('sabal', emptyState);
      updateExecutiveStateForCompany('ahm', emptyState);
      updateExecutiveRollupForCompany('sabal', emptyRollup);
      updateExecutiveRollupForCompany('ahm', emptyRollup);
      return;
    }
    let cancelled = false;

    const companyKey = 'sabal';
    const cacheKey = [
      normalizeEmailKey(user?.email || ''),
      companyKey,
      executiveRange.startDate,
      executiveRange.endDate,
    ].join('|');

    const now = Date.now();
    const cachedEntry = executiveDashboardCache.get(cacheKey);
    if (cachedEntry && now - cachedEntry.timestamp < EXECUTIVE_DASHBOARD_CACHE_TTL_MS) {
      if (cachedEntry.state) {
        executiveStateByCompanyRef.current.sabal = cachedEntry.state;
        if (activeExecutiveCompany === 'sabal') {
          setExecutiveDashboardState(cachedEntry.state);
        }
      }
      if (cachedEntry.rollupTotals) {
        executiveRollupByCompanyRef.current.sabal = cachedEntry.rollupTotals;
        if (activeExecutiveCompany === 'sabal') {
          setExecutiveRollupTotals(cachedEntry.rollupTotals);
        }
      }
    }

    const collectRosterMembers = (groups = []) =>
      groups.flatMap((group) =>
        (group.members || []).filter((member) => member && !isOldAgentDescriptor(member))
      );

    const buildHierarchyFromRosterGroups = (seniorName, rosterGroups = []) => ({
      senior: {
        full_name: seniorName || '',
        email: '',
        company: 'SABAL',
      },
      managers: rosterGroups.map((group, idx) => {
        const teamLead = (group.members || []).find((member) => {
          const membership = (member?.membership || '').toLowerCase();
          return (
            membership.includes('lead') ||
            membership.includes('manager') ||
            membership.includes('coach') ||
            membership.includes('director')
          );
        });
        const fallbackName = group.displayName || formatTeamDisplayName(group.teamName) || group.teamName || `Team ${idx + 1}`;
        const managerName = teamLead?.agent_name || fallbackName || `Manager ${idx + 1}`;
        const managerEmail = teamLead?.email || '';
        return {
          id: managerEmail || `${normalizeAgentKey(managerName) || 'manager'}-${idx}`,
          full_name: managerName,
          email: managerEmail,
          company: teamLead?.organization_name || 'SABAL',
          agents: (group.members || []).map((member) => ({
            full_name: member?.agent_name || '',
            email: member?.email || '',
            membership: member?.membership || '',
            company: member?.organization_name || 'SABAL',
          })),
        };
      }),
    });

    const buildExecutiveEntry = async ({
      seniorIdSeed,
      senior,
      hierarchy,
      rosterGroups = [],
      agentNames = [],
      agentEmails = [],
    }) => {
      let agentKpis = [];
      const meetingMap = {};
      let entryError = null;

      const registerMeeting = (name, email, stats) => {
        if (!name) return;
        const nameKey = normalizeAgentKey(name);
        if (!nameKey || meetingMap[nameKey]) return;
        if (stats) {
          meetingMap[nameKey] = stats;
        } else {
          meetingMap[nameKey] = createEmptyMeetingStats();
        }
      };

      if (agentNames.length) {
        try {
          const payloadKey = 'manager_dashboard_with_dates';
          const kpiPromise = fetch(AHM_KPI_REPORT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              [payloadKey]: {
                start_date: executiveRange.startDate,
                end_date: executiveRange.endDate,
                agent_names: agentNames,
                include_unmatched: false,
              },
              }),
            });
              const meetingPromise = agentEmails.length
                ? api
                    .post('/coaching/meeting-stats', {
                      agent_emails: agentEmails,
                      start_date: executiveRange.startDate,
                      end_date: executiveRange.endDate,
                    })
                    .catch((meetingErr) => {
                      console.error(
                        `Failed to load meeting stats for ${senior?.full_name || 'senior'}`,
                        meetingErr
                  );
                  return null;
                })
            : Promise.resolve(null);

          const [kpiResponse, meetingPayload] = await Promise.all([kpiPromise, meetingPromise]);

          if (!kpiResponse.ok) {
            const text = await kpiResponse.text();
            throw new Error(text || `Manager dashboard API returned ${kpiResponse.status}`);
          }

          const kpiPayload = await kpiResponse.json();
          agentKpis = Array.isArray(kpiPayload?.agents) ? kpiPayload.agents : [];

          const meetingLookup = new Map();
          if (meetingPayload && Array.isArray(meetingPayload.agents)) {
            meetingPayload.agents.forEach((agentStats) => {
              const emailKey = normalizeEmailKey(agentStats?.agent_email);
              if (emailKey) {
                meetingLookup.set(emailKey, normalizeMeetingPayload(agentStats));
              }
            });
          }

          const resolveMeetingStats = (email, fallbackMeetings) => {
            const emailKey = normalizeEmailKey(email);
            if (emailKey && meetingLookup.has(emailKey)) {
              return meetingLookup.get(emailKey);
            }
            return fallbackMeetings ? normalizeMeetingPayload(fallbackMeetings) : null;
          };

          agentKpis.forEach((agent) => {
            const stats = resolveMeetingStats(agent?.agent_email || agent?.email, agent?.meetings);
            registerMeeting(agent?.agent_name, agent?.agent_email || agent?.email, stats);
          });
        } catch (error) {
          entryError = error?.message || 'Failed to load KPI data.';
        }
      }

      (hierarchy?.managers || []).forEach((manager) => {
        (manager?.agents || []).forEach((agent) => registerMeeting(agent?.full_name, agent?.email));
      });
      (rosterGroups || []).forEach((group) => {
        (group.members || []).forEach((member) => registerMeeting(member?.agent_name, member?.email));
      });

      return {
        id: senior?.email || normalizeAgentKey(senior?.full_name || '') || seniorIdSeed,
        senior,
        hierarchy,
        rosterGroups,
        agentKpis,
        meetingMap,
        error: entryError,
      };
    };

    const loadExecutiveDashboard = async () => {
      updateExecutiveStateForCompany('sabal', (prev) => ({ ...prev, loading: true, error: null }));
      try {
        const payload = await fetchSabalRosterHierarchyPayload();
        const seniors = Array.isArray(payload?.seniors)
          ? payload.seniors.filter((record) => !isOldAgentDescriptor(record))
          : [];
        const filtered = seniors.filter((record) => {
          const recordKey = normalizeCompanyKey(record?.company);
          if (!recordKey && companyKey === 'sabal') return true;
          return recordKey === companyKey;
        });
        const targetRecords = filtered.length ? filtered : seniors;
        const seenSeniorKeys = new Set();

        const entryTasks = targetRecords.map(async (record, index) => {
          const normalized = normalizeSeniorRecord(record);
          if (!normalized) return null;
          const hierarchy = buildHierarchyStateFromSenior(normalized);
          if (!hierarchy) return null;
          const seniorKey =
            normalizeAgentKey(hierarchy.senior?.full_name || hierarchy.senior?.email || '') || '';
          if (seniorKey) seenSeniorKeys.add(seniorKey);
          const rosterSupplement = sabalExecutiveRosterGroups.get(seniorKey);
          const rosterGroups = rosterSupplement?.rosterGroups || [];
          const hierarchyAgents = flattenHierarchyAgents(hierarchy, { excludeOldAgents: true });
          const rosterMembers = collectRosterMembers(rosterGroups);
          const combinedAgents = [
            ...hierarchyAgents.map((agent) => ({ name: agent?.name, email: agent?.email })),
            ...rosterMembers.map((member) => ({ name: member?.agent_name, email: member?.email })),
          ];
          const { names: agentNames, emails: agentEmails } = buildUniqueAgentLists(combinedAgents);

          return buildExecutiveEntry({
            seniorIdSeed: `senior-${index}`,
            senior: hierarchy.senior,
            hierarchy,
            rosterGroups,
            agentNames,
            agentEmails,
          });
        });

        const rosterOnlyEntries = [];
        sabalExecutiveRosterGroups.forEach((value, key) => {
          if (!value || !value.rosterGroups?.length) return;
          if (seenSeniorKeys.has(key)) return;
          rosterOnlyEntries.push({ key, value });
        });

        const rosterTasks = rosterOnlyEntries.map(async ({ key, value }, rosterIndex) => {
          const rosterGroups = value.rosterGroups;
          const rosterMembers = collectRosterMembers(rosterGroups);
          const { names: agentNames, emails: agentEmails } = buildUniqueAgentLists(
            rosterMembers.map((member) => ({
              name: member?.agent_name,
              email: member?.email,
            }))
          );
          const hierarchy = buildHierarchyFromRosterGroups(value.seniorName, rosterGroups);
          return buildExecutiveEntry({
            seniorIdSeed: `roster-senior-${rosterIndex}`,
            senior: hierarchy.senior,
            hierarchy,
            rosterGroups,
            agentNames,
            agentEmails,
          });
        });

        const entriesRaw = await Promise.all([...entryTasks, ...rosterTasks]);
        const entries = entriesRaw.filter(Boolean);

        if (!cancelled) {
          const nextState = { loading: false, error: null, seniors: entries };
          updateExecutiveStateForCompany('sabal', nextState);
          const existing = executiveDashboardCache.get(cacheKey) || {};
          executiveDashboardCache.set(cacheKey, {
            ...existing,
            state: nextState,
            timestamp: Date.now(),
          });
        }
      } catch (error) {
        if (!cancelled) {
          const nextState = {
            loading: false,
            error: error?.message || 'Failed to load executive dashboard.',
            seniors: [],
          };
          updateExecutiveStateForCompany('sabal', nextState);
          const existing = executiveDashboardCache.get(cacheKey) || {};
          executiveDashboardCache.set(cacheKey, {
            ...existing,
            state: nextState,
            timestamp: Date.now(),
          });
        }
      }
    };

    const loadExecutiveRollupTotals = async () => {
      updateExecutiveRollupForCompany('sabal', (prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));
      try {
        const response = await fetch(AHM_KPI_REPORT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              simple_total_sales: {
                start_date: executiveRange.startDate,
                end_date: executiveRange.endDate,
              },
            }),
          });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `Total sales API returned ${response.status}`);
        }
        const payload = await response.json();
        if (cancelled) return;
        const totalSales = Number(payload?.total_sales) || 0;
        const ancillary = normalizeAncillaryBreakdown(payload?.ancillary_breakdown, totalSales);
        const nextRollup = {
          loading: false,
          error: null,
          totalSales,
          ancillary,
        };
        updateExecutiveRollupForCompany('sabal', nextRollup);
        const existing = executiveDashboardCache.get(cacheKey) || {};
        executiveDashboardCache.set(cacheKey, {
          ...existing,
          rollupTotals: nextRollup,
          timestamp: Date.now(),
        });
      } catch (error) {
        if (cancelled) return;
        const nextRollup = {
          loading: false,
          error: error?.message || 'Failed to load total sales.',
          totalSales: null,
          ancillary: null,
        };
        updateExecutiveRollupForCompany('sabal', nextRollup);
        const existing = executiveDashboardCache.get(cacheKey) || {};
        executiveDashboardCache.set(cacheKey, {
          ...existing,
          rollupTotals: nextRollup,
          timestamp: Date.now(),
        });
      }
    };

    loadExecutiveDashboard();
    loadExecutiveRollupTotals();

    return () => {
      cancelled = true;
    };
  }, [
    canViewExecutiveDashboard,
    sabalExecutiveRosterGroups,
    user?.email,
    executiveRange.endDate,
    executiveRange.startDate,
    activeExecutiveCompany,
    updateExecutiveRollupForCompany,
    updateExecutiveStateForCompany,
  ]);

  useEffect(() => {
    if (!canViewExecutiveDashboard) return;
    if (activeExecutiveCompany !== 'ahm') return;
    if (!ahmActiveRange.startDate || !ahmActiveRange.endDate) {
      setAhmExecutiveState((prev) => ({ ...prev, loading: false, error: null, summary: null, teams: [], genesysAnalytics: null, lastUpdated: null }));
      updateExecutiveStateForCompany('ahm', { loading: false, error: null, seniors: [] });
      updateExecutiveRollupForCompany('ahm', {
        loading: false,
        error: null,
        totalSales: null,
        ancillary: null,
      });
      return;
    }
    let cancelled = false;

    const loadAhmExecutive = async () => {
      setAhmExecutiveState((prev) => ({ ...prev, loading: true, error: null }));
      updateExecutiveStateForCompany('ahm', (prev) => ({ ...prev, loading: true, error: null }));
      updateExecutiveRollupForCompany('ahm', (prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await fetch(AHM_KPI_RUN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            start_date: ahmActiveRange.startDate,
            end_date: ahmActiveRange.endDate,
            organization_name: 'ahm_users',
          }),
        });
        const text = await response.text();
        let parsed = null;
        if (text) {
          try {
            parsed = JSON.parse(text);
          } catch (_) {
            parsed = null;
          }
        }
        if (!response.ok) {
          const details = parsed?.error || parsed?.details || text || `ACA KPI API returned ${response.status}`;
          throw new Error(typeof details === 'string' ? details : 'Failed to process ACA applications.');
        }
        const kpiPayload = parsed?.kpi || parsed || {};
        const genesysAnalytics = parsed?.genesys_analytics || null;
        if (cancelled) return;
        const summary = kpiPayload?.summary || null;
        const teams = Array.isArray(kpiPayload?.team_leads) ? kpiPayload.team_leads : [];
        const seniors = Array.isArray(kpiPayload?.seniors) ? kpiPayload.seniors : [];
        updateExecutiveStateForCompany('ahm', (prev) => ({
          ...prev,
          loading: false,
          error: null,
          seniors: seniors.length ? seniors : prev.seniors,
        }));
        updateExecutiveRollupForCompany('ahm', (prev) => ({
          ...prev,
          loading: false,
          error: null,
          totalSales: summary?.total_sales ?? prev.totalSales ?? null,
          ancillary: summary?.coverage_counts || prev.ancillary || null,
        }));
        setAhmExecutiveState({
          loading: false,
          error: null,
          summary,
          teams,
          genesysAnalytics: genesysAnalytics || null,
          lastUpdated: new Date().toISOString(),
        });
      } catch (error) {
        if (cancelled) return;
        const message = error?.message || 'Failed to process ACA applications.';
        setAhmExecutiveState({ loading: false, error: message, summary: null, teams: [], genesysAnalytics: null, lastUpdated: null });
        updateExecutiveStateForCompany('ahm', { loading: false, error: message, seniors: [] });
        updateExecutiveRollupForCompany('ahm', {
          loading: false,
          error: message,
          totalSales: null,
          ancillary: null,
        });
      }
    };

    loadAhmExecutive();
    return () => {
      cancelled = true;
    };
  }, [
    activeExecutiveCompany,
    canViewExecutiveDashboard,
    ahmActiveRange.endDate,
    ahmActiveRange.startDate,
    ahmRangeSubmitNonce,
    refreshNonce,
    AHM_KPI_RUN_URL,
    updateExecutiveRollupForCompany,
    updateExecutiveStateForCompany,
  ]);

  const agentNameForKpi = useMemo(() => {
    const candidate = (agentDirectoryProfile?.agent_name || rosterEntry?.agent_name || resolvedAgentName || '').trim();
    return candidate || null;
  }, [agentDirectoryProfile?.agent_name, rosterEntry?.agent_name, resolvedAgentName]);

  useEffect(() => {
    if (!shouldShowKpiSection || !agentNameForKpi) {
      setAgentKpi(null);
      setAgentKpiError(null);
      setAgentKpiLoading(false);
      return;
    }
    let cancelled = false;
    const fetchAgentKpis = async () => {
      setAgentKpiLoading(true);
      setAgentKpiError(null);
      try {
        const response = await fetch(AHM_KPI_REPORT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            simple_single_agent: {
              agent_name: agentNameForKpi,
              start_date: weekWindow.startDate,
              end_date: weekWindow.endDate,
            },
          }),
        });
        if (!response.ok) {
          throw new Error(`KPI API responded with ${response.status}`);
        }
        const payload = await response.json();
        const toNumber = (value) => {
          const num = Number(value);
          return Number.isFinite(num) ? num : null;
        };
        let salesTotal = null;
        let ipcTotal = null;
        let rawCallCount = null;
        let rawCallConversion = null;
        let ancillaryCounts = null;
        if (payload?.totals) {
          salesTotal = toNumber(payload.totals.sales);
          ipcTotal = toNumber(payload.totals.ipc);
          rawCallCount = toNumber(payload.totals.raw_call_count);
          rawCallConversion = toNumber(payload.totals.raw_call_conversion);
          ancillaryCounts = payload.totals.ancillary
            ? normalizeAncillaryBreakdown(payload.totals.ancillary, salesTotal ?? 0)
            : null;
        } else if (Array.isArray(payload?.results)) {
          const latest = payload.results.reduce((acc, curr) => {
            if (!curr) return acc;
            if (acc == null) return curr;
            const aWeek = Number(acc.week) || 0;
            const cWeek = Number(curr.week) || 0;
            return cWeek >= aWeek ? curr : acc;
          }, null);
          if (latest) {
            salesTotal = toNumber(latest.closed);
            ipcTotal = toNumber(latest.ipc);
          }
        }
        if (salesTotal == null) {
          salesTotal = toNumber(payload?.total_sales ?? payload?.totalSales ?? payload?.sales);
        }
        if (ipcTotal == null) {
          ipcTotal = toNumber(payload?.total_ipc ?? payload?.ipc);
        }
        if (rawCallCount == null) {
          rawCallCount = toNumber(payload?.raw_call_count ?? payload?.total_calls);
        }
        if (rawCallConversion == null) {
          rawCallConversion = toNumber(payload?.raw_call_conversion ?? payload?.total_conversion);
        }
        if (!ancillaryCounts) {
          const ancillarySource = payload?.ancillary_breakdown || payload?.ancillary;
          if (ancillarySource) {
            ancillaryCounts = normalizeAncillaryBreakdown(ancillarySource, salesTotal ?? 0);
          }
        }
        if (!cancelled) {
          const normalized = {
            sales: Number.isFinite(salesTotal) ? salesTotal : null,
            ipc: Number.isFinite(ipcTotal) ? ipcTotal : null,
            rawCallCount: Number.isFinite(rawCallCount) ? rawCallCount : null,
            rawCallConversion: Number.isFinite(rawCallConversion) ? rawCallConversion : null,
            ancillary: ancillaryCounts,
          };
          const hasAnyValue =
            Object.entries(normalized).some(([key, value]) =>
              key === 'ancillary' ? Boolean(value) && hasAncillaryCounts(value) : value !== null
            );
          setAgentKpi(
            hasAnyValue
              ? {
                  sales: normalized.sales ?? 0,
                  ipc: normalized.ipc ?? 0,
                  rawCallCount: normalized.rawCallCount ?? 0,
                  rawCallConversion: normalized.rawCallConversion ?? 0,
                  ancillary: normalized.ancillary || null,
                }
              : null
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch agent KPIs:', error);
          setAgentKpi(null);
          setAgentKpiError(error?.message || 'Failed to load KPIs.');
        }
      } finally {
        if (!cancelled) {
          setAgentKpiLoading(false);
        }
      }
    };
    fetchAgentKpis();
    return () => {
      cancelled = true;
    };
  }, [agentNameForKpi, refreshNonce, shouldShowKpiSection, weekWindow.startDate, weekWindow.endDate]);

  useEffect(() => {
    if (!user?.email) {
      setAgentMeetingStats({ loading: false, error: null, stats: null });
      return;
    }
    let cancelled = false;
    const loadMeetingStats = async () => {
      setAgentMeetingStats((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const payload = await api.post('/coaching/meeting-stats', {
          agent_emails: [user.email],
          start_date: weekWindow.startDate,
          end_date: weekWindow.endDate,
        });
        let stats = null;
        if (payload && Array.isArray(payload.agents)) {
          const emailKey = normalizeEmailKey(user.email);
          const entry = payload.agents.find(
            (agent) => normalizeEmailKey(agent?.agent_email) === emailKey
          );
          if (entry) {
            stats = normalizeMeetingPayload(entry);
          }
        }
        if (!cancelled) {
          setAgentMeetingStats({ loading: false, error: null, stats });
        }
      } catch (error) {
        if (!cancelled) {
          setAgentMeetingStats({
            loading: false,
            error: error?.message || 'Failed to load meeting reminders.',
            stats: null,
          });
        }
      }
    };
    loadMeetingStats();
    return () => {
      cancelled = true;
    };
  }, [user?.email, refreshNonce, weekWindow.startDate, weekWindow.endDate]);

  const shouldHideMetaChips = teamRosterStatus === 'legacy' || canViewExecutiveDashboard;

  const agentMetaChips = shouldHideMetaChips ? [] : [
    displayTeamName ? { key: 'team', icon: <GroupIcon />, label: displayTeamName } : null,
    agentDirectoryProfile?.agent_role ? { key: 'role', icon: <AssuredWorkloadIcon />, label: formatChipValue(agentDirectoryProfile.agent_role) } : null,
    agentDirectoryProfile?.agent_type ? { key: 'type', icon: <HeadsetIcon />, label: formatChipValue(agentDirectoryProfile.agent_type) } : null,
    agentDirectoryProfile?.agent_company ? { key: 'company', icon: <BusinessIcon />, label: formatChipValue(agentDirectoryProfile.agent_company) } : null,
    agentDirectoryProfile?.agent_manager &&
    !isOldAgentDescriptor(agentDirectoryProfile.agent_manager)
      ? { key: 'manager', icon: <PeopleIcon />, label: `Mgr: ${formatChipValue(agentDirectoryProfile.agent_manager)}` }
      : null,
    agentDirectoryProfile?.agent_senior_manager &&
    !isOldAgentDescriptor(agentDirectoryProfile.agent_senior_manager)
      ? { key: 'sr_manager', icon: <PeopleIcon />, label: `Sr Mgr: ${formatChipValue(agentDirectoryProfile.agent_senior_manager)}` }
      : null,
  ].filter(Boolean);
  const [manualRefreshBusy, setManualRefreshBusy] = useState(false);
  const canManualRefresh = typeof onRefreshDashboard === 'function';
  const handleManualRefresh = useCallback(async () => {
    if (!canManualRefresh || manualRefreshBusy) return;
    setManualRefreshBusy(true);
    try {
      await onRefreshDashboard?.({ showLoader: true });
      await fetchPublisherLive({ forceRefresh: true, showLoader: false });
      setRefreshNonce((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to refresh dashboard data:', error);
    } finally {
      setManualRefreshBusy(false);
    }
  }, [canManualRefresh, manualRefreshBusy, onRefreshDashboard, fetchPublisherLive]);
  const handleExecutiveCompanyChange = useCallback((_event, next) => {
    if (!next) return;
    executiveCompanyManualRef.current = true;
    setActiveExecutiveCompany(next);
    syncExecutiveViewForCompany(next);
  }, [syncExecutiveViewForCompany]);
  const manualRefreshButton = (
    <Tooltip title="Refresh dashboard data" placement="top" arrow>
      <span>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={handleManualRefresh}
          disabled={!canManualRefresh || manualRefreshBusy}
          sx={{
            whiteSpace: 'nowrap',
            height: 32,
            minHeight: 32,
            borderRadius: 999,
            px: 1.5,
          }}
        >
          {manualRefreshBusy ? 'Refreshing…' : 'Refresh data'}
        </Button>
      </span>
    </Tooltip>
  );
  const renderAgentMetaRow = (extraSx = {}) => {
    if (agentMetaChips.length === 0 && !dialerUserValue && !agentDirectValue) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          '& .MuiChip-root': {
            height: 32,
            borderRadius: 999,
          },
          '& .MuiChip-label': {
            px: 1.25,
          },
          ...extraSx,
        }}
      >
        {agentMetaChips.map(({ key, icon, label }) => (
          <Chip key={`meta-${key}`} icon={icon} label={label} color="primary" variant="outlined" size="small" />
        ))}
        {dialerUserValue && (
          <Tooltip title={`Dialer User: ${dialerUserValue}`} placement="top" arrow>
            <Chip
              icon={<DialpadIcon fontSize="small" />}
              label={dialerUserValue}
              color="primary"
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: 'rgba(25, 118, 210, 0.08)',
              }}
            />
          </Tooltip>
        )}
        {agentDirectValue && (
          <Box key="meta-agent-direct" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={`Agent Direct: ${agentDirectValue}`} placement="top" arrow>
              <Chip
                icon={<LinkIcon fontSize="small" />}
                label={agentDirectValue}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: agentDirectCopied ? 'success.main' : 'primary.light',
                  borderWidth: 1,
                  bgcolor: agentDirectCopied ? 'success.main' : 'rgba(25, 118, 210, 0.12)',
                  color: agentDirectCopied ? '#fff' : 'primary.main',
                  fontWeight: 600,
                  maxWidth: 260,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  '& .MuiChip-icon': {
                    color: agentDirectCopied ? '#fff' : 'primary.main',
                  },
                }}
              />
            </Tooltip>
            <Tooltip title={agentDirectCopied ? 'Copied!' : 'Copy to clipboard'} placement="top" arrow>
              <IconButton
                size="small"
                onClick={handleCopyAgentDirect}
                aria-label="Copy Agent Direct value to clipboard"
                sx={{
                  bgcolor: agentDirectCopied ? 'success.main' : 'primary.main',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: agentDirectCopied ? 'success.dark' : 'primary.dark',
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  };
  const renderMetaControlsRow = (extraSx = {}) => {
    const metaRow = renderAgentMetaRow();
    return (
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: 1,
          '& .MuiButton-root': {
            height: 32,
            minHeight: 32,
            borderRadius: 999,
          },
          ...extraSx,
        }}
      >
        <Box sx={{ minWidth: 0 }}>{metaRow || <Box />}</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          {manualRefreshButton}
        </Box>
      </Box>
    );
  };

  const ManagerFilterControls = ({ sx = {} } = {}) => (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ mb: 3, ...sx }}
    >
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>KPI Focus</InputLabel>
        <Select
          label="KPI Focus"
          value={teamKpiFilter.kpi}
          onChange={(event) =>
            setTeamKpiFilter((prev) => ({ ...prev, kpi: event.target.value }))
          }
        >
          {Object.entries(TEAM_KPI_STANDARD_OPTIONS).map(([key, config]) => (
            <MenuItem key={key} value={key}>
              {config.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={teamKpiFilter.direction}
        onChange={(_, value) => {
          if (!value) return;
          setTeamKpiFilter((prev) => ({ ...prev, direction: value }));
          if (value === 'below') setManagerSortOrder('asc');
          else if (value === 'above' || value === 'all') setManagerSortOrder('desc');
        }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="above">Highest</ToggleButton>
        <ToggleButton value="below">Lowest</ToggleButton>
      </ToggleButtonGroup>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Meeting Filter</InputLabel>
        <Select
          label="Meeting Filter"
          value={meetingRowFilter}
          onChange={(event) => setMeetingRowFilter(event.target.value)}
        >
          <MenuItem value="all">All meeting outcomes</MenuItem>
          <MenuItem value="scheduled">Has scheduled meetings</MenuItem>
          <MenuItem value="missed">Has missed meetings</MenuItem>
        </Select>
      </FormControl>
      <TextField
        size="small"
        type="date"
        label="Pick a day"
        InputLabelProps={{ shrink: true }}
        value={kpiSelectedDate}
        onChange={(event) => handleKpiDateChange(event.target.value)}
        inputProps={{ min: weekWindow.startDate, max: weekWindow.endDate }}
      />
    </Stack>
  );

  const renderResponsiveCollapse = (items = [], options = {}) => {
    const {
      accordion = false,
      defaultActiveKey,
      ghost = false,
      className,
      style,
    } = options;
    const normalizedItems = items.map((item, index) => ({
      key: String(item.key ?? index),
      label: item.label,
      children: item.children,
      style: item.panelStyle || item.style,
      className: item.panelClassName || item.className,
    }));

    if (isMobile) {
      return (
        <AMCollapse accordion={accordion} defaultActiveKey={defaultActiveKey}>
          {normalizedItems.map((item) => (
            <AMCollapse.Panel key={item.key} title={item.label} style={item.style}>
              {item.children}
            </AMCollapse.Panel>
          ))}
        </AMCollapse>
      );
    }

    return (
      <AntCollapse
        items={normalizedItems}
        accordion={accordion}
        defaultActiveKey={defaultActiveKey}
        ghost={ghost}
        expandIconPlacement="end"
        className={className}
        style={style}
      />
    );
  };

  const renderManagerKpiCard = ({
    title = 'Manager KPI Overview',
    subheader = kpiDisplayLabel,
    managerGroups = [],
    loading = false,
    error = null,
    hasTargets = true,
    showFilters = true,
    levelLabel = null,
  }) => {
    const renderNotice = (type, message, action) => {
      if (isMobile) {
        const tone = type === 'error' ? '#a8071a' : '#1d4ed8';
        const border = type === 'error' ? '#ffccc7' : '#d6e4ff';
        const background = type === 'error' ? '#fff2f0' : '#f0f6ff';
        return (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${border}`,
              background,
              color: tone,
              fontSize: 13,
            }}
          >
            <div>{message}</div>
            {action ? <div style={{ marginTop: 6 }}>{action}</div> : null}
          </div>
        );
      }
      return (
        <AntAlert
          type={type}
          message={message}
          showIcon
          action={action}
          style={{ marginTop: 12 }}
        />
      );
    };
    const content = loading ? (
      isMobile ? (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <AMSpin />
          <div style={{ marginTop: 8, color: '#667085' }}>Loading hierarchy KPIs…</div>
        </div>
      ) : (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <AntSpin />
          <AntTypography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Loading hierarchy KPIs…
          </AntTypography.Text>
        </div>
      )
    ) : error ? (
      renderNotice('error', error)
    ) : !hasTargets ? (
      renderNotice(
        'info',
        'No teams are mapped to this hierarchy. Please contact operations if this seems incorrect.'
      )
    ) : managerGroups.length === 0 ? (
      <>
        {showFilters && <ManagerFilterControls />}
        {renderNotice(
          'info',
          'No managers currently match the selected filters.',
          isMobile ? (
            <AMButton size="mini" onClick={resetTeamKpiFilters}>
              Reset filters
            </AMButton>
          ) : (
            <AntButton size="small" type="link" onClick={resetTeamKpiFilters}>
              Reset filters
            </AntButton>
          )
        )}
      </>
    ) : (
      <>
        {showFilters && <ManagerFilterControls />}
        {(() => {
          const sortedManagerGroups = [...managerGroups].sort((a, b) => {
            const focusA = summaryMetricForSort(a?.summary || {}, teamKpiFilter.kpi);
            const focusB = summaryMetricForSort(b?.summary || {}, teamKpiFilter.kpi);
            if (focusA !== focusB) {
              return managerSortOrder === 'asc' ? focusA - focusB : focusB - focusA;
            }
            const salesA = a?.summary?.sales ?? 0;
            const salesB = b?.summary?.sales ?? 0;
            if (salesA !== salesB) {
              return managerSortOrder === 'asc' ? salesA - salesB : salesB - salesA;
            }
            return (a.manager?.full_name || a.manager?.teamName || '').localeCompare(
              b.manager?.full_name || b.manager?.teamName || ''
            );
          });
          const managerRankMap = new Map();
          sortedManagerGroups.slice(0, 3).forEach((group, rankIdx) => {
            const key =
              group.id ||
              group.manager?.email ||
              group.manager?.teamName ||
              group.manager?.full_name ||
              `manager-${rankIdx}`;
            managerRankMap.set(key, rankIdx);
          });

          const managerPanels = sortedManagerGroups.map((group, index) => {
            const managerName =
              group.manager?.teamName || group.manager?.full_name || 'Manager';
            const managerEmail = group.manager?.email || '';
            const teamLeadLabel = group.manager?.teamLeadName || '';
            const summary = group.summary || {};
            const summaryMetrics = [
              { label: 'Sales', value: summary.sales ?? 0 },
              { label: 'IPC', value: summary.ipc ?? 0 },
              { label: 'Raw Calls', value: summary.rawCalls ?? 0 },
              { label: 'Conversion', value: summary.conversion ?? 0, isPercent: true },
              { label: 'Scheduled', value: summary.scheduled ?? 0 },
              { label: 'Missed', value: summary.missed ?? 0 },
              Number.isFinite(summary.complianceAverage)
                ? {
                    label: 'Compliance',
                    value: summary.complianceAverage,
                    precision: 1,
                  }
                : null,
            ].filter(Boolean);
            const summaryAncillaryEntries = summary.ancillary
              ? formatAncillaryEntries(summary.ancillary).filter((entry) => entry.value > 0)
              : [];
            const panelKey = group.id || managerEmail || managerName || `manager-${index}`;
            const trophyRank = managerRankMap.get(panelKey);
            const trophy = typeof trophyRank === 'number' ? getTrophyIcon(trophyRank, { size: 'small' }) : null;
            const formatSummaryValue = (metric) => {
              if (metric.isPercent) return formatPercent(metric.value);
              if (typeof metric.precision === 'number') {
                return Number(metric.value || 0).toFixed(metric.precision);
              }
              return metric.value;
            };
            const summaryTags = summaryMetrics.map((metric) => {
              const label = `${metric.label}: ${formatSummaryValue(metric)}`;
              return isMobile ? (
                <AMTag key={`${panelKey}-summary-chip-${metric.label}`} fill="outline">
                  {label}
                </AMTag>
              ) : (
                <AntTag key={`${panelKey}-summary-chip-${metric.label}`}>{label}</AntTag>
              );
            });
            const sortedGroupRows = sortAgentsByPerformance(group.rows || []);
            const rankMap = buildAgentRankMap(sortedGroupRows);
            const header = isMobile ? (
              <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
                <AMSpace align="center" wrap style={{ '--gap': '6px' }}>
                  <span style={{ fontWeight: 600 }}>Manager: {managerName}</span>
                  {trophy}
                  <AMTag color="primary" fill="outline">Manager</AMTag>
                </AMSpace>
                {teamLeadLabel ? (
                  <div style={{ fontSize: 12, color: '#667085' }}>Team Lead: {teamLeadLabel}</div>
                ) : managerEmail ? (
                  <div style={{ fontSize: 12, color: '#667085' }}>{managerEmail}</div>
                ) : null}
                {summaryTags.length ? (
                  <AMSpace wrap style={{ '--gap': '6px' }}>
                    {summaryTags}
                  </AMSpace>
                ) : null}
              </AMSpace>
            ) : (
              <AntSpace direction="vertical" size={4} style={{ width: '100%' }}>
                <AntSpace align="center" wrap>
                  <AntTypography.Text strong>Manager: {managerName}</AntTypography.Text>
                  {trophy}
                  <AntTag color="blue">Manager</AntTag>
                </AntSpace>
                {teamLeadLabel ? (
                  <AntTypography.Text type="secondary">Team Lead: {teamLeadLabel}</AntTypography.Text>
                ) : managerEmail ? (
                  <AntTypography.Text type="secondary">{managerEmail}</AntTypography.Text>
                ) : null}
                {summaryTags.length ? (
                  <AntSpace wrap size={[8, 8]}>
                    {summaryTags}
                  </AntSpace>
                ) : null}
              </AntSpace>
            );
            return {
              key: panelKey,
              label: header,
              children: (
                <div>
                  {isMobile ? (
                    <AMGrid columns={2} gap={10} style={{ marginBottom: 12 }}>
                      {summaryMetrics.map((metric) => (
                        <AMGrid.Item key={`${panelKey}-${metric.label}`} style={{ display: 'flex' }}>
                          <div
                            style={{
                              borderRadius: 10,
                              border: '1px solid #e6e8ec',
                              padding: 10,
                              background: '#fff',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 4,
                            }}
                          >
                            <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                              {metric.label}
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 700 }}>
                              {formatSummaryValue(metric)}
                            </div>
                          </div>
                        </AMGrid.Item>
                      ))}
                    </AMGrid>
                  ) : (
                    <AntRow gutter={[12, 12]} style={{ marginBottom: 12 }}>
                      {summaryMetrics.map((metric) => (
                        <AntCol xs={12} sm={8} md={6} lg={4} key={`${panelKey}-${metric.label}`} style={{ display: 'flex' }}>
                          <AntCard
                            size="small"
                            style={{ borderRadius: 12, width: '100%', height: '100%' }}
                            styles={{ body: { display: 'flex', flexDirection: 'column', gap: 4, height: '100%' } }}
                          >
                            <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                              {metric.label}
                            </AntTypography.Text>
                            <AntTypography.Text strong>{formatSummaryValue(metric)}</AntTypography.Text>
                          </AntCard>
                        </AntCol>
                      ))}
                    </AntRow>
                  )}
                  {summaryAncillaryEntries.length ? (
                    <div style={{ marginBottom: 12 }}>
                      {isMobile ? (
                        <div style={{ fontSize: 12, color: '#667085', textTransform: 'uppercase', marginBottom: 6 }}>
                          Ancillary totals
                        </div>
                      ) : (
                        <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                          Ancillary totals
                        </AntTypography.Text>
                      )}
                      <div style={{ marginTop: 6 }}>
                        {isMobile ? (
                          <AMSpace wrap style={{ '--gap': '6px' }}>
                            {summaryAncillaryEntries.map((entry) => (
                              <AMTag
                                key={`${panelKey}-summary-anc-${entry.key}`}
                                color={entry.key === 'total' ? 'primary' : 'default'}
                                fill="outline"
                              >
                                {entry.label}: {entry.value}
                              </AMTag>
                            ))}
                          </AMSpace>
                        ) : (
                          <AntSpace wrap size={[8, 8]}>
                            {summaryAncillaryEntries.map((entry) => (
                              <AntTag
                                key={`${panelKey}-summary-anc-${entry.key}`}
                                color={entry.key === 'total' ? 'blue' : 'default'}
                              >
                                {entry.label}: {entry.value}
                              </AntTag>
                            ))}
                          </AntSpace>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {sortedGroupRows.length ? (
                    isMobile ? (
                      <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                        {sortedGroupRows.map((row) => (
                          <AgentAccordionRow
                            key={row.id}
                            row={row}
                            trophyRank={rankMap.get(getAgentRowKey(row))}
                          />
                        ))}
                      </AMSpace>
                    ) : (
                      <AntSpace direction="vertical" style={{ width: '100%' }} size="middle">
                        {sortedGroupRows.map((row) => (
                          <AgentAccordionRow
                            key={row.id}
                            row={row}
                            trophyRank={rankMap.get(getAgentRowKey(row))}
                          />
                        ))}
                      </AntSpace>
                    )
                  ) : (
                    isMobile ? (
                      <div style={{ color: '#667085' }}>
                        No agent-level KPIs matched the current filters for this manager.
                      </div>
                    ) : (
                      <AntTypography.Text type="secondary">
                        No agent-level KPIs matched the current filters for this manager.
                      </AntTypography.Text>
                    )
                  )}
                </div>
              ),
            };
          });
          return renderResponsiveCollapse(managerPanels, { ghost: false });
        })()}
      </>
    );

    if (isMobile) {
      return (
        <AMCard style={{ marginTop: 16 }}>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
              <div style={{ fontWeight: 700 }}>{title}</div>
              {subheader ? <div style={{ color: '#667085' }}>{subheader}</div> : null}
              {levelLabel ? <AMTag color="primary" fill="outline">{levelLabel}</AMTag> : null}
              {content}
            </AMSpace>
          </AMCard.Body>
        </AMCard>
      );
    }

    return (
      <AntCard
        title={<span style={{ fontWeight: 700 }}>{title}</span>}
        extra={levelLabel ? <AntTag color="blue">{levelLabel}</AntTag> : null}
        style={{ marginTop: 24 }}
      >
        {subheader ? <AntTypography.Text type="secondary">{subheader}</AntTypography.Text> : null}
        <div style={{ marginTop: subheader ? 12 : 0 }}>{content}</div>
      </AntCard>
    );
  };

  const renderSeniorManagerKpis = () => {
    if (!isSeniorManager) return null;
    const hasHierarchyTargets = seniorTeamRosterGroups.length
      ? true
      : (seniorHierarchy?.managers || []).length > 0;
    return renderManagerKpiCard({
      title: 'Senior Manager KPI Overview',
      subheader:
        seniorHierarchy?.senior?.full_name
          ? `${seniorHierarchy.senior.full_name} · ${kpiDisplayLabel}`
          : kpiDisplayLabel,
      managerGroups: seniorManagerGroups,
      loading: seniorHierarchy.loading || seniorTeamKpis.loading,
      error: seniorHierarchy.error || seniorTeamKpis.error,
      hasTargets: hasHierarchyTargets,
      levelLabel: 'Senior Manager',
    });
  };

  const renderExecutiveManagerDeck = () => {
    if (!canViewExecutiveDashboard) return null;

    const isAhm = activeExecutiveCompany === 'ahm';
    const execLoading = isAhm ? ahmExecutiveState.loading : executiveDashboardState.loading;
    const execError = isAhm ? ahmExecutiveState.error : executiveDashboardState.error;
    const hasEntries =
      (executiveManagerGroups && executiveManagerGroups.length > 0) ||
      (executiveDashboardState?.seniors && executiveDashboardState.seniors.length > 0);
    const simpleTotalsAvailable = executiveRollupTotals.totalSales != null;
    const effectiveRollupSales = simpleTotalsAvailable
      ? Number(executiveRollupTotals.totalSales) || 0
      : executiveAggregateStats?.totalSales ?? 0;

    const effectiveAncillaryTotals =
      simpleTotalsAvailable && executiveRollupTotals.ancillary
        ? executiveRollupTotals.ancillary
        : executiveAggregateStats?.ancillaryTotals || {};
    const executiveDateLabel = isAhm ? ahmActiveRange.label : executiveRange.label;

    const renderExecutiveNotice = (type, message) => {
      if (isMobile) {
        const palette = {
          info: { border: '#d6e4ff', background: '#f0f6ff', color: '#1d4ed8' },
          warning: { border: '#ffe58f', background: '#fffbe6', color: '#ad6800' },
          error: { border: '#ffccc7', background: '#fff2f0', color: '#a8071a' },
          success: { border: '#b7eb8f', background: '#f6ffed', color: '#237804' },
        };
        const tone = palette[type] || palette.info;
        return (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${tone.border}`,
              background: tone.background,
              color: tone.color,
              fontSize: 12,
            }}
          >
            {message}
          </div>
        );
      }
      return <AntAlert type={type} message={message} showIcon />;
    };

    const renderExecutiveSpinner = (label) =>
      isMobile ? (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <AMSpin />
          <div style={{ marginTop: 8, color: '#667085' }}>{label}</div>
        </div>
      ) : (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <AntSpin />
          <AntTypography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {label}
          </AntTypography.Text>
        </div>
      );

    const renderAhmTeamsLegacy = () => {
      if (execLoading) {
        return (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Processing ACA applications…
                </Typography>
          </Box>
        );
      }
      if (!ahmExecutiveState.summary) return null;
      if (ahmTeamLeads.length === 0) {
        return (
          <Alert severity="info">No team lead matches were found in the ACA roster for this date range.</Alert>
        );
      }
      return (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Team lead performance</Typography>
            <Chip size="small" variant="outlined" label="Sorted by conversion %, then closed sales" />
          </Box>
          {(() => {
            const detailBoxSx = {
              pt: isMobile ? 1 : 0,
              px: isMobile ? 1.5 : 2,
              pb: isMobile ? 1.5 : 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            };
            const teamPanels = sortedAhmTeamLeads.map((team, index) => {
              const trophy = index < 3 ? getTrophyIcon(index, { size: 'small' }) : null;
              const agents = Array.isArray(team.agents) ? team.agents : [];
              const visibleAgents = agents;
              const remainingAgents = 0;
              const teamChips = [
                { key: 'sales', label: `${Number(team.approved_sales || 0).toLocaleString()} sales`, value: team.approved_sales, color: 'primary' },
                { key: 'submitted', label: `${Number(team.submitted || 0).toLocaleString()} submitted`, value: team.submitted },
                { key: 'agents', label: `${Number(team.agent_count || agents.length || 0).toLocaleString()} agents`, value: team.agent_count || agents.length || 0, variant: 'outlined' },
                { key: 'members', label: `${Number(team.member_count || 0).toLocaleString()} members`, value: team.member_count, variant: 'outlined', color: 'secondary' },
                { key: 'verified', label: `${Number(team.verified_count || 0).toLocaleString()} verified`, value: team.verified_count },
                { key: 'active', label: `${Number(team.active_count || 0).toLocaleString()} active`, value: team.active_count },
                { key: 'kicked', label: `${Number(team.kicked_count || 0).toLocaleString()} kicked`, value: team.kicked_count },
                { key: 'inactive', label: `${Number(team.inactive_count || 0).toLocaleString()} inactive`, value: team.inactive_count },
                { key: 'same_day', label: `${Number(team.same_day_close_count || 0).toLocaleString()} same-day`, value: team.same_day_close_count },
                { key: 'fl_dental', label: `${Number(team.fl_dental_count || 0).toLocaleString()} FL dental`, value: team.fl_dental_count },
                { key: 'non_fl_mm', label: `${Number(team.non_fl_mm_count || 0).toLocaleString()} non-FL MM`, value: team.non_fl_mm_count },
                { key: 'non_fl_ameritas', label: `${Number(team.non_fl_ameritas_count || 0).toLocaleString()} non-FL Ameritas`, value: team.non_fl_ameritas_count },
                { key: 'aca', label: `${Number(team.aca_count || 0).toLocaleString()} ACA`, value: team.aca_count },
                { key: 'fed_market', label: `${Number(team.federal_marketplace_count || 0).toLocaleString()} Federal Mkt`, value: team.federal_marketplace_count },
                { key: 'state_market', label: `${Number(team.state_marketplace_count || 0).toLocaleString()} State Mkt`, value: team.state_marketplace_count },
                { key: 'ai_not_attributed', label: `${Number(team.ai_not_attributed_count || 0).toLocaleString()} AI not-attributed`, value: team.ai_not_attributed_count, color: 'secondary', variant: 'outlined' },
                {
                  key: 'member_per',
                  label: `${Number(team.member_per || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} members/verified`,
                  value: team.member_per,
                  variant: 'outlined',
                },
              ].filter((chip) => chip.value != null && Number(chip.value) !== 0);
              const panelKey = team.id || team.team_lead_email || `ahm-team-${index}`;
              return {
                key: panelKey,
                panelStyle: {
                  border: '1px solid rgba(15, 23, 42, 0.12)',
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: 'linear-gradient(145deg, #0f172a08, #1e3a8a08)',
                  marginBottom: 16,
                },
                label: (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'flex-start', md: 'center' },
                      justifyContent: 'space-between',
                      gap: 1.5,
                      flexWrap: 'wrap',
                      width: '100%',
                    }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {team.team_lead_name || team.team_lead_email || 'Team Lead'}
                        </Typography>
                        {trophy}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {team.team_name || 'Unassigned Team'}
                      </Typography>
                      {team.team_lead_email ? (
                        <Typography variant="caption" color="text.secondary">
                          {team.team_lead_email}
                        </Typography>
                      ) : null}
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {teamChips.map((chip) => (
                        <Chip
                          key={`${panelKey}-${chip.key}`}
                          label={chip.label}
                          size="small"
                          color={chip.color}
                          variant={chip.variant}
                        />
                      ))}
                    </Stack>
                  </Box>
                ),
                children: (
                  <Box sx={detailBoxSx}>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Status</Typography>
                        <Stack spacing={0.5}>
                          {team.statusEntries.slice(0, 4).map((entry) => (
                            <Box key={`${panelKey}-status-${entry.key}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">{entry.label}</Typography>
                              <Chip size="small" label={Number(entry.count || 0).toLocaleString()} />
                            </Box>
                          ))}
                          {team.statusEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No status data.</Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Issuers</Typography>
                        <Stack spacing={0.5}>
                          {team.issuerEntries.slice(0, 4).map((entry) => (
                            <Box key={`${panelKey}-issuer-${entry.key}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">{entry.label}</Typography>
                              <Chip size="small" label={Number(entry.count || 0).toLocaleString()} />
                            </Box>
                          ))}
                          {team.issuerEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No issuer data.</Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Coverage</Typography>
                        <Stack spacing={0.5}>
                          {team.coverageEntries.slice(0, 4).map((entry) => (
                            <Box key={`${panelKey}-coverage-${entry.key}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">{entry.label}</Typography>
                              <Chip size="small" label={Number(entry.count || 0).toLocaleString()} />
                            </Box>
                          ))}
                          {team.coverageEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No coverage data.</Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Hotspots</Typography>
                        <Stack spacing={0.5}>
                          {team.cityEntries.slice(0, 3).map((entry) => (
                            <Box key={`${panelKey}-hotspot-${entry.key}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">{entry.label}</Typography>
                              <Chip size="small" label={Number(entry.count || 0).toLocaleString()} />
                            </Box>
                          ))}
                          {team.cityEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No approved hotspots.</Typography>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Agents</Typography>
                      <Stack spacing={0.75}>
                        {visibleAgents.map((agent) => {
                          const genesysMatch =
                            (agent.agent_email && ahmGenesysAgentMap.get(normalizeAhmAgentKey(agent.agent_email))) ||
                            (agent.agent_name && ahmGenesysAgentMap.get(normalizeAhmAgentKey(agent.agent_name))) ||
                            null;
                          const rawCallsValue = Number(
                            genesysMatch?.raw_calls ??
                              genesysMatch?.calls ??
                              genesysMatch?.metrics?.nConnected ??
                              genesysMatch?.metrics?.nOutboundConnected ??
                              0
                          );
                          const billedCallsValue = Number(
                            genesysMatch?.billed_calls ??
                              genesysMatch?.metrics?.nConnected_gt150 ??
                              genesysMatch?.metrics?.tAnswered_gt150 ??
                              0
                          );
                          const totalSalesValue = Number(agent.sales_count || 0);
                          const submittedValue = Number(
                            agent.submitted_count ??
                              genesysMatch?.submitted ??
                              genesysMatch?.metrics?.submitted ??
                              0
                          );
                          const rawConversionPct =
                            genesysMatch?.raw_conversion_pct != null
                              ? Number(genesysMatch.raw_conversion_pct)
                              : rawCallsValue > 0
                                ? (totalSalesValue / rawCallsValue) * 100
                                : null;
                          const billedConversionPct =
                            genesysMatch?.billed_conversion_pct != null
                              ? Number(genesysMatch.billed_conversion_pct)
                              : billedCallsValue > 0
                                ? (totalSalesValue / billedCallsValue) * 100
                                : null;
                          const answeredMs = genesysMatch?.metrics?.tAnswered;
                          const handleMs = genesysMatch?.metrics?.tHandle;
                          const talkMs = genesysMatch?.metrics?.tTalk;
                          const heldMs = genesysMatch?.metrics?.tHeld;
                          const waitMs = genesysMatch?.metrics?.tWait;
                          const vmMs = genesysMatch?.metrics?.tVoicemail;
                          const aiScorecards = Array.isArray(agent.ai_not_attributed_scorecards)
                            ? agent.ai_not_attributed_scorecards
                            : [];
                          return (
                            <Box
                              key={`${panelKey}-agent-${agent.agent_email || agent.agent_name || Math.random()}`}
                              sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {agent.agent_name || agent.agent_email || 'Agent'}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {[
                                  { key: 'sales', label: `${totalSalesValue.toLocaleString()} sales`, value: totalSalesValue, color: 'primary' },
                                  { key: 'submitted', label: `${Number(agent.submitted_count || 0).toLocaleString()} submitted`, value: agent.submitted_count },
                                  { key: 'approved', label: `${Number(agent.approved_from_submitted || 0).toLocaleString()} approved`, value: agent.approved_from_submitted },
                                  { key: 'members', label: `${Number(agent.member_count || 0).toLocaleString()} members`, value: agent.member_count, variant: 'outlined' },
                                  { key: 'verified', label: `${Number(agent.verified_count || 0).toLocaleString()} verified`, value: agent.verified_count },
                                  { key: 'active', label: `${Number(agent.active_count || 0).toLocaleString()} active`, value: agent.active_count },
                                  { key: 'kicked', label: `${Number(agent.kicked_count || 0).toLocaleString()} kicked`, value: agent.kicked_count },
                                  { key: 'inactive', label: `${Number(agent.inactive_count || 0).toLocaleString()} inactive`, value: agent.inactive_count },
                                  { key: 'same_day', label: `${Number(agent.same_day_close_count || 0).toLocaleString()} same-day`, value: agent.same_day_close_count },
                                  { key: 'fl_dental', label: `${Number(agent.fl_dental_count || 0).toLocaleString()} FL dental`, value: agent.fl_dental_count },
                                  { key: 'non_fl_mm', label: `${Number(agent.non_fl_mm_count || 0).toLocaleString()} non-FL MM`, value: agent.non_fl_mm_count },
                                  { key: 'non_fl_ameritas', label: `${Number(agent.non_fl_ameritas_count || 0).toLocaleString()} non-FL Ameritas`, value: agent.non_fl_ameritas_count },
                                  { key: 'aca', label: `${Number(agent.aca_count || 0).toLocaleString()} ACA`, value: agent.aca_count },
                                  { key: 'fed_market', label: `${Number(agent.federal_marketplace_count || 0).toLocaleString()} Federal Mkt`, value: agent.federal_marketplace_count },
                                  { key: 'state_market', label: `${Number(agent.state_marketplace_count || 0).toLocaleString()} State Mkt`, value: agent.state_marketplace_count },
                                  { key: 'ai_not_attributed', label: `${Number(agent.ai_not_attributed_count || 0).toLocaleString()} AI not-attributed`, value: agent.ai_not_attributed_count, color: 'secondary' },
                                  { key: 'sub_active', label: `${formatPercent(agent.sub_to_active_pct || 0)} sub→active`, value: agent.sub_to_active_pct },
                                  {
                                    key: 'member_per',
                                    label: `${Number(agent.member_per || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} members/verified`,
                                    value: agent.member_per,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_raw_calls',
                                    label: `${rawCallsValue.toLocaleString()} raw calls (Genesys)`,
                                    value: rawCallsValue,
                                    color: 'secondary',
                                  },
                                  {
                                    key: 'genesys_billed_calls',
                                    label: `${billedCallsValue.toLocaleString()} billed calls (>150s)`,
                                    value: billedCallsValue,
                                    color: 'secondary',
                                  },
                                  {
                                    key: 'genesys_rows',
                                    label: `${submittedValue.toLocaleString()} Genesys rows`,
                                    value: submittedValue,
                                  },
                                  {
                                    key: 'genesys_raw_conv',
                                    label:
                                      rawConversionPct != null
                                        ? `${rawConversionPct.toFixed(1)}% raw→sales`
                                        : '',
                                    value: rawConversionPct,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_billed_conv',
                                    label:
                                      billedConversionPct != null
                                        ? `${billedConversionPct.toFixed(1)}% billed→sales`
                                        : '',
                                    value: billedConversionPct,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_answered',
                                    label: answeredMs != null ? `${formatDuration(answeredMs)} answered` : '',
                                    value: answeredMs,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_handle',
                                    label: handleMs != null ? `${formatDuration(handleMs)} handle` : '',
                                    value: handleMs,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_talk',
                                    label: talkMs != null ? `${formatDuration(talkMs)} talk` : '',
                                    value: talkMs,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_hold',
                                    label: heldMs != null ? `${formatDuration(heldMs)} hold` : '',
                                    value: heldMs,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_wait',
                                    label: waitMs != null ? `${formatDuration(waitMs)} wait` : '',
                                    value: waitMs,
                                    variant: 'outlined',
                                  },
                                  {
                                    key: 'genesys_vm',
                                    label: vmMs != null ? `${formatDuration(vmMs)} voicemail` : '',
                                    value: vmMs,
                                    variant: 'outlined',
                                  },
                                ]
                                  .filter((chip) => chip.value != null && chip.value !== '')
                                  .map((chip) => (
                                    <Chip
                                      key={`${panelKey}-${agent.agent_email || agent.agent_name || 'agent'}-${chip.key}`}
                                      size="small"
                                      label={chip.label}
                                      color={chip.color}
                                      variant={chip.variant}
                                    />
                                  ))}
                                {aiScorecards.length > 0 && (
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                    {aiScorecards.map((sc, idx) => (
                                      <Button
                                        key={`${panelKey}-${agent.agent_email || agent.agent_name || 'agent'}-ai-${idx}`}
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        href={sc.url || undefined}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        AI Scorecard {sc.ai_vcid || idx + 1}
                                      </Button>
                                    ))}
                                  </Stack>
                                )}
                              </Stack>
                            </Box>
                          );
                        })}
                        {visibleAgents.length === 0 && (
                          <Typography variant="body2" color="text.secondary">No agents mapped to this team.</Typography>
                        )}
                        {remainingAgents > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            +{remainingAgents} more agents not shown
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                ),
              };
            });
            return renderResponsiveCollapse(teamPanels, { ghost: true });
          })()}
        </Box>
      );
    };
    void renderAhmTeamsLegacy;
    const renderAhmTeams = () => {
      if (execLoading) {
        return renderExecutiveSpinner('Processing ACA applications…');
      }
      if (!ahmExecutiveState.summary) return null;
      if (ahmTeamLeads.length === 0) {
        return renderExecutiveNotice(
          'info',
          'No team lead matches were found in the ACA roster for this date range.'
        );
      }

      const buildEntryTags = (entries = [], max, emptyText) => {
        const items = entries.slice(0, max);
        if (!items.length) {
          return isMobile ? (
            <div style={{ color: '#667085', fontSize: 12 }}>{emptyText}</div>
          ) : (
            <AntTypography.Text type="secondary">{emptyText}</AntTypography.Text>
          );
        }
        return isMobile ? (
          <AMSpace wrap style={{ '--gap': '6px' }}>
            {items.map((entry) => (
              <AMTag key={entry.key} fill="outline">
                {entry.label} ({Number(entry.count || 0).toLocaleString()})
              </AMTag>
            ))}
          </AMSpace>
        ) : (
          <AntSpace wrap size={[6, 6]}>
            {items.map((entry) => (
              <AntTag key={entry.key}>
                {entry.label} ({Number(entry.count || 0).toLocaleString()})
              </AntTag>
            ))}
          </AntSpace>
        );
      };

      const teamPanels = sortedAhmTeamLeads.map((team, index) => {
        const trophy = index < 3 ? getTrophyIcon(index, { size: 'small' }) : null;
        const agents = Array.isArray(team.agents) ? team.agents : [];
        const teamTags = [
          { key: 'sales', label: `${Number(team.approved_sales || 0).toLocaleString()} sales`, value: team.approved_sales },
          { key: 'submitted', label: `${Number(team.submitted || 0).toLocaleString()} submitted`, value: team.submitted },
          { key: 'agents', label: `${Number(team.agent_count || agents.length || 0).toLocaleString()} agents`, value: team.agent_count || agents.length || 0 },
          { key: 'members', label: `${Number(team.member_count || 0).toLocaleString()} members`, value: team.member_count },
          { key: 'verified', label: `${Number(team.verified_count || 0).toLocaleString()} verified`, value: team.verified_count },
          { key: 'active', label: `${Number(team.active_count || 0).toLocaleString()} active`, value: team.active_count },
          { key: 'kicked', label: `${Number(team.kicked_count || 0).toLocaleString()} kicked`, value: team.kicked_count },
          { key: 'inactive', label: `${Number(team.inactive_count || 0).toLocaleString()} inactive`, value: team.inactive_count },
          { key: 'same_day', label: `${Number(team.same_day_close_count || 0).toLocaleString()} same-day`, value: team.same_day_close_count },
          { key: 'fl_dental', label: `${Number(team.fl_dental_count || 0).toLocaleString()} FL dental`, value: team.fl_dental_count },
          { key: 'non_fl_mm', label: `${Number(team.non_fl_mm_count || 0).toLocaleString()} non-FL MM`, value: team.non_fl_mm_count },
          { key: 'non_fl_ameritas', label: `${Number(team.non_fl_ameritas_count || 0).toLocaleString()} non-FL Ameritas`, value: team.non_fl_ameritas_count },
          { key: 'aca', label: `${Number(team.aca_count || 0).toLocaleString()} ACA`, value: team.aca_count },
          { key: 'fed_market', label: `${Number(team.federal_marketplace_count || 0).toLocaleString()} Federal Mkt`, value: team.federal_marketplace_count },
          { key: 'state_market', label: `${Number(team.state_marketplace_count || 0).toLocaleString()} State Mkt`, value: team.state_marketplace_count },
          { key: 'ai_not_attributed', label: `${Number(team.ai_not_attributed_count || 0).toLocaleString()} AI not-attributed`, value: team.ai_not_attributed_count },
          {
            key: 'member_per',
            label: `${Number(team.member_per || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} members/verified`,
            value: team.member_per,
          },
        ].filter((chip) => chip.value != null && Number(chip.value) !== 0);
        const panelKey = team.id || team.team_lead_email || `ahm-team-${index}`;

        const header = isMobile ? (
          <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
            <AMSpace align="center" wrap style={{ '--gap': '6px' }}>
              <span style={{ fontWeight: 700 }}>
                {team.team_lead_name || team.team_lead_email || 'Team Lead'}
              </span>
              {trophy}
            </AMSpace>
            <div style={{ color: '#667085', fontSize: 12 }}>{team.team_name || 'Unassigned Team'}</div>
            {team.team_lead_email ? (
              <div style={{ color: '#667085', fontSize: 12 }}>{team.team_lead_email}</div>
            ) : null}
            <AMSpace wrap style={{ '--gap': '6px' }}>
              {teamTags.map((chip) => (
                <AMTag key={`${panelKey}-${chip.key}`} fill="outline">
                  {chip.label}
                </AMTag>
              ))}
            </AMSpace>
          </AMSpace>
        ) : (
          <AntSpace direction="vertical" size={4} style={{ width: '100%' }}>
            <AntSpace align="center" wrap>
              <AntTypography.Text strong>
                {team.team_lead_name || team.team_lead_email || 'Team Lead'}
              </AntTypography.Text>
              {trophy}
            </AntSpace>
            <AntTypography.Text type="secondary">
              {team.team_name || 'Unassigned Team'}
            </AntTypography.Text>
            {team.team_lead_email ? (
              <AntTypography.Text type="secondary" style={{ fontSize: 12 }}>
                {team.team_lead_email}
              </AntTypography.Text>
            ) : null}
            <AntSpace wrap size={[6, 6]}>
              {teamTags.map((chip) => (
                <AntTag key={`${panelKey}-${chip.key}`}>{chip.label}</AntTag>
              ))}
            </AntSpace>
          </AntSpace>
        );

        const sectionBlocks = [
          { title: 'Status', entries: team.statusEntries, max: 4, emptyText: 'No status data.' },
          { title: 'Issuers', entries: team.issuerEntries, max: 4, emptyText: 'No issuer data.' },
          { title: 'Coverage', entries: team.coverageEntries, max: 4, emptyText: 'No coverage data.' },
          { title: 'Hotspots', entries: team.cityEntries, max: 3, emptyText: 'No approved hotspots.' },
        ];

        const sectionContent = isMobile ? (
          <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
            {sectionBlocks.map((section) => (
              <AMCard key={`${panelKey}-${section.title}`} style={{ borderRadius: 10 }}>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
                    <div style={{ fontWeight: 600 }}>{section.title}</div>
                    {buildEntryTags(section.entries, section.max, section.emptyText)}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
            ))}
          </AMSpace>
        ) : (
          <AntRow gutter={[12, 12]}>
            {sectionBlocks.map((section) => (
              <AntCol xs={24} md={6} key={`${panelKey}-${section.title}`} style={{ display: 'flex' }}>
                <AntCard
                  size="small"
                  style={{ borderRadius: 12, width: '100%', height: '100%' }}
                  styles={{ body: { display: 'flex', flexDirection: 'column', gap: 8 } }}
                >
                  <AntTypography.Text strong>{section.title}</AntTypography.Text>
                  {buildEntryTags(section.entries, section.max, section.emptyText)}
                </AntCard>
              </AntCol>
            ))}
          </AntRow>
        );

        const agentCards = agents.map((agent, agentIndex) => {
          const genesysMatch =
            (agent.agent_email && ahmGenesysAgentMap.get(normalizeAhmAgentKey(agent.agent_email))) ||
            (agent.agent_name && ahmGenesysAgentMap.get(normalizeAhmAgentKey(agent.agent_name))) ||
            null;
          const rawCallsValue = Number(
            genesysMatch?.raw_calls ??
              genesysMatch?.calls ??
              genesysMatch?.metrics?.nConnected ??
              genesysMatch?.metrics?.nOutboundConnected ??
              0
          );
          const billedCallsValue = Number(
            genesysMatch?.billed_calls ??
              genesysMatch?.metrics?.nConnected_gt150 ??
              genesysMatch?.metrics?.tAnswered_gt150 ??
              0
          );
          const totalSalesValue = Number(agent.sales_count || 0);
          const submittedValue = Number(
            agent.submitted_count ??
              genesysMatch?.submitted ??
              genesysMatch?.metrics?.submitted ??
              0
          );
          const rawConversionPct =
            genesysMatch?.raw_conversion_pct != null
              ? Number(genesysMatch.raw_conversion_pct)
              : rawCallsValue > 0
                ? (totalSalesValue / rawCallsValue) * 100
                : null;
          const billedConversionPct =
            genesysMatch?.billed_conversion_pct != null
              ? Number(genesysMatch.billed_conversion_pct)
              : billedCallsValue > 0
                ? (totalSalesValue / billedCallsValue) * 100
                : null;
          const answeredMs = genesysMatch?.metrics?.tAnswered;
          const handleMs = genesysMatch?.metrics?.tHandle;
          const talkMs = genesysMatch?.metrics?.tTalk;
          const heldMs = genesysMatch?.metrics?.tHeld;
          const waitMs = genesysMatch?.metrics?.tWait;
          const vmMs = genesysMatch?.metrics?.tVoicemail;
          const aiScorecards = Array.isArray(agent.ai_not_attributed_scorecards)
            ? agent.ai_not_attributed_scorecards
            : [];

          const tagItems = [
            { key: 'sales', label: `${totalSalesValue.toLocaleString()} sales`, value: totalSalesValue },
            { key: 'submitted', label: `${Number(agent.submitted_count || 0).toLocaleString()} submitted`, value: agent.submitted_count },
            { key: 'approved', label: `${Number(agent.approved_from_submitted || 0).toLocaleString()} approved`, value: agent.approved_from_submitted },
            { key: 'members', label: `${Number(agent.member_count || 0).toLocaleString()} members`, value: agent.member_count },
            { key: 'verified', label: `${Number(agent.verified_count || 0).toLocaleString()} verified`, value: agent.verified_count },
            { key: 'active', label: `${Number(agent.active_count || 0).toLocaleString()} active`, value: agent.active_count },
            { key: 'kicked', label: `${Number(agent.kicked_count || 0).toLocaleString()} kicked`, value: agent.kicked_count },
            { key: 'inactive', label: `${Number(agent.inactive_count || 0).toLocaleString()} inactive`, value: agent.inactive_count },
            { key: 'same_day', label: `${Number(agent.same_day_close_count || 0).toLocaleString()} same-day`, value: agent.same_day_close_count },
            { key: 'fl_dental', label: `${Number(agent.fl_dental_count || 0).toLocaleString()} FL dental`, value: agent.fl_dental_count },
            { key: 'non_fl_mm', label: `${Number(agent.non_fl_mm_count || 0).toLocaleString()} non-FL MM`, value: agent.non_fl_mm_count },
            { key: 'non_fl_ameritas', label: `${Number(agent.non_fl_ameritas_count || 0).toLocaleString()} non-FL Ameritas`, value: agent.non_fl_ameritas_count },
            { key: 'aca', label: `${Number(agent.aca_count || 0).toLocaleString()} ACA`, value: agent.aca_count },
            { key: 'fed_market', label: `${Number(agent.federal_marketplace_count || 0).toLocaleString()} Federal Mkt`, value: agent.federal_marketplace_count },
            { key: 'state_market', label: `${Number(agent.state_marketplace_count || 0).toLocaleString()} State Mkt`, value: agent.state_marketplace_count },
            { key: 'ai_not_attributed', label: `${Number(agent.ai_not_attributed_count || 0).toLocaleString()} AI not-attributed`, value: agent.ai_not_attributed_count },
            { key: 'sub_active', label: `${formatPercent(agent.sub_to_active_pct || 0)} sub→active`, value: agent.sub_to_active_pct },
            {
              key: 'member_per',
              label: `${Number(agent.member_per || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} members/verified`,
              value: agent.member_per,
            },
            {
              key: 'genesys_raw_calls',
              label: `${rawCallsValue.toLocaleString()} raw calls (Genesys)`,
              value: rawCallsValue,
            },
            {
              key: 'genesys_billed_calls',
              label: `${billedCallsValue.toLocaleString()} billed calls (>150s)`,
              value: billedCallsValue,
            },
            {
              key: 'genesys_rows',
              label: `${submittedValue.toLocaleString()} Genesys rows`,
              value: submittedValue,
            },
            {
              key: 'genesys_raw_conv',
              label:
                rawConversionPct != null ? `${rawConversionPct.toFixed(1)}% raw→sales` : '',
              value: rawConversionPct,
            },
            {
              key: 'genesys_billed_conv',
              label:
                billedConversionPct != null
                  ? `${billedConversionPct.toFixed(1)}% billed→sales`
                  : '',
              value: billedConversionPct,
            },
            {
              key: 'genesys_answered',
              label: answeredMs != null ? `${formatDuration(answeredMs)} answered` : '',
              value: answeredMs,
            },
            {
              key: 'genesys_handle',
              label: handleMs != null ? `${formatDuration(handleMs)} handle` : '',
              value: handleMs,
            },
            {
              key: 'genesys_talk',
              label: talkMs != null ? `${formatDuration(talkMs)} talk` : '',
              value: talkMs,
            },
            {
              key: 'genesys_hold',
              label: heldMs != null ? `${formatDuration(heldMs)} hold` : '',
              value: heldMs,
            },
            {
              key: 'genesys_wait',
              label: waitMs != null ? `${formatDuration(waitMs)} wait` : '',
              value: waitMs,
            },
            {
              key: 'genesys_vm',
              label: vmMs != null ? `${formatDuration(vmMs)} voicemail` : '',
              value: vmMs,
            },
          ].filter((chip) => chip.value != null && chip.label);

          const agentName = agent.agent_name || agent.agent_email || 'Agent';
          if (isMobile) {
            return (
              <AMCard key={`${panelKey}-agent-${agentIndex}`} style={{ borderRadius: 10 }}>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
                    <div style={{ fontWeight: 600 }}>{agentName}</div>
                    <AMSpace wrap style={{ '--gap': '6px' }}>
                      {tagItems.map((chip) => (
                        <AMTag key={`${panelKey}-${agentName}-${chip.key}`} fill="outline">
                          {chip.label}
                        </AMTag>
                      ))}
                    </AMSpace>
                    {aiScorecards.length > 0 && (
                      <AMSpace wrap style={{ '--gap': '6px' }}>
                        {aiScorecards.map((sc, idx) => (
                          <AMButton
                            key={`${panelKey}-${agentName}-ai-${idx}`}
                            size="mini"
                            fill="outline"
                            onClick={() => sc.url && window.open(sc.url, '_blank', 'noopener')}
                          >
                            AI Scorecard {sc.ai_vcid || idx + 1}
                          </AMButton>
                        ))}
                      </AMSpace>
                    )}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
            );
          }

          return (
            <AntCard
              key={`${panelKey}-agent-${agentIndex}`}
              size="small"
              style={{ borderRadius: 12 }}
              styles={{ body: { display: 'flex', flexDirection: 'column', gap: 8 } }}
            >
              <AntTypography.Text strong>{agentName}</AntTypography.Text>
              <AntSpace wrap size={[6, 6]}>
                {tagItems.map((chip) => (
                  <AntTag key={`${panelKey}-${agentName}-${chip.key}`}>{chip.label}</AntTag>
                ))}
              </AntSpace>
              {aiScorecards.length > 0 && (
                <AntSpace wrap size={[6, 6]}>
                  {aiScorecards.map((sc, idx) => (
                    <AntButton
                      key={`${panelKey}-${agentName}-ai-${idx}`}
                      size="small"
                      type="link"
                      href={sc.url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      AI Scorecard {sc.ai_vcid || idx + 1}
                    </AntButton>
                  ))}
                </AntSpace>
              )}
            </AntCard>
          );
        });

        const children = (
          <div style={{ padding: isMobile ? '8px 0 4px' : '12px 0 4px' }}>
            {sectionContent}
            <div style={{ marginTop: 12 }}>
              {isMobile ? (
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Agents</div>
              ) : (
                <AntTypography.Text strong>Agents</AntTypography.Text>
              )}
              <div style={{ marginTop: 8 }}>
                {agents.length ? (
                  isMobile ? (
                    <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                      {agentCards}
                    </AMSpace>
                  ) : (
                    <AntSpace direction="vertical" style={{ width: '100%' }} size="small">
                      {agentCards}
                    </AntSpace>
                  )
                ) : (
                  renderExecutiveNotice('info', 'No agents mapped to this team.')
                )}
              </div>
            </div>
          </div>
        );

        return {
          key: panelKey,
          panelStyle: {
            border: '1px solid rgba(15, 23, 42, 0.12)',
            borderRadius: 12,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #0f172a08, #1e3a8a08)',
            marginBottom: 16,
          },
          label: header,
          children,
        };
      });

      return (
        <div style={{ marginTop: 16 }}>
          {isMobile ? (
            <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
              <div style={{ fontWeight: 700 }}>Team lead performance</div>
              <AMTag color="primary" fill="outline">
                Sorted by conversion %, then closed sales
              </AMTag>
            </AMSpace>
          ) : (
            <AntSpace align="center" style={{ marginBottom: 8 }} wrap>
              <AntTypography.Text strong>Team lead performance</AntTypography.Text>
              <AntTag>Sorted by conversion %, then closed sales</AntTag>
            </AntSpace>
          )}
          {renderResponsiveCollapse(teamPanels, { ghost: true })}
        </div>
      );
    };
    const genesysConnectedCalls = Number(
      ahmGenesysAnalytics?.genesys?.totals?.nConnected || 0
    );
    const rawToSubmittedPct =
      genesysConnectedCalls > 0
        ? (Number(ahmExecutiveState.summary?.submitted_count || 0) / genesysConnectedCalls) * 100
        : null;
    const getGenesysMetric = (key) => {
      const entry = ahmGenesysTotals.find((metric) => metric.key === key);
      return entry ? entry.value : null;
    };
    const genesysRawCalls = getGenesysMetric('raw_calls');
    const genesysBilledCalls = getGenesysMetric('billed_calls');
    const genesysRawConv = getGenesysMetric('raw_conversion_pct');
    const genesysBilledConv = getGenesysMetric('billed_conversion_pct');
    const ahmSummaryMetrics =
      isAhm && ahmExecutiveState.summary
        ? [
            {
              key: 'total_sales',
              label: 'Total Sales',
              value: ahmExecutiveState.summary.total_sales ?? ahmExecutiveState.summary.approved_count ?? ahmExecutiveState.summary.submitted_count,
              helper: 'All sales counted',
              tone: 'primary',
            },
            {
              key: 'submitted',
              label: 'Sales Submitted',
              value: ahmExecutiveState.summary.submitted_count,
              helper: 'Submitted rows',
            },
            {
              key: 'member_count',
              label: 'Member Count',
              value: ahmExecutiveState.summary.member_count,
              helper: 'Dependents + primary',
            },
            {
              key: 'verified_count',
              label: 'Verified (Approved)',
              value: ahmExecutiveState.summary.verified_count,
              helper: 'STATUS = Approved',
            },
            {
              key: 'active_count',
              label: 'Active',
              value: ahmExecutiveState.summary.active_count,
              helper: 'STATUS = Active',
            },
            {
              key: 'raw_to_submitted_pct',
              label: 'Raw → Submitted %',
              value: rawToSubmittedPct,
              helper: 'Submitted ÷ connected calls (Genesys)',
              formatter: formatPercent,
            },
            genesysRawCalls != null
              ? {
                  key: 'genesys_raw_calls',
                  label: 'Raw Calls (Genesys)',
                  value: genesysRawCalls,
                  helper: 'Outbound connected calls',
                }
              : null,
            genesysBilledCalls != null
              ? {
                  key: 'genesys_billed_calls',
                  label: 'Billed Calls (>150s)',
                  value: genesysBilledCalls,
                  helper: 'Connected calls over 150s',
                }
              : null,
            genesysRawConv != null
              ? {
                  key: 'genesys_raw_conv',
                  label: 'Raw Conversion %',
                  value: genesysRawConv,
                  helper: 'Sales ÷ raw calls',
                  formatter: formatPercent,
                }
              : null,
            genesysBilledConv != null
              ? {
                  key: 'genesys_billed_conv',
                  label: 'Billed Conversion %',
                  value: genesysBilledConv,
                  helper: 'Sales ÷ billed calls',
                  formatter: formatPercent,
                }
              : null,
            {
              key: 'kicked_count',
              label: 'Kicked',
              value: ahmExecutiveState.summary.kicked_count,
              helper: 'STATUS = Kicked',
            },
            {
              key: 'same_day_close',
              label: 'Same Day Close',
              value: ahmExecutiveState.summary.same_day_close_count,
              helper: 'Created and approved same day',
            },
            {
              key: 'fl_dental',
              label: 'FL Dental',
              value: ahmExecutiveState.summary.fl_dental_count,
              helper: 'Dental with FL residence',
            },
            {
              key: 'non_fl_mm',
              label: 'Non-FL Major Medical',
              value: ahmExecutiveState.summary.non_fl_mm_count,
              helper: 'Major Medical outside FL',
            },
            {
              key: 'non_fl_ameritas',
              label: 'Non-FL Ameritas',
              value: ahmExecutiveState.summary.non_fl_ameritas_count,
              helper: 'Ameritas outside FL',
            },
            {
              key: 'hios_healthsherpa_count',
              label: 'HealthSherpa Enrollments',
              value: ahmExecutiveState.summary.hios_healthsherpa_count,
              helper: 'HIOS ID blank',
            },
            {
              key: 'ai_not_attributed_count',
              label: 'AI Not-Attributed',
              value: ahmExecutiveState.summary.ai_not_attributed_count,
              helper: 'AI-detected sales with no attributed submitter',
            },
            {
              key: 'member_per',
              label: 'Members / Verified',
              value: ahmExecutiveState.summary.member_per,
              helper: 'Members per verified',
              formatter: (val) =>
                Number(val || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
            },
            {
              key: 'sub_to_active_pct',
              label: 'Sub → Active %',
              value: ahmExecutiveState.summary.sub_to_active_pct,
              helper: 'Active ÷ Verified',
              formatter: formatPercent,
            },
            {
              key: 'aca_count',
              label: 'ACA Enrollments',
              value: ahmExecutiveState.summary.aca_count,
              helper: 'Any ACA ID present',
              clusterKey: 'aca_family',
              clusterItems: [
                { key: 'federal', label: 'Federal', value: ahmExecutiveState.summary.federal_marketplace_count },
                { key: 'state', label: 'State', value: ahmExecutiveState.summary.state_marketplace_count },
              ],
            },
            {
              key: 'federal_marketplace_count',
              label: 'Federal Marketplace Enrollments',
              value: ahmExecutiveState.summary.federal_marketplace_count,
              helper: 'ACA ID + FFM ID',
              clusterKey: 'aca_family',
            },
            {
              key: 'state_marketplace_count',
              label: 'State Marketplace Enrollments',
              value: ahmExecutiveState.summary.state_marketplace_count,
              helper: 'ACA ID, blank FFM ID',
              clusterKey: 'aca_family',
            },
            {
              key: 'inactive_count',
              label: 'Inactive Rows',
              value: ahmExecutiveState.summary.inactive_count,
              helper: 'Status Inactive (phone matched)',
            },
          ]
        : [];
    const visibleAhmSummaryMetrics = ahmSummaryMetrics.filter(Boolean);

    const renderAhmSummary = () => {
      const chartSections = [
        { title: 'Statuses', data: ahmStatusPieData },
        { title: 'Issuers', data: ahmIssuerPieData },
        { title: 'Coverage', data: ahmCoveragePieData },
        { title: 'Hotspots', data: ahmHotspotPieData },
      ];

      const renderChartCard = (section) => {
        const chartHeight = isMobile ? 200 : 220;
        const chartCardMinHeight = isMobile ? 320 : 300;
        const tagAreaMinHeight = isMobile ? 44 : 40;
        const chartData = section.data.map((item, index) => ({
          ...item,
          color: item.color || KPI_PALETTE[index % KPI_PALETTE.length],
        }));
        const chartContent = chartData.length ? (
          <div style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                <XAxis type="number" tickLine={false} axisLine={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={isMobile ? 100 : 130}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => {
                    const label = String(val || '');
                    return label.length > 18 ? `${label.slice(0, 18)}…` : label;
                  }}
                />
                <RechartsTooltip
                  formatter={(value) => Number(value || 0).toLocaleString()}
                  labelFormatter={(label) => String(label || '')}
                />
                <Bar dataKey="value" radius={[4, 4, 4, 4]} label={{ position: 'right', fontSize: 11 }}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${section.title}-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div
            style={{
              height: chartHeight,
              color: '#667085',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            No data available.
          </div>
        );

        const tagList = section.data.length ? (
          isMobile ? (
            <AMSpace wrap style={{ '--gap': '6px' }}>
              {section.data.map((item) => (
                <AMTag key={`${section.title}-${item.name}`} fill="outline">
                  {item.name} ({Number(item.value).toLocaleString()})
                </AMTag>
              ))}
            </AMSpace>
          ) : (
            <AntSpace wrap size={[6, 6]}>
              {section.data.map((item) => (
                <AntTag key={`${section.title}-${item.name}`}>{`${item.name} (${Number(item.value).toLocaleString()})`}</AntTag>
              ))}
            </AntSpace>
          )
        ) : null;

        if (isMobile) {
          return (
            <AMCard key={section.title} style={{ borderRadius: 12, height: '100%' }}>
              <AMCard.Body>
                <AMSpace
                  direction="vertical"
                  block
                  style={{ '--gap': '8px', minHeight: chartCardMinHeight }}
                >
                  <div style={{ fontWeight: 700 }}>{section.title}</div>
                  <div>{chartContent}</div>
                  <div style={{ minHeight: tagAreaMinHeight }}>{tagList}</div>
                </AMSpace>
              </AMCard.Body>
            </AMCard>
          );
        }

        return (
          <AntCard
            key={section.title}
            style={{ borderRadius: 12, height: '100%' }}
            size="small"
            styles={{ body: { display: 'flex', flexDirection: 'column', gap: 12, height: '100%', minHeight: chartCardMinHeight } }}
          >
            <AntSpace direction="vertical" style={{ width: '100%' }} size="middle">
              <AntTypography.Text strong>{section.title}</AntTypography.Text>
              <div>{chartContent}</div>
              <div style={{ minHeight: tagAreaMinHeight }}>{tagList}</div>
            </AntSpace>
          </AntCard>
        );
      };

      const summaryGrid = isMobile ? (
        <AMGrid columns={2} gap={10}>
          {visibleAhmSummaryMetrics.map((metric) => {
            const displayValue =
              typeof metric.formatter === 'function'
                ? metric.formatter(metric.value)
                : Number(metric.value || 0).toLocaleString();
            return (
              <AMGrid.Item key={metric.key} style={{ display: 'flex' }}>
                <AMCard style={{ borderRadius: 10, width: '100%' }}>
                  <AMCard.Body>
                    <AMSpace direction="vertical" block style={{ '--gap': '4px' }}>
                      <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                        {metric.label}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 18 }}>{displayValue}</div>
                      {metric.helper ? (
                        <div style={{ fontSize: 12, color: '#667085' }}>{metric.helper}</div>
                      ) : null}
                      {metric.clusterKey && Array.isArray(metric.clusterItems) && metric.clusterItems.length ? (
                        <AMSpace wrap style={{ '--gap': '6px' }}>
                          {metric.clusterItems.map((item) => (
                            <AMTag key={`${metric.key}-${item.key}`} fill="outline">
                              {item.label}: {Number(item.value || 0).toLocaleString()}
                            </AMTag>
                          ))}
                        </AMSpace>
                      ) : null}
                    </AMSpace>
                  </AMCard.Body>
                </AMCard>
              </AMGrid.Item>
            );
          })}
        </AMGrid>
      ) : (
        <AntRow gutter={[12, 12]}>
          {visibleAhmSummaryMetrics.map((metric) => {
            const displayValue =
              typeof metric.formatter === 'function'
                ? metric.formatter(metric.value)
                : Number(metric.value || 0).toLocaleString();
            return (
              <AntCol xs={24} sm={12} md={8} lg={6} xl={4} key={metric.key} style={{ display: 'flex' }}>
                <AntCard
                  size="small"
                  style={{ borderRadius: 12, width: '100%' }}
                  styles={{ body: { display: 'flex', flexDirection: 'column', gap: 6 } }}
                >
                  <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                    {metric.label}
                  </AntTypography.Text>
                  <AntTypography.Title level={4} style={{ margin: 0 }}>
                    {displayValue}
                  </AntTypography.Title>
                  {metric.helper ? (
                    <AntTypography.Text type="secondary">{metric.helper}</AntTypography.Text>
                  ) : null}
                  {metric.clusterKey && Array.isArray(metric.clusterItems) && metric.clusterItems.length ? (
                    <AntSpace wrap size={[6, 6]}>
                      {metric.clusterItems.map((item) => (
                        <AntTag key={`${metric.key}-${item.key}`} color="blue">
                          {item.label}: {Number(item.value || 0).toLocaleString()}
                        </AntTag>
                      ))}
                    </AntSpace>
                  ) : null}
                </AntCard>
              </AntCol>
            );
          })}
        </AntRow>
      );

      const genesysRangeLabel = ahmGenesysAnalytics?.genesys?.interval
        ? `Interval: ${ahmGenesysAnalytics.genesys.interval}`
        : ahmGenesysAnalytics?.dateRange
          ? `Range: ${ahmGenesysAnalytics.dateRange.start || ''} → ${ahmGenesysAnalytics.dateRange.end || ''}`
          : 'Range derived from selected dates';

      const renderGenesysTotals = () => {
        if (!ahmGenesysTotals.length) return null;
        if (isMobile) {
          return (
            <AMGrid columns={2} gap={10} style={{ marginTop: 8 }}>
              {ahmGenesysTotals.map((metric) => {
                const displayValue = metric.isDuration
                  ? formatDuration(metric.value)
                  : metric.isPercent
                    ? `${Number(metric.value || 0).toFixed(1)}%`
                    : Number(metric.value || 0).toLocaleString();
                return (
                  <AMGrid.Item key={metric.key} style={{ display: 'flex' }}>
                    <AMCard style={{ borderRadius: 10, width: '100%' }}>
                      <AMCard.Body>
                        <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                          {metric.label}
                        </div>
                        <div style={{ fontWeight: 700 }}>{displayValue}</div>
                      </AMCard.Body>
                    </AMCard>
                  </AMGrid.Item>
                );
              })}
            </AMGrid>
          );
        }
        return (
          <AntRow gutter={[12, 12]} style={{ marginTop: 8 }}>
            {ahmGenesysTotals.map((metric) => {
              const displayValue = metric.isDuration
                ? formatDuration(metric.value)
                : metric.isPercent
                  ? `${Number(metric.value || 0).toFixed(1)}%`
                  : Number(metric.value || 0).toLocaleString();
              return (
                <AntCol xs={24} sm={12} md={6} key={metric.key} style={{ display: 'flex' }}>
                  <AntCard
                    size="small"
                    style={{ borderRadius: 12, width: '100%' }}
                    styles={{ body: { display: 'flex', flexDirection: 'column', gap: 6 } }}
                  >
                    <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                      {metric.label}
                    </AntTypography.Text>
                    <AntTypography.Text strong>{displayValue}</AntTypography.Text>
                  </AntCard>
                </AntCol>
              );
            })}
          </AntRow>
        );
      };

      const genesysTeamsData = ahmGenesysTeams.map((team) => {
        const topDispo = Object.entries(team.dispositions || {})
          .map(([name, count]) => ({ name: name || 'unspecified', count: Number(count) || 0 }))
          .sort((a, b) => b.count - a.count)[0];
        const rawCalls = Number(team.metrics?.raw_calls || 0);
        const billedCalls = Number(team.metrics?.billed_calls || 0);
        const sales = Number(team.metrics?.sales || 0);
        const rawConv = rawCalls > 0 ? (sales / rawCalls) * 100 : null;
        const billedConv = billedCalls > 0 ? (sales / billedCalls) * 100 : null;
        return {
          key: team.name,
          team: team.name,
          rows: Number(team.rows || 0),
          agents: Number(team.agentCount || 0),
          rawCalls,
          billedCalls,
          sales,
          rawConv,
          billedConv,
          avgHandle: team.avg_handle_duration,
          avgTalk: team.avg_talk_duration,
          avgWait: team.avg_wait_duration,
          avgVm: team.avg_vm_duration,
          topDispo: topDispo ? `${topDispo.name} (${Number(topDispo.count).toLocaleString()})` : '—',
        };
      });

      const genesysAgentsData = ahmGenesysAgents.map((agent) => {
        const topDispo = Object.entries(agent.dispositions || {})
          .map(([name, count]) => ({ name: name || 'unspecified', count: Number(count) || 0 }))
          .sort((a, b) => b.count - a.count)[0];
        return {
          key: agent.id,
          name: agent.name,
          email: agent.email,
          team: agent.team || '—',
          rawCalls: Number(agent.raw_calls || 0),
          billedCalls: Number(agent.billed_calls || 0),
          sales: Number(agent.sales || 0),
          rawConv: agent.raw_conversion_pct != null ? Number(agent.raw_conversion_pct) : null,
          billedConv: agent.billed_conversion_pct != null ? Number(agent.billed_conversion_pct) : null,
          avgHandle: agent.avg_handle_duration,
          avgTalk: agent.avg_talk_duration,
          avgWait: agent.avg_wait_duration,
          avgVm: agent.avg_vm_duration,
          topDispo: topDispo ? `${topDispo.name} (${Number(topDispo.count).toLocaleString()})` : '—',
        };
      });

      const genesysTeamColumns = [
        { title: 'Team', dataIndex: 'team', key: 'team' },
        { title: 'Rows', dataIndex: 'rows', key: 'rows', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Agents', dataIndex: 'agents', key: 'agents', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Raw Calls', dataIndex: 'rawCalls', key: 'rawCalls', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Billed Calls', dataIndex: 'billedCalls', key: 'billedCalls', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Sales', dataIndex: 'sales', key: 'sales', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Raw Conv', dataIndex: 'rawConv', key: 'rawConv', align: 'right', render: (val) => (val != null ? `${val.toFixed(1)}%` : '—') },
        { title: 'Billed Conv', dataIndex: 'billedConv', key: 'billedConv', align: 'right', render: (val) => (val != null ? `${val.toFixed(1)}%` : '—') },
        { title: 'Avg Handle', dataIndex: 'avgHandle', key: 'avgHandle', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Avg Talk', dataIndex: 'avgTalk', key: 'avgTalk', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Avg Wait', dataIndex: 'avgWait', key: 'avgWait', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Avg VM', dataIndex: 'avgVm', key: 'avgVm', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Top Dispo', dataIndex: 'topDispo', key: 'topDispo', align: 'right' },
      ];

      const genesysAgentColumns = [
        {
          title: 'Agent',
          dataIndex: 'name',
          key: 'name',
          render: (_val, record) => (
            <div>
              <div style={{ fontWeight: 600 }}>{record.name}</div>
              {record.email ? <div style={{ color: '#667085', fontSize: 12 }}>{record.email}</div> : null}
            </div>
          ),
        },
        { title: 'Team', dataIndex: 'team', key: 'team' },
        { title: 'Raw Calls', dataIndex: 'rawCalls', key: 'rawCalls', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Billed Calls', dataIndex: 'billedCalls', key: 'billedCalls', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Sales', dataIndex: 'sales', key: 'sales', align: 'right', render: (val) => Number(val || 0).toLocaleString() },
        { title: 'Raw Conv', dataIndex: 'rawConv', key: 'rawConv', align: 'right', render: (val) => (val != null ? `${Number(val).toFixed(1)}%` : '—') },
        { title: 'Billed Conv', dataIndex: 'billedConv', key: 'billedConv', align: 'right', render: (val) => (val != null ? `${Number(val).toFixed(1)}%` : '—') },
        { title: 'Avg Handle', dataIndex: 'avgHandle', key: 'avgHandle', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Avg Talk', dataIndex: 'avgTalk', key: 'avgTalk', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Avg Wait', dataIndex: 'avgWait', key: 'avgWait', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Avg VM', dataIndex: 'avgVm', key: 'avgVm', align: 'right', render: (val) => formatDuration(val) },
        { title: 'Top Dispo', dataIndex: 'topDispo', key: 'topDispo', align: 'right' },
      ];

      return (
        <AntSpace direction="vertical" size="middle" style={{ width: '100%' }}>
          {isMobile ? (
            <AMSpace direction="vertical" block style={{ '--gap': '10px' }}>
              {chartSections.map(renderChartCard)}
            </AMSpace>
          ) : (
            <AntRow gutter={[12, 12]}>
              {chartSections.map((section) => (
                <AntCol xs={24} md={12} key={section.title} style={{ display: 'flex' }}>
                  {renderChartCard(section)}
                </AntCol>
              ))}
            </AntRow>
          )}
          <div>{summaryGrid}</div>
          {ahmGenesysAnalytics && (
            <>
              {isMobile ? (
                <AMCard style={{ borderRadius: 12 }}>
                  <AMCard.Body>
                    <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                      <div style={{ fontWeight: 700 }}>Genesys Call Analytics</div>
                      <div style={{ color: '#667085', fontSize: 12 }}>{genesysRangeLabel}</div>
                      {ahmGenesysError ? (
                        renderExecutiveNotice('warning', `Genesys analytics: ${ahmGenesysError}`)
                      ) : null}
                      {renderGenesysTotals()}
                      <AMSpace wrap style={{ '--gap': '6px' }}>
                        <AMTag fill="outline">
                          Rows: {Number(ahmGenesysAnalytics?.csvStats?.rowCount || 0).toLocaleString()}
                        </AMTag>
                        {ahmGenesysTotals.map((metric) => (
                          <AMTag key={metric.key} fill="outline">
                            {metric.label}:{' '}
                            {metric.isDuration
                              ? formatDuration(metric.value)
                              : metric.isPercent
                                ? `${Number(metric.value || 0).toFixed(1)}%`
                                : Number(metric.value || 0).toLocaleString()}
                          </AMTag>
                        ))}
                      </AMSpace>
                      <AMSpace wrap style={{ '--gap': '6px' }}>
                        {ahmGenesysDispositions.map((d) => (
                          <AMTag key={`disp-${d.name}`} fill="outline">
                            {d.name}: {Number(d.count || 0).toLocaleString()}
                          </AMTag>
                        ))}
                        {!ahmGenesysDispositions.length && (
                          <div style={{ color: '#667085', fontSize: 12 }}>No dispositions found.</div>
                        )}
                      </AMSpace>
                    </AMSpace>
                  </AMCard.Body>
                </AMCard>
              ) : (
                <AntCard
                  title="Genesys Call Analytics"
                  extra={<AntTypography.Text type="secondary">{genesysRangeLabel}</AntTypography.Text>}
                  style={{ borderRadius: 12 }}
                >
                  {ahmGenesysError ? (
                    <div style={{ marginBottom: 12 }}>
                      {renderExecutiveNotice('warning', `Genesys analytics: ${ahmGenesysError}`)}
                    </div>
                  ) : null}
                  {renderGenesysTotals()}
                  <AntSpace wrap size={[6, 6]} style={{ marginTop: 8 }}>
                    <AntTag color="blue">
                      Rows: {Number(ahmGenesysAnalytics?.csvStats?.rowCount || 0).toLocaleString()}
                    </AntTag>
                    {ahmGenesysTotals.map((metric) => (
                      <AntTag key={metric.key} color="blue">
                        {metric.label}:{' '}
                        {metric.isDuration
                          ? formatDuration(metric.value)
                          : metric.isPercent
                            ? `${Number(metric.value || 0).toFixed(1)}%`
                            : Number(metric.value || 0).toLocaleString()}
                      </AntTag>
                    ))}
                  </AntSpace>
                  <AntSpace wrap size={[6, 6]} style={{ marginTop: 8 }}>
                    {ahmGenesysDispositions.map((d) => (
                      <AntTag key={`disp-${d.name}`}>{`${d.name}: ${Number(d.count || 0).toLocaleString()}`}</AntTag>
                    ))}
                    {!ahmGenesysDispositions.length && (
                      <AntTypography.Text type="secondary">No dispositions found.</AntTypography.Text>
                    )}
                  </AntSpace>
                </AntCard>
              )}
            </>
          )}
          {ahmGenesysTeams.length > 0 && (
            isMobile ? (
              <AMCard style={{ borderRadius: 12 }}>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                    <div style={{ fontWeight: 700 }}>Top Teams (Genesys + ACA)</div>
                    {genesysTeamsData.map((row) => (
                      <AMCard key={row.key} style={{ borderRadius: 10 }}>
                        <AMCard.Body>
                          <AMSpace direction="vertical" block style={{ '--gap': '4px' }}>
                            <div style={{ fontWeight: 600 }}>{row.team}</div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Rows {row.rows.toLocaleString()} · Agents {row.agents.toLocaleString()}
                            </div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Raw {row.rawCalls.toLocaleString()} · Billed {row.billedCalls.toLocaleString()} · Sales {row.sales.toLocaleString()}
                            </div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Raw Conv {row.rawConv != null ? `${row.rawConv.toFixed(1)}%` : '—'} · Billed Conv {row.billedConv != null ? `${row.billedConv.toFixed(1)}%` : '—'}
                            </div>
                            <div style={{ fontSize: 12, color: '#667085' }}>Top Dispo {row.topDispo}</div>
                          </AMSpace>
                        </AMCard.Body>
                      </AMCard>
                    ))}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
            ) : (
              <AntCard title="Top Teams (Genesys + ACA)" style={{ borderRadius: 12 }}>
                <AntTable
                  columns={genesysTeamColumns}
                  dataSource={genesysTeamsData}
                  size="small"
                  pagination={false}
                />
              </AntCard>
            )
          )}
          {ahmGenesysAgents.length > 0 && (
            isMobile ? (
              <AMCard style={{ borderRadius: 12 }}>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                    <div style={{ fontWeight: 700 }}>Top Agents</div>
                    {genesysAgentsData.map((row) => (
                      <AMCard key={row.key} style={{ borderRadius: 10 }}>
                        <AMCard.Body>
                          <AMSpace direction="vertical" block style={{ '--gap': '4px' }}>
                            <div style={{ fontWeight: 600 }}>{row.name}</div>
                            {row.email ? <div style={{ fontSize: 12, color: '#667085' }}>{row.email}</div> : null}
                            <div style={{ fontSize: 12, color: '#667085' }}>Team {row.team}</div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Raw {row.rawCalls.toLocaleString()} · Billed {row.billedCalls.toLocaleString()} · Sales {row.sales.toLocaleString()}
                            </div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Raw Conv {row.rawConv != null ? `${row.rawConv.toFixed(1)}%` : '—'} · Billed Conv {row.billedConv != null ? `${row.billedConv.toFixed(1)}%` : '—'}
                            </div>
                            <div style={{ fontSize: 12, color: '#667085' }}>Top Dispo {row.topDispo}</div>
                          </AMSpace>
                        </AMCard.Body>
                      </AMCard>
                    ))}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
            ) : (
              <AntCard title="Top Agents" style={{ borderRadius: 12 }}>
                <AntTable
                  columns={genesysAgentColumns}
                  dataSource={genesysAgentsData}
                  size="small"
                  pagination={false}
                />
              </AntCard>
            )
          )}
        </AntSpace>
      );
    };

    const renderExecutiveKpiVisualizer = () => {
      const chartHeight = isMobile ? 260 : 240;
      const renderChart = () => {
        if (kpiGraphData.length === 0) {
          return (
            <div style={{ color: '#667085', fontSize: 13 }}>
              No data available for the selected configuration.
            </div>
          );
        }
        if (kpiGraphType === 'line') {
          return (
            <div style={{ height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={kpiGraphData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                  <XAxis
                    dataKey="name"
                    hide={kpiGraphData.length > 10}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RechartsTooltip
                    formatter={(value) => Number(value || 0).toLocaleString()}
                    labelFormatter={(label) => String(label || '')}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name={kpiGraphMetricLabel}
                    stroke={KPI_GRAPH_COLORS[0]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          );
        }
        if (kpiGraphType === 'pie') {
          return (
            <AntPlotPie
              data={kpiGraphData}
              angleField="value"
              colorField="name"
              height={chartHeight}
              radius={0.9}
              innerRadius={0.55}
              color={KPI_GRAPH_COLORS}
            />
          );
        }
        return (
          <AntPlotColumn
            data={kpiGraphData}
            xField="name"
            yField="value"
            height={chartHeight}
            columnWidthRatio={0.6}
            color={KPI_GRAPH_COLORS[0]}
            xAxis={{ label: { autoHide: true } }}
          />
        );
      };

      const controls = isMobile ? (
        <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
          <AMSelector
            options={kpiGraphMetricOptions.map((option) => ({ label: option.label, value: option.value }))}
            value={[kpiGraphMetric]}
            onChange={(val) => val?.[0] && setKpiGraphMetric(val[0])}
          />
          <AMSelector
            options={kpiGraphDimensionOptions.map((option) => ({ label: option.label, value: option.value }))}
            value={[kpiGraphDimension]}
            onChange={(val) => val?.[0] && setKpiGraphDimension(val[0])}
          />
          <AMSelector
            options={kpiGraphTypeOptions.map((option) => ({ label: option.label, value: option.value }))}
            value={[kpiGraphType]}
            onChange={(val) => val?.[0] && setKpiGraphType(val[0])}
          />
          <AMInput
            placeholder="Filter by name"
            value={kpiGraphSearch}
            onChange={(val) => setKpiGraphSearch(val)}
          />
        </AMSpace>
      ) : (
        <AntSpace wrap size={[12, 12]}>
          <AntSelect
            value={kpiGraphMetric}
            onChange={(val) => setKpiGraphMetric(val)}
            options={kpiGraphMetricOptions.map((option) => ({ label: option.label, value: option.value }))}
            style={{ minWidth: 160 }}
          />
          <AntSelect
            value={kpiGraphDimension}
            onChange={(val) => setKpiGraphDimension(val)}
            options={kpiGraphDimensionOptions.map((option) => ({ label: option.label, value: option.value }))}
            style={{ minWidth: 160 }}
          />
          <AntSelect
            value={kpiGraphType}
            onChange={(val) => setKpiGraphType(val)}
            options={kpiGraphTypeOptions.map((option) => ({ label: option.label, value: option.value }))}
            style={{ minWidth: 140 }}
          />
          <AntInput
            placeholder="Filter by name"
            value={kpiGraphSearch}
            onChange={(e) => setKpiGraphSearch(e.target.value)}
            style={{ minWidth: 180 }}
          />
        </AntSpace>
      );

      if (isMobile) {
        return (
          <AMCard style={{ borderRadius: 12, marginBottom: 16 }}>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                <div style={{ fontWeight: 700 }}>KPI Visualizer</div>
                <div style={{ color: '#667085', fontSize: 12 }}>
                  Select a KPI, grouping, and chart style to visualize executive performance.
                </div>
                {controls}
                <div style={{ minHeight: chartHeight }}>{renderChart()}</div>
              </AMSpace>
            </AMCard.Body>
          </AMCard>
        );
      }

      return (
        <AntCard style={{ borderRadius: 12, marginBottom: 16 }}>
          <AntSpace direction="vertical" style={{ width: '100%' }} size="middle">
            <AntTypography.Text strong>KPI Visualizer</AntTypography.Text>
            <AntTypography.Text type="secondary">
              Select a KPI, grouping, and chart style to visualize executive performance.
            </AntTypography.Text>
            {controls}
            <div>{renderChart()}</div>
          </AntSpace>
        </AntCard>
      );
    };

    const renderExecutiveRollup = () => {
      if (!executiveAggregateStats) return null;
      const ancillaryCards = buildAncillaryMetricCards(effectiveAncillaryTotals, { includeTotal: true });
      const salesContent = (
        <>
          <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
            Sales Performance
          </AntTypography.Text>
          <AntTypography.Title level={3} style={{ margin: '4px 0' }}>
            {effectiveRollupSales.toLocaleString()}
          </AntTypography.Title>
          <AntTypography.Text type="secondary">
            Company-wide closed sales for this window.
          </AntTypography.Text>
          <AntSpace direction="vertical" size={4} style={{ marginTop: 8 }}>
            <div>
              <strong>IPC:</strong> {Number(executiveAggregateStats.totalIpc || 0).toLocaleString()}
            </div>
            <div>
              <strong>Total Raw Call Volume:</strong>{' '}
              {Number(executiveAggregateStats.rawCallCount || 0).toLocaleString()}
            </div>
            <div>
              <strong>AVG Raw-to-Close Conversion:</strong>{' '}
              {`${Number(executiveAggregateStats.avgConversion || 0).toFixed(1)}%`}
            </div>
          </AntSpace>
          {ancillaryCards.length ? (
            <AntSpace wrap size={[6, 6]} style={{ marginTop: 8 }}>
              {ancillaryCards.map((metric) => (
                <AntTag key={`exec-anc-${metric.label}`} color={metric.label.toLowerCase().includes('total') ? 'blue' : 'default'}>
                  {metric.label}: {metric.value}
                </AntTag>
              ))}
            </AntSpace>
          ) : null}
        </>
      );

      const publisherContent = publisherLiveSummary ? (
        <>
          <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
            Publisher CPA Snapshot (Today)
          </AntTypography.Text>
          <AntTypography.Title level={4} style={{ margin: '4px 0' }}>
            {publisherLiveSummary.totalCallsLabel} calls · {publisherLiveSummary.salesCountLabel} sales
          </AntTypography.Title>
          <AntTypography.Text type="secondary">
            Billed calls {publisherLiveSummary.billedCallsLabel} · Billed cost {publisherLiveSummary.billedCostLabel}
          </AntTypography.Text>
          <AntSpace direction="vertical" size={4} style={{ marginTop: 8 }}>
            <div>
              <strong>Average raw to close %:</strong> {publisherLiveSummary.totalConversionLabel}
            </div>
            <div>
              <strong>Avg billed to close %:</strong> {publisherLiveSummary.billedConversionLabel}
            </div>
            <div>
              <strong>TOTAL CPA:</strong> {publisherLiveSummary.cpaLabel}
            </div>
          </AntSpace>
        </>
      ) : (
        <AntTypography.Text type="secondary">
          Live publisher CPA metrics are not available yet for today. Use the Publisher Live CPA&apos;s tab to pull a fresh snapshot.
        </AntTypography.Text>
      );

      const complianceContent = (
        <>
          <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
            Quality & Compliance
          </AntTypography.Text>
          <AntTypography.Title level={4} style={{ margin: '4px 0' }}>
            {executiveAggregateStats.complianceAverage != null
              ? Number(executiveAggregateStats.complianceAverage || 0).toFixed(1)
              : '—'}
          </AntTypography.Title>
          <AntTypography.Text type="secondary">Avg compliance score</AntTypography.Text>
        </>
      );

      if (isMobile) {
        return (
          <AMCard style={{ borderRadius: 12, marginBottom: 16 }}>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '10px' }}>
                <div style={{ fontWeight: 700 }}>Executive Rollup</div>
                <div style={{ color: '#667085', fontSize: 12 }}>
                  {executiveCompanyLabel} KPIs · {executiveDateLabel}
                </div>
                {executiveRollupTotals.loading && (
                  <div style={{ color: '#667085', fontSize: 12 }}>Refreshing company sales snapshot…</div>
                )}
                {executiveRollupTotals.error && renderExecutiveNotice('warning', executiveRollupTotals.error)}
                <AMCard style={{ borderRadius: 10 }}>
                  <AMCard.Body>{salesContent}</AMCard.Body>
                </AMCard>
                <AMCard style={{ borderRadius: 10 }}>
                  <AMCard.Body>{publisherContent}</AMCard.Body>
                </AMCard>
                <AMCard style={{ borderRadius: 10 }}>
                  <AMCard.Body>{complianceContent}</AMCard.Body>
                </AMCard>
              </AMSpace>
            </AMCard.Body>
          </AMCard>
        );
      }

      return (
        <AntCard
          title="Executive Rollup"
          style={{ borderRadius: 12, marginBottom: 16 }}
          extra={<AntTypography.Text type="secondary">{`${executiveCompanyLabel} KPIs · ${executiveDateLabel}`}</AntTypography.Text>}
        >
          {executiveRollupTotals.loading && (
            <AntTypography.Text type="secondary">Refreshing company sales snapshot…</AntTypography.Text>
          )}
          {executiveRollupTotals.error && (
            <div style={{ marginTop: 8 }}>{renderExecutiveNotice('warning', executiveRollupTotals.error)}</div>
          )}
          <AntRow gutter={[12, 12]} style={{ marginTop: 12 }}>
            <AntCol xs={24} md={8} style={{ display: 'flex' }}>
              <AntCard size="small" style={{ borderRadius: 12, width: '100%' }}>
                {salesContent}
              </AntCard>
            </AntCol>
            <AntCol xs={24} md={8} style={{ display: 'flex' }}>
              <AntCard size="small" style={{ borderRadius: 12, width: '100%' }}>
                {publisherContent}
              </AntCard>
            </AntCol>
            <AntCol xs={24} md={8} style={{ display: 'flex' }}>
              <AntCard size="small" style={{ borderRadius: 12, width: '100%' }}>
                {complianceContent}
              </AntCard>
            </AntCol>
          </AntRow>
        </AntCard>
      );
    };

    const renderExecutiveFilters = () => {
      if (executiveViewMode === 'publisherLive') return null;
      const directionOptions = [
        { label: 'All', value: 'all' },
        { label: 'Highest', value: 'above' },
        { label: 'Lowest', value: 'below' },
      ];
      const meetingOptions = [
        { label: 'All meeting outcomes', value: 'all' },
        { label: 'Has scheduled meetings', value: 'scheduled' },
        { label: 'Has missed meetings', value: 'missed' },
      ];
      if (isMobile) {
        return (
          <AMSpace direction="vertical" block style={{ '--gap': '8px', marginBottom: 12 }}>
            <AMSelector
              options={Object.entries(TEAM_KPI_STANDARD_OPTIONS).map(([key, config]) => ({
                label: config.label,
                value: key,
              }))}
              value={[teamKpiFilter.kpi]}
              onChange={(val) => val?.[0] && setTeamKpiFilter((prev) => ({ ...prev, kpi: val[0] }))}
            />
            <AMSelector
              options={directionOptions}
              value={[teamKpiFilter.direction]}
              onChange={(val) => {
                const value = val?.[0];
                if (!value) return;
                setTeamKpiFilter((prev) => ({ ...prev, direction: value }));
                if (value === 'below') setManagerSortOrder('asc');
                else setManagerSortOrder('desc');
              }}
            />
            <AMSelector
              options={meetingOptions}
              value={[meetingRowFilter]}
              onChange={(val) => val?.[0] && setMeetingRowFilter(val[0])}
            />
            <AMInput
              type="date"
              value={kpiSelectedDate}
              onChange={(val) => handleKpiDateChange(val)}
            />
          </AMSpace>
        );
      }
      return (
        <AntSpace wrap size={[12, 12]} style={{ marginBottom: 12 }}>
          <AntSelect
            value={teamKpiFilter.kpi}
            onChange={(val) => setTeamKpiFilter((prev) => ({ ...prev, kpi: val }))}
            options={Object.entries(TEAM_KPI_STANDARD_OPTIONS).map(([key, config]) => ({
              label: config.label,
              value: key,
            }))}
            style={{ minWidth: 180 }}
          />
          <AntSegmented
            value={teamKpiFilter.direction}
            options={directionOptions}
            onChange={(val) => {
              setTeamKpiFilter((prev) => ({ ...prev, direction: val }));
              if (val === 'below') setManagerSortOrder('asc');
              else setManagerSortOrder('desc');
            }}
          />
          <AntSelect
            value={meetingRowFilter}
            onChange={(val) => setMeetingRowFilter(val)}
            options={meetingOptions}
            style={{ minWidth: 200 }}
          />
          <AntInput type="date" value={kpiSelectedDate} onChange={(e) => handleKpiDateChange(e.target.value)} />
        </AntSpace>
      );
    };

    const renderExecutiveControls = () => {
      const viewModeOptions = [
        { label: 'By Manager', value: 'hierarchy' },
        { label: 'All Agents', value: 'agents' },
        { label: "Publisher Live CPA's", value: 'publisherLive' },
      ];
      const viewModeSelector = isMobile ? (
        <AMSelector
          options={viewModeOptions}
          value={[executiveViewMode]}
          onChange={(val) => val?.[0] && setExecutiveViewMode(val[0])}
        />
      ) : (
        <AntSegmented value={executiveViewMode} options={viewModeOptions} onChange={(val) => setExecutiveViewMode(val)} />
      );

      const dateRangeControls =
        executiveViewMode !== 'publisherLive' && !isAhm ? (
          isMobile ? (
            <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
              <AMInput
                type="date"
                value={executiveRange.startDate}
                onChange={(val) => handleExecutiveDateChange('startDate', val)}
              />
              <AMInput
                type="date"
                value={executiveRange.endDate}
                onChange={(val) => handleExecutiveDateChange('endDate', val)}
              />
              <AMTag fill="outline">{executiveRange.label}</AMTag>
            </AMSpace>
          ) : (
            <AntSpace wrap>
              <AntInput
                type="date"
                value={executiveRange.startDate}
                onChange={(e) => handleExecutiveDateChange('startDate', e.target.value)}
              />
              <AntInput
                type="date"
                value={executiveRange.endDate}
                onChange={(e) => handleExecutiveDateChange('endDate', e.target.value)}
              />
              <AntTag>{executiveRange.label}</AntTag>
            </AntSpace>
          )
        ) : null;

      const statusTags = isMobile ? (
        <AMSpace wrap style={{ '--gap': '6px' }}>
          <AMTag fill="outline">{executiveDashboardState.loading ? 'Medicare: Loading…' : 'Medicare'}</AMTag>
          <AMTag fill="outline">{ahmExecutiveState.loading ? 'ACA: Loading…' : 'ACA'}</AMTag>
        </AMSpace>
      ) : (
        <AntSpace wrap>
          <AntTag>{executiveDashboardState.loading ? 'Medicare: Loading…' : 'Medicare'}</AntTag>
          <AntTag>{ahmExecutiveState.loading ? 'ACA: Loading…' : 'ACA'}</AntTag>
        </AntSpace>
      );

      if (isMobile) {
        return (
          <AMSpace direction="vertical" block style={{ '--gap': '10px', marginBottom: 16 }}>
            {renderExecutiveFilters()}
            {viewModeSelector}
            {dateRangeControls}
            {statusTags}
          </AMSpace>
        );
      }
      return (
        <AntSpace direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          {renderExecutiveFilters()}
          <AntSpace wrap size={[12, 12]} align="center">
            {viewModeSelector}
            {dateRangeControls}
            {statusTags}
          </AntSpace>
        </AntSpace>
      );
    };

    const renderPublisherLiveSection = () => {
      const summaryMetrics = [
        { label: 'Total Calls', value: publisherLiveSummary?.totalCallsLabel },
        { label: 'Sales', value: publisherLiveSummary?.salesCountLabel },
        { label: 'Billed Calls', value: publisherLiveSummary?.billedCallsLabel },
        { label: 'Billed Cost', value: publisherLiveSummary?.billedCostLabel },
        { label: 'Total Conv. Rate', value: publisherLiveSummary?.totalConversionLabel },
        { label: 'CPA', value: publisherLiveSummary?.cpaLabel },
      ].filter((metric) => metric.value);

      const tableColumns = [
        { title: 'Publisher', dataIndex: 'publisher', key: 'publisher' },
        { title: 'Total Calls', dataIndex: 'totalCalls', key: 'totalCalls', align: 'right', render: (val) => (Number.isFinite(val) ? val.toLocaleString() : '—') },
        { title: 'Sales', dataIndex: 'salesCount', key: 'salesCount', align: 'right', render: (val) => (Number.isFinite(val) ? val.toLocaleString() : '—') },
        { title: 'Billed Calls', dataIndex: 'billedCalls', key: 'billedCalls', align: 'right', render: (val) => (Number.isFinite(val) ? val.toLocaleString() : '—') },
        {
          title: 'Billed Cost',
          dataIndex: 'billedCost',
          key: 'billedCost',
          align: 'right',
          render: (val, row) =>
            Number.isFinite(val)
              ? new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: row.currency || 'USD',
                  maximumFractionDigits: 0,
                }).format(val)
              : '—',
        },
        {
          title: 'Total Conv. %',
          dataIndex: 'totalConversionPct',
          key: 'totalConversionPct',
          align: 'right',
          render: (val) => (Number.isFinite(val) ? formatPercent(val) : '—'),
        },
        {
          title: 'CPA',
          dataIndex: 'cpaValue',
          key: 'cpaValue',
          align: 'right',
          render: (val, row) =>
            Number.isFinite(val)
              ? new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: row.currency || 'USD',
                  maximumFractionDigits: 0,
                }).format(val)
              : '—',
        },
      ];

      if (isMobile) {
        return (
          <AMCard style={{ borderRadius: 12 }}>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                <div style={{ fontWeight: 700 }}>Publisher Live CPA&apos;s</div>
                <div style={{ color: '#667085', fontSize: 12 }}>
                  {publisherLiveSummary
                    ? `Today · ${publisherLiveLastUpdatedLabel ? `Last updated ${publisherLiveLastUpdatedLabel}` : 'Live snapshot'}`
                    : 'Today · Live publisher CPA snapshot'}
                </div>
                <AMSpace wrap style={{ '--gap': '6px' }}>
                  <AMButton
                    size="mini"
                    fill="outline"
                    onClick={() => fetchPublisherLive({ forceRefresh: true, showLoader: true })}
                    disabled={publisherLiveState.loading}
                  >
                    Refresh live
                  </AMButton>
                </AMSpace>
                {publisherLiveState.error && renderExecutiveNotice('error', publisherLiveState.error)}
                {publisherLiveState.loading && !publisherLiveState.data && !publisherLiveState.error
                  ? renderExecutiveSpinner('Loading publisher live CPA snapshot…')
                  : null}
                {summaryMetrics.length > 0 && (
                  <AMGrid columns={2} gap={10}>
                    {summaryMetrics.map((metric) => (
                      <AMGrid.Item key={metric.label} style={{ display: 'flex' }}>
                        <AMCard style={{ borderRadius: 10, width: '100%' }}>
                          <AMCard.Body>
                            <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                              {metric.label}
                            </div>
                            <div style={{ fontWeight: 700 }}>{metric.value}</div>
                          </AMCard.Body>
                        </AMCard>
                      </AMGrid.Item>
                    ))}
                  </AMGrid>
                )}
                {publisherLiveRows.length > 0 ? (
                  <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                    {publisherLiveRows.slice(0, 25).map((row) => (
                      <AMCard key={row.id} style={{ borderRadius: 10 }}>
                        <AMCard.Body>
                          <AMSpace direction="vertical" block style={{ '--gap': '4px' }}>
                            <div style={{ fontWeight: 600 }}>{row.publisher}</div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Calls {Number(row.totalCalls || 0).toLocaleString()} · Sales {Number(row.salesCount || 0).toLocaleString()}
                            </div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Billed {Number(row.billedCalls || 0).toLocaleString()} · Cost{' '}
                              {Number.isFinite(row.billedCost)
                                ? new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: row.currency || 'USD',
                                    maximumFractionDigits: 0,
                                  }).format(row.billedCost)
                                : '—'}
                            </div>
                            <div style={{ fontSize: 12, color: '#667085' }}>
                              Conv {Number.isFinite(row.totalConversionPct) ? formatPercent(row.totalConversionPct) : '—'} · CPA{' '}
                              {Number.isFinite(row.cpaValue)
                                ? new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: row.currency || 'USD',
                                    maximumFractionDigits: 0,
                                  }).format(row.cpaValue)
                                : '—'}
                            </div>
                          </AMSpace>
                        </AMCard.Body>
                      </AMCard>
                    ))}
                  </AMSpace>
                ) : (
                  !publisherLiveState.loading &&
                  !publisherLiveState.error &&
                  renderExecutiveNotice('info', 'No live publisher CPA data is available for today.')
                )}
              </AMSpace>
            </AMCard.Body>
          </AMCard>
        );
      }

      return (
        <AntCard
          title="Publisher Live CPA&apos;s"
          extra={
            <AntSpace>
              {publisherLiveState.loading ? <AntSpin size="small" /> : null}
              <AntButton
                size="small"
                onClick={() => fetchPublisherLive({ forceRefresh: true, showLoader: true })}
                disabled={publisherLiveState.loading}
              >
                Refresh live
              </AntButton>
            </AntSpace>
          }
          style={{ borderRadius: 12 }}
        >
          <AntTypography.Text type="secondary">
            {publisherLiveSummary
              ? `Today · ${publisherLiveLastUpdatedLabel ? `Last updated ${publisherLiveLastUpdatedLabel}` : 'Live snapshot'}`
              : 'Today · Live publisher CPA snapshot'}
          </AntTypography.Text>
          {publisherLiveState.error && (
            <div style={{ marginTop: 12 }}>{renderExecutiveNotice('error', publisherLiveState.error)}</div>
          )}
          {publisherLiveState.loading && !publisherLiveState.data && !publisherLiveState.error
            ? renderExecutiveSpinner('Loading publisher live CPA snapshot…')
            : null}
          {summaryMetrics.length > 0 && (
            <AntRow gutter={[12, 12]} style={{ marginTop: 12 }}>
              {summaryMetrics.map((metric) => (
                <AntCol xs={24} sm={12} md={8} lg={6} key={metric.label} style={{ display: 'flex' }}>
                  <AntCard size="small" style={{ borderRadius: 12, width: '100%' }}>
                    <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                      {metric.label}
                    </AntTypography.Text>
                    <AntTypography.Title level={4} style={{ margin: 0 }}>
                      {metric.value}
                    </AntTypography.Title>
                  </AntCard>
                </AntCol>
              ))}
            </AntRow>
          )}
          {publisherLiveRows.length > 0 ? (
            <AntTable
              columns={tableColumns}
              dataSource={publisherLiveRows.slice(0, 25)}
              size="small"
              pagination={false}
              style={{ marginTop: 12 }}
            />
          ) : (
            !publisherLiveState.loading &&
            !publisherLiveState.error &&
            renderExecutiveNotice('info', 'No live publisher CPA data is available for today.')
          )}
        </AntCard>
      );
    };

    return (
      <div style={{ marginTop: 16 }}>
        {isMobile ? (
          <AMCard style={{ marginBottom: 12 }}>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '10px' }}>
                <div style={{ fontWeight: 700, fontSize: 18 }}>
                  Executive View · {executiveCompanyLabel}
                </div>
                <AMSelector
                  options={[
                    { label: 'Medicare', value: 'sabal' },
                    { label: 'ACA', value: 'ahm' },
                  ]}
                  value={[activeExecutiveCompany]}
                  onChange={(val) => handleExecutiveCompanyChange(null, val?.[0])}
                />
                {activeExecutiveCompany === 'ahm' && (
                  <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
                    <div style={{ fontSize: 12, color: '#667085' }}>Start date</div>
                    <AMInput
                      type="date"
                      value={ahmSelectedRange.startDate}
                      onChange={(value) => handleAhmDateChange('startDate', value)}
                    />
                    <div style={{ fontSize: 12, color: '#667085' }}>End date</div>
                    <AMInput
                      type="date"
                      value={ahmSelectedRange.endDate}
                      onChange={(value) => handleAhmDateChange('endDate', value)}
                    />
                    <AMButton
                      size="small"
                      color="primary"
                      onClick={handleAhmDateSubmit}
                      disabled={!ahmSelectedRange.startDate || !ahmSelectedRange.endDate || ahmExecutiveState.loading}
                    >
                      Submit
                    </AMButton>
                  </AMSpace>
                )}
                <AMSpace wrap style={{ '--gap': '8px' }}>
                  <AMButton
                    size="small"
                    color="primary"
                    fill="outline"
                    onClick={handleManualRefresh}
                    disabled={!canManualRefresh || manualRefreshBusy}
                  >
                    {manualRefreshBusy ? 'Refreshing…' : 'Refresh data'}
                  </AMButton>
                </AMSpace>
                {activeExecutiveCompany === 'ahm' && ahmUpdatedLabel && (
                  <div style={{ color: '#667085', fontSize: 12 }}>Last processed: {ahmUpdatedLabel}</div>
                )}
              </AMSpace>
            </AMCard.Body>
          </AMCard>
        ) : (
          <AntCard style={{ marginBottom: 12 }}>
            <AntSpace direction="vertical" size="small" style={{ width: '100%' }}>
              <AntSpace align="center" wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                <AntTypography.Title level={4} style={{ margin: 0 }}>
                  Executive View · {executiveCompanyLabel}
                </AntTypography.Title>
                <AntSpace wrap>
                  <AntSegmented
                    value={activeExecutiveCompany}
                    onChange={(val) => handleExecutiveCompanyChange(null, val)}
                    options={[
                      { label: 'Medicare', value: 'sabal' },
                      { label: 'ACA', value: 'ahm' },
                    ]}
                  />
                  {activeExecutiveCompany === 'ahm' && (
                    <AntSpace wrap>
                      <AntInput
                        type="date"
                        value={ahmSelectedRange.startDate}
                        onChange={(e) => handleAhmDateChange('startDate', e.target.value)}
                      />
                      <AntInput
                        type="date"
                        value={ahmSelectedRange.endDate}
                        onChange={(e) => handleAhmDateChange('endDate', e.target.value)}
                      />
                      <AntButton
                        type="primary"
                        size="small"
                        onClick={handleAhmDateSubmit}
                        disabled={!ahmSelectedRange.startDate || !ahmSelectedRange.endDate || ahmExecutiveState.loading}
                      >
                        Submit
                      </AntButton>
                    </AntSpace>
                  )}
                  <AntButton
                    size="small"
                    onClick={handleManualRefresh}
                    disabled={!canManualRefresh || manualRefreshBusy}
                  >
                    {manualRefreshBusy ? 'Refreshing…' : 'Refresh data'}
                  </AntButton>
                </AntSpace>
              </AntSpace>
              {activeExecutiveCompany === 'ahm' && ahmUpdatedLabel && (
                <AntTypography.Text type="secondary">Last processed: {ahmUpdatedLabel}</AntTypography.Text>
              )}
            </AntSpace>
          </AntCard>
        )}
        <div style={{ marginBottom: 12 }}>
          {isMobile ? (
            <div style={{ color: '#667085', fontSize: 13 }}>
              {activeExecutiveCompany === 'ahm'
                ? 'Choose a date range to fetch ACA applications and compute executive KPIs for ACA by senior, team, and agent.'
                : 'Displays every senior manager, their teams, and assigned agents for this organization.'}
            </div>
          ) : (
            <AntTypography.Text type="secondary">
              {activeExecutiveCompany === 'ahm'
                ? 'Choose a date range to fetch ACA applications and compute executive KPIs for ACA by senior, team, and agent.'
                : 'Displays every senior manager, their teams, and assigned agents for this organization.'}
            </AntTypography.Text>
          )}
        </div>
        {execError && <div style={{ marginBottom: 12 }}>{renderExecutiveNotice('error', execError)}</div>}
        {activeExecutiveCompany === 'ahm' &&
          !execLoading &&
          !ahmExecutiveState.summary &&
          !ahmExecutiveState.error && (
            <div style={{ marginBottom: 12 }}>
              {renderExecutiveNotice('info', 'Select a start and end date to populate ACA executive KPIs.')}
            </div>
          )}
        {!isAhm && shouldFetchComplianceScores && complianceScoreState.error && (
          <div style={{ marginBottom: 12 }}>
            {renderExecutiveNotice(
              'warning',
              `Unable to load compliance averages: ${complianceScoreState.error}`
            )}
          </div>
        )}
        {activeExecutiveCompany === 'ahm' && ahmExecutiveState.summary && !execLoading && (
          <div style={{ marginBottom: 16 }}>{renderAhmSummary()}</div>
        )}
        {false && activeExecutiveCompany === 'ahm' && ahmExecutiveState.summary && !execLoading && (
          <div style={{ marginBottom: 16 }}>
            <Stack spacing={2} sx={{ mb: 1 }}>
              {[
                { title: 'Statuses', data: ahmStatusPieData },
                { title: 'Issuers', data: ahmIssuerPieData },
                { title: 'Coverage', data: ahmCoveragePieData },
                { title: 'Hotspots', data: ahmHotspotPieData },
              ].map((section) => (
                <Paper key={section.title} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {section.title}
                  </Typography>
                  {section.data.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No data available.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 260 }, height: 220 }}>
                        <ResponsiveContainer>
                          <BarChart
                            data={section.data}
                            layout="vertical"
                            margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                            <XAxis type="number" hide />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={120}
                              tick={{ fontSize: 12 }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 4, 4]} label={{ position: 'right', fontSize: 12, fill: '#0f172a' }}>
                              {section.data.map((entry, index) => (
                                <Cell key={`cell-${section.title}-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                      <Stack spacing={1} sx={{ minWidth: 160 }}>
                        {section.data.map((item) => (
                          <Chip
                            key={`${section.title}-${item.name}`}
                            label={`${item.name} (${Number(item.value).toLocaleString()})`}
                            size="small"
                            sx={{
                              bgcolor: item.color,
                              color: '#fff',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              {visibleAhmSummaryMetrics.map((metric) => {
                const displayValue =
                  typeof metric.formatter === 'function'
                    ? metric.formatter(metric.value)
                    : Number(metric.value || 0).toLocaleString();
                const cardBg =
                  metric.tone === 'primary'
                    ? 'linear-gradient(145deg, #0f172a0d, #2563eb0d)'
                    : metric.tone === 'accent'
                      ? 'linear-gradient(145deg, #0f172a0d, #0ea5e90d)'
                      : undefined;
                const isCluster = metric.clusterKey;
                return (
                  <Paper
                    key={metric.key}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      height: '100%',
                      background: cardBg,
                      borderColor: metric.tone ? 'primary.light' : 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      {metric.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                      {displayValue}
                    </Typography>
                    {metric.helper ? (
                      <Typography variant="body2" color="text.secondary">
                        {metric.helper}
                      </Typography>
                    ) : null}
                    {isCluster && Array.isArray(metric.clusterItems) && metric.clusterItems.length ? (
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {metric.clusterItems.map((item) => (
                          <Chip
                            key={`${metric.key}-${item.key}`}
                            label={`${item.label}: ${Number(item.value || 0).toLocaleString()}`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    ) : null}
                  </Paper>
                );
              })}
            </Box>
            {ahmGenesysAnalytics && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Genesys Call Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ahmGenesysAnalytics?.genesys?.interval
                        ? `Interval: ${ahmGenesysAnalytics.genesys.interval}`
                        : ahmGenesysAnalytics?.dateRange
                          ? `Range: ${ahmGenesysAnalytics.dateRange.start || ''} → ${ahmGenesysAnalytics.dateRange.end || ''}`
                          : 'Range derived from selected dates'}
                    </Typography>
                  </Stack>
                  {ahmGenesysError && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Genesys analytics: {ahmGenesysError}
                    </Alert>
                  )}
                  {ahmGenesysTotals.length ? (
                    <Grid container spacing={1.5} sx={{ mt: 1 }}>
                      {ahmGenesysTotals.map((metric) => {
                        const displayValue = metric.isDuration
                          ? formatDuration(metric.value)
                          : metric.isPercent
                            ? `${Number(metric.value || 0).toFixed(1)}%`
                            : Number(metric.value || 0).toLocaleString();
                        return (
                          <Grid item xs={12} sm={6} md={4} key={metric.key}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                background: 'linear-gradient(135deg, rgba(15,23,42,0.04), rgba(37,99,235,0.04))',
                                height: '100%',
                              }}
                            >
                              <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                                {metric.label}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                                {displayValue}
                              </Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : null}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Chip label={`Rows: ${Number(ahmGenesysAnalytics?.csvStats?.rowCount || 0).toLocaleString()}`} size="small" />
                    {ahmGenesysTotals.map((metric) => (
                      <Chip
                        key={metric.key}
                        label={`${metric.label}: ${
                          metric.isDuration
                            ? formatDuration(metric.value)
                            : metric.isPercent
                              ? `${Number(metric.value || 0).toFixed(1)}%`
                              : Number(metric.value || 0).toLocaleString()
                        }`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {ahmGenesysDispositions.map((d) => (
                      <Chip
                        key={`disp-${d.name}`}
                        label={`${d.name}: ${Number(d.count || 0).toLocaleString()}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {!ahmGenesysDispositions.length && (
                      <Typography variant="body2" color="text.secondary">
                        No dispositions found.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
                {ahmGenesysTeams.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Top Teams (Genesys + ACA)
                    </Typography>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Team</TableCell>
                            <TableCell align="right">Rows</TableCell>
                            <TableCell align="right">Agents</TableCell>
                            <TableCell align="right">Raw Calls</TableCell>
                            <TableCell align="right">Billed Calls</TableCell>
                            <TableCell align="right">Sales</TableCell>
                            <TableCell align="right">Raw Conv</TableCell>
                            <TableCell align="right">Billed Conv</TableCell>
                            <TableCell align="right">Avg Handle</TableCell>
                            <TableCell align="right">Avg Talk</TableCell>
                            <TableCell align="right">Avg Wait</TableCell>
                            <TableCell align="right">Avg VM</TableCell>
                            <TableCell align="right">Top Dispo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ahmGenesysTeams.map((team) => {
                            const topDispo = Object.entries(team.dispositions || {})
                              .map(([name, count]) => ({ name: name || 'unspecified', count: Number(count) || 0 }))
                              .sort((a, b) => b.count - a.count)[0];
                            const rawCalls = Number(team.metrics?.raw_calls || 0);
                            const billedCalls = Number(team.metrics?.billed_calls || 0);
                            const sales = Number(team.metrics?.sales || 0);
                            const rawConv = rawCalls > 0 ? (sales / rawCalls) * 100 : null;
                            const billedConv = billedCalls > 0 ? (sales / billedCalls) * 100 : null;
                            return (
                              <TableRow key={team.name}>
                                <TableCell>{team.name}</TableCell>
                                <TableCell align="right">{Number(team.rows || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{Number(team.agentCount || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{rawCalls.toLocaleString()}</TableCell>
                                <TableCell align="right">{billedCalls.toLocaleString()}</TableCell>
                                <TableCell align="right">{sales.toLocaleString()}</TableCell>
                                <TableCell align="right">{rawConv != null ? `${rawConv.toFixed(1)}%` : '—'}</TableCell>
                                <TableCell align="right">{billedConv != null ? `${billedConv.toFixed(1)}%` : '—'}</TableCell>
                                <TableCell align="right">{formatDuration(team.avg_handle_duration)}</TableCell>
                                <TableCell align="right">{formatDuration(team.avg_talk_duration)}</TableCell>
                                <TableCell align="right">{formatDuration(team.avg_wait_duration)}</TableCell>
                                <TableCell align="right">{formatDuration(team.avg_vm_duration)}</TableCell>
                                <TableCell align="right">
                                  {topDispo ? `${topDispo.name} (${Number(topDispo.count).toLocaleString()})` : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
                {ahmGenesysAgents.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Top Agents
                    </Typography>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Agent</TableCell>
                              <TableCell>Team</TableCell>
                              <TableCell align="right">Raw Calls</TableCell>
                              <TableCell align="right">Billed Calls</TableCell>
                              <TableCell align="right">Sales</TableCell>
                              <TableCell align="right">Raw Conv</TableCell>
                              <TableCell align="right">Billed Conv</TableCell>
                              <TableCell align="right">Avg Handle</TableCell>
                              <TableCell align="right">Avg Talk</TableCell>
                              <TableCell align="right">Avg Wait</TableCell>
                              <TableCell align="right">Avg VM</TableCell>
                              <TableCell align="right">Top Dispo</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ahmGenesysAgents.map((agent) => {
                            const topDispo = Object.entries(agent.dispositions || {})
                              .map(([name, count]) => ({ name: name || 'unspecified', count: Number(count) || 0 }))
                              .sort((a, b) => b.count - a.count)[0];
                            return (
                              <TableRow key={agent.id}>
                                <TableCell>
                                  <Stack spacing={0.3}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {agent.name}
                                    </Typography>
                                    {agent.email && (
                                      <Typography variant="caption" color="text.secondary">
                                        {agent.email}
                                      </Typography>
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell>{agent.team || '—'}</TableCell>
                                <TableCell align="right">{Number(agent.raw_calls || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{Number(agent.billed_calls || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{Number(agent.sales || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">
                                  {agent.raw_conversion_pct != null ? `${Number(agent.raw_conversion_pct).toFixed(1)}%` : '—'}
                                </TableCell>
                                <TableCell align="right">
                                  {agent.billed_conversion_pct != null ? `${Number(agent.billed_conversion_pct).toFixed(1)}%` : '—'}
                                </TableCell>
                                <TableCell align="right">{formatDuration(agent.avg_handle_duration)}</TableCell>
                                <TableCell align="right">{formatDuration(agent.avg_talk_duration)}</TableCell>
                                <TableCell align="right">{formatDuration(agent.avg_wait_duration)}</TableCell>
                                <TableCell align="right">{formatDuration(agent.avg_vm_duration)}</TableCell>
                                <TableCell align="right">
                                  {topDispo ? `${topDispo.name} (${Number(topDispo.count).toLocaleString()})` : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Stack>
            )}
          </div>
        )}

        {isAhm && renderAhmTeams()}

        {!isAhm &&
          (execLoading ? (
            renderExecutiveSpinner('Loading hierarchies…')
          ) : !hasEntries ? (
            renderExecutiveNotice(
              'info',
              `No senior manager hierarchies are available for ${executiveCompanyLabel}.`
            )
          ) : (
            <>
              {renderExecutiveKpiVisualizer()}
              {renderExecutiveRollup()}
              {renderExecutiveControls()}
              {/*
                <Box sx={{ mt: 3, mb: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    background:
                      'linear-gradient(135deg, rgba(15,23,42,0.05), rgba(30,64,175,0.02))',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    KPI Visualizer
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select a KPI, grouping, and chart style to visualize executive performance.
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ mb: 2 }}
                  >
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Metric</InputLabel>
                      <Select
                        label="Metric"
                        value={kpiGraphMetric}
                        onChange={(event) => setKpiGraphMetric(event.target.value)}
                      >
                        {kpiGraphMetricOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Grouping</InputLabel>
                      <Select
                        label="Grouping"
                        value={kpiGraphDimension}
                        onChange={(event) => setKpiGraphDimension(event.target.value)}
                      >
                        {kpiGraphDimensionOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Chart Type</InputLabel>
                      <Select
                        label="Chart Type"
                        value={kpiGraphType}
                        onChange={(event) => setKpiGraphType(event.target.value)}
                      >
                        {kpiGraphTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Filter by name"
                      value={kpiGraphSearch}
                      onChange={(event) => setKpiGraphSearch(event.target.value)}
                      sx={{ minWidth: 180 }}
                    />
                  </Stack>
                  <Box
                    sx={{
                      height: isMobile ? 320 : 260,
                      overflowX: { xs: 'auto', md: 'visible' },
                    }}
                  >
                    {kpiGraphData.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected configuration.
                      </Typography>
                    ) : (
                      <Box sx={{ minWidth: chartMinWidth ? chartMinWidth : 'auto', height: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {kpiGraphType === 'line' ? (
                            <LineChart
                              data={kpiGraphData}
                              margin={{ top: 5, right: 24, left: 8, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="name"
                                hide={kpiGraphData.length > 10}
                                tick={{ fontSize: 10 }}
                              />
                              <YAxis />
                              <RechartsTooltip />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="value"
                                name={kpiGraphMetricLabel}
                                stroke={KPI_GRAPH_COLORS[0]}
                                strokeWidth={2}
                                dot={false}
                              />
                            </LineChart>
                          ) : kpiGraphType === 'pie' ? (
                            <PieChart>
                              <RechartsTooltip />
                              <Legend />
                              <Pie
                                data={kpiGraphData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={50}
                                outerRadius={90}
                                paddingAngle={2}
                              >
                                {kpiGraphData.map((entry, index) => (
                                  <Cell
                                    key={`kpi-pie-${entry.name}-${index}`}
                                    fill={KPI_GRAPH_COLORS[index % KPI_GRAPH_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                            </PieChart>
                          ) : (
                            <BarChart
                              data={kpiGraphData}
                              margin={{ top: 5, right: 24, left: 8, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="name"
                                hide={kpiGraphData.length > 10}
                                tick={{ fontSize: 10 }}
                              />
                              <YAxis />
                              <RechartsTooltip />
                              <Legend />
                              <Bar
                                dataKey="value"
                                name={kpiGraphMetricLabel}
                                radius={[4, 4, 0, 0]}
                              >
                                {kpiGraphData.map((entry, index) => (
                                  <Cell
                                    key={`kpi-bar-${entry.name}-${index}`}
                                    fill={KPI_GRAPH_COLORS[index % KPI_GRAPH_COLORS.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      </Box>
                    )}
                  </Box>
                </Paper>
                  </Box>
                  {executiveAggregateStats ? (
                    <Card sx={{ mb: 3, borderLeft: '6px solid', borderLeftColor: 'secondary.main' }}>
                      <CardHeader
                        title="Executive Rollup"
                        subheader={`${executiveCompanyLabel} KPIs · ${executiveDateLabel}`}
                      />
                  <CardContent>
                    {executiveRollupTotals.loading && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Refreshing company sales snapshot…
                      </Typography>
                    )}
                    {executiveRollupTotals.error && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {executiveRollupTotals.error}
                      </Alert>
                    )}
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            background:
                              'linear-gradient(135deg, rgba(76,175,80,0.06), rgba(33,150,243,0.02))',
                            height: '100%',
                          }}
                        >
                          <Typography
                            variant="overline"
                            sx={{ letterSpacing: 1, color: 'text.secondary' }}
                          >
                            Sales Performance
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              mt: 0.5,
                              color: 'success.dark',
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            {effectiveRollupSales.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Company-wide closed sales for this window.
                          </Typography>
                          <Stack
                            spacing={0.5}
                            sx={{ textAlign: { xs: 'center', sm: 'left' }, alignItems: { xs: 'center', sm: 'flex-start' } }}
                          >
                            <Typography variant="body2">
                              <strong>IPC:</strong>{' '}
                              {Number(executiveAggregateStats.totalIpc || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Total Raw Call Volume:</strong>{' '}
                              {Number(executiveAggregateStats.rawCallCount || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                              <strong>AVG Raw-to-Close Conversion:</strong>{' '}
                              {`${Number(executiveAggregateStats.avgConversion || 0).toFixed(
                                1
                              )}%`}
                            </Typography>
                          </Stack>
                          {effectiveAncillaryTotals &&
                          Object.keys(effectiveAncillaryTotals).length ? (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: 'uppercase' }}
                              >
                                Sales breakdown
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mt: 0.5 }}
                              >
                                {buildAncillaryMetricCards(effectiveAncillaryTotals, {
                                  includeTotal: true,
                                }).map((metric) => (
                                  <Chip
                                    key={`exec-anc-${metric.label}`}
                                    size="small"
                                    label={`${metric.label}: ${metric.value}`}
                                    color={
                                      metric.label.toLowerCase().includes('total')
                                        ? 'primary'
                                        : 'default'
                                    }
                                  />
                                ))}
                              </Stack>
                            </Box>
                          ) : null}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            background:
                              'linear-gradient(135deg, rgba(67,56,202,0.06), rgba(30,64,175,0.02))',
                            height: '100%',
                          }}
                        >
                          <Typography
                            variant="overline"
                            sx={{ letterSpacing: 1, color: 'text.secondary' }}
                          >
                            Publisher CPA Snapshot (Today)
                          </Typography>
                          {publisherLiveSummary ? (
                            <>
                              <Typography
                                variant="h5"
                                sx={{ fontWeight: 800, mt: 0.5, color: 'primary.main' }}
                              >
                                {publisherLiveSummary.totalCallsLabel} calls ·{' '}
                                {publisherLiveSummary.salesCountLabel} sales
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1.5 }}
                              >
                                Billed calls {publisherLiveSummary.billedCallsLabel} · Billed cost{' '}
                                {publisherLiveSummary.billedCostLabel}
                              </Typography>
                              <Stack spacing={0.5}>
                                <Typography variant="body2">
                                  <strong>Average raw to close %:</strong>{' '}
                                  {publisherLiveSummary.totalConversionLabel}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Avg billed to close %:</strong>{' '}
                                  {publisherLiveSummary.billedConversionLabel}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>TOTAL CPA:</strong>{' '}
                                  {publisherLiveSummary.cpaLabel}
                                </Typography>
                              </Stack>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Live publisher CPA metrics are not available yet for today. Use the
                              Publisher Live CPA&apos;s tab to pull a fresh snapshot.
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            background:
                              'linear-gradient(135deg, rgba(251,191,36,0.06), rgba(15,23,42,0.02))',
                            height: '100%',
                          }}
                        >
                          <Typography
                            variant="overline"
                            sx={{ letterSpacing: 1, color: 'text.secondary' }}
                          >
                            Quality & Compliance
                          </Typography>
                          <Box sx={{ mt: 0.75 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: 'uppercase' }}
                            >
                              Avg compliance score
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 800, mt: 0.25, color: 'secondary.main' }}
                            >
                              {executiveAggregateStats.complianceAverage != null
                                ? Number(
                                    executiveAggregateStats.complianceAverage || 0
                                  ).toFixed(1)
                                : '—'}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ) : null}
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'column' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                {executiveViewMode !== 'publisherLive' && (
                  <ManagerFilterControls sx={{ mb: 0 }} />
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={executiveViewMode}
                    onChange={(_, value) => {
                      if (!value) return;
                      setExecutiveViewMode(value);
                    }}
                  >
                    <ToggleButton value="hierarchy">By Manager</ToggleButton>
                    <ToggleButton value="agents">All Agents</ToggleButton>
                    <ToggleButton value="publisherLive">Publisher Live CPA&apos;s</ToggleButton>
                  </ToggleButtonGroup>
                  {executiveViewMode !== 'publisherLive' && !isAhm && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <TextField
                        size="small"
                        type="date"
                        label="Start date"
                        value={executiveRange.startDate}
                        onChange={(e) => handleExecutiveDateChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="End date"
                        value={executiveRange.endDate}
                        onChange={(e) => handleExecutiveDateChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Chip size="small" variant="outlined" label={executiveRange.label} />
                    </Stack>
                  )}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      color={executiveDashboardState.loading ? 'primary' : 'default'}
                      icon={executiveDashboardState.loading ? <CircularProgress size={14} /> : undefined}
                      label="Medicare"
                      variant={executiveDashboardState.loading ? 'filled' : 'outlined'}
                    />
                    <Chip
                      size="small"
                      color={ahmExecutiveState.loading ? 'primary' : 'default'}
                      icon={ahmExecutiveState.loading ? <CircularProgress size={14} /> : undefined}
                      label="ACA"
                      variant={ahmExecutiveState.loading ? 'filled' : 'outlined'}
                    />
                  </Stack>
                </Stack>
              </Box>
              */}
        {executiveViewMode === 'hierarchy' ? (
          (() => {
            const panelData = executiveManagerGroups.map((entry, idx) => {
              const seniorName =
                entry.senior?.full_name || entry.senior?.email || 'Senior Manager';
              const summary = summarizeManagerGroupsForSenior(entry.groups);
              const conversion =
                summary.rawCalls > 0
                  ? (summary.sales / summary.rawCalls) * 100
                  : summary.conversionDen > 0
                  ? summary.conversionSum / summary.conversionDen
                  : 0;
              const compliance =
                summary.complianceDen > 0
                  ? summary.complianceSum / summary.complianceDen
                  : null;
              const panelKey = entry.id || entry.senior?.email || `senior-${idx}`;
              const sortSummary = { ...summary, conversion };
              const sortValue = summaryMetricForSort(sortSummary, teamKpiFilter.kpi);
              return {
                entry,
                idx,
                panelKey,
                seniorName,
                summary,
                conversion,
                compliance,
                sortValue,
              };
            });
            const sortedPanels = panelData.sort((a, b) => {
              if (a.sortValue !== b.sortValue) {
                return managerSortOrder === 'asc'
                  ? a.sortValue - b.sortValue
                  : b.sortValue - a.sortValue;
              }
              if (a.summary.sales !== b.summary.sales) {
                return managerSortOrder === 'asc'
                  ? a.summary.sales - b.summary.sales
                  : b.summary.sales - a.summary.sales;
              }
              return (a.seniorName || '').localeCompare(b.seniorName || '');
            });
            const seniorRankMap = new Map();
            sortedPanels.slice(0, 3).forEach((panel, rankIdx) => {
              seniorRankMap.set(panel.panelKey, rankIdx);
            });

            const panels = sortedPanels.map((panel) => {
              const summaryTagItems = [
                { label: `Sales ${panel.summary.sales}`, color: 'blue' },
                { label: `IPC ${panel.summary.ipc}`, color: 'default' },
                { label: `Conv ${formatPercent(panel.conversion)}`, color: 'default' },
                { label: `Calls ${panel.summary.rawCalls}`, color: 'default' },
                panel.compliance != null
                  ? { label: `Compliance ${panel.compliance.toFixed(1)}`, color: 'default' }
                  : null,
                { label: `${panel.summary.agentCount} agents`, color: 'default' },
              ].filter(Boolean);
              const trophyRank = seniorRankMap.get(panel.panelKey);
              const trophy = typeof trophyRank === 'number' ? getTrophyIcon(trophyRank, { size: 'small' }) : null;
              const header = isMobile ? (
                <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
                  <AMSpace align="center" wrap style={{ '--gap': '6px' }}>
                    <div style={{ fontWeight: 700 }}>{panel.seniorName}</div>
                    {trophy}
                  </AMSpace>
                  <div style={{ color: '#667085', fontSize: 12 }}>
                    {(panel.entry.senior?.company || executiveCompanyLabel) + ' · ' + executiveDateLabel}
                  </div>
                  <AMSpace wrap style={{ '--gap': '6px' }}>
                    {summaryTagItems.map((item, tagIndex) => (
                      <AMTag key={`${panel.panelKey}-summary-${tagIndex}`} fill="outline">
                        {item.label}
                      </AMTag>
                    ))}
                  </AMSpace>
                </AMSpace>
              ) : (
                <AntSpace direction="vertical" size={4} style={{ width: '100%' }}>
                  <AntSpace align="center" wrap>
                    <AntTypography.Text strong>{panel.seniorName}</AntTypography.Text>
                    {trophy}
                  </AntSpace>
                  <AntTypography.Text type="secondary">
                    {(panel.entry.senior?.company || executiveCompanyLabel) + ' · ' + executiveDateLabel}
                  </AntTypography.Text>
                  <AntSpace wrap size={[8, 8]}>
                    {summaryTagItems.map((item, tagIndex) => (
                      <AntTag key={`${panel.panelKey}-summary-${tagIndex}`} color={item.color}>
                        {item.label}
                      </AntTag>
                    ))}
                  </AntSpace>
                </AntSpace>
              );
              return {
                key: panel.panelKey,
                label: header,
                children: renderManagerKpiCard({
                  title: 'Teams',
                  subheader: '',
                  managerGroups: panel.entry.groups,
                  loading: false,
                  error: panel.entry.error,
                  hasTargets: panel.entry.groups.length > 0,
                  showFilters: false,
                  levelLabel: 'Manager',
                }),
              };
            });
            return renderResponsiveCollapse(panels, { ghost: false });
          })()
        ) : executiveViewMode === 'agents' ? (
          <div style={{ marginTop: 8 }}>
            {isMobile ? (
              <AMCard>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                    <div style={{ fontWeight: 700 }}>All Agents KPI Overview</div>
                    <div style={{ color: '#667085' }}>
                      {executiveAgentRows.length} agents · {executiveDateLabel}
                    </div>
                    <AMTag color="primary" fill="outline">Agent</AMTag>
                    {executiveAgentRows.length === 0 ? (
                      <div style={{ color: '#667085' }}>
                        No agent-level KPIs matched the current filters.
                      </div>
                    ) : (
                      (() => {
                        const sortedRows = sortAgentsByPerformance(executiveAgentRows || []);
                        const rankMap = buildAgentRankMap(sortedRows);
                        return (
                          <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                            {sortedRows.map((row) => (
                              <AgentAccordionRow
                                key={row.id}
                                row={row}
                                trophyRank={rankMap.get(getAgentRowKey(row))}
                              />
                            ))}
                          </AMSpace>
                        );
                      })()
                    )}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
            ) : (
              <AntCard
                title={<span style={{ fontWeight: 700 }}>All Agents KPI Overview</span>}
                extra={<AntTag color="blue">Agent</AntTag>}
              >
                <AntTypography.Text type="secondary">
                  {executiveAgentRows.length} agents · {executiveDateLabel}
                </AntTypography.Text>
                <div style={{ marginTop: 12 }}>
                  {executiveAgentRows.length === 0 ? (
                    <AntAlert type="info" message="No agent-level KPIs matched the current filters." showIcon />
                  ) : (
                    (() => {
                      const sortedRows = sortAgentsByPerformance(executiveAgentRows || []);
                      const rankMap = buildAgentRankMap(sortedRows);
                      return (
                        <AntSpace direction="vertical" style={{ width: '100%' }} size="middle">
                          {sortedRows.map((row) => (
                            <AgentAccordionRow
                              key={row.id}
                              row={row}
                              trophyRank={rankMap.get(getAgentRowKey(row))}
                            />
                          ))}
                        </AntSpace>
                      );
                    })()
                  )}
                </div>
              </AntCard>
            )}
          </div>
        ) : (
          <>
            {renderPublisherLiveSection()}
            {/*
              <Box sx={{ mt: 2 }}>
                  <Card elevation={3}>
                    <CardHeader
                      title="Publisher Live CPA&apos;s"
                      subheader={
                        publisherLiveSummary
                          ? `Today · ${
                              publisherLiveLastUpdatedLabel
                                ? `Last updated ${publisherLiveLastUpdatedLabel}`
                                : 'Live snapshot'
                            }`
                          : 'Today · Live publisher CPA snapshot'
                      }
                      action={
                        <Stack direction="row" spacing={1} alignItems="center">
                          {publisherLiveState.loading && <CircularProgress size={20} />}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RefreshIcon fontSize="small" />}
                            onClick={() => fetchPublisherLive({ forceRefresh: true, showLoader: true })}
                            disabled={publisherLiveState.loading}
                          >
                            Refresh live
                          </Button>
                        </Stack>
                      }
                    />
                    <CardContent>
                      {publisherLiveState.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {publisherLiveState.error}
                        </Alert>
                      )}
                      {publisherLiveState.loading &&
                      !publisherLiveState.data &&
                      !publisherLiveState.error ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <CircularProgress />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Loading publisher live CPA snapshot…
                          </Typography>
                        </Box>
                      ) : null}
                      {publisherLiveSummary && (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {[
                            { label: 'Total Calls', value: publisherLiveSummary.totalCallsLabel },
                            { label: 'Sales', value: publisherLiveSummary.salesCountLabel },
                            { label: 'Billed Calls', value: publisherLiveSummary.billedCallsLabel },
                            { label: 'Billed Cost', value: publisherLiveSummary.billedCostLabel },
                            {
                              label: 'Total Conv. Rate',
                              value: publisherLiveSummary.totalConversionLabel,
                            },
                            { label: 'CPA', value: publisherLiveSummary.cpaLabel },
                          ].map((metric) => (
                            <Grid item xs={12} sm={6} md={3} key={`pub-live-metric-${metric.label}`}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: 3,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  background:
                                    'linear-gradient(120deg, rgba(76,175,80,0.05), rgba(25,118,210,0.01))',
                                  height: '100%',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
                                >
                                  {metric.label}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                                  {metric.value}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      {publisherLiveRows.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Publisher</TableCell>
                                <TableCell align="right">Total Calls</TableCell>
                                <TableCell align="right">Sales</TableCell>
                                <TableCell align="right">Billed Calls</TableCell>
                                <TableCell align="right">Billed Cost</TableCell>
                                <TableCell align="right">Total Conv. %</TableCell>
                                <TableCell align="right">CPA</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {publisherLiveRows.slice(0, 25).map((row) => {
                                const cpa = Number.isFinite(row.cpaValue) ? row.cpaValue : null;
                                let rowColor = undefined;
                                if (cpa == null) {
                                  // Missing / non-finite CPA (renders as "—") → red
                                  rowColor = 'rgba(239, 68, 68, 0.08)';
                                } else if (cpa >= 300) {
                                  rowColor = 'rgba(239, 68, 68, 0.08)'; // red
                                } else if (cpa >= 200) {
                                  rowColor = 'rgba(245, 158, 11, 0.08)'; // yellow
                                } else if (cpa > 0 && cpa < 200) {
                                  rowColor = 'rgba(34, 197, 94, 0.08)'; // green
                                }
                                return (
                                  <TableRow
                                    key={row.id}
                                    sx={rowColor ? { backgroundColor: rowColor } : undefined}
                                  >
                                    <TableCell component="th" scope="row">
                                      {row.publisher}
                                    </TableCell>
                                    <TableCell align="right">
                                      {Number.isFinite(row.totalCalls)
                                        ? row.totalCalls.toLocaleString()
                                        : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {Number.isFinite(row.salesCount)
                                        ? row.salesCount.toLocaleString()
                                        : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {Number.isFinite(row.billedCalls)
                                        ? row.billedCalls.toLocaleString()
                                        : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {Number.isFinite(row.billedCost)
                                        ? new Intl.NumberFormat(undefined, {
                                            style: 'currency',
                                            currency: row.currency || 'USD',
                                            maximumFractionDigits: 0,
                                          }).format(row.billedCost)
                                        : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {Number.isFinite(row.totalConversionPct)
                                        ? formatPercent(row.totalConversionPct)
                                        : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {Number.isFinite(row.cpaValue)
                                        ? new Intl.NumberFormat(undefined, {
                                            style: 'currency',
                                            currency: row.currency || 'USD',
                                            maximumFractionDigits: 0,
                                          }).format(row.cpaValue)
                                        : '—'}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        !publisherLiveState.loading &&
                        !publisherLiveState.error && (
                          <Alert severity="info">
                            No live publisher CPA data is available for today.
                          </Alert>
                        )
                      )}
                    </CardContent>
                  </Card>
              </Box>
            */}
          </>
        )}
            </>
        ))}
      </div>
    );
  };

  // --- AI Mock Call: Level Completion Panel state ---
  const [npcLevels, setNpcLevels] = useState([]);
  const [npcLoading, setNpcLoading] = useState(false);
  const [npcError, setNpcError] = useState('');
  const dialerUserRaw = agentDirectoryProfile?.dialer_user
    ?? agentDirectoryProfile?.dialerUser
    ?? agentDirectoryProfile?.dialer?.user
    ?? agentDirectoryProfile?.dialer?.username
    ?? null;
  const dialerUserValue = typeof dialerUserRaw === 'string' ? dialerUserRaw.trim() : '';
  const agentDirectRaw = agentDirectoryProfile?.agent_direct
    ?? agentDirectoryProfile?.agentDirect
    ?? agentDirectoryProfile?.agent_direct_link
    ?? null;
  const agentDirectValue = typeof agentDirectRaw === 'string' ? agentDirectRaw.trim() : '';
  const [agentDirectCopied, setAgentDirectCopied] = useState(false);
  const agentDirectCopyTimeoutRef = useRef(null);

  useEffect(() => {
    setAgentDirectCopied(false);
    if (agentDirectCopyTimeoutRef.current) {
      clearTimeout(agentDirectCopyTimeoutRef.current);
      agentDirectCopyTimeoutRef.current = null;
    }
  }, [agentDirectValue]);

  useEffect(() => {
    return () => {
      if (agentDirectCopyTimeoutRef.current) {
        clearTimeout(agentDirectCopyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyAgentDirect = useCallback(async () => {
    if (!agentDirectValue) return;
    try {
      await navigator.clipboard.writeText(agentDirectValue);
      setAgentDirectCopied(true);
      if (agentDirectCopyTimeoutRef.current) {
        clearTimeout(agentDirectCopyTimeoutRef.current);
      }
      agentDirectCopyTimeoutRef.current = setTimeout(() => {
        setAgentDirectCopied(false);
        agentDirectCopyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy agent_direct value:', error);
    }
  }, [agentDirectValue]);

  useEffect(() => {
    if (!isTeamManager || isSeniorManager || managerTeamMembers.length === 0) {
      setTeamManagerKpis({ loading: false, error: null, agents: [], meetings: {} });
      return;
    }
    let cancelled = false;
    const loadManagerDashboard = async () => {
      setTeamManagerKpis((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const agentNames = managerTeamMembers.map((member) => member.agent_name).filter(Boolean);
        const agentEmails = managerTeamMembers
          .map((member) => normalizeEmailKey(member.email))
          .filter(Boolean);
        if (!agentNames.length) {
          setTeamManagerKpis({ loading: false, error: null, agents: [], meetings: {} });
          return;
        }
        const payloadKey = 'manager_dashboard_with_dates';
        const response = await fetch(AHM_KPI_REPORT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            [payloadKey]: {
              start_date: kpiDateRange.startDate,
              end_date: kpiDateRange.endDate,
              agent_names: agentNames,
              include_unmatched: true,
            },
          }),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `Manager dashboard API returned ${response.status}`);
        }
        const payload = await response.json();
        let meetingStatsPayload = null;
        if (agentEmails.length) {
          try {
            meetingStatsPayload = await api.post('/coaching/meeting-stats', {
              agent_emails: agentEmails,
              start_date: kpiDateRange.startDate,
              end_date: kpiDateRange.endDate,
            });
          } catch (meetingErr) {
            console.error('Failed to load meeting stats:', meetingErr);
          }
        }
        if (!cancelled) {
          const rosterByName = new Map();
          managerTeamMembers.forEach((member) => {
            const nameKey = normalizeAgentKey(member?.agent_name || '');
            if (nameKey && !rosterByName.has(nameKey)) {
              rosterByName.set(nameKey, member);
            }
          });
          const meetingsByEmail = new Map();
          if (meetingStatsPayload && Array.isArray(meetingStatsPayload.agents)) {
            meetingStatsPayload.agents.forEach((agentStats) => {
              const emailKey = normalizeEmailKey(agentStats?.agent_email);
              if (emailKey) {
                meetingsByEmail.set(emailKey, normalizeMeetingPayload(agentStats));
              }
            });
          }
          const resolveMeetingStats = (agent) => {
            const directEmailKey = normalizeEmailKey(agent?.agent_email);
            if (directEmailKey && meetingsByEmail.has(directEmailKey)) {
              return meetingsByEmail.get(directEmailKey);
            }
            const rosterMatch = rosterByName.get(normalizeAgentKey(agent?.agent_name || ''));
            if (rosterMatch) {
              const rosterEmailKey = normalizeEmailKey(rosterMatch?.email);
              if (rosterEmailKey && meetingsByEmail.has(rosterEmailKey)) {
                return meetingsByEmail.get(rosterEmailKey);
              }
            }
            if (agent?.meetings) {
              return normalizeMeetingPayload(agent.meetings);
            }
            return createEmptyMeetingStats();
          };
          const meetingMap = (payload?.agents || []).reduce((acc, agent) => {
            const key = normalizeAgentKey(agent?.agent_name || '');
            if (key) {
              acc[key] = resolveMeetingStats(agent);
            }
            return acc;
          }, {});
          managerTeamMembers.forEach((member) => {
            const nameKey = normalizeAgentKey(member?.agent_name || '');
            if (!nameKey || meetingMap[nameKey]) return;
            const emailKey = normalizeEmailKey(member?.email);
            if (emailKey && meetingsByEmail.has(emailKey)) {
              meetingMap[nameKey] = meetingsByEmail.get(emailKey);
            }
          });
          setTeamManagerKpis({
            loading: false,
            error: null,
            agents: Array.isArray(payload?.agents) ? payload.agents : [],
            meetings: meetingMap,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setTeamManagerKpis({
            loading: false,
            error: error?.message || 'Failed to load team KPI dashboard.',
            agents: [],
            meetings: {},
          });
        }
      }
    };
    loadManagerDashboard();
    return () => {
      cancelled = true;
    };
  }, [isTeamManager, isSeniorManager, kpiDateRange.endDate, kpiDateRange.startDate, managerTeamMembers, refreshNonce]);

  useEffect(() => {
    if (!isSeniorManager) {
      setSeniorHierarchy({ loading: false, error: null, managers: [], senior: null });
      return;
    }
    let cancelled = false;

    const matchSeniorRecords = (records, identity) => {
      return records.filter((record) => {
        if (!record) return false;
        const seniorEmail = normalizeEmailKey(record.email);
        if (identity.emailKey && seniorEmail && seniorEmail === identity.emailKey) return true;
        const candidateName = normalizeAgentKey(record.full_name || '');
        if (identity.nameKey && candidateName === identity.nameKey) return true;
        if (identity.compactKey && candidateName.replace(/\s+/g, '') === identity.compactKey) return true;
        return false;
      });
    };

    const applyHierarchyPayload = (payload, identity) => {
      const seniors = Array.isArray(payload?.seniors) ? payload.seniors : [];
      const matches = matchSeniorRecords(seniors, identity);
      if (!matches.length) return false;
      const normalized = normalizeSeniorRecord(matches[0]);
      if (!normalized) return false;
      const hierarchyState = buildHierarchyStateFromSenior(normalized);
      if (!hierarchyState) return false;
      setSeniorHierarchy({
        loading: false,
        error: null,
        managers: hierarchyState.managers,
        senior: hierarchyState.senior,
      });
      return true;
    };

    const fetchHierarchy = async () => {
      const identity = {
        emailKey: normalizeEmailKey(user?.email),
        nameKey: normalizeAgentKey(rosterEntry?.agent_name || resolvedAgentName || ''),
      };
      identity.compactKey = normalizeCompactKey(identity.nameKey);

      if (!identity.emailKey && !identity.nameKey) {
        setSeniorHierarchy({
          loading: false,
          error: 'Unable to resolve your senior manager profile.',
          managers: [],
          senior: null,
        });
        return;
      }

      setSeniorHierarchy({ loading: true, error: null, managers: [], senior: null });

      try {
        const payload = await fetchSabalHierarchyPayload();
        if (!cancelled && applyHierarchyPayload(payload, identity)) {
          return;
        }
        if (!cancelled) {
          setSeniorHierarchy({
            loading: false,
            error: 'No matching senior manager hierarchy found.',
            managers: [],
            senior: null,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load senior hierarchy:', error);
          setSeniorHierarchy({
            loading: false,
            error: error?.message || 'Failed to load senior hierarchy.',
            managers: [],
            senior: null,
          });
        }
      }
    };

    fetchHierarchy();
    return () => {
      cancelled = true;
    };
  }, [isSeniorManager, refreshNonce, resolvedAgentName, rosterEntry?.agent_name, user?.email]);

  useEffect(() => {
    if (!isSeniorManager) {
      setSeniorTeamKpis({ loading: false, error: null, agents: [], meetings: {} });
      return;
    }
    const agentNames = Array.from(
      new Set((seniorAgentAssignments.names || []).map((name) => (name || '').trim()).filter(Boolean))
    );
    const agentEmails = Array.from(
      new Set((seniorAgentAssignments.emails || []).map((email) => (email || '').trim()).filter(Boolean))
    );
    const rosterEmailGroups = seniorTeamRosterGroups.length
      ? seniorTeamRosterGroups
          .map((group) => {
            const emails = Array.from(
              new Set(
                (group.members || [])
                  .map((member) => {
                    const direct = (member?.email || '').trim();
                    if (direct) return direct;
                    const fallback = seniorHierarchyEmailLookup.get(
                      normalizeAgentKey(member?.agent_name || '')
                    );
                    return fallback || '';
                  })
                  .filter(Boolean)
              )
            );
            return { id: group.id || group.teamKey || group.teamName || '', emails };
          })
          .filter((group) => group.emails.length)
      : [];
    const meetingEmailGroups = rosterEmailGroups.length
      ? rosterEmailGroups
      : [{ id: 'all', emails: agentEmails }];
    if (!agentNames.length) {
      setSeniorTeamKpis({ loading: false, error: null, agents: [], meetings: {} });
      return;
    }
    let cancelled = false;
    const loadSeniorKpis = async () => {
      setSeniorTeamKpis((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const payloadKey = 'manager_dashboard_with_dates';
        const response = await fetch(AHM_KPI_REPORT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            [payloadKey]: {
              start_date: kpiDateRange.startDate,
              end_date: kpiDateRange.endDate,
              agent_names: agentNames,
            },
          }),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `Manager dashboard API returned ${response.status}`);
        }
        const payload = await response.json();
        const meetingLookup = new Map();
        if (meetingEmailGroups.length) {
          await Promise.all(
            meetingEmailGroups.map(async (group) => {
              if (!group.emails.length) return;
                  try {
                    const payload = await api.post('/coaching/meeting-stats', {
                      agent_emails: group.emails,
                      start_date: kpiDateRange.startDate,
                      end_date: kpiDateRange.endDate,
                    });
                if (payload && Array.isArray(payload.agents)) {
                  payload.agents.forEach((agentStats) => {
                    const emailKey = normalizeEmailKey(agentStats?.agent_email);
                    if (emailKey) {
                      meetingLookup.set(emailKey, normalizeMeetingPayload(agentStats));
                    }
                  });
                }
              } catch (meetingErr) {
                console.error(
                  `Failed to load senior meeting stats for group ${group.id || 'n/a'}:`,
                  meetingErr
                );
              }
            })
          );
        }
        if (!cancelled) {
          const registerMeeting = (name, email, fallbackMeetings) => {
            if (!name) return;
            const nameKey = normalizeAgentKey(name);
            if (!nameKey || meetings[nameKey]) return;
            const emailKey = normalizeEmailKey(email);
            if (emailKey && meetingLookup.has(emailKey)) {
              meetings[nameKey] = meetingLookup.get(emailKey);
            } else if (fallbackMeetings) {
              meetings[nameKey] = normalizeMeetingPayload(fallbackMeetings);
            } else {
              meetings[nameKey] = createEmptyMeetingStats();
            }
          };
          const meetings = {};
          seniorEmailToNameMap.forEach((names, emailKey) => {
            if (!meetingLookup.has(emailKey)) return;
            names.forEach((name) => {
              registerMeeting(name, emailKey, meetingLookup.get(emailKey));
            });
          });

          (payload?.agents || []).forEach((agent) => {
            registerMeeting(agent?.agent_name, agent?.agent_email || agent?.email, agent?.meetings);
          });

          if (seniorTeamRosterGroups.length) {
            seniorTeamRosterGroups.forEach((group) => {
              (group.members || []).forEach((member) => registerMeeting(member?.agent_name, member?.email));
            });
          } else {
            (Array.isArray(seniorHierarchy?.managers) ? seniorHierarchy.managers : []).forEach((manager) => {
              (manager?.agents || []).forEach((agent) => registerMeeting(agent?.full_name, agent?.email));
            });
          }

          setSeniorTeamKpis({
            loading: false,
            error: null,
            agents: Array.isArray(payload?.agents) ? payload.agents : [],
            meetings,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load senior manager KPIs:', error);
          setSeniorTeamKpis({
            loading: false,
            error: error?.message || 'Failed to load manager KPIs for your hierarchy.',
            agents: [],
            meetings: {},
          });
        }
      }
    };
    loadSeniorKpis();
    return () => {
      cancelled = true;
    };
  }, [
    isSeniorManager,
    refreshNonce,
    seniorAgentAssignments,
    seniorHierarchy?.managers,
    seniorHierarchyEmailLookup,
    seniorTeamRosterGroups,
    kpiDateRange.startDate,
    kpiDateRange.endDate,
  ]);

const computeAgentMetrics = useCallback((agentData, meetings) => {
  const totals = agentData?.totals || {};
  const sales = Number(totals.sales ?? 0);
  const ipc = Number(totals.ipc ?? 0);
  const conversion = Number(totals.raw_call_conversion ?? 0);
    const ipcRate = sales > 0 ? (ipc / sales) * 100 : 0;
    const missedCount = Number(meetings?.missed_meetings ?? 0);
    const scheduledCount = Number(meetings?.scheduled_meetings ?? 0);
    const conversionLow = conversion < 9;
    const salesLow = sales <= 15;
    const ipcHigh = sales > 0 ? ipc >= sales * 0.2 : ipc > 0;
    const missedAny = missedCount >= 1;
    const riskScore = [conversionLow, salesLow, ipcHigh, missedAny].reduce(
      (sum, flag) => sum + (flag ? 1 : 0),
      0
    );
    return {
      totals,
      sales,
      ipc,
      conversion,
      ipcRate,
      scheduledCount,
      missedCount,
      conversionLow,
      salesLow,
      ipcHigh,
      missedAny,
    riskScore,
    meetings,
  };
}, []);
const summarizeManagerGroupsForSenior = (groups = []) => {
  return (groups || []).reduce(
    (acc, group) => {
      const summary = group?.summary || {};
      acc.sales += Number(summary.sales ?? 0);
      acc.ipc += Number(summary.ipc ?? 0);
      acc.agentCount += Number(summary.agentCount ?? 0);
      acc.rawCalls += Number(summary.rawCalls ?? 0);
      acc.missed += Number(summary.missed ?? 0);
      acc.scheduled += Number(summary.scheduled ?? 0);
      acc.complianceSum += Number(summary.complianceAverage ?? 0) * (Number(summary.complianceAgentCount ?? 0) > 0 ? 1 : 0);
      acc.complianceDen += Number(summary.complianceAgentCount ?? 0) > 0 ? 1 : 0;
      acc.conversionSum += Number(summary.conversion ?? 0) * (Number(summary.rawCalls ?? 0) > 0 ? 1 : 0);
      acc.conversionDen += Number(summary.rawCalls ?? 0) > 0 ? 1 : 0;
      return acc;
    },
    {
      sales: 0,
      ipc: 0,
      agentCount: 0,
      rawCalls: 0,
      missed: 0,
      scheduled: 0,
      complianceSum: 0,
      complianceDen: 0,
      conversionSum: 0,
      conversionDen: 0,
    }
  );
};
const metricsForSort = (metrics = {}, kpiKey = 'sales') => {
  switch (kpiKey) {
    case 'ipc':
      return Number(metrics.ipc ?? 0);
    case 'conversion':
      return Number(metrics.conversion ?? 0);
    case 'calls':
      return Number(metrics.totals?.raw_call_count ?? metrics.rawCallCount ?? 0);
    case 'missed':
      return Number(metrics.missedCount ?? 0);
    case 'scheduled':
      return Number(metrics.scheduledCount ?? 0);
    default:
      return Number(metrics.sales ?? 0);
  }
};
const summaryMetricForSort = (summary = {}, kpiKey = 'sales') => {
  switch (kpiKey) {
    case 'ipc':
      return Number(summary.ipc ?? 0);
    case 'conversion':
      return Number(summary.conversion ?? 0);
    case 'calls':
      return Number(summary.rawCalls ?? 0);
    case 'missed':
      return Number(summary.missed ?? 0);
    case 'scheduled':
      return Number(summary.scheduled ?? 0);
    default:
      return Number(summary.sales ?? 0);
  }
};

const buildManagerGroupRows = useCallback(
  (
    { rosterGroups = [], hierarchyManagers = [], agentKpis = [], meetingMap = {}, complianceMap = new Map() },
    { excludeOldAgents = false, sortOrder = 'desc' } = {}
  ) => {
      const agentDataMap = new Map();
      (agentKpis || []).forEach((agent) => {
        const key = normalizeAgentKey(agent?.agent_name || '');
        if (key) {
          agentDataMap.set(key, agent);
        }
      });
      const defaultMeetings = createEmptyMeetingStats();
      const buildRows = (members = [], parentId, getDisplay) =>
        members
          .filter((member) => !excludeOldAgents || !isOldAgentDescriptor(member))
          .map((member, idx) => {
            const display = getDisplay(member);
            const name = display?.name;
            if (!name) return null;
            const nameKey = normalizeAgentKey(name);
            if (!nameKey) return null;
            const agentData = agentDataMap.get(nameKey) || {};
            const meetings = meetingMap[nameKey] || defaultMeetings;
            const complianceStats = complianceMap.get(nameKey);
            const complianceScore =
              complianceStats && Number.isFinite(complianceStats.average)
                ? complianceStats.average
                : null;
            const row = {
              id: `${parentId}-${nameKey || idx}`,
              member: {
                agent_name: name || 'Unassigned Agent',
                email: display?.email || '',
                membership: display?.membership || '',
              },
              agentData,
              meetings,
              metrics: computeAgentMetrics(agentData, meetings),
              complianceScore,
            };
            return agentMatchesTeamFilters(row) ? row : null;
          })
          .filter(Boolean);
    const summarizeRows = (rows = []) => {
      const summaryTotals = rows.reduce(
        (acc, row) => {
          acc.sales += row.metrics?.sales ?? 0;
          acc.ipc += row.metrics?.ipc ?? 0;
          acc.scheduled += row.metrics?.scheduledCount ?? 0;
          acc.missed += row.metrics?.missedCount ?? 0;
          if (Number.isFinite(row.metrics?.conversion)) {
            acc.conversionTotal += row.metrics.conversion;
            acc.conversionCount += 1;
          }
          acc.rawCalls += Number(row.metrics?.totals?.raw_call_count ?? 0);
          if (Number.isFinite(row.complianceScore)) {
            acc.complianceSum += row.complianceScore;
            acc.complianceCount += 1;
          }
          return acc;
        },
        {
          sales: 0,
          ipc: 0,
          scheduled: 0,
          missed: 0,
          conversionTotal: 0,
          conversionCount: 0,
          rawCalls: 0,
          complianceSum: 0,
          complianceCount: 0,
        }
      );
      const conversion =
        summaryTotals.rawCalls > 0
          ? (summaryTotals.sales / summaryTotals.rawCalls) * 100
          : summaryTotals.conversionCount
            ? summaryTotals.conversionTotal / summaryTotals.conversionCount
            : 0;
      const complianceAverage =
        summaryTotals.complianceCount > 0
          ? summaryTotals.complianceSum / summaryTotals.complianceCount
          : null;
      const summaryAncillary = rows.reduce((acc, row) => {
        if (row?.agentData?.ancillary) {
          mergeAncillaryTotals(acc, row.agentData.ancillary);
        }
        return acc;
      }, {});
      return {
        sales: summaryTotals.sales,
        ipc: summaryTotals.ipc,
        scheduled: summaryTotals.scheduled,
        missed: summaryTotals.missed,
        conversion,
        ancillary: summaryAncillary,
        rawCalls: summaryTotals.rawCalls,
        agentCount: rows.length,
        complianceAverage,
        complianceAgentCount: summaryTotals.complianceCount,
      };
    };

      if (Array.isArray(rosterGroups) && rosterGroups.length) {
        const groups = rosterGroups
          .map((group, index) => {
            if (excludeOldAgents && (isOldAgentDescriptor(group.teamName) || isOldAgentDescriptor(group.displayName))) return null;
            const rows = buildRows(
              group.members || [],
              group.id || `team-${index}`,
              (member) => ({
                name: member?.agent_name,
                email: member?.email,
                membership: member?.membership,
              })
            );
            if (!rows.length) return null;
            const summary = summarizeRows(rows);
            const teamLead = (group.members || []).find((member) =>
              /lead|manager|coach|director/i.test((member?.membership || '').toLowerCase())
            );
            const result = {
              id: group.id || `team-${index}`,
              manager: {
                full_name: group.displayName || formatTeamDisplayName(group.teamName) || group.teamName,
                email: teamLead?.email || '',
                teamLeadName: teamLead?.agent_name || '',
                teamName: group.displayName || formatTeamDisplayName(group.teamName) || group.teamName,
              },
              rows,
              summary,
            };
            result.rows = [...result.rows].sort((a, b) => {
              const focusA = metricsForSort(a?.metrics || {}, teamKpiFilter.kpi);
              const focusB = metricsForSort(b?.metrics || {}, teamKpiFilter.kpi);
              if (focusA !== focusB) {
                return sortOrder === 'asc' ? focusA - focusB : focusB - focusA;
              }
              const salesA = a?.metrics?.sales ?? 0;
              const salesB = b?.metrics?.sales ?? 0;
              if (salesA !== salesB) {
                return sortOrder === 'asc' ? salesA - salesB : salesB - salesA;
              }
              const riskA = a?.metrics?.riskScore ?? 0;
              const riskB = b?.metrics?.riskScore ?? 0;
              if (riskA !== riskB) return riskB - riskA;
              return (a.member?.agent_name || '').localeCompare(b.member?.agent_name || '');
            });
            return result;
          })
          .filter(Boolean);
        return groups.sort((a, b) => {
          const focusA = summaryMetricForSort(a?.summary || {}, teamKpiFilter.kpi);
          const focusB = summaryMetricForSort(b?.summary || {}, teamKpiFilter.kpi);
          if (focusA !== focusB) {
            return sortOrder === 'asc' ? focusA - focusB : focusB - focusA;
          }
          const salesA = a?.summary?.sales ?? 0;
          const salesB = b?.summary?.sales ?? 0;
          if (salesA !== salesB) {
            return sortOrder === 'asc' ? salesA - salesB : salesB - salesA;
          }
          return (a.manager?.full_name || '').localeCompare(b.manager?.full_name || '');
        });
      }

      return (hierarchyManagers || [])
        .map((manager, managerIndex) => {
        if (excludeOldAgents && isOldAgentDescriptor(manager)) return null;
        const managerId =
            manager?.id ||
            manager?.email ||
            `${normalizeAgentKey(manager?.full_name || '') || 'manager'}-${managerIndex}`;
        let rows = buildRows(manager?.agents || [], managerId, (agent) => ({
          name: agent?.full_name,
          email: agent?.email,
          membership: agent?.membership,
        }));
        if (!rows.length) return null;
        rows = [...rows].sort((a, b) => {
          const focusA = metricsForSort(a?.metrics || {}, teamKpiFilter.kpi);
          const focusB = metricsForSort(b?.metrics || {}, teamKpiFilter.kpi);
          if (focusA !== focusB) {
            return sortOrder === 'asc' ? focusA - focusB : focusB - focusA;
          }
          const salesA = a?.metrics?.sales ?? 0;
          const salesB = b?.metrics?.sales ?? 0;
          if (salesA !== salesB) {
            return sortOrder === 'asc' ? salesA - salesB : salesB - salesA;
          }
          const riskA = a?.metrics?.riskScore ?? 0;
          const riskB = b?.metrics?.riskScore ?? 0;
          if (riskA !== riskB) return riskB - riskA;
          return (a.member?.agent_name || '').localeCompare(b.member?.agent_name || '');
        });
        return {
          id: managerId,
          manager,
          rows,
          summary: summarizeRows(rows),
        };
      })
        .filter(Boolean)
        .sort((a, b) => {
          const focusA = summaryMetricForSort(a?.summary || {}, teamKpiFilter.kpi);
          const focusB = summaryMetricForSort(b?.summary || {}, teamKpiFilter.kpi);
          if (focusA !== focusB) {
            return sortOrder === 'asc' ? focusA - focusB : focusB - focusA;
          }
          const salesA = a?.summary?.sales ?? 0;
          const salesB = b?.summary?.sales ?? 0;
          if (salesA !== salesB) {
            return sortOrder === 'asc' ? salesA - salesB : salesB - salesA;
          }
          return (a.manager?.full_name || '').localeCompare(b.manager?.full_name || '');
        });
    },
    [agentMatchesTeamFilters, computeAgentMetrics, createEmptyMeetingStats, teamKpiFilter.kpi]
  );

  const managerTeamRows = useMemo(() => {
    if (!isTeamManager) return [];
    const agentMap = new Map();
    (teamManagerKpis?.agents || []).forEach((agent) => {
      const key = normalizeTeamLabel(agent?.agent_name);
      if (key) agentMap.set(key, agent);
    });
    const meetingMap = teamManagerKpis?.meetings || {};
    const defaultMeetings = { scheduled_meetings: 0, missed_meetings: 0, scheduled_times: [], missed_times: [] };

    const rows = managerTeamMembers.map((member) => {
      const nameKey = normalizeAgentKey(member?.agent_name);
      const agentData = nameKey ? agentMap.get(nameKey) : null;
      const meetingStats = meetingMap[nameKey] || agentData?.meetings || defaultMeetings;
      const metrics = computeAgentMetrics(agentData, meetingStats);
      const complianceStats = nameKey ? complianceScoreMap.get(nameKey) : null;
      const complianceScore =
        complianceStats && Number.isFinite(complianceStats.average) ? complianceStats.average : null;
      return {
        id: `${member.agent_name || 'agent'}-${member.email || 'unknown'}`,
        member,
        agentData,
        meetings: metrics.meetings,
        metrics,
        complianceScore,
      };
    });

    agentMap.forEach((agentData, key) => {
      const exists = rows.some((row) => normalizeAgentKey(row.member?.agent_name) === key);
      if (!exists) {
        const meetingStats = meetingMap[key] || agentData?.meetings || defaultMeetings;
        rows.push({
          id: `${agentData.agent_name || 'agent'}-api`,
          member: { agent_name: agentData.agent_name || 'Unassigned', email: '', membership: '' },
          agentData,
          meetings: meetingStats,
          metrics: computeAgentMetrics(agentData, meetingStats),
          complianceScore:
            complianceScoreMap.get(key)?.average ?? null,
        });
      }
    });

    const sortedRows = rows
      .map((row, index) => ({ ...row, originalIndex: index }))
      .sort((a, b) => {
        const focusA = metricsForSort(a?.metrics || {}, teamKpiFilter.kpi);
        const focusB = metricsForSort(b?.metrics || {}, teamKpiFilter.kpi);
        if (focusA !== focusB) {
          return managerSortOrder === 'asc' ? focusA - focusB : focusB - focusA;
        }
        const riskA = a?.metrics?.riskScore ?? 0;
        const riskB = b?.metrics?.riskScore ?? 0;
        if (riskA !== riskB) return riskB - riskA;
        const nameA = (a.member?.agent_name || '').toLowerCase();
        const nameB = (b.member?.agent_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

    const performanceCandidates = sortedRows
      .filter((row) => row.metrics.riskScore === 0)
      .sort((a, b) => b.metrics.conversion - a.metrics.conversion)
      .slice(0, 3);

    performanceCandidates.forEach((row, index) => {
      row.performanceRank = index;
    });

    return sortedRows;
  }, [isTeamManager, managerSortOrder, managerTeamMembers, teamManagerKpis, complianceScoreMap, teamKpiFilter.kpi]);

  const teamAggregateStats = useMemo(() => {
    if (!isManagerView) return null;
    const source = isSeniorManager ? seniorTeamKpis : teamManagerKpis;
    const agentEntries = Array.isArray(source?.agents) ? source.agents : [];
    const meetingEntries = Object.values(source?.meetings || {});
    if (!agentEntries.length && !meetingEntries.length) {
      return null;
    }
    const totals = agentEntries.reduce(
      (acc, agent) => {
        const metrics = agent?.totals || {};
        acc.sales += Number(metrics.sales ?? 0) || 0;
        acc.ipc += Number(metrics.ipc ?? 0) || 0;
        acc.calls += Number(metrics.raw_call_count ?? 0) || 0;
        mergeAncillaryTotals(acc.ancillaryTotals, agent?.ancillary || {});
        const nameKey = normalizeAgentKey(agent?.agent_name || agent?.agentName || '');
        const complianceStats = nameKey ? complianceScoreMap.get(nameKey) : null;
        if (complianceStats && Number.isFinite(complianceStats.average)) {
          acc.complianceSum += complianceStats.average;
          acc.complianceCount += 1;
        }
        return acc;
      },
      { sales: 0, ipc: 0, calls: 0, ancillaryTotals: {}, complianceSum: 0, complianceCount: 0 }
    );
    const meetingTotals = meetingEntries.reduce(
      (acc, stats) => {
        acc.scheduled += Number(stats?.scheduled_meetings ?? 0) || 0;
        acc.missed += Number(stats?.missed_meetings ?? 0) || 0;
        return acc;
      },
      { scheduled: 0, missed: 0 }
    );
    const conversion = totals.calls > 0 ? (totals.sales / totals.calls) * 100 : 0;
    const complianceAverage =
      totals.complianceCount > 0 ? totals.complianceSum / totals.complianceCount : null;
    return {
      totalSales: totals.sales,
      totalIpc: totals.ipc,
      totalCalls: totals.calls,
      conversion,
      scheduledMeetings: meetingTotals.scheduled,
      missedMeetings: meetingTotals.missed,
      agentCount: agentEntries.length,
      ancillaryTotals: totals.ancillaryTotals,
      complianceAverage,
    };
  }, [isManagerView, isSeniorManager, seniorTeamKpis, teamManagerKpis, complianceScoreMap]);

  const filteredManagerRows = useMemo(() => {
    if (!isTeamManager || isSeniorManager) return managerTeamRows;
    return managerTeamRows.filter((row) => agentMatchesTeamFilters(row));
  }, [agentMatchesTeamFilters, isSeniorManager, isTeamManager, managerTeamRows]);

  const seniorManagerGroups = useMemo(() => {
    if (!isSeniorManager) return [];
    return buildManagerGroupRows(
      {
        rosterGroups: seniorTeamRosterGroups,
        hierarchyManagers: seniorHierarchy?.managers || [],
        agentKpis: seniorTeamKpis?.agents || [],
        meetingMap: seniorTeamKpis?.meetings || {},
        complianceMap: complianceScoreMap,
      },
      { sortOrder: managerSortOrder }
    );
  }, [
    buildManagerGroupRows,
    isSeniorManager,
    seniorHierarchy?.managers,
    seniorTeamKpis?.agents,
    seniorTeamKpis?.meetings,
    seniorTeamRosterGroups,
    complianceScoreMap,
    managerSortOrder,
  ]);
  const executiveManagerGroups = useMemo(() => {
    if (!canViewExecutiveDashboard) return [];
    return executiveDashboardState.seniors.map((entry, index) => ({
      id: entry.id || `executive-senior-${index}`,
      senior: entry.senior,
      groups: buildManagerGroupRows(
        {
          rosterGroups: entry.rosterGroups || [],
          hierarchyManagers: entry.hierarchy?.managers || [],
          agentKpis: entry.agentKpis || [],
          meetingMap: entry.meetingMap || {},
          complianceMap: complianceScoreMap,
        },
        { excludeOldAgents: true, sortOrder: managerSortOrder }
      ),
      error: entry.error,
    }));
  }, [
    buildManagerGroupRows,
    canViewExecutiveDashboard,
    executiveDashboardState.seniors,
    complianceScoreMap,
    managerSortOrder,
  ]);
  const executiveAggregateStats = useMemo(() => {
    if (!canViewExecutiveDashboard || executiveManagerGroups.length === 0) return null;
    const totals = executiveManagerGroups.reduce(
      (acc, entry) => {
        acc.seniorCount += 1;
        entry.groups.forEach((group) => {
          acc.managerCount += 1;
          acc.agentCount += group.summary?.agentCount ?? 0;
          acc.totalSales += group.summary?.sales ?? 0;
          acc.totalIpc += group.summary?.ipc ?? 0;
          acc.totalScheduled += group.summary?.scheduled ?? 0;
          acc.totalMissed += group.summary?.missed ?? 0;
          acc.rawCallCount += group.summary?.rawCalls ?? 0;
          acc.totalBilledCalls += group.summary?.billedCalls ?? 0;
          acc.totalBilledCost += group.summary?.billedCost ?? 0;
          mergeAncillaryTotals(acc.ancillaryTotals, group.summary?.ancillary || {});
          const complianceAgents = group.summary?.complianceAgentCount ?? 0;
          if (Number.isFinite(group.summary?.complianceAverage) && complianceAgents > 0) {
            acc.complianceSum += group.summary.complianceAverage * complianceAgents;
            acc.complianceCount += complianceAgents;
          }
        });
        return acc;
      },
      {
        seniorCount: 0,
        managerCount: 0,
        agentCount: 0,
        totalSales: 0,
        totalIpc: 0,
        totalScheduled: 0,
        totalMissed: 0,
        rawCallCount: 0,
        totalBilledCalls: 0,
        totalBilledCost: 0,
        ancillaryTotals: {},
        complianceSum: 0,
        complianceCount: 0,
      }
    );
    const avgConversion =
      totals.rawCallCount > 0 ? (totals.totalSales / totals.rawCallCount) * 100 : 0;
    const avgBilledConversion =
      totals.totalBilledCalls > 0 ? (totals.totalSales / totals.totalBilledCalls) * 100 : 0;
    const complianceAverage =
      totals.complianceCount > 0 ? totals.complianceSum / totals.complianceCount : null;
    const {
      complianceSum,
      complianceCount,
      ...restTotals
    } = totals;
    return { ...restTotals, avgConversion, avgBilledConversion, complianceAverage };
  }, [canViewExecutiveDashboard, executiveManagerGroups]);

  const executiveAgentRows = useMemo(() => {
    if (!canViewExecutiveDashboard) return [];
    const rows = [];
    executiveManagerGroups.forEach((entry) => {
      entry.groups.forEach((group) => {
        group.rows.forEach((row) => {
          rows.push(row);
        });
      });
    });
    return rows;
  }, [canViewExecutiveDashboard, executiveManagerGroups]);
  const formatDuration = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return '0s';
    const seconds = Math.round(num / 1000);
    if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)}h`;
    if (seconds >= 60) return `${(seconds / 60).toFixed(1)}m`;
    return `${seconds}s`;
  };
  const ahmSummary = ahmExecutiveState.summary;
  const ahmStatusEntries = useMemo(() => {
    const counts = ahmSummary?.status_counts || {};
    return Object.entries(counts)
      .map(([key, value]) => ({
        key: key || 'status-unknown',
        label: key || 'Unknown status',
        count: Number(value) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [ahmSummary]);
  const ahmIssuerEntries = useMemo(() => {
    const counts = ahmSummary?.issuer_counts || {};
    return Object.entries(counts)
      .map(([key, value]) => ({
        key: key || 'issuer-unknown',
        label: key || 'Unknown issuer',
        count: Number(value) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [ahmSummary]);
  const ahmCoverageEntries = useMemo(() => {
    const counts = ahmSummary?.coverage_counts || {};
    return Object.entries(counts)
      .map(([key, value]) => ({
        key: key || 'coverage-unknown',
        label: key || 'Unknown coverage',
        count: Number(value) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [ahmSummary]);
  const ahmStateHotspots = useMemo(() => {
    const entries = ahmSummary?.state_hotspots || [];
    return entries
      .map((entry, index) => ({
        key: entry?.state || entry?.name || `state-${index}`,
        label: entry?.state || entry?.name || 'Unknown state',
        count: Number(entry?.count) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [ahmSummary]);
  const ahmCityHotspots = useMemo(() => {
    const entries = ahmSummary?.city_hotspots || [];
    return entries
      .map((entry, index) => ({
        key: `${entry?.city || 'city'}-${entry?.state || index}`,
        label: `${entry?.city || 'Unknown city'}, ${entry?.state || 'NA'}`,
        count: Number(entry?.count) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [ahmSummary]);
  const ahmGenesysAnalytics = useMemo(() => ahmExecutiveState.genesysAnalytics || null, [ahmExecutiveState.genesysAnalytics]);
  const ahmGenesysError = useMemo(
    () =>
      ahmGenesysAnalytics?.genesys?.error ||
      ahmGenesysAnalytics?.error ||
      null,
    [ahmGenesysAnalytics]
  );
  const ahmTeamLookup = useMemo(() => {
    const map = new Map();
    const teams = Array.isArray(ahmExecutiveState.teams) ? ahmExecutiveState.teams : [];
    teams.forEach((team) => {
      const teamName = toTrimmedString(team?.team_name || team?.team_lead_name || team?.team_lead_email);
      const agents = Array.isArray(team?.agents) ? team.agents : [];
      agents.forEach((agent) => {
        const emailKey = normalizeEmailKey(agent?.agent_email || agent?.email);
        const nameKey = normalizeAgentKey(agent?.agent_name || agent?.name);
        if (teamName) {
          if (emailKey) map.set(emailKey, teamName);
          if (nameKey) map.set(nameKey, teamName);
        }
      });
    });
    // Roster fallback using scoped roster rows (if present)
    (Array.isArray(scopedRosterRows) ? scopedRosterRows : []).forEach((member) => {
      const emailKey = normalizeEmailKey(member?.email);
      const nameKey = normalizeAgentKey(member?.agent_name);
      const teamName = toTrimmedString(member?.team_name);
      if (!teamName) return;
      if (emailKey && !map.has(emailKey)) map.set(emailKey, teamName);
      if (nameKey && !map.has(nameKey)) map.set(nameKey, teamName);
    });
    return map;
  }, [ahmExecutiveState.teams, scopedRosterRows]);
  const ahmGenesysTotals = useMemo(() => {
    const summary = ahmGenesysAnalytics?.summary || {};
    const fallbackTotals = ahmGenesysAnalytics?.genesys?.totals || {};
    const get = (key) => {
      const val = summary[key];
      if (val == null) return Number(fallbackTotals[key]) || 0;
      return Number(val) || 0;
    };
    const rawCalls = get('raw_calls');
    const billedCalls = get('billed_calls');
    const sales = get('sales');
    const rawConv =
      Number.isFinite(Number(summary.raw_conversion_pct))
        ? Number(summary.raw_conversion_pct)
        : rawCalls > 0
          ? (sales / rawCalls) * 100
          : null;
    const billedConv =
      Number.isFinite(Number(summary.billed_conversion_pct))
        ? Number(summary.billed_conversion_pct)
        : billedCalls > 0
          ? (sales / billedCalls) * 100
          : null;
    const metrics = [
      { key: 'raw_calls', label: 'Raw Calls', value: rawCalls },
      { key: 'billed_calls', label: 'Billed Calls (>150s)', value: billedCalls },
      { key: 'sales', label: 'Sales', value: sales },
      { key: 'raw_conversion_pct', label: 'Raw Conversion', value: rawConv, isPercent: true },
      { key: 'billed_conversion_pct', label: 'Billed Conversion', value: billedConv, isPercent: true },
      { key: 'avg_handle_duration', label: 'Avg Handle', value: get('avg_handle_duration'), isDuration: true },
      { key: 'avg_hold_duration', label: 'Avg Hold', value: get('avg_hold_duration'), isDuration: true },
      { key: 'avg_talk_duration', label: 'Avg Talk', value: get('avg_talk_duration'), isDuration: true },
      { key: 'avg_vm_duration', label: 'Avg Voicemail', value: get('avg_vm_duration'), isDuration: true },
      { key: 'avg_wait_duration', label: 'Avg Wait', value: get('avg_wait_duration'), isDuration: true },
    ];
    return metrics.filter((m) => m.value != null && Number.isFinite(Number(m.value)));
  }, [ahmGenesysAnalytics]);
  const ahmGenesysDispositions = useMemo(() => {
    const dispositions = ahmGenesysAnalytics?.csvStats?.dispositions || {};
    return Object.entries(dispositions)
      .map(([name, count]) => ({ name: name || 'unspecified', count: Number(count) || 0 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [ahmGenesysAnalytics]);
  const ahmGenesysAgentKpis = useMemo(() => {
    const list = Array.isArray(ahmGenesysAnalytics?.agentKpis) ? ahmGenesysAnalytics.agentKpis : [];
    const toNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    return list
      .map((agent) => {
        const rawCalls =
          toNumber(agent.metrics?.nOutboundConnected) ??
          toNumber(agent.raw_calls) ??
          toNumber(agent.metrics?.nConnected) ??
          toNumber(agent.calls) ??
          0;
        const billedCalls =
          toNumber(agent.billed_calls) ??
          toNumber(agent.metrics?.nConnected_gt150) ??
          toNumber(agent.metrics?.tAnswered_gt150) ??
          0;
        const salesCount = toNumber(agent.sales_count ?? agent.sales) ?? 0;
        const submitted = toNumber(agent.submitted ?? agent.rows) ?? 0;
        const rawConv =
          rawCalls > 0
            ? (salesCount / rawCalls) * 100
            : toNumber(agent.raw_conversion_pct);
        const billedConv =
          billedCalls > 0
            ? (salesCount / billedCalls) * 100
            : toNumber(agent.billed_conversion_pct);
        return {
          ...agent,
          team:
            agent.team ||
            ahmTeamLookup.get(normalizeEmailKey(agent?.email)) ||
            ahmTeamLookup.get(normalizeAgentKey(agent?.name)) ||
            'Unassigned',
          calls: rawCalls,
          raw_calls: rawCalls,
          billed_calls: billedCalls,
          submitted,
          sales: salesCount,
          raw_conversion_pct: rawConv != null ? rawConv : null,
          billed_conversion_pct: billedConv != null ? billedConv : null,
          avg_handle_duration: toNumber(agent.avg_handle_duration) ?? 0,
          avg_hold_duration: toNumber(agent.avg_hold_duration) ?? 0,
          avg_talk_duration: toNumber(agent.avg_talk_duration) ?? 0,
          avg_vm_duration: toNumber(agent.avg_vm_duration) ?? 0,
          avg_wait_duration: toNumber(agent.avg_wait_duration) ?? 0,
        };
      })
      .sort((a, b) => {
        const convA = Number.isFinite(a.billed_conversion_pct) ? a.billed_conversion_pct : -Infinity;
        const convB = Number.isFinite(b.billed_conversion_pct) ? b.billed_conversion_pct : -Infinity;
        if (convB !== convA) return convB - convA;
        return b.billed_calls - a.billed_calls;
      });
  }, [ahmGenesysAnalytics, ahmTeamLookup]);
  const normalizeAhmAgentKey = useCallback((value) => {
    if (value == null) return '';
    return String(value).trim().toLowerCase();
  }, []);
  const ahmGenesysAgentMap = useMemo(() => {
    const map = new Map();
    ahmGenesysAgentKpis.forEach((agent) => {
      const keys = [agent.email, agent.name, agent.userId, agent.queueId, agent.id];
      keys.forEach((key) => {
        const normalized = normalizeAhmAgentKey(key);
        if (normalized) {
          map.set(normalized, agent);
        }
      });
    });
    return map;
  }, [ahmGenesysAgentKpis, normalizeAhmAgentKey]);
  const ahmGenesysTeams = useMemo(() => {
    const teams = new Map();
    const normalizeTeamKey = (value) => (value == null ? 'unassigned' : String(value).trim().toLowerCase() || 'unassigned');
    const displayName = (value) => {
      const str = value == null ? '' : String(value).trim();
      if (!str) return 'Unassigned';
      return str.charAt(0).toUpperCase() + str.slice(1);
    };
    ahmGenesysAgentKpis.forEach((agent) => {
      const key = normalizeTeamKey(agent.team);
      if (!teams.has(key)) {
        teams.set(key, {
          name: displayName(agent.team),
          rows: 0,
          agentCount: 0,
          metrics: { raw_calls: 0, billed_calls: 0, sales: 0, tHandle: 0, tTalk: 0, tHeld: 0, tVoicemail: 0, tWait: 0 },
          dispositions: {},
        });
      }
      const bucket = teams.get(key);
      bucket.agentCount += 1;
      bucket.rows += Number(agent.submitted || 0);
      bucket.metrics.raw_calls += Number(agent.raw_calls || 0);
      bucket.metrics.billed_calls += Number(agent.billed_calls || 0);
      bucket.metrics.sales += Number(agent.sales || 0);
      bucket.metrics.tHandle += Number(agent.metrics?.tHandle || 0);
      bucket.metrics.tTalk += Number(agent.metrics?.tTalk || 0);
      bucket.metrics.tHeld += Number(agent.metrics?.tHeld || 0);
      bucket.metrics.tVoicemail += Number(agent.metrics?.tVoicemail || 0);
      bucket.metrics.tWait += Number(agent.metrics?.tWait || 0);
    });
    // fallback dispositions from csv stats if available
    const csvTeams = ahmGenesysAnalytics?.csvStats?.teams || {};
    Object.entries(csvTeams).forEach(([teamName, info]) => {
      const key = normalizeTeamKey(teamName);
      if (teams.has(key)) {
        teams.get(key).dispositions = info?.dispositions || {};
      }
    });
    return Array.from(teams.values())
      .map((team) => {
        const raw = Number(team.metrics.raw_calls || 0);
        return {
          ...team,
          avg_handle_duration: raw > 0 ? team.metrics.tHandle / raw : null,
          avg_talk_duration: raw > 0 ? team.metrics.tTalk / raw : null,
          avg_hold_duration: raw > 0 ? team.metrics.tHeld / raw : null,
          avg_vm_duration: raw > 0 ? team.metrics.tVoicemail / raw : null,
          avg_wait_duration: raw > 0 ? team.metrics.tWait / raw : null,
        };
      })
      .sort((a, b) => b.rows - a.rows)
      .slice(0, 8);
  }, [ahmGenesysAgentKpis, ahmGenesysAnalytics]);
  const ahmGenesysAgents = useMemo(() => {
    if (ahmGenesysAgentKpis.length) return ahmGenesysAgentKpis.slice(0, 10);
    return [];
  }, [ahmGenesysAgentKpis]);
  const ahmStatusPieData = useMemo(
    () =>
      ahmStatusEntries
        .filter((entry) => Number(entry.count) > 0)
        .slice(0, 6)
        .map((entry, idx) => ({ name: entry.label, value: entry.count, color: KPI_PALETTE[idx % KPI_PALETTE.length] })),
    [ahmStatusEntries]
  );
  const ahmIssuerPieData = useMemo(
    () =>
      ahmIssuerEntries
        .filter((entry) => Number(entry.count) > 0)
        .slice(0, 6)
        .map((entry, idx) => ({ name: entry.label, value: entry.count, color: KPI_PALETTE[idx % KPI_PALETTE.length] })),
    [ahmIssuerEntries]
  );
  const ahmCoveragePieData = useMemo(
    () =>
      ahmCoverageEntries
        .filter((entry) => Number(entry.count) > 0)
        .slice(0, 6)
        .map((entry, idx) => ({ name: entry.label, value: entry.count, color: KPI_PALETTE[idx % KPI_PALETTE.length] })),
    [ahmCoverageEntries]
  );
  const ahmHotspotPieData = useMemo(
    () =>
      ahmCityHotspots
        .filter((entry) => Number(entry.count) > 0)
        .slice(0, 5)
        .map((entry, idx) => ({ name: entry.label, value: entry.count, color: KPI_PALETTE[idx % KPI_PALETTE.length] })),
    [ahmCityHotspots]
  );
  const ahmTeamLeads = useMemo(() => {
    const teams = Array.isArray(ahmExecutiveState.teams) ? ahmExecutiveState.teams : [];
    return teams
      .map((team, index) => {
        const statusEntries = Object.entries(team?.status_counts || {})
          .map(([key, value]) => ({ key: key || `status-${index}`, label: key || 'Unknown status', count: Number(value) || 0 }))
          .sort((a, b) => b.count - a.count);
        const issuerEntries = Object.entries(team?.issuer_counts || {})
          .map(([key, value]) => ({ key: key || `issuer-${index}`, label: key || 'Unknown issuer', count: Number(value) || 0 }))
          .sort((a, b) => b.count - a.count);
        const coverageEntries = Object.entries(team?.coverage_counts || {})
          .map(([key, value]) => ({ key: key || `coverage-${index}`, label: key || 'Unknown coverage', count: Number(value) || 0 }))
          .sort((a, b) => b.count - a.count);
        const cityEntries = (team?.city_hotspots || []).map((entry, idx) => ({
          key: `${entry?.city || 'city'}-${entry?.state || idx}`,
          label: `${entry?.city || 'Unknown city'}, ${entry?.state || 'NA'}`,
          count: Number(entry?.count) || 0,
        }));
        return {
          ...team,
          id: team?.team_name || team?.team_lead_email || `ahm-team-${index}`,
          statusEntries,
          issuerEntries,
          coverageEntries,
          cityEntries,
        };
      })
      .sort((a, b) => (Number(b?.approved_sales || 0) - Number(a?.approved_sales || 0)));
  }, [ahmExecutiveState.teams]);
  const sortedAhmTeamLeads = useMemo(() => {
    const arr = [...ahmTeamLeads];
    const getApproved = (team) =>
      Number(team.approved_sales ?? team.approved_from_submitted ?? team.sales_count ?? 0);
    const getSubmitted = (team) =>
      Number(team.submitted ?? team.submitted_count ?? 0);
    const getConversion = (team) => {
      if (team.submitted_to_approved_pct != null) return Number(team.submitted_to_approved_pct);
      const submitted = getSubmitted(team);
      const approved = getApproved(team);
      if (!submitted) return 0;
      return (approved / submitted) * 100;
    };
    arr.sort((a, b) => {
      const convA = getConversion(a);
      const convB = getConversion(b);
      if (convB !== convA) return convB - convA;
      const salesA = getApproved(a);
      const salesB = getApproved(b);
      if (salesB !== salesA) return salesB - salesA;
      return (a.team_name || '').localeCompare(b.team_name || '');
    });
    return arr;
  }, [ahmTeamLeads]);
  const ahmUpdatedLabel = useMemo(() => {
    if (!ahmExecutiveState.lastUpdated) return null;
    try {
      return new Date(ahmExecutiveState.lastUpdated).toLocaleString();
    } catch (_) {
      return ahmExecutiveState.lastUpdated;
    }
  }, [ahmExecutiveState.lastUpdated]);

  const publisherLiveOverall = publisherLiveState.data?.overall || null;
  const publisherLiveSummary = useMemo(() => {
    if (!publisherLiveOverall) return null;
    const isEmpty = publisherLiveOverall.__isEmpty;
    const currency =
      publisherLiveOverall.currency ||
      publisherLiveOverall.Currency ||
      publisherLiveState.data?.overall?.currency ||
      'USD';
    const totalCalls = Number(publisherLiveOverall.total_calls);
    const salesCount = Number(publisherLiveOverall.sales_count);
    const billedCalls = Number(
      publisherLiveOverall.billed_calls_count != null
        ? publisherLiveOverall.billed_calls_count
          : publisherLiveOverall.billed_count != null
          ? publisherLiveOverall.billed_count
          : publisherLiveOverall.billed_calls
    );
    const billedCost = Number(
      publisherLiveOverall.total_cost_billed_calls != null
        ? publisherLiveOverall.total_cost_billed_calls
        : publisherLiveOverall.total_billed_cost
    );
    const totalConversion = Number(
      publisherLiveOverall.total_calls_to_sale_conversion_pct != null
        ? publisherLiveOverall.total_calls_to_sale_conversion_pct
        : publisherLiveOverall.total_conversion_pct
    );
    const billedConversion = Number(
      publisherLiveOverall.billed_to_sale_conversion_pct != null
        ? publisherLiveOverall.billed_to_sale_conversion_pct
        : publisherLiveOverall.billed_conversion_pct
    );
    const cpa =
      Number.isFinite(billedCost) && Number.isFinite(salesCount) && salesCount > 0
        ? billedCost / salesCount
        : null;
    return {
      currency,
      totalCallsLabel: isEmpty || !Number.isFinite(totalCalls) ? '—' : totalCalls.toLocaleString(),
      salesCountLabel: isEmpty || !Number.isFinite(salesCount) ? '—' : salesCount.toLocaleString(),
      billedCallsLabel:
        isEmpty || !Number.isFinite(billedCalls) ? '—' : billedCalls.toLocaleString(),
      billedCostLabel:
        isEmpty || !Number.isFinite(billedCost)
          ? '—'
          : new Intl.NumberFormat(undefined, {
              style: 'currency',
              currency,
              maximumFractionDigits: 0,
            }).format(billedCost),
      totalConversionLabel:
        isEmpty || !Number.isFinite(totalConversion)
          ? '—'
          : `${Number(totalConversion || 0).toFixed(1)}%`,
      billedConversionLabel:
        isEmpty || !Number.isFinite(billedConversion)
          ? '—'
          : `${Number(billedConversion || 0).toFixed(1)}%`,
      cpaLabel:
        isEmpty || !Number.isFinite(cpa)
          ? '—'
          : new Intl.NumberFormat(undefined, {
              style: 'currency',
              currency,
              maximumFractionDigits: 0,
            }).format(cpa),
    };
  }, [publisherLiveOverall, publisherLiveState.data?.overall?.currency]);

  const publisherLiveRows = useMemo(
    () => (publisherLiveState.data ? buildPublisherLiveRows(publisherLiveState.data) : []),
    [publisherLiveState.data]
  );

  const publisherLiveLastUpdatedLabel = publisherLiveState.lastUpdated
    ? (() => {
        const date = new Date(publisherLiveState.lastUpdated);
        if (Number.isNaN(date.getTime())) return null;
        return date.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
      })()
    : null;

  const KPI_GRAPH_COLORS = [
    '#1976d2',
    '#9c27b0',
    '#ff9800',
    '#2e7d32',
    '#d32f2f',
    '#0288d1',
    '#7b1fa2',
    '#c2185b',
    '#689f38',
    '#fbc02d',
    '#5d4037',
    '#455a64',
  ];

  const [kpiGraphMetric, setKpiGraphMetric] = useState('sales');
  const [kpiGraphDimension, setKpiGraphDimension] = useState('senior');
  const [kpiGraphType, setKpiGraphType] = useState('bar');
  const [kpiGraphSearch, setKpiGraphSearch] = useState('');

  const kpiGraphMetricOptions = [
    { value: 'sales', label: 'Total Sales' },
    { value: 'ipc', label: 'Total IPC' },
    { value: 'rawCalls', label: 'Raw Call Volume' },
    { value: 'conversion', label: 'Average Raw to Close %' },
    { value: 'compliance', label: 'Avg Compliance Score' },
  ];

  const kpiGraphDimensionOptions = [
    { value: 'senior', label: 'By Senior Manager' },
    { value: 'manager', label: 'By Manager' },
    { value: 'team', label: 'By Team' },
    { value: 'company', label: 'By Company' },
  ];

  const kpiGraphTypeOptions = [
    { value: 'bar', label: 'Bar' },
    { value: 'line', label: 'Line' },
    { value: 'pie', label: 'Pie' },
  ];

  const kpiGraphMetricLabel = useMemo(
    () => kpiGraphMetricOptions.find((opt) => opt.value === kpiGraphMetric)?.label || 'Value',
    [kpiGraphMetric]
  );

  const kpiGraphData = useMemo(() => {
    if (!canViewExecutiveDashboard || executiveManagerGroups.length === 0) return [];
    const rows = [];
    if (kpiGraphDimension === 'senior') {
      executiveManagerGroups.forEach((entry) => {
        const totals = entry.groups.reduce(
          (acc, group) => {
            const summary = group.summary || {};
            acc.sales += summary.sales ?? 0;
            acc.ipc += summary.ipc ?? 0;
            acc.rawCalls += summary.rawCalls ?? 0;
            if (Number.isFinite(summary.complianceAverage)) {
              acc.complianceSum += summary.complianceAverage;
              acc.complianceCount += 1;
            }
            return acc;
          },
          { sales: 0, ipc: 0, rawCalls: 0, complianceSum: 0, complianceCount: 0 }
        );
        const valueMap = {
          sales: totals.sales,
          ipc: totals.ipc,
          rawCalls: totals.rawCalls,
          conversion:
            totals.rawCalls > 0 ? (totals.sales / totals.rawCalls) * 100 : 0,
          compliance:
            totals.complianceCount > 0
              ? totals.complianceSum / totals.complianceCount
              : null,
        };
        const rawValue = valueMap[kpiGraphMetric];
        if (rawValue == null) return;
        const value = Number(rawValue) || 0;
        if (!Number.isFinite(value)) return;
        const name =
          entry.senior?.full_name ||
          entry.senior?.email ||
          'Senior Manager';
        rows.push({ name, value });
      });
    } else if (kpiGraphDimension === 'manager') {
      executiveManagerGroups.forEach((entry) => {
        entry.groups.forEach((group) => {
          const summary = group.summary || {};
          const valueMap = {
            sales: summary.sales,
            ipc: summary.ipc,
            rawCalls: summary.rawCalls,
            conversion: summary.conversion,
            compliance: summary.complianceAverage,
          };
          const rawValue = valueMap[kpiGraphMetric];
          if (rawValue == null) return;
          const value = Number(rawValue) || 0;
          if (!Number.isFinite(value)) return;
          const name =
            group.manager?.full_name ||
            group.manager?.email ||
            group.manager?.teamName ||
            'Manager';
          rows.push({ name, value });
        });
      });
    } else if (kpiGraphDimension === 'team') {
      const byTeam = new Map();
      executiveManagerGroups.forEach((entry) => {
        entry.groups.forEach((group) => {
          const summary = group.summary || {};
          const teamName =
            group.manager?.teamName ||
            group.manager?.full_name ||
            group.manager?.email ||
            'Team';
          if (!byTeam.has(teamName)) {
            byTeam.set(teamName, {
              sales: 0,
              ipc: 0,
              rawCalls: 0,
              complianceSum: 0,
              complianceCount: 0,
            });
          }
          const totals = byTeam.get(teamName);
          totals.sales += summary.sales ?? 0;
          totals.ipc += summary.ipc ?? 0;
          totals.rawCalls += summary.rawCalls ?? 0;
          if (Number.isFinite(summary.complianceAverage)) {
            totals.complianceSum += summary.complianceAverage;
            totals.complianceCount += 1;
          }
        });
      });
      byTeam.forEach((totals, teamName) => {
        const valueMap = {
          sales: totals.sales,
          ipc: totals.ipc,
          rawCalls: totals.rawCalls,
          conversion:
            totals.rawCalls > 0 ? (totals.sales / totals.rawCalls) * 100 : 0,
          compliance:
            totals.complianceCount > 0
              ? totals.complianceSum / totals.complianceCount
              : null,
        };
        const rawValue = valueMap[kpiGraphMetric];
        if (rawValue == null) return;
        const value = Number(rawValue) || 0;
        if (!Number.isFinite(value)) return;
        rows.push({ name: teamName, value });
      });
    } else if (kpiGraphDimension === 'company') {
      const byCompany = new Map();
      executiveManagerGroups.forEach((entry) => {
        const companyName = entry.senior?.company || 'Company';
        entry.groups.forEach((group) => {
          const summary = group.summary || {};
          if (!byCompany.has(companyName)) {
            byCompany.set(companyName, {
              sales: 0,
              ipc: 0,
              rawCalls: 0,
              complianceSum: 0,
              complianceCount: 0,
            });
          }
          const totals = byCompany.get(companyName);
          totals.sales += summary.sales ?? 0;
          totals.ipc += summary.ipc ?? 0;
          totals.rawCalls += summary.rawCalls ?? 0;
          if (Number.isFinite(summary.complianceAverage)) {
            totals.complianceSum += summary.complianceAverage;
            totals.complianceCount += 1;
          }
        });
      });
      byCompany.forEach((totals, companyName) => {
        const valueMap = {
          sales: totals.sales,
          ipc: totals.ipc,
          rawCalls: totals.rawCalls,
          conversion:
            totals.rawCalls > 0 ? (totals.sales / totals.rawCalls) * 100 : 0,
          compliance:
            totals.complianceCount > 0
              ? totals.complianceSum / totals.complianceCount
              : null,
        };
        const rawValue = valueMap[kpiGraphMetric];
        if (rawValue == null) return;
        const value = Number(rawValue) || 0;
        if (!Number.isFinite(value)) return;
        rows.push({ name: companyName, value });
      });
    }
    const search = kpiGraphSearch.trim().toLowerCase();
    const filtered = search
      ? rows.filter((row) =>
          row.name.toLowerCase().includes(search)
        )
      : rows;
    const sorted = filtered
      .map((row) => ({ ...row }))
      .sort((a, b) => b.value - a.value);
    return sorted.slice(0, 12);
  }, [
    canViewExecutiveDashboard,
    executiveManagerGroups,
    kpiGraphDimension,
    kpiGraphMetric,
    kpiGraphSearch,
  ]);

  const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
  const renderBoldText = (text) => {
    const parts = String(text || '').split('**');
    return parts.map((part, idx) =>
      idx % 2 === 1 ? <strong key={`bold-${idx}`}>{part}</strong> : <span key={`bold-${idx}`}>{part}</span>
    );
  };
  const renderBulletText = (text) => (
    <span>
      <span style={{ marginRight: 6 }}>•</span>
      {renderBoldText(text)}
    </span>
  );
  const formatMeetingTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };
  const getTrophyIcon = (rank, { size = 'small' } = {}) => {
    if (typeof rank !== 'number') return null;
    const colors = ['#f5b700', '#9ca3af', '#b45309'];
    const color = colors[rank] || '#9ca3af';
    return <EmojiEventsIcon style={{ color }} fontSize={size} />;
  };
  const getAgentRowKey = (row) => {
    if (!row) return '';
    if (row.id) return String(row.id);
    const name =
      row.member?.agent_name ||
      row.agentData?.agent_name ||
      row.agent_name ||
      '';
    const email =
      row.member?.email ||
      row.agentData?.agent_email ||
      row.agent_email ||
      '';
    return (
      normalizeAgentKey(name) ||
      normalizeEmailKey(email) ||
      String(name || email || '')
    );
  };
  const buildAgentRankMap = (rows = []) => {
    const ranked = rows
      .map((row, index) => ({
        row,
        index,
        conversion: Number(row?.metrics?.conversion ?? 0) || 0,
        sales: Number(row?.metrics?.sales ?? 0) || 0,
      }))
      .sort((a, b) => {
        if (b.conversion !== a.conversion) return b.conversion - a.conversion;
        if (b.sales !== a.sales) return b.sales - a.sales;
        return a.index - b.index;
      });
    const map = new Map();
    ranked.slice(0, 3).forEach((entry, rank) => {
      const key = getAgentRowKey(entry.row);
      if (key) map.set(key, rank);
    });
    return map;
  };
  const sortAgentsByPerformance = (rows = []) =>
    rows
      .map((row, index) => ({
        row,
        index,
        conversion: Number(row?.metrics?.conversion ?? 0) || 0,
        sales: Number(row?.metrics?.sales ?? 0) || 0,
      }))
      .sort((a, b) => {
        if (b.conversion !== a.conversion) return b.conversion - a.conversion;
        if (b.sales !== a.sales) return b.sales - a.sales;
        return a.index - b.index;
      })
      .map((entry) => entry.row);

  const AgentAccordionRow = ({ row, trophyRank }) => {
    if (!row) return null;
    const metrics = row.metrics || {};
    const meetingStats =
      row.meetings && (row.meetings.scheduled_times || row.meetings.missed_times)
        ? row.meetings
        : createEmptyMeetingStats();
    const conversion = metrics.conversion ?? 0;
    const sales = metrics.sales ?? 0;
    const ipc = metrics.ipc ?? 0;
    const scheduledCount = metrics.scheduledCount ?? 0;
    const missedCount = metrics.missedCount ?? 0;
    const complianceScore =
      typeof row?.complianceScore === 'number' && Number.isFinite(row.complianceScore)
        ? row.complianceScore
        : null;
    const performerIcon =
      typeof trophyRank === 'number'
        ? (
            <span title="Top Performer" style={{ display: 'inline-flex', alignItems: 'center' }}>
              {getTrophyIcon(trophyRank)}
            </span>
          )
        : typeof row.performanceRank === 'number'
          ? (
              <span title="Top Performer" style={{ display: 'inline-flex', alignItems: 'center' }}>
                {{
                  0: <EmojiEventsIcon style={{ color: '#f59e0b' }} fontSize="small" />,
                  1: <MilitaryTechIcon style={{ color: '#2563eb' }} fontSize="small" />,
                  2: <WorkspacePremiumIcon style={{ color: '#7c3aed' }} fontSize="small" />,
                }[row.performanceRank]}
              </span>
            )
          : null;
    const chips = [
      { label: `Sales ${sales}` },
      { label: `IPC ${ipc}` },
      { label: `Conversion ${formatPercent(conversion)}` },
      scheduledCount > 0 ? { label: `Scheduled ${scheduledCount}` } : null,
      missedCount > 0 ? { label: `Missed ${missedCount}` } : null,
      complianceScore != null
        ? { label: `Compliance ${complianceScore.toFixed(1)}` }
        : null,
    ].filter(Boolean);
    const ancillaryEntries = row.agentData?.ancillary ? formatAncillaryEntries(row.agentData.ancillary) : [];
    const agentDisplayName =
      row.member?.agent_name || row.agentData?.agent_name || 'Unassigned Agent';
    const hasAncillary = row.agentData?.ancillary && ancillaryEntries.length;
    const panelKey = row.id || normalizeAgentKey(agentDisplayName) || 'agent';
    const resolveMetricColor = (token) => {
      if (!token) return undefined;
      const normalized = String(token);
      const palette = {
        'error.main': '#d32f2f',
        'success.main': '#2e7d32',
        'secondary.main': '#7c3aed',
        'text.primary': '#111012',
      };
      return palette[normalized] || normalized;
    };
    const chipLabels = chips.map((chip) => chip.label);
    const header = isMobile ? (
      <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
        <AMSpace align="center" wrap style={{ '--gap': '6px' }}>
          <span style={{ fontWeight: 600 }}>Agent: {agentDisplayName}</span>
          <AMTag color="primary" fill="outline">Agent</AMTag>
          {performerIcon}
        </AMSpace>
        {row.member?.email ? (
          <div style={{ fontSize: 12, color: '#667085' }}>{row.member.email}</div>
        ) : null}
        {chipLabels.length ? (
          <AMSpace wrap style={{ '--gap': '6px' }}>
            {chipLabels.map((label) => (
              <AMTag key={`${panelKey}-${label}`} fill="outline">
                {label}
              </AMTag>
            ))}
          </AMSpace>
        ) : null}
      </AMSpace>
    ) : (
      <AntSpace direction="vertical" size={4} style={{ width: '100%' }}>
        <AntSpace align="center" wrap>
          <AntTypography.Text strong>Agent: {agentDisplayName}</AntTypography.Text>
          <AntTag color="blue">Agent</AntTag>
          {performerIcon}
        </AntSpace>
        {row.member?.email ? (
          <AntTypography.Text type="secondary">{row.member.email}</AntTypography.Text>
        ) : null}
        {chipLabels.length ? (
          <AntSpace wrap size={[8, 8]}>
            {chipLabels.map((label) => (
              <AntTag key={`${panelKey}-${label}`}>{label}</AntTag>
            ))}
          </AntSpace>
        ) : null}
      </AntSpace>
    );

    const metricCards = [
      { label: 'Closed Sales', value: sales, color: metrics.salesLow ? 'error.main' : 'success.main' },
      { label: 'IPC Sales', value: ipc, color: metrics.ipcHigh ? 'error.main' : 'text.primary' },
      { label: 'Raw Call Volume', value: metrics.totals?.raw_call_count ?? 0 },
      { label: 'Call Conversion', value: conversion, formatter: formatPercent, color: metrics.conversionLow ? 'error.main' : 'success.main' },
      { label: 'Scheduled Meetings', value: scheduledCount },
      { label: 'Missed Meetings', value: missedCount, color: metrics.missedAny ? 'error.main' : 'text.primary' },
      complianceScore != null
        ? {
            label: 'Avg Compliance',
            value: complianceScore,
            formatter: (val) => `${val.toFixed(1)}`,
            color: 'secondary.main',
          }
        : null,
    ].filter(Boolean);
    const renderMetricValue = (metric) =>
      typeof metric.formatter === 'function' ? metric.formatter(metric.value) : metric.value;
    const body = (
      <div>
        {row.agentData?.error ? (
          isMobile ? (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                borderRadius: 8,
                border: '1px solid #ffe58f',
                background: '#fffbe6',
                color: '#ad6800',
                fontSize: 13,
              }}
            >
              Unable to load KPI metrics: {row.agentData.error}
            </div>
          ) : (
            <AntAlert
              type="warning"
              message={`Unable to load KPI metrics: ${row.agentData.error}`}
              showIcon
              style={{ marginBottom: 12 }}
            />
          )
        ) : null}
        {isMobile ? (
          <AMGrid columns={2} gap={10}>
            {metricCards.map((metric) => (
              <AMGrid.Item key={`${panelKey}-${metric.label}`} style={{ display: 'flex' }}>
                <div
                  style={{
                    borderRadius: 10,
                    border: '1px solid #e6e8ec',
                    padding: 10,
                    background: '#fff',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                    {metric.label}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: resolveMetricColor(metric.color) }}>
                    {renderMetricValue(metric)}
                  </div>
                </div>
              </AMGrid.Item>
            ))}
          </AMGrid>
        ) : (
          <AntRow gutter={[12, 12]}>
            {metricCards.map((metric) => (
              <AntCol xs={24} sm={12} md={8} key={`${panelKey}-${metric.label}`} style={{ display: 'flex' }}>
                <AntCard
                  size="small"
                  style={{ borderRadius: 12, width: '100%', height: '100%' }}
                  styles={{ body: { display: 'flex', flexDirection: 'column', gap: 6, height: '100%' } }}
                >
                  <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                    {metric.label}
                  </AntTypography.Text>
                  <AntTypography.Title
                    level={4}
                    style={{ margin: 0, color: resolveMetricColor(metric.color) }}
                  >
                    {renderMetricValue(metric)}
                  </AntTypography.Title>
                </AntCard>
              </AntCol>
            ))}
          </AntRow>
        )}
        <div style={{ marginTop: 16 }}>
          {isMobile ? (
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Meeting Schedule</div>
          ) : (
            <AntTypography.Text strong>Meeting Schedule</AntTypography.Text>
          )}
          {isMobile ? (
            <div style={{ marginTop: 8 }}>
              {[
                {
                  title: 'Scheduled Meetings',
                  times: Array.isArray(meetingStats.scheduled_times) ? meetingStats.scheduled_times : [],
                  accent: '#1976d2',
                  emptyText: 'No scheduled meetings recorded in this range.',
                },
                {
                  title: 'Missed Meetings',
                  times: Array.isArray(meetingStats.missed_times) ? meetingStats.missed_times : [],
                  accent: '#d32f2f',
                  emptyText: 'No missed meetings recorded in this range.',
                },
              ].map((section) => {
                const total = section.times.length;
                const displayCount = Math.min(total, 6);
                const visible = section.times.slice(0, displayCount);
                const countLabel = `${total} ${total === 1 ? 'meeting' : 'meetings'}`;
                return (
                  <AMCard
                    key={`${panelKey}-${section.title}`}
                    style={{
                      marginBottom: 12,
                      borderRadius: 12,
                      border: `1px solid ${section.accent}33`,
                      background: `linear-gradient(135deg, ${section.accent}0d, rgba(255,255,255,0.95))`,
                    }}
                  >
                    <AMCard.Body>
                      <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          <span style={{ fontWeight: 600, color: section.accent }}>{section.title}</span>
                          <AMTag color="primary" fill="outline">{countLabel}</AMTag>
                        </div>
                        {visible.length ? (
                          <AMList style={{ '--border-inner': 'none' }}>
                            {visible.map((timestamp, idx) => (
                              <AMList.Item key={`${panelKey}-${section.title}-${idx}`}>
                                <AMSpace align="center" style={{ '--gap': '6px' }}>
                                  <AMTag color="primary" fill="outline">{idx + 1}</AMTag>
                                  <span>{formatMeetingTime(timestamp)}</span>
                                </AMSpace>
                              </AMList.Item>
                            ))}
                          </AMList>
                        ) : (
                          <div style={{ color: '#667085', fontSize: 12 }}>{section.emptyText}</div>
                        )}
                        {total > displayCount ? (
                          <div style={{ color: '#667085', fontSize: 12 }}>+{total - displayCount} more</div>
                        ) : null}
                      </AMSpace>
                    </AMCard.Body>
                  </AMCard>
                );
              })}
            </div>
          ) : (
            <AntRow gutter={[12, 12]} style={{ marginTop: 8 }}>
              {[
                {
                  title: 'Scheduled Meetings',
                  times: Array.isArray(meetingStats.scheduled_times) ? meetingStats.scheduled_times : [],
                  accent: '#1976d2',
                  emptyText: 'No scheduled meetings recorded in this range.',
                },
                {
                  title: 'Missed Meetings',
                  times: Array.isArray(meetingStats.missed_times) ? meetingStats.missed_times : [],
                  accent: '#d32f2f',
                  emptyText: 'No missed meetings recorded in this range.',
                },
              ].map((section) => {
                const total = section.times.length;
                const displayCount = Math.min(total, 6);
                const visible = section.times.slice(0, displayCount);
                const countLabel = `${total} ${total === 1 ? 'meeting' : 'meetings'}`;
                return (
                  <AntCol xs={24} md={12} key={`${panelKey}-${section.title}`} style={{ display: 'flex' }}>
                    <AntCard
                      size="small"
                      style={{
                        borderRadius: 12,
                        width: '100%',
                        borderColor: `${section.accent}33`,
                        background: `linear-gradient(135deg, ${section.accent}0d, rgba(255,255,255,0.95))`,
                      }}
                    >
                      <AntSpace direction="vertical" style={{ width: '100%' }} size={6}>
                        <AntSpace align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                          <AntTypography.Text strong style={{ color: section.accent }}>
                            {section.title}
                          </AntTypography.Text>
                          <AntTag color="blue">{countLabel}</AntTag>
                        </AntSpace>
                        {visible.length ? (
                          <AntList
                            size="small"
                            dataSource={visible}
                            renderItem={(timestamp, idx) => (
                              <AntList.Item>
                                <AntSpace>
                                  <AntTag color="blue">{idx + 1}</AntTag>
                                  <span>{formatMeetingTime(timestamp)}</span>
                                </AntSpace>
                              </AntList.Item>
                            )}
                          />
                        ) : (
                          <AntTypography.Text type="secondary">{section.emptyText}</AntTypography.Text>
                        )}
                        {total > displayCount ? (
                          <AntTypography.Text type="secondary">
                            +{total - displayCount} more
                          </AntTypography.Text>
                        ) : null}
                      </AntSpace>
                    </AntCard>
                  </AntCol>
                );
              })}
            </AntRow>
          )}
        </div>
        {hasAncillary ? (
          <div style={{ marginTop: 16 }}>
            {isMobile ? (
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Ancillary Products</div>
            ) : (
              <AntTypography.Text strong>Ancillary Products</AntTypography.Text>
            )}
            <div style={{ marginTop: 8 }}>
              {isMobile ? (
                <AMSpace wrap style={{ '--gap': '6px' }}>
                  {ancillaryEntries.map((entry) => (
                    <AMTag
                      key={`${panelKey}-anc-${entry.key}`}
                      color={entry.key === 'total' ? 'primary' : 'default'}
                      fill="outline"
                    >
                      {entry.label}: {entry.value}
                    </AMTag>
                  ))}
                </AMSpace>
              ) : (
                <AntSpace wrap size={[8, 8]}>
                  {ancillaryEntries.map((entry) => (
                    <AntTag key={`${panelKey}-anc-${entry.key}`} color={entry.key === 'total' ? 'blue' : 'default'}>
                      {entry.label}: {entry.value}
                    </AntTag>
                  ))}
                </AntSpace>
              )}
            </div>
          </div>
        ) : null}
        {isMobile ? (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 8,
              border: '1px solid #d6e4ff',
              background: '#f0f6ff',
              color: '#1d4ed8',
              fontSize: 12,
            }}
          >
            Metrics reflect the same KPI definitions used for the individual dashboard view during {weekWindow.label}.
          </div>
        ) : (
          <AntAlert
            type="info"
            message={`Metrics reflect the same KPI definitions used for the individual dashboard view during ${weekWindow.label}.`}
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </div>
    );

    return renderResponsiveCollapse(
      [
        {
          key: panelKey,
          label: header,
          children: body,
        },
      ],
      { ghost: false }
    );
  };

  const renderManagerTeamKpis = ({ showForSenior = false, title = 'Manager Team KPI Overview' } = {}) => {
    if (!isTeamManager) return null;
    if (isSeniorManager && !showForSenior) return null;
    const hasBaseRows = managerTeamRows.length > 0;
    const rowsToRender = filteredManagerRows;
    const content = teamManagerKpis.loading ? (
      isMobile ? (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <AMSpin />
          <div style={{ marginTop: 8, color: '#667085' }}>Loading team KPIs…</div>
        </div>
      ) : (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <AntSpin />
          <AntTypography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Loading team KPIs…
          </AntTypography.Text>
        </div>
      )
    ) : teamManagerKpis.error ? (
      <Alert severity="error">{teamManagerKpis.error}</Alert>
    ) : !hasBaseRows ? (
      <Alert severity="info">No KPI data is available for your team during this range.</Alert>
    ) : rowsToRender.length === 0 ? (
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={resetTeamKpiFilters}>
            Reset filters
          </Button>
        }
      >
        No team members match the current filters.
      </Alert>
    ) : (
      <>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 3 }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>KPI Focus</InputLabel>
            <Select
              label="KPI Focus"
              value={teamKpiFilter.kpi}
              onChange={(event) =>
                setTeamKpiFilter((prev) => ({ ...prev, kpi: event.target.value }))
              }
            >
              {Object.entries(TEAM_KPI_STANDARD_OPTIONS).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={teamKpiFilter.direction}
            onChange={(_, value) => {
              if (!value) return;
              setTeamKpiFilter((prev) => ({ ...prev, direction: value }));
              if (value === 'below') setManagerSortOrder('asc');
              else if (value === 'above' || value === 'all') setManagerSortOrder('desc');
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="above">Highest</ToggleButton>
            <ToggleButton value="below">Lowest</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Meeting Filter</InputLabel>
            <Select
              label="Meeting Filter"
              value={meetingRowFilter}
              onChange={(event) => setMeetingRowFilter(event.target.value)}
            >
              <MenuItem value="all">All meeting outcomes</MenuItem>
              <MenuItem value="scheduled">Has scheduled meetings</MenuItem>
              <MenuItem value="missed">Has missed meetings</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="Pick a day"
            InputLabelProps={{ shrink: true }}
            value={kpiSelectedDate}
            onChange={(event) => handleKpiDateChange(event.target.value)}
            inputProps={{ min: weekWindow.startDate, max: weekWindow.endDate }}
          />
        </Stack>
        {(() => {
          const sortedRows = sortAgentsByPerformance(rowsToRender || []);
          const rankMap = buildAgentRankMap(sortedRows);
          return (
            <Stack spacing={1.5}>
              {sortedRows.map((row) => (
                <AgentAccordionRow
                  key={row.id}
                  row={row}
                  trophyRank={rankMap.get(getAgentRowKey(row))}
                />
              ))}
            </Stack>
          );
        })()}
      </>
    );

    const subheader =
      managerTeamMembers.length
        ? `${managerTeamMembers.length} team members · ${kpiDisplayLabel}`
        : 'No rostered team members found.';

    if (isMobile) {
      return (
        <AMCard style={{ marginTop: 16 }}>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
              <div style={{ fontWeight: 700 }}>{title}</div>
              <div style={{ color: '#667085' }}>{subheader}</div>
              {content}
            </AMSpace>
          </AMCard.Body>
        </AMCard>
      );
    }

    return (
      <AntCard
        title={<span style={{ fontWeight: 700 }}>{title}</span>}
        style={{ marginTop: 24 }}
      >
        <AntTypography.Text type="secondary">{subheader}</AntTypography.Text>
        <div style={{ marginTop: 12 }}>{content}</div>
      </AntCard>
    );
  };

  // Fetch AI mock call completion manifest for this agent
  useEffect(() => {
    const displayName = (user?.name || '').trim();
    const email = (user?.email || '').trim();
    if (!displayName && !email) return;
    let cancelled = false;
    (async () => {
      try {
        setNpcLoading(true);

        // One-time per session: trigger manifest append to ensure latest completions are recorded
        try {
          const flag = sessionStorage.getItem('npcManifestRefreshed');
          if (!flag) {
            await api.post('/ai-npc/level-completed', {});
            sessionStorage.setItem('npcManifestRefreshed', '1');
          }
        } catch (_) {}

        // Build candidate agent names (roster match, display name, email-derived proper name)
        const local = (email.split('@')[0] || '').trim();
        const toProper = (s) => s
          .replace(/[._-]+/g, ' ')
          .split(' ')
          .filter(Boolean)
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');

        let rosterName = null;
        try {
          const cand = (teamRoster || []).find(m => (
            (m.agent_name || '').toLowerCase().trim() === displayName.toLowerCase().trim() ||
            (m.agent_name || '').toLowerCase().trim() === toProper(local).toLowerCase().trim()
          ));
          rosterName = cand?.agent_name || null;
        } catch (_) {}

        const candidates = Array.from(new Set([
          rosterName,
          displayName || null,
          (local ? toProper(local) : null)
        ].filter(Boolean)));

        let found = [];
        for (const name of candidates) {
          try {
            const rows = await api.getNpcLevelCompletedByAgent(name);
            if (Array.isArray(rows) && rows.length > 0) { found = rows; break; }
          } catch (_) { /* try next */ }
        }

        if (!cancelled) setNpcLevels(found);
      } catch (e) {
        if (!cancelled) setNpcError(e?.message || 'Failed to load AI level stats');
      } finally {
        if (!cancelled) setNpcLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, teamRoster]);

  const levelOrder = ['L1','L2','L3','L4','L5'];
  const levelMap = useMemo(() => {
    const m = new Map();
    (npcLevels || []).forEach(r => { if (r?.level_completed) m.set(String(r.level_completed).toUpperCase(), r); });
    return m;
  }, [npcLevels]);

  const LevelPanel = () => {
    // Silent load: show nothing while loading unless we already have rows
    if (npcLoading && (!npcLevels || npcLevels.length === 0)) return null;
    if (npcError || !npcLevels || npcLevels.length === 0) return null; // hide if no data
    const refreshing = npcLoading && npcLevels.length > 0;
    if (isMobile) {
      return (
        <AMCard style={{ marginBottom: 16 }}>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '10px' }}>
              <AMSpace align="center" style={{ fontWeight: 700 }}>
                <EmojiEventsIcon fontSize="small" />
                <span>AI Mock Call Levels</span>
              </AMSpace>
              <div style={{ color: '#667085', fontSize: 13 }}>
                {refreshing ? 'Refreshing…' : 'Completed levels show a trophy and your highest score. Aim for L5!'}
              </div>
              <AMGrid columns={2} gap={10}>
                {levelOrder.map((lvl) => {
                  const row = levelMap.get(lvl);
                  const completed = !!row;
                  const high = completed ? Number(row.high_score) : null;
                  return (
                    <AMGrid.Item key={lvl}>
                      <div
                        style={{
                          borderRadius: 12,
                          border: `1px solid ${completed ? '#f3c969' : '#e6e8ec'}`,
                          padding: 12,
                          background: completed ? 'rgba(255, 215, 64, 0.12)' : '#fff',
                        }}
                      >
                        <AMSpace align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                          <span style={{ fontWeight: 700 }}>{lvl}</span>
                          {completed ? <AMTag color="success">Done</AMTag> : <AMTag>Pending</AMTag>}
                        </AMSpace>
                        <div style={{ fontSize: 12, color: '#667085', marginTop: 6 }}>
                          High: {completed && Number.isFinite(high) ? Math.round(high) : '—'}
                        </div>
                      </div>
                    </AMGrid.Item>
                  );
                })}
              </AMGrid>
            </AMSpace>
          </AMCard.Body>
        </AMCard>
      );
    }

    return (
      <AntCard
        title={
          <AntSpace align="center">
            <EmojiEventsIcon color="warning" />
            <span style={{ fontWeight: 700 }}>AI Mock Call Levels</span>
          </AntSpace>
        }
        extra={refreshing ? <AntSpin size="small" /> : null}
        style={{ marginBottom: 24 }}
      >
        <AntTypography.Text type="secondary">
          {refreshing ? 'Refreshing…' : 'Completed levels show a trophy and your highest score. Aim for L5!'}
        </AntTypography.Text>
        <AntRow gutter={[16, 16]} style={{ marginTop: 16 }}>
          {levelOrder.map((lvl) => {
            const row = levelMap.get(lvl);
            const completed = !!row;
            const high = completed ? Number(row.high_score) : null;
            return (
              <AntCol xs={24} sm={12} md={8} lg={4} key={lvl}>
                <AntCard size="small" style={{ borderRadius: 12 }}>
                  <AntSpace direction="vertical" style={{ width: '100%' }}>
                    <AntSpace align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                      <span style={{ fontWeight: 700 }}>{lvl}</span>
                      {completed ? <AntTag color="gold">Completed</AntTag> : <AntTag>Not completed</AntTag>}
                    </AntSpace>
                    <AntTypography.Text type="secondary">
                      High: {completed && Number.isFinite(high) ? Math.round(high) : '—'}
                    </AntTypography.Text>
                  </AntSpace>
                </AntCard>
              </AntCol>
            );
          })}
        </AntRow>
      </AntCard>
    );
  };

  const AgentMeetingBanner = () => {
    if (isManagerView) return null;
    const stats = agentMeetingStats.stats;
    const scheduledCount = Number(stats?.scheduled_meetings ?? 0);
    if (!stats || scheduledCount <= 0) return null;
    const upcomingTimes = (stats.scheduled_times || [])
      .slice(0, 3)
      .map((time, index) => (
        isMobile ? (
          <AMTag key={`meeting-${index}`} color="primary">
            {formatMeetingTime(time)}
          </AMTag>
        ) : (
          <AntTag key={`meeting-${index}`} color="blue">
            {formatMeetingTime(time)}
          </AntTag>
        )
      ));
    const message = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 700 }}>
          You have {scheduledCount} coaching meeting{scheduledCount === 1 ? '' : 's'} scheduled this week.
        </div>
        <div style={{ color: '#667085', fontSize: 13 }}>
          Check your work email for the calendar invite details and look for the e-sign coaching form sent from{' '}
          <strong>compliance_guard@sabalgrp.com</strong> before the session begins and be advised, attendance at the
          scheduled time is mandatory. Failure to attend mandatory compliance remediations can and may result in your
          suspension from live calls and Sunfire. Being tardy to the assigned coaching may also result in an automatic
          disciplinary notice equal to that of a missed meeting.
        </div>
        {upcomingTimes.length ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{upcomingTimes}</div> : null}
      </div>
    );

    if (isMobile) {
      return (
        <AMCard style={{ marginBottom: 16, borderRadius: 12 }}>
          <AMCard.Body>{message}</AMCard.Body>
        </AMCard>
      );
    }

    return (
      <AntAlert
        type="info"
        showIcon
        message={message}
        style={{ marginBottom: 24, borderLeft: '4px solid #facc15', background: '#fffbeb' }}
      />
    );
  };

  const AgentKpiPanel = () => {
    if (!shouldShowKpiSection) return null;
    const metricCards = [
      { label: 'Weekly Sales', value: agentKpi?.sales ?? 0, caption: 'Closed apps submitted', accent: 'success.main' },
      { label: 'Weekly IPC', value: agentKpi?.ipc ?? 0, caption: 'Internal Plan Changes', accent: 'info.main' },
      { label: 'Call Volume', value: agentKpi?.rawCallCount ?? 0, caption: 'Total handled calls', accent: 'warning.main' },
      {
        label: 'Call Conversion',
        value: agentKpi?.rawCallConversion ?? 0,
        caption: 'Sales ÷ call volume',
        accent: 'secondary.main',
        formatter: (val) => `${val.toFixed(1)}%`,
      },
    ];
    const showPlaceholder = !agentKpi && !agentKpiLoading && !agentKpiError;
    const formatMetricValue = (metric) => {
      if (metric.value == null) {
        return metric.nullDisplay ?? '—';
      }
      const numeric = Number(metric.value);
      if (!Number.isFinite(numeric)) {
        return metric.nullDisplay ?? '—';
      }
      return typeof metric.formatter === 'function' ? metric.formatter(numeric) : numeric;
    };
    const ancillaryEntries = agentKpi?.ancillary ? formatAncillaryEntries(agentKpi.ancillary) : [];
    const hasAncillary = ancillaryEntries.length > 0 && agentKpi?.ancillary;

    if (isMobile) {
      return (
        <AMCard style={{ marginBottom: 16 }}>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <AMSpace align="center" style={{ fontWeight: 700 }}>
                <TrendingUpIcon fontSize="small" />
                <span>My Weekly KPIs</span>
              </AMSpace>
              <div style={{ color: '#667085', fontSize: 13 }}>Tracking week of {weekWindow.label}</div>
              {agentKpiError ? <AMTag color="warning">{agentKpiError}</AMTag> : null}
              {agentKpi ? (
                <AMGrid columns={2} gap={10}>
                  {metricCards.map((metric) => (
                    <AMGrid.Item key={metric.label}>
                      <div
                        style={{
                          borderRadius: 12,
                          border: '1px solid #e6e8ec',
                          padding: 12,
                          background: '#fff',
                        }}
                      >
                        <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                          {metric.label}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, marginTop: 6 }}>
                          {formatMetricValue(metric)}
                        </div>
                        <div style={{ fontSize: 12, color: '#667085' }}>{metric.caption}</div>
                      </div>
                    </AMGrid.Item>
                  ))}
                </AMGrid>
              ) : null}
              {showPlaceholder ? (
                <div style={{ color: '#667085' }}>
                  KPIs will appear here once your Medicare-week production posts.
                </div>
              ) : null}
              {hasAncillary ? (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Ancillary Product Counts</div>
                  <AMGrid columns={2} gap={8}>
                    {ancillaryEntries.map((entry) => (
                      <AMGrid.Item key={`agent-anc-${entry.key}`}>
                        <div
                          style={{
                            borderRadius: 10,
                            border: '1px solid #e6e8ec',
                            padding: 10,
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                            {entry.label}
                          </div>
                          <div style={{ fontWeight: 700 }}>{entry.value}</div>
                        </div>
                      </AMGrid.Item>
                    ))}
                  </AMGrid>
                </div>
              ) : null}
            </AMSpace>
          </AMCard.Body>
        </AMCard>
      );
    }

    return (
      <AntCard
        title={
          <AntSpace align="center">
            <TrendingUpIcon color="primary" />
            <span style={{ fontWeight: 700 }}>My Weekly KPIs</span>
          </AntSpace>
        }
        extra={agentKpiLoading && !agentKpi ? <AntSpin size="small" /> : null}
        style={{ marginBottom: 24 }}
      >
        <AntTypography.Text type="secondary">
          Tracking week of {weekWindow.label}
        </AntTypography.Text>
        {agentKpiError ? (
          <AntAlert type="warning" message={agentKpiError} showIcon style={{ marginTop: 12 }} />
        ) : null}
        {agentKpi ? (
          <AntRow gutter={[16, 16]} style={{ marginTop: 16 }}>
            {metricCards.map((metric) => (
              <AntCol xs={24} sm={12} md={6} key={metric.label}>
                <AntCard size="small" style={{ borderRadius: 12 }}>
                  <AntSpace direction="vertical">
                    <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                      {metric.label}
                    </AntTypography.Text>
                    <AntTypography.Title level={3} style={{ margin: 0 }}>
                      {formatMetricValue(metric)}
                    </AntTypography.Title>
                    <AntTypography.Text type="secondary">{metric.caption}</AntTypography.Text>
                  </AntSpace>
                </AntCard>
              </AntCol>
            ))}
          </AntRow>
        ) : null}
        {showPlaceholder ? (
          <AntTypography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            KPIs will appear here once your Medicare-week production posts.
          </AntTypography.Text>
        ) : null}
        {hasAncillary ? (
          <div style={{ marginTop: 16 }}>
            <AntTypography.Text strong>Ancillary Product Counts</AntTypography.Text>
            <AntRow gutter={[12, 12]} style={{ marginTop: 8 }}>
              {ancillaryEntries.map((entry) => (
                <AntCol xs={12} sm={8} md={6} key={`agent-anc-${entry.key}`}>
                  <AntCard size="small" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#667085', textTransform: 'uppercase' }}>
                      {entry.label}
                    </div>
                    <div style={{ fontWeight: 700 }}>{entry.value}</div>
                  </AntCard>
                </AntCol>
              ))}
            </AntRow>
          </div>
        ) : null}
      </AntCard>
    );
  };

  const TeamKpiPanel = () => {
    if (!isManagerView) return null;
    const hasStats = Boolean(teamAggregateStats);
    const aggregateSource = isSeniorManager ? seniorTeamKpis : teamManagerKpis;
    const ancillaryTotals = teamAggregateStats?.ancillaryTotals || {};
    const totalAgents =
      (teamAggregateStats && Number.isFinite(teamAggregateStats.agentCount)
        ? teamAggregateStats.agentCount
        : isSeniorManager
          ? seniorManagerGroups.reduce((sum, group) => sum + group.rows.length, 0)
          : managerTeamMembers.length) || 0;
    const baseMetricCards = hasStats
      ? [
          {
            label: 'Team Sales',
            value: teamAggregateStats.totalSales,
            caption: 'Sum of all agent sales in this range.',
            accent: 'success.main',
          },
          {
            label: 'Team IPC',
            value: teamAggregateStats.totalIpc,
            caption: 'Sum of all agent IPC sales in this range.',
            accent: 'info.main',
          },
          {
            label: 'Call Volume',
            value: teamAggregateStats.totalCalls,
            caption: 'Sum of all agent call counts for calls greater than 5 seconds in the range.',
            accent: 'warning.main',
          },
          {
            label: 'Avg Conversion',
            value: teamAggregateStats.conversion,
            caption: 'Team sales ÷ team raw calls × 100.',
            accent: 'secondary.main',
            formatter: (val) => `${val.toFixed(1)}%`,
          },
          {
            label: 'Avg Compliance',
            value: teamAggregateStats.complianceAverage,
            caption: 'Average of available compliance scores across team agents.',
            accent: 'secondary.light',
            formatter: (val) => `${val.toFixed(1)}`,
            nullDisplay: '—',
          },
          {
            label: 'Scheduled Meetings',
            value: teamAggregateStats.scheduledMeetings,
            caption: 'Sum of scheduled meetings across team.',
            accent: 'primary.main',
          },
          {
            label: 'Missed Meetings',
            value: teamAggregateStats.missedMeetings,
            caption: 'Sum of missed meetings across team.',
            accent: 'error.main',
          },
        ]
      : [];
    const ancillaryMetricCards = hasStats
      ? buildAncillaryMetricCards(ancillaryTotals).map((metric) => ({
          ...metric,
          caption:
            metric.key === 'total'
              ? 'Sum of all ancillary product counts across team.'
              : `Sum of ${metric.label} sales across team.`,
        }))
      : [];
    const teamMetricCards = [...baseMetricCards, ...ancillaryMetricCards];
    const formatMetricValue = (metric) => {
      const numeric = Number(metric.value);
      const safeValue = Number.isFinite(numeric) ? numeric : 0;
      return typeof metric.formatter === 'function' ? metric.formatter(safeValue) : safeValue;
    };

    if (isMobile) {
      return (
        <AMCard style={{ marginBottom: 16 }}>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <AMSpace align="center" style={{ fontWeight: 700 }}>
                <GroupIcon fontSize="small" />
                <span>Team Overall KPIs</span>
              </AMSpace>
              <div style={{ color: '#667085', fontSize: 13 }}>
                {totalAgents > 0 ? `${totalAgents} agents · ${weekWindow.label}` : `Week of ${weekWindow.label}`}
              </div>
              {aggregateSource.error ? <AMTag color="warning">{aggregateSource.error}</AMTag> : null}
              {shouldFetchComplianceScores && complianceScoreState.error ? (
                <AMTag color="warning">Unable to load compliance averages: {complianceScoreState.error}</AMTag>
              ) : null}
              {hasStats ? (
                <AMGrid columns={2} gap={10}>
                  {teamMetricCards.map((metric) => (
                    <AMGrid.Item key={metric.label} style={{ display: 'flex' }}>
                      <div
                        style={{
                          borderRadius: 12,
                          border: '1px solid #e6e8ec',
                          padding: 12,
                          background: '#fff',
                          height: '100%',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ fontSize: 11, color: '#667085', textTransform: 'uppercase' }}>
                          {metric.label}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, marginTop: 6 }}>
                          {formatMetricValue(metric)}
                        </div>
                        <div style={{ fontSize: 12, color: '#667085' }}>{metric.caption}</div>
                      </div>
                    </AMGrid.Item>
                  ))}
                </AMGrid>
              ) : (
                <div style={{ color: '#667085' }}>
                  {aggregateSource.loading
                    ? 'Crunching the latest team totals…'
                    : 'No team KPI data is available for this range yet.'}
                </div>
              )}
            </AMSpace>
          </AMCard.Body>
        </AMCard>
      );
    }

    return (
      <AntCard
        title={
          <AntSpace align="center">
            <GroupIcon color="primary" />
            <span style={{ fontWeight: 700 }}>Team Overall KPIs</span>
          </AntSpace>
        }
        extra={aggregateSource.loading ? <AntSpin size="small" /> : null}
        style={{ marginBottom: 24 }}
      >
        <AntTypography.Text type="secondary">
          {totalAgents > 0 ? `${totalAgents} agents · ${weekWindow.label}` : `Week of ${weekWindow.label}`}
        </AntTypography.Text>
        {aggregateSource.error ? (
          <AntAlert type="warning" message={aggregateSource.error} showIcon style={{ marginTop: 12 }} />
        ) : null}
        {shouldFetchComplianceScores && complianceScoreState.error ? (
          <AntAlert
            type="warning"
            message={`Unable to load compliance averages: ${complianceScoreState.error}`}
            showIcon
            style={{ marginTop: 12 }}
          />
        ) : null}
        {hasStats ? (
          <AntRow gutter={[16, 16]} style={{ marginTop: 16 }}>
            {teamMetricCards.map((metric) => (
              <AntCol xs={24} sm={12} md={8} key={metric.label} style={{ display: 'flex' }}>
                <AntCard
                  size="small"
                  style={{ borderRadius: 12, width: '100%', height: '100%' }}
                  styles={{ body: { display: 'flex', flexDirection: 'column', gap: 6, height: '100%' } }}
                >
                  <AntSpace direction="vertical" style={{ height: '100%' }}>
                    <AntTypography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12 }}>
                      {metric.label}
                    </AntTypography.Text>
                    <AntTypography.Title level={4} style={{ margin: 0 }}>
                      {formatMetricValue(metric)}
                    </AntTypography.Title>
                    <AntTypography.Text type="secondary">{metric.caption}</AntTypography.Text>
                  </AntSpace>
                </AntCard>
              </AntCol>
            ))}
          </AntRow>
        ) : (
          <AntTypography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            {aggregateSource.loading
              ? 'Crunching the latest team totals…'
              : 'No team KPI data is available for this range yet.'}
          </AntTypography.Text>
        )}
      </AntCard>
    );
  };

  const RosterUpdatePanel = () => {
    if (!isManagerView || !sabalOrgPresent || !showRosterPanel) return null;
    const saving = rosterEditorStatus.saving;
    const hasRows = rosterEditorRows.length > 0;
    const scopeLabel = isSeniorManager ? 'Senior Manager' : 'Team Lead';
    const managerName = toTrimmedString(rosterEntry?.agent_name || resolvedAgentName);
    const seniorName = toTrimmedString(
      rosterEntry?.senior_manager_name ||
        (isSeniorManager ? managerName : '') ||
        resolvedAgentName
    );
    const orgName =
      toTrimmedString(rosterEntry?.organization_name) || 'sabal';

    return (
      <Card
        sx={{
          mb: 3,
          borderLeft: '6px solid',
          borderLeftColor: 'secondary.main',
          background: 'linear-gradient(135deg, rgba(82,67,194,0.06), rgba(0,184,217,0.06))',
        }}
      >
        <CardHeader
          avatar={<UploadFileIcon color="secondary" />}
          title={<Typography variant="h6" sx={{ fontWeight: 800 }}>Roster Updates</Typography>}
          subheader={
            <Typography variant="body2" color="text.secondary">
              {scopeLabel} access: refresh your team roster and sync changes to broker-resources.
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRosterReset}
                disabled={saving}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleRosterSave}
                disabled={saving || !hasRows}
              >
                {saving ? 'Saving…' : 'Save roster'}
              </Button>
            </Stack>
          }
        />
        <CardContent>
          <Stack spacing={2}>
            <Alert severity="info" icon={<GroupIcon />}>
              Add new agents, tweak team names, or correct senior/manager labels. Saving will replace the active Sabal
              roster validation set.
            </Alert>
            {rosterEditorStatus.error ? (
              <Alert severity="error">{rosterEditorStatus.error}</Alert>
            ) : null}
            {rosterEditorStatus.message ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                {rosterEditorStatus.message}
              </Alert>
            ) : null}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'linear-gradient(120deg, rgba(0,0,0,0.02), rgba(0,0,0,0.04))',
              }}
            >
              <Grid container spacing={1.5} alignItems="flex-end">
                <Grid item xs={12} sm={5} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Agent name"
                    value={rosterDraftRow.agent_name}
                    onChange={(event) =>
                      setRosterDraftRow((prev) => ({ ...prev, agent_name: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Team</InputLabel>
                    <Select
                      label="Team"
                      value={rosterDraftRow.team_name || ''}
                      onChange={(event) =>
                        setRosterDraftRow((prev) => ({ ...prev, team_name: event.target.value }))
                      }
                      disabled={!availableTeams.length}
                    >
                      {availableTeams.map((team) => (
                        <MenuItem key={team} value={team}>
                          {formatTeamDisplayName(team) || team}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Senior manager"
                    value={seniorName}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Org"
                    value={orgName}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md="auto">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddDraftRow}
                    disabled={saving}
                    sx={{ mt: { xs: 1, md: 0 } }}
                  >
                    Add row
                  </Button>
                </Grid>
              </Grid>
            </Paper>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 360, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Senior manager</TableCell>
                    <TableCell>Org</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasRows ? (
                    rosterEditorRows.map((row, idx) => (
                      <TableRow key={`${row.agent_name || 'agent'}-${idx}`}>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Typography variant="body2">{row.agent_name}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Typography variant="body2">
                            {formatTeamDisplayName(row.team_name) || row.team_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Typography variant="body2">{row.senior_manager_name}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          <Typography variant="body2">{row.organization_name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveRosterRow(idx)}
                            disabled={saving}
                            aria-label="Remove row"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No roster rows loaded. Use the form above to add your first agent.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // Removed global KPI overlay; we'll overlay only inside the KPI panel

  // 1. Handle Loading State
  if (loading || authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <AntSpin size="large" />
      </div>
    );
  }

  // 2. Handle API Error State (data is null or undefined)
  if (!data) {
    if (isMobile) {
      return (
        <AMCard>
          <AMCard.Body>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Dashboard</div>
            <div style={{ color: '#d92d20' }}>Could not load dashboard data. Please try again later.</div>
          </AMCard.Body>
        </AMCard>
      );
    }

    return (
      <AntCard title="Dashboard">
        <AntAlert
          type="error"
          message="Could not load dashboard data. Please try again later."
          showIcon
        />
      </AntCard>
    );
  }

  // Helper to format the date string
  const formattedDate = data.processedAt
    ? new Date(data.processedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : 'N/A';

  // 3. Handle "No Data" State (API returned a valid "NO_DATA" status)
  if (data.status === 'NO_DATA') {
    return (
      <div>
        {!isManagerView && !canViewExecutiveDashboard && (
          isMobile ? (
            <AMCard style={{ marginBottom: 16 }}>
              <AMCard.Body>
                <div style={{ fontSize: 20, fontWeight: 700 }}>Welcome back!</div>
                <div style={{ color: '#667085' }}>Check back in a bit for your realtime call insights.</div>
              </AMCard.Body>
            </AMCard>
          ) : (
            <div style={{ borderBottom: '1px solid #e6e8ec', paddingBottom: 16, marginBottom: 24 }}>
              <AntTypography.Title level={2} style={{ margin: 0 }}>
                Welcome back!
              </AntTypography.Title>
              <AntTypography.Text type="secondary">
                Check back in a bit for your realtime call insights.
              </AntTypography.Text>
            </div>
          )
        )}
        {!canViewExecutiveDashboard && renderMetaControlsRow({ mb: 3 })}
        {!isManagerView && <AgentMeetingBanner />}
        {canViewExecutiveDashboard ? (
          renderExecutiveManagerDeck()
        ) : isSeniorManager ? (
          <>
            <TeamKpiPanel />
            {renderSeniorManagerKpis()}
            <RosterUpdatePanel />
          </>
        ) : isTeamManager ? (
          <>
            <TeamKpiPanel />
            {renderManagerTeamKpis()}
            <RosterUpdatePanel />
          </>
        ) : (
          <>
            <AgentKpiPanel />
            <LevelPanel />
          </>
        )}

        {/* --- START: STYLIZED FRIENDLY MESSAGE --- */}
        {!isManagerView && !canViewExecutiveDashboard && (
          isMobile ? (
            <AMCard style={{ marginTop: 16 }}>
              <AMCard.Body>
                <AMSpace direction="vertical" block style={{ '--gap': '8px', textAlign: 'center' }}>
                  <WbSunnyIcon fontSize="large" />
                  <div style={{ fontWeight: 700 }}>Looks like a fresh start!</div>
                  <div style={{ color: '#667085' }}>
                    {data.message ||
                      "I'm all caught up and waiting for new calls to review. Check back here later for personalized coaching and insights to help you shine!"}
                  </div>
                  <div style={{ color: '#667085', fontSize: 12 }}>Last checked on {formattedDate}</div>
                </AMSpace>
              </AMCard.Body>
            </AMCard>
          ) : (
            <AntCard style={{ marginTop: 24, textAlign: 'center' }}>
              <AntSpace direction="vertical" style={{ width: '100%' }}>
                <WbSunnyIcon style={{ fontSize: 48 }} />
                <AntTypography.Title level={4}>Looks like a fresh start!</AntTypography.Title>
                <AntTypography.Text type="secondary">
                  {data.message ||
                    "I'm all caught up and waiting for new calls to review. Check back here later for personalized coaching and insights to help you shine!"}
                </AntTypography.Text>
                <AntTypography.Text type="secondary">Last checked on {formattedDate}</AntTypography.Text>
              </AntSpace>
            </AntCard>
          )
        )}
        {/* --- END: STYLIZED FRIENDLY MESSAGE --- */}
      </div>
    );
  }

  // 4. Handle Success State (Display the coaching feedback)
  const { agentName, coachingFeedback } = data;

  // --- New Logic to Parse Summary ---
  const summary = coachingFeedback?.summary || '';
  let isSummaryList = false;
  let summaryIntro = summary;
  let summaryItems = [];

  // Regex to find the start of a numbered list (e.g., "1. ")
  const listStartIndex = summary.search(/\s\d\.\s/);

  if (listStartIndex !== -1) {
    isSummaryList = true;
    summaryIntro = summary.substring(0, listStartIndex).trim();
    const listContent = summary.substring(listStartIndex).trim();
    summaryItems = listContent.split(/\s*\d\.\s/).filter((item) => item.trim());
  }
  // --- End of New Logic ---

  return (
    <div>
      {!canViewExecutiveDashboard && (
        <>
          {isMobile ? (
            <AMCard style={{ marginBottom: 16 }}>
              <AMCard.Body>
                <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    Welcome Back, {agentName || resolvedAgentName || 'Agent'}
                  </div>
                  <div style={{ color: '#667085' }}>
                    I finished checking out your calls on: {formattedDate}.
                  </div>
                  {renderMetaControlsRow({ width: '100%' })}
                  {!isManagerView && <AgentMeetingBanner />}
                </AMSpace>
              </AMCard.Body>
            </AMCard>
          ) : (
            <div style={{ marginBottom: 24 }}>
              <AntTypography.Title level={3} style={{ marginBottom: 4 }}>
                Welcome Back, {agentName || resolvedAgentName || 'Agent'}
              </AntTypography.Title>
              <AntTypography.Text type="secondary">
                I finished checking out your calls on: {formattedDate}.
              </AntTypography.Text>
              <div style={{ marginTop: 12 }}>
                {renderMetaControlsRow({ width: '100%' })}
              </div>
              {!isManagerView && <AgentMeetingBanner />}
            </div>
          )}
        </>
      )}
      {canViewExecutiveDashboard ? (
        renderExecutiveManagerDeck()
      ) : isSeniorManager ? (
        <>
          <TeamKpiPanel />
          {renderSeniorManagerKpis()}
          <RosterUpdatePanel />
        </>
      ) : isTeamManager ? (
        <>
          <TeamKpiPanel />
          {renderManagerTeamKpis()}
          <RosterUpdatePanel />
        </>
      ) : (
        <>
          <AgentKpiPanel />
          <LevelPanel />
          {isMobile ? (
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Conversation Insights</div>
              <AMCard>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                    <AMSpace align="center" style={{ fontWeight: 700 }}>
                      <AnnouncementIcon fontSize="small" />
                      <span>Summary</span>
                    </AMSpace>
                    {isSummaryList ? (
                      <>
                        {summaryIntro ? <div>{renderBoldText(summaryIntro)}</div> : null}
                        <AMList>
                          {summaryItems.map((item, index) => (
                            <AMList.Item key={`sum-${index}`}>{renderBulletText(item)}</AMList.Item>
                          ))}
                        </AMList>
                      </>
                    ) : (
                      <div>{renderBoldText(summary)}</div>
                    )}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
              <AMCard>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                    <AMSpace align="center" style={{ fontWeight: 700 }}>
                      <WhatshotIcon fontSize="small" />
                      <span>Recommendations</span>
                    </AMSpace>
                    {Array.isArray(coachingFeedback?.recommendations) && coachingFeedback.recommendations.length > 0 ? (
                      <AMList>
                        {coachingFeedback.recommendations.map((rec, index) => (
                          <AMList.Item key={`rec-${index}`}>{renderBulletText(rec)}</AMList.Item>
                        ))}
                      </AMList>
                    ) : (
                      <div style={{ color: '#667085' }}>No specific recommendations were provided in this review.</div>
                    )}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
              <AMCard>
                <AMCard.Body>
                  <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                    <AMSpace align="center" style={{ fontWeight: 700 }}>
                      <CheckCircleIcon fontSize="small" />
                      <span>Strengths</span>
                    </AMSpace>
                    {Array.isArray(coachingFeedback?.strengths) && coachingFeedback.strengths.length > 0 ? (
                      <AMList>
                        {coachingFeedback.strengths.map((strength, index) => (
                          <AMList.Item key={`str-${index}`}>{renderBulletText(strength)}</AMList.Item>
                        ))}
                      </AMList>
                    ) : (
                      <div style={{ color: '#667085' }}>No specific strengths were highlighted in this review.</div>
                    )}
                  </AMSpace>
                </AMCard.Body>
              </AMCard>
            </AMSpace>
          ) : (
            <>
              <AntTypography.Title level={4} style={{ marginTop: 32 }}>
                Conversation Insights
              </AntTypography.Title>
              <AntRow gutter={[16, 16]}>
                <AntCol span={24}>
                  <AntCard
                    title={
                      <AntSpace align="center">
                        <AnnouncementIcon color="primary" />
                        <span style={{ fontWeight: 700 }}>Summary</span>
                      </AntSpace>
                    }
                  >
                    {isSummaryList ? (
                      <>
                        {summaryIntro ? (
                          <AntTypography.Paragraph>{renderBoldText(summaryIntro)}</AntTypography.Paragraph>
                        ) : null}
                        <AntList>
                          {summaryItems.map((item, index) => (
                            <AntList.Item key={`sum-${index}`}>{renderBulletText(item)}</AntList.Item>
                          ))}
                        </AntList>
                      </>
                    ) : (
                      <AntTypography.Paragraph>{renderBoldText(summary)}</AntTypography.Paragraph>
                    )}
                  </AntCard>
                </AntCol>
                <AntCol span={24}>
                  <AntCard
                    title={
                      <AntSpace align="center">
                        <WhatshotIcon color="error" />
                        <span style={{ fontWeight: 700 }}>Recommendations</span>
                      </AntSpace>
                    }
                  >
                    {Array.isArray(coachingFeedback?.recommendations) && coachingFeedback.recommendations.length > 0 ? (
                      <AntList>
                        {coachingFeedback.recommendations.map((rec, index) => (
                          <AntList.Item key={`rec-${index}`}>{renderBulletText(rec)}</AntList.Item>
                        ))}
                      </AntList>
                    ) : (
                      <AntTypography.Text type="secondary">
                        No specific recommendations were provided in this review.
                      </AntTypography.Text>
                    )}
                  </AntCard>
                </AntCol>
                <AntCol span={24}>
                  <AntCard
                    title={
                      <AntSpace align="center">
                        <CheckCircleIcon color="success" />
                        <span style={{ fontWeight: 700 }}>Strengths</span>
                      </AntSpace>
                    }
                  >
                    {Array.isArray(coachingFeedback?.strengths) && coachingFeedback.strengths.length > 0 ? (
                      <AntList>
                        {coachingFeedback.strengths.map((strength, index) => (
                          <AntList.Item key={`str-${index}`}>{renderBulletText(strength)}</AntList.Item>
                        ))}
                      </AntList>
                    ) : (
                      <AntTypography.Text type="secondary">
                        No specific strengths were highlighted in this review.
                      </AntTypography.Text>
                    )}
                  </AntCard>
                </AntCol>
              </AntRow>
            </>
          )}
        </>
      )}
    </div>
  );
};


const CarrierIcon = ({ letter, bgColor, color = '#fff' }) => (
  <Box sx={{
    width: 24,
    height: 24,
    backgroundColor: 'white',
    clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <Box sx={{
      width: 22,
      height: 22,
      backgroundColor: bgColor,
      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Typography sx={{ color: color, fontWeight: 'bold', fontSize: '14px', lineHeight: 1 }}>
        {letter}
      </Typography>
    </Box>
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const FemaSepTool = () => {
  const { getAdminReportButtons } = useAuth();
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('N/A');
  const [filters, setFilters] = useState({ carrier: 'all', state: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [uniqueCarriers, setUniqueCarriers] = useState([]);
  const [statesForCarrier, setStatesForCarrier] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('carrier');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRows, setNewRows] = useState([{ carrier: '', state: '', location: '', disaster: '', start_date: '', end_date: '' }]);

  const canEdit = useMemo(() => {
    const perms = getAdminReportButtons;
    return perms === 'all' || (Array.isArray(perms) && perms.includes('fema-list-editor'));
  }, [getAdminReportButtons]);

  const fetchFemaData = async () => {
    if (!loading) setLoading(true);
    try {
      const response = await api.post('/fema-data-query', {});
      setLastUpdated(response.creationDate || 'N/A');
      const allRows = [];
      if (response.data) {
        for (const carrier in response.data) {
          response.data[carrier].forEach(item => allRows.push({ ...item, carrier }));
        }
      }
      setData(allRows);
      const carrierSet = new Set(allRows.map(row => row.carrier));
      setUniqueCarriers(Array.from(carrierSet).sort());
    } catch (error) {
      console.error('Error fetching FEMA data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFemaData();
  }, []);

  useEffect(() => {
    let result = data;
    if (filters.carrier && filters.carrier !== 'all') {
      result = result.filter(row => row.carrier === filters.carrier);
    }
    if (filters.state) {
      result = result.filter(row => row.state === filters.state);
    }
    if (searchTerm) {
        const lowercasedTerm = searchTerm.toLowerCase();
        result = result.filter(row => 
            Object.values(row).some(value => 
                String(value).toLowerCase().includes(lowercasedTerm)
            )
        );
    }
    setFilteredData(result);
  }, [data, filters, searchTerm]);

  useEffect(() => {
    if (filters.carrier && filters.carrier !== 'all') {
      const states = new Set(data.filter(row => row.carrier === filters.carrier).map(row => row.state));
      setStatesForCarrier(Array.from(states).sort());
    } else {
      setStatesForCarrier([]);
    }
  }, [filters.carrier, data]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value, ...(name === 'carrier' && { state: '' }) }));
  };
  
  const handleSearchChange = (eventOrValue) => {
    const nextValue = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    setSearchTerm(nextValue);
  };
  const handleDelete = async (rowToDelete) => {
    if (window.confirm("Are you sure you want to delete this entry? This action cannot be undone.")) {
      setLoading(true);
      try {
        await api.femaDataDelete(rowToDelete);
        await fetchFemaData(); // Refetch data to update the table
      } catch (error) {
        console.error('Failed to delete row:', error);
        alert(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewRows([{ carrier: '', state: '', location: '', disaster: '', start_date: '', end_date: '' }]);
  };

  const handleAddNewRow = () => setNewRows([...newRows, { carrier: '', state: '', location: '', disaster: '', start_date: '', end_date: '' }]);
  const handleRemoveNewRow = (index) => setNewRows(newRows.filter((_, i) => i !== index));

  const handleNewRowChange = (index, name, value) => {
    const updatedRows = [...newRows];
    updatedRows[index][name] = value;
    setNewRows(updatedRows);
  };

  const handleSubmitNewRows = async () => {
    const formatMDY = (dateStr) => {
      if (!dateStr.includes('-')) return dateStr;
      const [yyyy, mm, dd] = dateStr.split('-');
      return `${mm}/${dd}/${yyyy}`;
    };
    
    const rowsToInsert = newRows.map(row => ({
      ...row,
      start_date: formatMDY(row.start_date),
      end_date: formatMDY(row.end_date),
      active: true
    }));

    setLoading(true);
    handleCloseModal();
    try {
        await api.femaDataInsert(rowsToInsert);
        await fetchFemaData();
    } catch(error) {
        console.error('Failed to insert new rows:', error);
        alert(`Error: ${error.message}`);
        setLoading(false);
    }
  };
  
  const getCarrierTagColor = (carrier) => {
    switch ((carrier || '').toLowerCase()) {
      case 'aetna':
        return '#9c27b0';
      case 'humana':
        return '#4caf50';
      case 'uhc':
        return '#3f51b5';
      case 'centene':
        return '#009688';
      case 'devoted':
        return '#ff9800';
      case 'cigna':
        return '#1e1647';
      case 'elevance':
        return '#f44336';
      case 'scan':
        return '#ffeb3b';
      default:
        return '#6b7280';
    }
  };

  const carrierOptions = useMemo(
    () => uniqueCarriers.map((carrier) => ({ label: carrier, value: carrier })),
    [uniqueCarriers]
  );
  const stateOptions = useMemo(
    () => statesForCarrier.map((stateCode) => ({ label: stateCode, value: stateCode })),
    [statesForCarrier]
  );
  const sortedRows = useMemo(
    () => stableSort(filteredData, getComparator(order, orderBy)),
    [filteredData, order, orderBy]
  );

  const tableColumns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Carrier',
        dataIndex: 'carrier',
        key: 'carrier',
        sorter: true,
        sortOrder: orderBy === 'carrier' ? (order === 'asc' ? 'ascend' : 'descend') : null,
        render: (value) => (
          <AntTag color={getCarrierTagColor(value)} style={{ color: value?.toLowerCase() === 'scan' ? '#333' : '#fff' }}>
            {value}
          </AntTag>
        ),
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        sorter: true,
        sortOrder: orderBy === 'state' ? (order === 'asc' ? 'ascend' : 'descend') : null,
      },
      {
        title: 'Disaster',
        dataIndex: 'disaster',
        key: 'disaster',
        sorter: true,
        sortOrder: orderBy === 'disaster' ? (order === 'asc' ? 'ascend' : 'descend') : null,
      },
      {
        title: 'Location',
        dataIndex: 'location',
        key: 'location',
        sorter: true,
        sortOrder: orderBy === 'location' ? (order === 'asc' ? 'ascend' : 'descend') : null,
      },
      {
        title: 'Start Date',
        dataIndex: 'start_date',
        key: 'start_date',
        sorter: true,
        sortOrder: orderBy === 'start_date' ? (order === 'asc' ? 'ascend' : 'descend') : null,
      },
      {
        title: 'End Date',
        dataIndex: 'end_date',
        key: 'end_date',
        sorter: true,
        sortOrder: orderBy === 'end_date' ? (order === 'asc' ? 'ascend' : 'descend') : null,
      },
    ];

    if (!canEdit) return baseColumns;

    return [
      ...baseColumns,
      {
        title: 'Action',
        key: 'action',
        render: (_, row) => (
          <AntButton danger size="small" onClick={() => handleDelete(row)}>
            Delete
          </AntButton>
        ),
      },
    ];
  }, [canEdit, handleDelete, order, orderBy, getCarrierTagColor]);

  const handleTableChange = (_pagination, _filters, sorter) => {
    if (!sorter || Array.isArray(sorter)) return;
    if (!sorter.field) return;
    const nextOrder = sorter.order === 'descend' ? 'desc' : 'asc';
    setOrderBy(sorter.field);
    setOrder(nextOrder);
  };
  
  return (
    <>
      {isMobile ? (
        <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
          <AMCard>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                <AMSpace align="center">
                  <SearchIcon fontSize="small" />
                  <span style={{ fontWeight: 700, fontSize: 18 }}>FEMA SEP Search</span>
                </AMSpace>
                <AMTag color="primary">Last Updated: {lastUpdated}</AMTag>
              </AMSpace>
            </AMCard.Body>
          </AMCard>

          <AMCard>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '10px' }}>
                <div style={{ fontWeight: 600 }}>Carrier</div>
                <AMSelector
                  options={[{ label: 'All Carriers', value: 'all' }, ...carrierOptions]}
                  value={[filters.carrier]}
                  onChange={(values) => handleFilterChange('carrier', values[0] || 'all')}
                />
                <div style={{ fontWeight: 600 }}>State</div>
                <AMSelector
                  options={[{ label: 'All States', value: '' }, ...stateOptions]}
                  value={[filters.state]}
                  onChange={(values) => handleFilterChange('state', values[0] || '')}
                  disabled={filters.carrier === 'all'}
                />
                <div style={{ fontWeight: 600 }}>Search All Fields</div>
                <AMInput
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search all fields"
                  clearable
                />
                {canEdit ? (
                  <AMButton block color="primary" onClick={handleOpenModal}>
                    Add New
                  </AMButton>
                ) : null}
              </AMSpace>
            </AMCard.Body>
          </AMCard>

          <AMCard>
            <AMCard.Body>
              {loading ? (
                <AMSpace align="center" style={{ justifyContent: 'center', width: '100%' }}>
                  <AMSpin />
                  <span>Loading FEMA data…</span>
                </AMSpace>
              ) : (
                <AMList mode="card">
                  {sortedRows.map((row, index) => (
                    <AMList.Item
                      key={`${row.carrier}-${row.state}-${index}`}
                      description={(
                        <div style={{ fontSize: 12, color: '#667085' }}>
                          <div><strong>Disaster:</strong> {row.disaster}</div>
                          <div><strong>Location:</strong> {row.location}</div>
                          <div><strong>Start:</strong> {row.start_date}</div>
                          <div><strong>End:</strong> {row.end_date}</div>
                          {canEdit ? (
                            <AMButton
                              size="mini"
                              color="danger"
                              style={{ marginTop: 8 }}
                              onClick={() => handleDelete(row)}
                            >
                              Delete
                            </AMButton>
                          ) : null}
                        </div>
                      )}
                    >
                      <AMSpace align="center">
                        <AMTag color="primary" style={{ background: getCarrierTagColor(row.carrier) }}>
                          {row.carrier}
                        </AMTag>
                        <span>{row.state}</span>
                      </AMSpace>
                    </AMList.Item>
                  ))}
                </AMList>
              )}
            </AMCard.Body>
          </AMCard>
        </AMSpace>
      ) : (
        <AntCard
          title={(
            <AntSpace align="center">
              <SearchIcon />
              <span>FEMA SEP Search</span>
            </AntSpace>
          )}
          extra={<AntTag color="blue">Last Updated: {lastUpdated}</AntTag>}
        >
          <AntCard size="small" style={{ marginBottom: 16 }}>
            <AntRow gutter={[12, 12]} align="middle">
              <AntCol xs={24} sm={6} md={5}>
                <AntSelect
                  value={filters.carrier}
                  onChange={(value) => handleFilterChange('carrier', value)}
                  options={[{ label: 'All Carriers', value: 'all' }, ...carrierOptions]}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </AntCol>
              <AntCol xs={24} sm={6} md={5}>
                <AntSelect
                  value={filters.state}
                  onChange={(value) => handleFilterChange('state', value)}
                  options={[{ label: 'All States', value: '' }, ...stateOptions]}
                  style={{ width: '100%' }}
                  disabled={filters.carrier === 'all'}
                  showSearch
                  optionFilterProp="label"
                />
              </AntCol>
              <AntCol xs={24} sm={8} md={canEdit ? 8 : 12}>
                <AntInput
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search all fields"
                  prefix={<SearchIcon fontSize="small" />}
                />
              </AntCol>
              {canEdit ? (
                <AntCol xs={24} sm={4} md={4}>
                  <AntButton type="primary" block onClick={handleOpenModal}>
                    Add New
                  </AntButton>
                </AntCol>
              ) : null}
            </AntRow>
          </AntCard>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <AntSpin />
            </div>
          ) : (
            <AntTable
              columns={tableColumns}
              dataSource={sortedRows.map((row, index) => ({ ...row, key: `${row.carrier}-${row.state}-${index}` }))}
              onChange={handleTableChange}
              pagination={{
                current: page + 1,
                pageSize: rowsPerPage,
                total: sortedRows.length,
                onChange: (nextPage, nextPageSize) => {
                  setPage(nextPage - 1);
                  setRowsPerPage(nextPageSize);
                },
              }}
              scroll={{ x: true }}
            />
          )}
        </AntCard>
      )}

      {isMobile ? (
        <AMPopup visible={isModalOpen} onMaskClick={handleCloseModal} bodyStyle={{ padding: 0 }}>
          <AMSafeArea position="top" />
          <div style={{ padding: '12px 16px', fontWeight: 700 }}>Add New FEMA SEP Entries</div>
          <div style={{ padding: '0 16px 16px' }}>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              {newRows.map((row, index) => (
                <AMCard key={`new-row-${index}`}>
                  <AMCard.Body>
                    <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                      <div style={{ fontWeight: 600 }}>Carrier</div>
                      <AMSelector
                        options={carrierOptions}
                        value={[row.carrier]}
                        onChange={(values) => handleNewRowChange(index, 'carrier', values[0] || '')}
                      />
                      <div style={{ fontWeight: 600 }}>State</div>
                      <AMInput
                        value={row.state}
                        onChange={(value) => handleNewRowChange(index, 'state', value)}
                        placeholder="State"
                      />
                      <div style={{ fontWeight: 600 }}>Location</div>
                      <AMInput
                        value={row.location}
                        onChange={(value) => handleNewRowChange(index, 'location', value)}
                        placeholder="Location"
                      />
                      <div style={{ fontWeight: 600 }}>Disaster</div>
                      <AMInput
                        value={row.disaster}
                        onChange={(value) => handleNewRowChange(index, 'disaster', value)}
                        placeholder="Disaster"
                      />
                      <div style={{ fontWeight: 600 }}>Start Date</div>
                      <AMInput
                        type="date"
                        value={row.start_date}
                        onChange={(value) => handleNewRowChange(index, 'start_date', value)}
                      />
                      <div style={{ fontWeight: 600 }}>End Date</div>
                      <AMInput
                        type="date"
                        value={row.end_date}
                        onChange={(value) => handleNewRowChange(index, 'end_date', value)}
                      />
                      {newRows.length > 1 ? (
                        <AMButton
                          size="small"
                          color="danger"
                          onClick={() => handleRemoveNewRow(index)}
                        >
                          Remove Row
                        </AMButton>
                      ) : null}
                    </AMSpace>
                  </AMCard.Body>
                </AMCard>
              ))}
              <AMButton block onClick={handleAddNewRow}>
                Add Another Row
              </AMButton>
              <AMSpace block>
                <AMButton block onClick={handleCloseModal}>
                  Cancel
                </AMButton>
                <AMButton block color="primary" onClick={handleSubmitNewRows}>
                  Submit
                </AMButton>
              </AMSpace>
            </AMSpace>
          </div>
          <AMSafeArea position="bottom" />
        </AMPopup>
      ) : (
        <AntModal
          open={isModalOpen}
          onCancel={handleCloseModal}
          onOk={handleSubmitNewRows}
          title="Add New FEMA SEP Entries"
          width={960}
          zIndex={2000}
        >
          <AntSpace direction="vertical" style={{ width: '100%' }} size="middle">
            {newRows.map((row, index) => (
              <AntRow gutter={[12, 12]} align="middle" key={`new-row-${index}`}>
                <AntCol xs={24} sm={4}>
                  <AntSelect
                    placeholder="Carrier"
                    value={row.carrier || undefined}
                    onChange={(value) => handleNewRowChange(index, 'carrier', value)}
                    options={carrierOptions}
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                  />
                </AntCol>
                <AntCol xs={12} sm={3}>
                  <AntInput
                    placeholder="State"
                    value={row.state}
                    onChange={(event) => handleNewRowChange(index, 'state', event.target.value)}
                  />
                </AntCol>
                <AntCol xs={12} sm={4}>
                  <AntInput
                    placeholder="Location"
                    value={row.location}
                    onChange={(event) => handleNewRowChange(index, 'location', event.target.value)}
                  />
                </AntCol>
                <AntCol xs={12} sm={4}>
                  <AntInput
                    placeholder="Disaster"
                    value={row.disaster}
                    onChange={(event) => handleNewRowChange(index, 'disaster', event.target.value)}
                  />
                </AntCol>
                <AntCol xs={12} sm={4}>
                  <AntInput
                    type="date"
                    value={row.start_date}
                    onChange={(event) => handleNewRowChange(index, 'start_date', event.target.value)}
                  />
                </AntCol>
                <AntCol xs={12} sm={4}>
                  <AntInput
                    type="date"
                    value={row.end_date}
                    onChange={(event) => handleNewRowChange(index, 'end_date', event.target.value)}
                  />
                </AntCol>
                <AntCol xs={12} sm={1}>
                  {newRows.length > 1 ? (
                    <AntButton danger size="small" onClick={() => handleRemoveNewRow(index)}>
                      Remove
                    </AntButton>
                  ) : null}
                </AntCol>
              </AntRow>
            ))}
            <AntButton onClick={handleAddNewRow}>Add Another Row</AntButton>
          </AntSpace>
        </AntModal>
      )}
    </>
  );
};

const statesList = ["AL", "AZ", "AR", "CA", "CO", "FL", "GA", "IL", "IN", "KY", "KS", "LA", "ME", "MD", "MI", "MS", "MO", "NV", "NJ", "NM", "NC", "OH", "OK", "PA", "SC", "TN", "TX", "VA", "WV", "WI"];
const policyMatchBenefitOptions = [
  { label: 'More Dental', value: 'More Dental' },
  { label: 'More Vision', value: 'More Vision' },
  { label: 'More Hearing', value: 'More Hearing' },
  { label: 'Over the Counter allowance/Card', value: 'Over the Counter allowance' },
  { label: 'Flex card', value: 'Flex Card' },
  { label: 'Giveback', value: 'giveback_amount' },
  { label: 'Lower Medical Costs', value: 'Decrease Copays or Coinsurance' },
  { label: 'Enhanced Prescription Drug Coverage', value: 'Enhanced Prescription Drug Coverage' },
];
const policyMatchConditionOptions = [
  { label: 'COPD', value: 'COPD' },
  { label: 'Asthma', value: 'Asthma' },
  { label: 'Heart Disease/Failure', value: 'Heart Disease/Failure' },
  { label: 'Diabetes', value: 'Diabetes' },
  { label: 'Other Chronic Condition', value: 'Other' },
  { label: 'Cancer', value: 'Cancer' },
  { label: 'Stroke', value: 'Stroke' },
  { label: 'Not any listed', value: 'Not any listed' },
];
const yesNoOptions = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

const PolicyMatchTool = () => {
  const { user } = useAuth();
  const { activeGradient } = useUI();
  const isMobile = useIsMobile();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-indexed: October is 9
  const isOctThroughDec = currentMonth >= 9;
  const currentPlanYearOptions = [currentYear, currentYear - 1, currentYear - 2].map(String);
  const newPlanYearOptions = [currentYear + 1, currentYear, currentYear - 1].map(String);
  const allowedPlanText = ['ORIGINAL', 'SUPPLEMENT'];
  // Form States
  const [currentPlanId, setCurrentPlanId] = useState('ORIGINAL');
  const [currentPlanYear, setCurrentPlanYear] = useState(currentPlanYearOptions[0]);
  const [newPlanYear, setNewPlanYear] = useState(isOctThroughDec ? newPlanYearOptions[0] : newPlanYearOptions[1]);
  const [state, setState] = useState('');
  const [county, setCounty] = useState('');
  const [countiesForState, setCountiesForState] = useState([]);
  const [countyLoading, setCountyLoading] = useState(false);
  const [dob, setDob] = useState('');
  const [benefits, setBenefits] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [medicaid, setMedicaid] = useState('No');
  const [veteranStatus, setVeteranStatus] = useState('No');
  const [needsRx, setNeedsRx] = useState('Yes');
  const [addDesires, setAddDesires] = useState('No'); // Defaulted to 'No'
  const [clientDesires, setClientDesires] = useState('');

  const planTypeSelection = allowedPlanText.includes(currentPlanId.trim().toUpperCase()) ? currentPlanId.trim().toUpperCase() : null;

  // UI Control States
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [selectionMessage, setSelectionMessage] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (state) {
      setCountyLoading(true);
      setCounty('');
      api.getCounties(state)
        .then(countyList => {
          const uniqueCounties = Array.from(new Set(countyList || []));
          setCountiesForState(uniqueCounties);
        })
        .catch(error => { console.error("Error fetching counties:", error); setCountiesForState([]); })
        .finally(() => setCountyLoading(false));
    } else {
      setCountiesForState([]);
      setCounty('');
    }
  }, [state]);

  useEffect(() => {
    if (benefits.includes("Decrease Copays or Coinsurance") && benefits.length > 1) {
      setBenefits(["Decrease Copays or Coinsurance"]);
    }
  }, [benefits]);

  useEffect(() => {
    if (medicaid === 'Yes') setNeedsRx('Yes');
  }, [medicaid]);

  useEffect(() => {
    if (selectedPlans.length === 0) {
      setComparisonOpen(false);
    }
  }, [selectedPlans]);

  const handlePlanTypeToggle = (value) => {
    if (value) {
      setCurrentPlanId(value);
    }
  };

  const handleBenefitChange = (event) => {
    const { value, checked } = event.target;
    if (checked) {
      if (value === "Decrease Copays or Coinsurance") setBenefits([value]);
      else if (benefits.length < 2) setBenefits([...benefits, value]);
    } else {
      setBenefits(benefits.filter((b) => b !== value));
    }
  };

  const handleConditionChange = (event) => {
    const { value, checked } = event.target;
    if (checked) {
      if (value === "Not any listed") setConditions(["Not any listed"]);
      else setConditions([...conditions.filter(c => c !== "Not any listed"), value]);
    } else {
      setConditions(conditions.filter((c) => c !== value));
    }
  };

  const handleBenefitSelect = (values) => {
    if (values.includes("Decrease Copays or Coinsurance")) {
      setBenefits(["Decrease Copays or Coinsurance"]);
      return;
    }
    const trimmed = values.filter((value) => value !== "Decrease Copays or Coinsurance");
    if (trimmed.length > 2) {
      setBenefits(trimmed.slice(0, 2));
    } else {
      setBenefits(trimmed);
    }
  };

  const handleConditionSelect = (values) => {
    if (values.includes("Not any listed")) {
      setConditions(["Not any listed"]);
      return;
    }
    setConditions(values.filter((value) => value !== "Not any listed"));
  };
  
  const handlePlanToggle = (plan, key, label) => {
    setSelectionMessage('');
    setSelectedPlans((prev) => {
      const exists = prev.some((p) => p.key === key);
      if (exists) {
        return prev.filter((p) => p.key !== key);
      }
      if (prev.length >= 4) {
        setSelectionMessage('You can compare up to 4 plans at a time.');
        return prev;
      }
      return [...prev, { key, plan, label }];
    });
  };

  const handleClearSelection = () => {
    setSelectedPlans([]);
    setSelectionMessage('');
    setComparisonOpen(false);
  };

  const currentPlanColumns = useMemo(() => {
    const currentPlans = results?.planResult?.current_plan_data;
    if (!Array.isArray(currentPlans)) return [];
    return currentPlans
      .map((plan, index) => {
        if (!plan || typeof plan !== 'object') return null;
        const hasMappedData = Array.isArray(plan?.benefits) && plan.benefits.some((benefit) => {
          if (!benefit || typeof benefit !== 'object') return false;
          return Boolean(benefit.benefit || benefit.value || benefit.explanation);
        });
        if (!hasMappedData) return null;
        return {
          key: `current-${index}`,
          plan,
          label: `Current Plan${currentPlans.length > 1 ? ` ${index + 1}` : ''}`,
          isCurrent: true
        };
      })
      .filter(Boolean);
  }, [results]);

  const comparisonPlans = useMemo(() => [...currentPlanColumns, ...selectedPlans], [currentPlanColumns, selectedPlans]);

  const benefitKeys = useMemo(() => {
    const keys = new Set();
    comparisonPlans.forEach(({ plan }) => {
      plan?.benefits?.forEach((benefit) => {
        if (benefit?.benefit) keys.add(benefit.benefit);
      });
    });
    return Array.from(keys);
  }, [comparisonPlans]);

  const convertHexToRgba = useCallback((hex, alpha = 1) => {
    if (!hex) return `rgba(44, 71, 161, ${alpha})`;
    const sanitized = hex.replace('#', '');
    if (sanitized.length !== 6) return `rgba(44, 71, 161, ${alpha})`;
    const bigint = parseInt(sanitized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }, []);

  const gradientPrimaryColor = useMemo(() => {
    const match = activeGradient?.match(/#([0-9a-fA-F]{6})/);
    return match ? match[0] : '#2c47a1';
  }, [activeGradient]);

  const ancillaryPrimaryColor = '#1b5e20';
  const ancillaryGradient = 'linear-gradient(135deg, #1b5e20, #4caf50)';

  const planResults = useMemo(() => {
    const plans = results?.planResult?.results;
    return Array.isArray(plans) ? plans : [];
  }, [results]);

  const reachedSelectionLimit = selectedPlans.length >= 4;
  const canCompare = selectedPlans.length > 0;
  const hasBenefitData = benefitKeys.length > 0;
  const hasPlanResults = planResults.length > 0;
  const currentPlanFlagStatus = Array.isArray(results?.planResult?.current_plan_flag_status)
    ? results.planResult.current_plan_flag_status.filter(
        (item) => item && (item.plan_name || item.status)
      )
    : [];

  const comparisonColumns = useMemo(() => {
    const columns = [
      {
        title: 'Benefit',
        dataIndex: 'benefit',
        key: 'benefit',
        fixed: 'left',
        width: 220,
      },
    ];

    comparisonPlans.forEach(({ key, plan: planData, label }) => {
      columns.push({
        title: (
          <AntSpace direction="vertical" size={0}>
            <span style={{ fontWeight: 600 }}>{label}</span>
            {planData?.plan_name && planData.plan_name !== label ? (
              <AntTypography.Text type="secondary" style={{ fontSize: 12 }}>
                {planData.plan_name}
              </AntTypography.Text>
            ) : null}
            {planData?.carrier ? (
              <AntTypography.Text type="secondary" style={{ fontSize: 12 }}>
                {planData.carrier}
              </AntTypography.Text>
            ) : null}
            {planData?.plan_type ? (
              <AntTypography.Text type="secondary" style={{ fontSize: 12 }}>
                {planData.plan_type}
              </AntTypography.Text>
            ) : null}
          </AntSpace>
        ),
        dataIndex: key,
        key,
        render: (value) => {
          if (!value) {
            return <AntTypography.Text type="secondary">—</AntTypography.Text>;
          }
          return (
            <AntSpace direction="vertical" size={0}>
              {value.value ? (
                <AntTypography.Text strong>{value.value}</AntTypography.Text>
              ) : null}
              {value.explanation ? (
                <AntTypography.Text type="secondary" style={{ fontSize: 12 }}>
                  {value.explanation}
                </AntTypography.Text>
              ) : null}
              {!value.value && !value.explanation ? (
                <AntTypography.Text>Included</AntTypography.Text>
              ) : null}
            </AntSpace>
          );
        },
      });
    });

    return columns;
  }, [comparisonPlans]);

  const comparisonData = useMemo(() => {
    if (!hasBenefitData) return [];
    return benefitKeys.map((benefit) => {
      const row = { key: benefit, benefit };
      comparisonPlans.forEach(({ key, plan: planData }) => {
        const match = planData?.benefits?.find((b) => b?.benefit === benefit);
        row[key] = match ? { value: match.value, explanation: match.explanation } : null;
      });
      return row;
    });
  }, [benefitKeys, comparisonPlans, hasBenefitData]);

  const handleComparisonOpen = () => {
    if (canCompare) {
      setComparisonOpen(true);
    }
  };

  const handleComparisonClose = () => {
    setComparisonOpen(false);
  };

  /**
   * Corrected handleSubmit function.
   */
  const handleSubmit = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    // Safeguard: Ensure user and user.email are available.
    if (!user || !user.email) {
      console.error("Submission failed: User is not authenticated or email is missing.");
      setResults({ error: "Authentication error. Please refresh and try again." });
      return;
    }

    const normalizedPlanId = currentPlanId.trim().toUpperCase();
    if (normalizedPlanId !== currentPlanId) {
      setCurrentPlanId(normalizedPlanId);
    }
    const compactPlanId = normalizedPlanId.replace(/[\s-]/g, '');
    const embeddedPlanIdMatch = normalizedPlanId.match(/([A-Z]\d{4})[\s-]*(\d{3})[\s-]*(\d{3})/);
    const embeddedSimplePlanMatch = compactPlanId.match(/[A-Z][0-9]{7,10}/);
    let planIdForSubmission = null;

    if (allowedPlanText.includes(normalizedPlanId)) {
      planIdForSubmission = normalizedPlanId;
    } else if (embeddedPlanIdMatch) {
      planIdForSubmission = `${embeddedPlanIdMatch[1]}-${embeddedPlanIdMatch[2]}-${embeddedPlanIdMatch[3]}`;
    } else if (embeddedSimplePlanMatch) {
      planIdForSubmission = embeddedSimplePlanMatch[0];
    }

    if (!planIdForSubmission) {
      setFormError('Enter a valid current plan identifier (e.g., H5521673, H5253-059-000, Original, or Supplement).');
      return;
    }

    if (!state) {
      setFormError('Select the client’s state.');
      return;
    }

    if (!county) {
      setFormError('Select the client’s county.');
      return;
    }

    if (!dob.trim()) {
      setFormError('Enter the client’s date of birth.');
      return;
    }

    if (benefits.length === 0) {
      setFormError('Select at least one priority benefit.');
      return;
    }

    if (conditions.length === 0) {
      setFormError('Select at least one chronic condition or "Not any listed".');
      return;
    }

    setFormError('');
    setLoading(true);
    setResults(null);
    setSelectedPlans([]);
    setComparisonOpen(false);
    setSelectionMessage('');

    const formInput = {
      currentPlanName: planIdForSubmission,
      currentPlanYear,
      newPlanYear,
      state,
      ServiceArea: (county || '').trimEnd(), // Only trims trailing whitespace
      dob,
      benefits: benefits.length > 0 ? benefits : null,
      conditions: conditions.length > 0 ? conditions : null,
      medicaid,
      veteranStatus,
      needsRx,
      addDesires,
      clientDesires: addDesires === 'Yes' ? clientDesires : null
    };

    try {
      // Diagnostic Log: This will show the exact object being sent in the browser's developer console.
      console.log("Sending payload to /mapd-policy-matchv3:", { ...formInput, activeUserEmail: user.email });

      const response = await api.getPolicyMatch(formInput, user.email);
      setResults(response);
      setComparisonOpen(false);
    } catch (error) {
      console.error("Error fetching policy match:", error);
      setResults({ error: error.message || "An unknown error occurred." });
    } finally {
      setLoading(false);
    }
  };

  const isLowerCostsSelected = benefits.includes("Decrease Copays or Coinsurance");
  const isNotAnyConditionSelected = conditions.includes("Not any listed");

  const accentGradient = activeGradient || 'linear-gradient(135deg, #1f3d8b, #18a5d7)';
  const stateOptions = useMemo(
    () => statesList.map((stateCode) => ({ label: stateCode, value: stateCode })),
    []
  );
  const countyOptions = useMemo(
    () => countiesForState.map((countyName) => ({ label: countyName, value: countyName })),
    [countiesForState]
  );
  const mobileBenefitOptions = useMemo(
    () =>
      policyMatchBenefitOptions.map((option) => ({
        ...option,
        disabled:
          isLowerCostsSelected && option.value !== 'Decrease Copays or Coinsurance',
      })),
    [isLowerCostsSelected]
  );
  const mobileConditionOptions = useMemo(
    () =>
      policyMatchConditionOptions.map((option) => ({
        ...option,
        disabled: isNotAnyConditionSelected && option.value !== 'Not any listed',
      })),
    [isNotAnyConditionSelected]
  );

  const MobileSelectorField = ({ label, value, options, onChange, multiple = false, disabled = false }) => (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <AMSelector
        options={options}
        multiple={multiple}
        disabled={disabled}
        value={multiple ? value : value ? [value] : []}
        onChange={(nextValues) => {
          if (multiple) {
            onChange(nextValues);
          } else {
            onChange(nextValues[0] || '');
          }
        }}
      />
    </div>
  );

  const formatDobInput = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
    const parts = [];
    if (digits.length >= 2) {
      parts.push(digits.slice(0, 2));
    } else if (digits.length > 0) {
      parts.push(digits);
    }
    if (digits.length >= 4) {
      parts.push(digits.slice(2, 4));
    } else if (digits.length > 2) {
      parts.push(digits.slice(2));
    }
    if (digits.length > 4) {
      parts.push(digits.slice(4));
    }
    return parts.join('/');
  };

  const handleDobChange = (valueOrEvent) => {
    const raw = valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent;
    setDob(formatDobInput(raw));
  };

  if (isMobile) {
    return (
      <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
        <AMCard style={{ background: accentGradient, color: '#fff' }}>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>Policy Match</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>
                Capture plan details, location, and priorities to surface best-fit MAPD options with side-by-side comparison ready to go.
              </div>
              <AMSpace wrap>
                <AMTag color="primary">Plan Years {currentPlanYear} → {newPlanYear}</AMTag>
                <AMTag color="success">Side-by-side ready</AMTag>
              </AMSpace>
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <div style={{ fontWeight: 700 }}>Plan basics</div>
              <MobileSelectorField
                label="Current Year"
                value={currentPlanYear}
                options={currentPlanYearOptions.map((year) => ({ label: year, value: year }))}
                onChange={setCurrentPlanYear}
              />
              <MobileSelectorField
                label="New Year"
                value={newPlanYear}
                options={newPlanYearOptions.map((year) => ({ label: year, value: year }))}
                onChange={setNewPlanYear}
              />
              <MobileSelectorField
                label="Plan Type"
                value={planTypeSelection || ''}
                options={[
                  { label: 'Original', value: 'ORIGINAL' },
                  { label: 'Supplement', value: 'SUPPLEMENT' },
                ]}
                onChange={handlePlanTypeToggle}
              />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Current Plan ID</div>
                <AMInput
                  value={currentPlanId}
                  onChange={(value) => setCurrentPlanId(value)}
                  placeholder="e.g. H4461042 or Original"
                />
              </div>
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <div style={{ fontWeight: 700 }}>Location & demographic</div>
              <MobileSelectorField
                label="State"
                value={state}
                options={stateOptions}
                onChange={setState}
              />
              <MobileSelectorField
                label="County"
                value={county}
                options={countyOptions}
                onChange={setCounty}
                disabled={!state || countyLoading}
              />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Date of Birth</div>
                <AMInput
                  value={dob}
                  onChange={handleDobChange}
                  placeholder="MM/DD/YYYY"
                  inputMode="numeric"
                  maxLength={10}
                />
              </div>
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <div style={{ fontWeight: 700 }}>Most important benefits</div>
              <div style={{ fontSize: 12, color: '#667085' }}>Select up to two priorities</div>
              <MobileSelectorField
                label="Benefits"
                value={benefits}
                options={mobileBenefitOptions}
                onChange={handleBenefitSelect}
                multiple
              />
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <div style={{ fontWeight: 700 }}>Chronic conditions</div>
              <div style={{ fontSize: 12, color: '#667085' }}>Pick everything that applies</div>
              <MobileSelectorField
                label="Conditions"
                value={conditions}
                options={mobileConditionOptions}
                onChange={handleConditionSelect}
                multiple
              />
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
              <div style={{ fontWeight: 700 }}>Eligibility checks</div>
              <MobileSelectorField
                label="Medicaid?"
                value={medicaid}
                options={yesNoOptions}
                onChange={setMedicaid}
              />
              <MobileSelectorField
                label="Veteran Benefits?"
                value={veteranStatus}
                options={yesNoOptions}
                onChange={setVeteranStatus}
              />
              <MobileSelectorField
                label="Needs Rx Coverage (Part-D)?"
                value={needsRx}
                options={yesNoOptions}
                onChange={setNeedsRx}
                disabled={medicaid === 'Yes'}
              />
              <MobileSelectorField
                label="Additional wants or needs?"
                value={addDesires}
                options={yesNoOptions}
                onChange={setAddDesires}
              />
              {addDesires === 'Yes' ? (
                <AMTextArea
                  value={clientDesires}
                  onChange={(value) => setClientDesires(value)}
                  placeholder="Please specify additional wants or needs"
                  rows={4}
                />
              ) : null}
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        {formError ? (
          <AMCard>
            <AMCard.Body>
              <div style={{ color: '#b42318', fontWeight: 600 }}>{formError}</div>
            </AMCard.Body>
          </AMCard>
        ) : null}

        <AMButton block color="primary" loading={loading} onClick={handleSubmit}>
          Submit & view matches
        </AMButton>

        {results && !loading ? (
          <AMCard>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
                <div style={{ fontWeight: 700 }}>Results</div>
                {results.error ? (
                  <div style={{ color: '#b42318' }}>{results.error}</div>
                ) : (
                  <>
                    {currentPlanFlagStatus.map((flag, index) => (
                      <AMCard key={`${flag.plan_name || 'current-plan'}-${index}`}>
                        <AMCard.Body>
                          <AMTag color="danger">Plan change required</AMTag>
                          <div style={{ marginTop: 8 }}>
                            The carrier has indicated that for the new plan year the client's current plan{' '}
                            <strong>{flag.plan_name || 'Unknown Plan'}</strong> has the status of{' '}
                            <strong>{flag.status || 'Unknown Status'}</strong> for {newPlanYear}. This client must change plans or they may lose Medicare Advantage coverage entirely.
                          </div>
                        </AMCard.Body>
                      </AMCard>
                    ))}
                    <AMCard>
                      <AMCard.Body>
                        <AMTag color="warning">Reminder</AMTag>
                        <div style={{ marginTop: 8 }}>
                          Not all MAPD plans recommended are plans we are contracted with. Reference the sidebar menu carriers if you have questions about which carriers we are contracted with.
                        </div>
                      </AMCard.Body>
                    </AMCard>

                    {results.hipProductResult ? (
                      <AMCard>
                        <AMCard.Body>
                          <div style={{ fontWeight: 700, marginBottom: 8 }}>Ancillary Product Eligibility</div>
                          <div><strong>Age:</strong> {results.hipProductResult.age}</div>
                          {results.hipProductResult.eligibilityMessages?.length ? (
                            <AMSpace wrap style={{ marginTop: 8 }}>
                              {results.hipProductResult.eligibilityMessages.map((msg, idx) => (
                                <AMTag
                                  key={`${msg.text}-${idx}`}
                                  color={msg.text?.toLowerCase().includes('disqualified') ? 'danger' : 'success'}
                                >
                                  {msg.text}
                                </AMTag>
                              ))}
                            </AMSpace>
                          ) : null}
                          <div style={{ marginTop: 12, fontWeight: 600 }}>Eligible Ancillary Products</div>
                          {results.hipProductResult.eligiblePlans?.length ? (
                            <AMSpace wrap style={{ marginTop: 8 }}>
                              {results.hipProductResult.eligiblePlans.map((plan, idx) => (
                                <AMTag key={`${plan}-${idx}`} color="primary">
                                  {plan}
                                </AMTag>
                              ))}
                            </AMSpace>
                          ) : (
                            <AMTag color="default" style={{ marginTop: 6 }}>
                              No eligible ancillary products found.
                            </AMTag>
                          )}
                        </AMCard.Body>
                      </AMCard>
                    ) : null}

                    {hasPlanResults ? (
                      <AMSpace direction="vertical" block style={{ '--gap': '10px' }}>
                        <div style={{ color: '#667085' }}>
                          Selected {selectedPlans.length} of 4 plans for comparison
                        </div>
                        <AMSpace block>
                          <AMButton block color="primary" disabled={!canCompare} onClick={handleComparisonOpen}>
                            View Comparison
                          </AMButton>
                          <AMButton block onClick={handleClearSelection} disabled={!canCompare}>
                            Clear Selection
                          </AMButton>
                        </AMSpace>
                        {selectionMessage ? (
                          <div style={{ color: '#b42318', fontSize: 12 }}>{selectionMessage}</div>
                        ) : null}
                        <AMCollapse>
                          {planResults.map((plan, i) => {
                            const planKey = plan?.plan_id || `${plan.plan_name || 'plan'}-${i}`;
                            const displayLabel = plan.plan_name || `Recommendation ${i + 1}`;
                            const isSelected = selectedPlans.some((item) => item.key === planKey);
                            const selectionDisabled = !isSelected && reachedSelectionLimit;
                            return (
                              <AMCollapse.Panel
                                key={planKey}
                                title={`${displayLabel}${plan.carrier ? ` · ${plan.carrier}` : ''}`}
                              >
                                <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
                                  {plan.benefits?.map((benefit, index) => (
                                      <AMCard key={`${benefit?.benefit || 'benefit'}-${index}`}>
                                      <AMCard.Body>
                                        <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                          {benefit?.benefit || 'Benefit'}
                                        </div>
                                        {benefit?.value ? (
                                          <AMTag
                                            color="primary"
                                            style={{ marginTop: 6, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                          >
                                            {benefit.value}
                                          </AMTag>
                                        ) : null}
                                        {benefit?.explanation ? (
                                          <div style={{ fontSize: 12, color: '#667085', marginTop: 6, wordBreak: 'break-word' }}>
                                            {benefit.explanation}
                                          </div>
                                        ) : null}
                                      </AMCard.Body>
                                    </AMCard>
                                  ))}
                                  <AMButton
                                    size="small"
                                    color={isSelected ? 'primary' : 'default'}
                                    disabled={selectionDisabled}
                                    onClick={() => handlePlanToggle(plan, planKey, displayLabel)}
                                  >
                                    {isSelected ? 'Remove from comparison' : 'Select for comparison'}
                                  </AMButton>
                                </AMSpace>
                              </AMCollapse.Panel>
                            );
                          })}
                        </AMCollapse>
                      </AMSpace>
                    ) : null}

                    {results.planResult?.message ? (
                      <AMCard>
                        <AMCard.Body>
                          <div style={{ fontWeight: 600 }}>Plan note</div>
                          <div style={{ marginTop: 6 }}>{results.planResult.message}</div>
                        </AMCard.Body>
                      </AMCard>
                    ) : null}
                  </>
                )}
              </AMSpace>
            </AMCard.Body>
          </AMCard>
        ) : null}

        <AMPopup visible={comparisonOpen && canCompare} onMaskClick={handleComparisonClose}>
          <AMSafeArea position="top" />
          <div style={{ padding: '12px 16px', fontWeight: 700 }}>Plan Comparison</div>
          <div style={{ padding: '0 16px 16px' }}>
            {hasBenefitData ? (
              <AMList>
                {benefitKeys.map((benefit) => (
                  <AMList.Item key={benefit} description={(
                    <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
                      {comparisonPlans.map(({ key, label, plan }) => {
                        const match = plan?.benefits?.find((b) => b?.benefit === benefit);
                        const value = match?.value || match?.explanation || 'Included';
                        return (
                          <div key={`${key}-${benefit}`}>
                            <strong>{label}:</strong> {match ? value : '—'}
                          </div>
                        );
                      })}
                    </AMSpace>
                  )}>
                    {benefit}
                  </AMList.Item>
                ))}
              </AMList>
            ) : (
              <div style={{ color: '#667085' }}>
                Benefit-level comparison data is not available for the selected plans.
              </div>
            )}
            <AMButton block style={{ marginTop: 12 }} onClick={handleComparisonClose}>
              Close
            </AMButton>
          </div>
          <AMSafeArea position="bottom" />
        </AMPopup>
      </AMSpace>
    );
  }

  return (
    <AntCard style={{ borderRadius: 16, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
      <div style={{ background: accentGradient, color: '#fff', padding: '20px 24px' }}>
        <AntRow gutter={[16, 16]} align="middle" justify="space-between">
          <AntCol flex="auto">
            <AntSpace direction="vertical" size={6}>
            <img
              src="https://i.imgur.com/ofYHXdq.png"
              alt="Policy Match Logo"
              style={{ maxHeight: 72, height: 'auto', filter: 'brightness(0) invert(1)' }}
            />
              <AntTypography.Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                Capture plan details, location, and priorities to surface best-fit MAPD options with side-by-side comparison ready to go.
              </AntTypography.Text>
              <AntSpace wrap>
                <AntTag color="blue">Secure data handling</AntTag>
                <AntTag color="geekblue">Plan Years {currentPlanYear} → {newPlanYear}</AntTag>
                <AntTag color="cyan">Side-by-side ready</AntTag>
              </AntSpace>
            </AntSpace>
          </AntCol>
        </AntRow>
      </div>

      <div style={{ padding: 24, background: '#f6f8fb' }}>
        <AntSpace direction="vertical" size="large" style={{ width: '100%' }}>
          <AntCard
            size="small"
            title="Step 1 · Plan basics"
            extra={<AntTag color="processing">Required</AntTag>}
          >
            <AntRow gutter={[12, 12]} align="middle">
              <AntCol xs={24} sm={6} md={4}>
                <AntTypography.Text strong>Current Year</AntTypography.Text>
                <AntSelect
                  value={currentPlanYear}
                  onChange={setCurrentPlanYear}
                  options={currentPlanYearOptions.map((year) => ({ label: year, value: year }))}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </AntCol>
              <AntCol xs={24} sm={6} md={4}>
                <AntTypography.Text strong>New Year</AntTypography.Text>
                <AntSelect
                  value={newPlanYear}
                  onChange={setNewPlanYear}
                  options={newPlanYearOptions.map((year) => ({ label: year, value: year }))}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </AntCol>
              <AntCol xs={24} sm={12} md={6}>
                <AntTypography.Text strong>Plan Type</AntTypography.Text>
                <AntSegmented
                  block
                  options={[
                    { label: 'Original', value: 'ORIGINAL' },
                    { label: 'Supplement', value: 'SUPPLEMENT' },
                  ]}
                  value={planTypeSelection || undefined}
                  onChange={handlePlanTypeToggle}
                />
              </AntCol>
              <AntCol xs={24} md={10}>
                <AntTypography.Text strong>Current Plan ID</AntTypography.Text>
                <AntInput
                  value={currentPlanId}
                  onChange={(event) => setCurrentPlanId(event.target.value)}
                  placeholder="e.g. H4461042, H5253-059-000, Original, Supplement"
                />
              </AntCol>
            </AntRow>
          </AntCard>

          <AntCard size="small" title="Location & demographic">
            <AntRow gutter={[12, 12]} align="middle">
              <AntCol xs={24} sm={6} md={4}>
                <AntTypography.Text strong>State</AntTypography.Text>
                <AntSelect
                  value={state || undefined}
                  onChange={setState}
                  options={stateOptions}
                  placeholder="Select State"
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </AntCol>
              <AntCol xs={24} sm={10} md={6}>
                <AntTypography.Text strong>County</AntTypography.Text>
                <AntSelect
                  value={county || undefined}
                  onChange={setCounty}
                  options={countyOptions}
                  placeholder={countyLoading ? 'Loading counties...' : 'Select County'}
                  style={{ width: '100%' }}
                  disabled={!state || countyLoading}
                  showSearch
                  optionFilterProp="label"
                />
              </AntCol>
              <AntCol xs={24} sm={8} md={6}>
                <AntTypography.Text strong>Date of Birth</AntTypography.Text>
                <AntInput
                  value={dob}
                  onChange={handleDobChange}
                  placeholder="MM/DD/YYYY"
                  prefix={<CalendarTodayIcon fontSize="small" />}
                  inputMode="numeric"
                  maxLength={10}
                />
              </AntCol>
            </AntRow>
          </AntCard>

          <AntRow gutter={[16, 16]}>
            <AntCol xs={24} md={12}>
              <AntCard size="small" title="Most Important Benefits">
                <AntTypography.Text type="secondary">Select up to two priorities</AntTypography.Text>
                <AntSpace direction="vertical" style={{ width: '100%', marginTop: 12 }}>
                  {policyMatchBenefitOptions.map((option) => (
                    <AntCheckbox
                      key={option.value}
                      value={option.value}
                      checked={benefits.includes(option.value)}
                      onChange={handleBenefitChange}
                      disabled={
                        isLowerCostsSelected && option.value !== 'Decrease Copays or Coinsurance'
                      }
                    >
                      {option.label}
                    </AntCheckbox>
                  ))}
                </AntSpace>
              </AntCard>
            </AntCol>
            <AntCol xs={24} md={12}>
              <AntCard size="small" title="Chronic Conditions">
                <AntTypography.Text type="secondary">Pick everything that applies</AntTypography.Text>
                <AntSpace direction="vertical" style={{ width: '100%', marginTop: 12 }}>
                  {policyMatchConditionOptions.map((option) => (
                    <AntCheckbox
                      key={option.value}
                      value={option.value}
                      checked={conditions.includes(option.value)}
                      onChange={handleConditionChange}
                      disabled={isNotAnyConditionSelected && option.value !== 'Not any listed'}
                    >
                      {option.label}
                    </AntCheckbox>
                  ))}
                </AntSpace>
              </AntCard>
            </AntCol>
          </AntRow>

          <AntRow gutter={[16, 16]}>
            <AntCol xs={24} sm={12} md={6}>
              <AntCard size="small" title="Medicaid?">
                <AntRadio.Group value={medicaid} onChange={(e) => setMedicaid(e.target.value)}>
                  <AntSpace direction="vertical">
                    {yesNoOptions.map((option) => (
                      <AntRadio key={option.value} value={option.value}>
                        {option.label}
                      </AntRadio>
                    ))}
                  </AntSpace>
                </AntRadio.Group>
              </AntCard>
            </AntCol>
            <AntCol xs={24} sm={12} md={6}>
              <AntCard size="small" title="Veteran Benefits?">
                <AntRadio.Group value={veteranStatus} onChange={(e) => setVeteranStatus(e.target.value)}>
                  <AntSpace direction="vertical">
                    {yesNoOptions.map((option) => (
                      <AntRadio key={option.value} value={option.value}>
                        {option.label}
                      </AntRadio>
                    ))}
                  </AntSpace>
                </AntRadio.Group>
              </AntCard>
            </AntCol>
            <AntCol xs={24} sm={12} md={6}>
              <AntCard size="small" title="Needs Rx Coverage (Part-D)?">
                <AntRadio.Group value={needsRx} onChange={(e) => setNeedsRx(e.target.value)}>
                  <AntSpace direction="vertical">
                    {yesNoOptions.map((option) => (
                      <AntRadio key={option.value} value={option.value} disabled={medicaid === 'Yes'}>
                        {option.label}
                      </AntRadio>
                    ))}
                  </AntSpace>
                </AntRadio.Group>
              </AntCard>
            </AntCol>
            <AntCol xs={24} sm={12} md={6}>
              <AntCard size="small" title="Additional wants or needs?">
                <AntRadio.Group value={addDesires} onChange={(e) => setAddDesires(e.target.value)}>
                  <AntSpace direction="vertical">
                    {yesNoOptions.map((option) => (
                      <AntRadio key={option.value} value={option.value}>
                        {option.label}
                      </AntRadio>
                    ))}
                  </AntSpace>
                </AntRadio.Group>
              </AntCard>
            </AntCol>
          </AntRow>

          {addDesires === 'Yes' ? (
            <AntInput.TextArea
              rows={4}
              value={clientDesires}
              onChange={(event) => setClientDesires(event.target.value)}
              placeholder="Please specify additional wants or needs"
            />
          ) : null}

          {formError ? <AntAlert type="error" showIcon message={formError} /> : null}

          <AntButton type="primary" size="large" block loading={loading} onClick={handleSubmit}>
            Submit & view matches
          </AntButton>

          {results && !loading ? (
            <AntSpace direction="vertical" size="large" style={{ width: '100%' }}>
              <AntTypography.Title level={4} style={{ margin: 0 }}>Results</AntTypography.Title>
              {results.error ? (
                <AntAlert type="error" showIcon message={results.error} />
              ) : (
                <>
                  {currentPlanFlagStatus.map((flag, index) => (
                    <AntAlert
                      key={`${flag.plan_name || 'current-plan'}-${index}`}
                      type="error"
                      showIcon
                      message={(
                        <span>
                          The carrier has indicated that for the new plan year the client's current plan{' '}
                          <strong>{flag.plan_name || 'Unknown Plan'}</strong> has the status of{' '}
                          <strong>{flag.status || 'Unknown Status'}</strong> for {newPlanYear}. This client must change plans or they may lose Medicare Advantage coverage entirely.
                        </span>
                      )}
                    />
                  ))}
                  <AntAlert
                    type="warning"
                    showIcon
                    message="Not all MAPD plans recommended are plans we are contracted with. Reference the sidebar menu carriers if you have questions about which carriers we are contracted with."
                  />

                  {results.hipProductResult ? (
                    <AntCard size="small" title="Ancillary Product Eligibility" style={{ borderColor: ancillaryPrimaryColor }}>
                      <AntSpace direction="vertical" style={{ width: '100%' }}>
                        <AntTypography.Text><strong>Age:</strong> {results.hipProductResult.age}</AntTypography.Text>
                        {results.hipProductResult.eligibilityMessages?.length ? (
                          <AntSpace wrap>
                            {results.hipProductResult.eligibilityMessages.map((msg, idx) => (
                              <AntTag
                                key={`${msg.text}-${idx}`}
                                color={msg.text?.toLowerCase().includes('disqualified') ? 'red' : 'green'}
                              >
                                {msg.text}
                              </AntTag>
                            ))}
                          </AntSpace>
                        ) : null}
                        <div>
                          <AntTypography.Text strong>Eligible Ancillary Products</AntTypography.Text>
                          {results.hipProductResult.eligiblePlans?.length ? (
                            <AntSpace wrap style={{ marginTop: 8 }}>
                              {results.hipProductResult.eligiblePlans.map((plan, idx) => (
                                <AntTag key={`${plan}-${idx}`} color="blue">
                                  {plan}
                                </AntTag>
                              ))}
                            </AntSpace>
                          ) : (
                            <div style={{ marginTop: 8 }}>
                              <AntTag color="default">No eligible ancillary products found.</AntTag>
                            </div>
                          )}
                        </div>
                      </AntSpace>
                    </AntCard>
                  ) : null}

                  {hasPlanResults && selectedPlans.length > 0 ? (
                    <AntSpace direction="vertical" style={{ width: '100%' }} size="small">
                      <AntTypography.Text type="secondary">
                        Selected {selectedPlans.length} of 4 plans for comparison
                      </AntTypography.Text>
                      <AntSpace>
                        <AntButton type="primary" onClick={handleComparisonOpen}>
                          {comparisonOpen ? 'Comparison Open' : 'View Comparison'}
                        </AntButton>
                        <AntButton onClick={handleClearSelection} disabled={selectedPlans.length === 0}>
                          Clear Selection
                        </AntButton>
                      </AntSpace>
                    </AntSpace>
                  ) : null}

                  {selectionMessage ? (
                    <AntTypography.Text type="danger">{selectionMessage}</AntTypography.Text>
                  ) : null}

                  <AntRow gutter={[16, 16]}>
                    {planResults.map((plan, i) => {
                      const planKey = plan?.plan_id || `${plan.plan_name || 'plan'}-${i}`;
                      const displayLabel = plan.plan_name || `Recommendation ${i + 1}`;
                      const isSelected = selectedPlans.some((item) => item.key === planKey);
                      const selectionDisabled = !isSelected && reachedSelectionLimit;
                      return (
                        <AntCol xs={24} md={12} key={planKey} style={{ display: 'flex' }}>
                          <AntCard
                            title={(
                              <AntSpace direction="vertical" size={0}>
                                <AntTypography.Text type="secondary">Recommendation {i + 1}</AntTypography.Text>
                                <AntTypography.Text strong style={{ wordBreak: 'break-word' }}>
                                  {plan.plan_name || 'MAPD Plan Recommendation'}
                                </AntTypography.Text>
                                {plan.carrier || plan.plan_type ? (
                                  <AntTypography.Text type="secondary" style={{ wordBreak: 'break-word' }}>
                                    {plan.carrier || plan.plan_type}
                                  </AntTypography.Text>
                                ) : null}
                              </AntSpace>
                            )}
                            extra={(
                              <AntCheckbox
                                checked={isSelected}
                                disabled={selectionDisabled}
                                onChange={() => handlePlanToggle(plan, planKey, displayLabel)}
                              >
                                Compare
                              </AntCheckbox>
                            )}
                            style={{
                              borderColor: gradientPrimaryColor,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              boxShadow: isSelected
                                ? `0 12px 28px ${convertHexToRgba(gradientPrimaryColor, 0.25)}`
                                : undefined,
                            }}
                            styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column' } }}
                          >
                            <div style={{ flex: 1 }}>
                              <AntList
                                dataSource={plan.benefits || []}
                                renderItem={(benefit, idx) => (
                                  <AntList.Item key={`${benefit?.benefit || 'benefit'}-${idx}`}>
                                    <AntSpace direction="vertical" size={0}>
                                      <AntTypography.Text strong style={{ wordBreak: 'break-word' }}>
                                        {benefit?.benefit || 'Benefit'}
                                      </AntTypography.Text>
                                      {benefit?.value ? (
                                        <AntTag color="blue" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                          {benefit.value}
                                        </AntTag>
                                      ) : null}
                                      {benefit?.explanation ? (
                                        <AntTypography.Text type="secondary" style={{ wordBreak: 'break-word' }}>
                                          {benefit.explanation}
                                        </AntTypography.Text>
                                      ) : null}
                                    </AntSpace>
                                  </AntList.Item>
                                )}
                              />
                            </div>
                          </AntCard>
                        </AntCol>
                      );
                    })}
                  </AntRow>

                  {results.planResult?.message ? (
                    <AntAlert type="info" showIcon message={results.planResult.message} />
                  ) : null}
                </>
              )}
            </AntSpace>
          ) : null}
        </AntSpace>
      </div>

      {selectedPlans.length > 0 && !comparisonOpen ? (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleComparisonOpen}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 32 },
            right: { xs: 16, md: 32 },
            zIndex: (theme) => theme.zIndex.drawer + 10,
            boxShadow: '0 18px 40px rgba(13,71,161,0.35)',
          }}
        >
          <CompareArrowsIcon sx={{ mr: 1 }} />
          Compare {selectedPlans.length}
        </Fab>
      ) : null}

      <AntModal
        open={comparisonOpen && canCompare}
        onCancel={handleComparisonClose}
        footer={<AntButton onClick={handleComparisonClose}>Close</AntButton>}
        width={1100}
        title="Plan Comparison"
        zIndex={2000}
      >
        {hasBenefitData ? (
          <AntTable
            columns={comparisonColumns}
            dataSource={comparisonData}
            pagination={false}
            scroll={{ x: true }}
            size="small"
          />
        ) : (
          <AntTypography.Text type="secondary">
            Benefit-level comparison data is not available for the selected plans.
          </AntTypography.Text>
        )}
      </AntModal>
    </AntCard>
  );
};

// --- UPDATED DOCTOR SEARCH COMPONENT ---

const ResultCard = ({ carrier, data, isLoading, error }) => {
  const isMobile = useIsMobile();

  const getCarrierColor = (carrierName = '') => {
    switch (carrierName.toLowerCase()) {
      case 'aetna':
        return '#9c27b0';
      case 'humana':
        return '#4caf50';
      case 'uhc':
        return '#3f51b5';
      case 'centene':
        return '#009688';
      case 'devoted':
        return '#ff9800';
      case 'cigna':
        return '#1e1647';
      case 'healthspring':
        return '#1e1647';
      case 'elevance':
        return '#f44336';
      default:
        return '#6b7280';
    }
  };

  const isSuccess = data && data.length > 0 && !data[0].message;
  const message = data?.[0]?.message || 'No results found.';

  const renderDoctorList = () => {
    if (!Array.isArray(data)) return null;
    if (isMobile) {
      return (
        <AMList mode="card">
          {data.map((doctor, index) => (
            <AMList.Item
              key={`${doctor.npi || doctor.lastName || 'doctor'}-${index}`}
              description={(
                <div style={{ fontSize: 12, color: '#667085' }}>
                  <div><strong>NPI:</strong> {doctor.npi}</div>
                  <div><strong>Phone:</strong> {doctor.main_phone}</div>
                  <div><strong>Address:</strong> {doctor.address?.main}</div>
                  {doctor.affiliation ? (
                    <div><strong>Affiliation:</strong> {doctor.affiliation}</div>
                  ) : null}
                </div>
              )}
            >
              <div style={{ fontWeight: 700 }}>{doctor.firstName} {doctor.lastName}</div>
              <div style={{ fontSize: 12, color: '#667085' }}>{doctor.type}</div>
            </AMList.Item>
          ))}
        </AMList>
      );
    }

    return (
      <AntList
        dataSource={data}
        renderItem={(doctor, index) => (
          <AntList.Item key={`${doctor.npi || doctor.lastName || 'doctor'}-${index}`}>
            <AntSpace direction="vertical" size={0}>
              <AntTypography.Text strong>{doctor.firstName} {doctor.lastName}</AntTypography.Text>
              <AntTypography.Text type="secondary">{doctor.type}</AntTypography.Text>
              <AntTypography.Text><strong>NPI:</strong> {doctor.npi}</AntTypography.Text>
              <AntTypography.Text><strong>Phone:</strong> {doctor.main_phone}</AntTypography.Text>
              <AntTypography.Text><strong>Address:</strong> {doctor.address?.main}</AntTypography.Text>
              {doctor.affiliation ? (
                <AntTypography.Text><strong>Affiliation:</strong> {doctor.affiliation}</AntTypography.Text>
              ) : null}
            </AntSpace>
          </AntList.Item>
        )}
      />
    );
  };

  const bodyContent = () => {
    if (isLoading) {
      return isMobile ? (
        <AMSpace align="center" style={{ justifyContent: 'center', width: '100%' }}>
          <AMSpin />
          <span>Loading...</span>
        </AMSpace>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <AntSpin />
        </div>
      );
    }
    if (error) {
      return isMobile ? (
        <AMTag color="danger">Error: {error}</AMTag>
      ) : (
        <AntAlert type="error" showIcon message={`Error: ${error}`} />
      );
    }

    if (isSuccess) {
      return (
        <>
          {isMobile ? (
            <AMTag color="success" style={{ marginBottom: 8 }}>
              In Network
            </AMTag>
          ) : (
            <AntTag color="green" style={{ marginBottom: 8 }}>
              In Network
            </AntTag>
          )}
          {renderDoctorList()}
        </>
      );
    }

    return isMobile ? (
      <div style={{ textAlign: 'center' }}>
        <AMTag color="danger">Out of Network / No Results</AMTag>
        <div style={{ marginTop: 8, fontSize: 12, color: '#667085' }}>{message}</div>
      </div>
    ) : (
      <AntAlert
        type="warning"
        showIcon
        message="Out of Network / No Results"
        description={message}
      />
    );
  };

  if (isMobile) {
    return (
      <AMCard>
        <AMCard.Body>
          <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
            <AMTag color="primary" style={{ background: getCarrierColor(carrier) }}>
              {carrier}
            </AMTag>
            {bodyContent()}
          </AMSpace>
        </AMCard.Body>
      </AMCard>
    );
  }

  return (
    <AntCard
      title={(
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <AntTag color={getCarrierColor(carrier)} style={{ color: '#fff' }}>
            {carrier}
          </AntTag>
        </div>
      )}
    >
      {bodyContent()}
    </AntCard>
  );
};

const DoctorSearchTool = () => {
  const isMobile = useIsMobile();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [city, setCity] = useState('');
  const [carrier, setCarrier] = useState('allCarriers');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const carrierOptions = [
    { label: 'All Carriers', value: 'allCarriers' },
    { label: 'Aetna', value: 'Aetna' },
    { label: 'Centene', value: 'Centene' },
    { label: 'HealthSpring', value: 'Healthspring' },
    { label: 'Devoted', value: 'Devoted' },
    { label: 'Elevance', value: 'Elevance' },
    { label: 'Humana', value: 'Humana' },
    { label: 'UHC', value: 'UHC' },
  ];

  const handleSearch = async () => {
    if (!firstName || !lastName || !city) {
      setError('First Name, Last Name, and City are required.');
      return;
    }

    setLoading(true);
    setError('');
    
    const formData = { firstName, lastName, city };
    const carriersToSearch = carrier === 'allCarriers'
      ? carrierOptions.filter((opt) => opt.value !== 'allCarriers').map((opt) => opt.value)
      : [carrier];

    const initialResults = carriersToSearch.map(c => ({
      carrier: c,
      isLoading: true,
      data: null,
      error: null
    }));
    setResults(initialResults);

    // Using Promise.all to run searches in parallel for better performance
    await Promise.all(carriersToSearch.map(async (currentCarrier) => {
        try {
            const response = await api.post('/provider-directory', { formData, carrier: currentCarrier });
            setResults(prev => prev.map(res => 
                res.carrier === currentCarrier ? { ...res, isLoading: false, data: response.result } : res
            ));
        } catch (err) {
            setResults(prev => prev.map(res => 
                res.carrier === currentCarrier ? { ...res, isLoading: false, error: err.message || 'API call failed' } : res
            ));
        }
    }));

    setLoading(false);
  };

  if (isMobile) {
    return (
      <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '6px' }}>
              <AMSpace align="center" style={{ fontSize: 18, fontWeight: 700 }}>
                <LocalHospitalIcon fontSize="small" />
                <span>Doctor Search Tool</span>
              </AMSpace>
              <div style={{ fontSize: 12, color: '#667085' }}>
                Find in-network providers across multiple carriers.
              </div>
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '10px' }}>
              <AMInput value={firstName} onChange={setFirstName} placeholder="First Name" clearable />
              <AMInput value={lastName} onChange={setLastName} placeholder="Last Name" clearable />
              <AMInput value={city} onChange={setCity} placeholder="City" clearable />
              <AMSelector
                options={carrierOptions}
                value={[carrier]}
                onChange={(values) => setCarrier(values[0] || 'allCarriers')}
              />
              {error ? <AMTag color="danger">{error}</AMTag> : null}
              <AMButton block color="primary" onClick={handleSearch} loading={loading}>
                Search
              </AMButton>
            </AMSpace>
          </AMCard.Body>
        </AMCard>

        <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
          {results.map((result, index) => (
            <ResultCard key={`${result.carrier}-${index}`} {...result} />
          ))}
        </AMSpace>
      </AMSpace>
    );
  }

  return (
    <AntCard
      title={(
        <AntSpace align="center">
          <LocalHospitalIcon />
          <span>Doctor Search Tool</span>
        </AntSpace>
      )}
      extra={<AntTypography.Text type="secondary">Find in-network providers across multiple carriers</AntTypography.Text>}
    >
      <AntCard size="small" style={{ marginBottom: 16 }}>
        <AntRow gutter={[12, 12]} align="middle">
          <AntCol xs={24} sm={12} md={6}>
            <AntInput value={firstName} onChange={(event) => setFirstName(event.target.value)} placeholder="First Name" />
          </AntCol>
          <AntCol xs={24} sm={12} md={6}>
            <AntInput value={lastName} onChange={(event) => setLastName(event.target.value)} placeholder="Last Name" />
          </AntCol>
          <AntCol xs={24} sm={12} md={6}>
            <AntInput value={city} onChange={(event) => setCity(event.target.value)} placeholder="City" />
          </AntCol>
          <AntCol xs={24} sm={12} md={6}>
            <AntSelect
              value={carrier}
              onChange={setCarrier}
              options={carrierOptions}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </AntCol>
        </AntRow>
        {error ? <AntAlert type="error" showIcon message={error} style={{ marginTop: 12 }} /> : null}
        <AntSpace style={{ width: '100%', justifyContent: 'flex-end', marginTop: 12 }}>
          <AntButton type="primary" icon={<SearchIcon />} onClick={handleSearch} loading={loading}>
            Search
          </AntButton>
        </AntSpace>
      </AntCard>

      <AntRow gutter={[16, 16]}>
        {results.map((result, index) => (
          <AntCol xs={24} sm={12} md={8} key={`${result.carrier}-${index}`}>
            <ResultCard {...result} />
          </AntCol>
        ))}
      </AntRow>
    </AntCard>
  );
};

const MedicationRetrieverTool = () => {
  const { user } = useAuth();
  const fileInputRef = React.useRef(null);
  
  // State for the component
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/plain') {
      setSelectedFile(file);
      setError('');
      setResults(null); // Clear previous results
    } else {
      setSelectedFile(null);
      setError('Please select a valid .txt file.');
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setError('Please select a file first.');
      return;
    }
    setLoading(true);
    setError('');
    setResults(null);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const fileContent = e.target.result;
        const jsonData = await api.processRxFile(fileContent);
        
        // Structure the results for easier rendering
        setResults({
          clientInfo: {
            Name: jsonData.name || 'N/A',
            DOB: jsonData.dob || 'N/A',
            Address: `${jsonData.addressLine1 || ''} ${jsonData.addressLine2 || ''}, ${jsonData.city || ''}, ${jsonData.state || ''} ${jsonData.zip || ''}`.trim(),
            'Date of Retrieval': new Date().toLocaleDateString()
          },
          medications: jsonData.data || [],
          providers: jsonData.providers ? Object.values(jsonData.providers) : []
        });

      } catch (err) {
        setError(err.message || 'Failed to process file.');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(selectedFile);
  };
  

  return (
    <Card>
      <CardHeader
        avatar={<DocumentScannerIcon />}
        title={<Typography variant="h5">Medication Retriever Tool</Typography>}
        action={
            <Button
                startIcon={<ArticleIcon />}
                onClick={() => handleResourceClick({ type: 'api', actionValue: 'gd7', user, label: 'Rx Retriever Tool Guide' })}
            >
                View Use Guide
            </Button>
        }
      />
      <CardContent>
        {/* Upload Section */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Button variant="outlined" onClick={() => fileInputRef.current.click()}>
                Choose File
              </Button>
              <input ref={fileInputRef} type="file" accept=".txt" hidden onChange={handleFileChange} />
            </Grid>
            <Grid item xs>
              <Typography variant="body2" color="text.secondary">
                {selectedFile ? selectedFile.name : 'No file selected. Please upload the .txt from Medicare.gov.'}
              </Typography>
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={handleUpload} disabled={loading || !selectedFile}>
                {loading ? <CircularProgress size={24} /> : 'Upload & Process'}
              </Button>
            </Grid>
          </Grid>
          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        </Paper>

        {/* Results Section */}
        {results && (
          <Grid container spacing={3}>
            {/* Client Info */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Client Information</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {Object.entries(results.clientInfo).map(([key, value]) => (
                  <Typography key={key}><strong>{key}:</strong> {value}</Typography>
                ))}
              </Paper>
            </Grid>

            {/* Medications Table */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Medications</Typography>
              {results.medications?.length ? (
                <AntSpace wrap style={{ marginBottom: 12 }}>
                  {results.medications.map((med, index) => {
                    const name = med?.[2] || 'Medication';
                    const dosage = med?.[3] ? ` ${med[3]}` : '';
                    const packaging = med?.[4] ? ` (${med[4]})` : '';
                    return (
                      <AntTag key={`med-chip-${index}`} color="blue">
                        {`${name}${dosage}${packaging}`.trim()}
                      </AntTag>
                    );
                  })}
                </AntSpace>
              ) : (
                <AntTag color="default">No medications found.</AntTag>
              )}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Service Date', 'NDC', 'Brand Name', 'Dosage', 'Packaging', 'Days Supply'].map(head => <TableCell key={head}><b>{head}</b></TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.medications.map((med, index) => (
                      <TableRow key={index}>
                        {med.map((cell, cellIndex) => <TableCell key={cellIndex}>{cell}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            
            {/* Providers Table */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Providers</Typography>
              {results.providers?.length ? (
                <AntSpace wrap style={{ marginBottom: 12 }}>
                  {results.providers.map((provider, index) => {
                    const labelParts = [
                      provider?.fullName || 'Provider',
                      provider?.type ? `• ${provider.type}` : '',
                    ].filter(Boolean);
                    return (
                      <AntTag key={`provider-chip-${index}`} color="geekblue">
                        {labelParts.join(' ')}
                      </AntTag>
                    );
                  })}
                </AntSpace>
              ) : (
                <AntTag color="default">No providers found.</AntTag>
              )}
              <TableContainer component={Paper} variant="outlined">
                 <Table size="small">
                   <TableHead>
                     <TableRow>
                       {['Full Name', 'NPI', 'City', 'Main Address', 'Mailing Address', 'Type'].map(head => <TableCell key={head}><b>{head}</b></TableCell>)}
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {results.providers.map((p, index) => (
                       <TableRow key={index}>
                         <TableCell>{p.fullName}</TableCell>
                         <TableCell>{p.npi}</TableCell>
                         <TableCell>{p.city}</TableCell>
                         <TableCell>{p.address?.main}</TableCell>
                         <TableCell>{p.address?.mailing}</TableCell>
                         <TableCell>{p.type}</TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
              </TableContainer>
            </Grid>
            
            {/* CRM Upload Section removed */}

          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

const RequestToolForm = ({ title, icon, fields, actionType }) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  // Create an initial empty state object from the fields config
  const initialState = fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {});
  
  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: 'info' });
  const [responseHtml, setResponseHtml] = useState(null); // State specifically for the HTML response

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData(initialState);
    setMessage({ text: '', type: 'info' });
    setResponseHtml(null); // Also clear the HTML response on reset
  };
  
  const formatDataForAction = () => {
      return fields.map(field => {
          let value = formData[field.name] || '';
          if (field.type === 'date' && value) {
              const [year, month, day] = value.split('-');
              value = `${month}/${day}/${year}`;
          }
          return { label: field.label, value };
      });
  };

  const handleAction = async () => {
    setLoading(true);
    setMessage({ text: '', type: 'info' });
    setResponseHtml(null); // Clear previous HTML response on new action

    if (actionType === 'send') {
      const payload = {
        requestType: title,
        formData: { fields: formatDataForAction() },
        userEmail: user.email,
        userDisplayName: user.name
      };
      try {
        const response = await api.sendRequest(payload);
        
        // This logic correctly handles both string (HTML) and object (JSON) responses
        if (typeof response === 'string') {
            setResponseHtml(response); // Store the HTML to be rendered
        } else if (response && response.message) {
            if (title === 'ORC Request') {
              setResponseHtml(response.message);
              setMessage({ text: '', type: 'success' });
            } else {
              setMessage({ text: response.message, type: 'success' });
            }
        } else {
            setMessage({ text: 'Request sent successfully!', type: 'success' });
        }

      } catch (err) {
        setMessage({ text: err.message || 'Failed to send request.', type: 'error' });
      }
    } else if (actionType === 'copy') {
      const formattedFields = formatDataForAction();
      const textToCopy = formattedFields
          .map(field => `${field.label}: ${field.value}`)
          .join('\n');
      
      try {
        await navigator.clipboard.writeText(textToCopy);
        setMessage({ text: 'Copied to clipboard!', type: 'success' });
      } catch (err) {
        setMessage({ text: 'Failed to copy.', type: 'error' });
      }
    }
    
    setLoading(false);
  };

  const actionLabel = actionType === 'send' ? 'Send Request' : 'Copy to Clipboard';
  const messageTone =
    message.type === 'success' ? '#1a8f35' : message.type === 'error' ? '#d92d20' : '#667085';

  const formatOrcResponse = (input) => {
    let raw = String(input || '');
    if (!raw.trim()) return '';

    // Preserve explicit breaks before stripping tags.
    raw = raw.replace(/<br\s*\/?>/gi, '|').replace(/\r?\n/g, '|');
    raw = raw.replace(/<[^>]+>/g, ' ');
    raw = raw.replace(/\s+/g, ' ').trim();
    if (!raw) return '';

    const escapeHtml = (value) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const normalized = raw.replace(/APPROVED TO PROCEED!/gi, 'APPROVED TO PROCEED!');

    let withBreaks = normalized;
    // Always isolate the approval line.
    withBreaks = withBreaks.replace(/(APPROVED TO PROCEED!)/gi, '|$1|');
    // Break on sentence punctuation even when missing a space.
    withBreaks = withBreaks.replace(/([.!?])(?=[A-Z0-9"“])/g, '$1|');
    // Break after closing brackets/quotes when a new thought starts.
    withBreaks = withBreaks.replace(/([)\]"”])(?=[A-Z0-9])/g, '$1|');
    // Break after explicit pipe markers (existing line breaks).
    const segments = withBreaks
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);

    const formattedSegments = segments.map((segment) => {
      if (segment.toUpperCase() === 'APPROVED TO PROCEED!') {
        return `<strong>${escapeHtml(segment)}</strong>`;
      }

      const colonIndex = segment.indexOf(':');
      if (colonIndex > 0) {
        const label = segment.slice(0, colonIndex).trim();
        const value = segment.slice(colonIndex + 1).trim();
        const hasQuoteInLabel = /["“”]/.test(label);
        if (!hasQuoteInLabel && label.length <= 120) {
          return `<strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}`;
        }
      }

      return escapeHtml(segment);
    });

    return formattedSegments.join('<br/>');
  };

  const responseBlock = responseHtml ? (
    <div
      style={{
        color: '#1a8f35',
        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
        fontSize: '14px',
        lineHeight: 1.6,
      }}
      dangerouslySetInnerHTML={{
        __html: title === 'ORC Request' ? formatOrcResponse(responseHtml) : responseHtml,
      }}
    />
  ) : null;

  if (isMobile) {
    return (
      <AMCard>
        <AMCard.Body>
          <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
            <AMSpace align="center" style={{ fontSize: 18, fontWeight: 700 }}>
              {icon}
              <span>{title}</span>
            </AMSpace>
            <AMList mode="card">
              {fields.map((field) => (
                <AMList.Item key={field.name}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 13, color: '#667085', fontWeight: 600 }}>
                      {field.label}
                    </div>
                    {field.type === 'select' && Array.isArray(field.options) ? (
                      <AMSelector
                        options={field.options}
                        value={formData[field.name] ? [formData[field.name]] : []}
                        onChange={(values) => handleFieldChange(field.name, values[0] || '')}
                      />
                    ) : (
                      <AMInput
                        value={formData[field.name]}
                        onChange={(value) => handleFieldChange(field.name, value)}
                        placeholder={field.label}
                        type={field.type === 'date' ? 'date' : 'text'}
                        clearable
                      />
                    )}
                  </div>
                </AMList.Item>
              ))}
            </AMList>
            {responseHtml ? (
              <AMCard>
                <AMCard.Body>
                  {responseBlock}
                </AMCard.Body>
              </AMCard>
            ) : null}
            {message.text ? (
              <div style={{ color: messageTone, fontSize: 13, fontWeight: 500 }}>{message.text}</div>
            ) : null}
            <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
              <AMButton block fill="outline" onClick={handleReset}>
                Reset
              </AMButton>
              <AMButton block color="primary" loading={loading} onClick={handleAction}>
                {actionLabel}
              </AMButton>
            </AMSpace>
          </AMSpace>
        </AMCard.Body>
      </AMCard>
    );
  }

  return (
    <AntCard
      title={
        <AntSpace align="center">
          {icon}
          <span>{title}</span>
        </AntSpace>
      }
    >
      <AntRow gutter={[16, 16]}>
        {fields.map((field) => (
          <AntCol xs={24} sm={field.fullWidth ? 24 : 8} key={field.name}>
            {field.type === 'select' ? (
              <AntSelect
                style={{ width: '100%' }}
                value={formData[field.name] || undefined}
                onChange={(value) => handleFieldChange(field.name, value)}
                placeholder={field.label}
                options={field.options || []}
                showSearch
                optionFilterProp="label"
              />
            ) : (
              <AntInput
                value={formData[field.name]}
                onChange={(event) => handleFieldChange(field.name, event.target.value)}
                placeholder={field.label}
                type={field.type || 'text'}
              />
            )}
          </AntCol>
        ))}
      </AntRow>

      {responseHtml ? (
        <AntCard size="small" style={{ marginTop: 16 }}>
          {responseBlock}
        </AntCard>
      ) : null}

      <AntDivider />
      <AntSpace style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <div style={{ color: messageTone, fontSize: 13, minHeight: 18 }}>
          {message.text}
        </div>
        <AntSpace>
          <AntButton onClick={handleReset}>Reset</AntButton>
          <AntButton type="primary" loading={loading} onClick={handleAction}>
            {actionLabel}
          </AntButton>
        </AntSpace>
      </AntSpace>
    </AntCard>
  );
};

// --- Configuration for each tool's form fields ---
const marxFields = [
  { name: 'firstName', label: 'First Name' },
  { name: 'lastName', label: 'Last Name' },
  { name: 'dob', label: 'Date of Birth', type: 'date' },
  { name: 'ssnMbi', label: 'SSN or Partial MBI' },
];

const medicaidFields = [
  { name: 'name', label: 'Name' },
  { name: 'dob', label: 'Date of Birth', type: 'date' },
  { name: 'state', label: 'State' },
  { name: 'zip', label: 'Zip' },
  { name: 'county', label: 'County' },
  { name: 'medicareNumber', label: 'Medicare Number' },
  { name: 'medicaidLisOrSsn', label: 'Medicaid / LIS / SSN' },
  { name: 'planName', label: 'Plan Name' },
];

const orcFields = [
  { name: 'formerEmployer', label: 'Former Employer/Agency or Job Title', fullWidth: true },
  { name: 'employerPlanName', label: 'Employer Plan Name (REQUIRED)', fullWidth: true },
  { name: 'stateORC', label: 'State', type: 'select', fullWidth: true, options: statesList.map(s => ({ value: s, label: s }))},
];

// --- Page components that render the reusable form ---
const MARxCheckTool = () => <RequestToolForm title="MARx Check" icon={<CheckIcon />} fields={marxFields} actionType="copy" />;
const MedicaidCheckTool = () => <RequestToolForm title="Medicaid Check Formatter" icon={<ArticleIcon />} fields={medicaidFields} actionType="copy" />;
const ORCRequestTool = () => <RequestToolForm title="ORC Request" icon={<AssuredWorkloadIcon />} fields={orcFields} actionType="send" />;



function isGcsLink(content) {
  return (
    typeof content === "string" &&
    (content.startsWith("https://storage.googleapis.com/") ||
     content.startsWith("https://storage.cloud.google.com/"))
  );
}

// Utility: Determine file type based on URL
function getFileType(url) {
  if (!url) return "unknown";
  if (url.endsWith(".pdf")) return "pdf";
  if (url.match(/\.(jpg|jpeg|png|gif)$/i)) return "image";
  if (url.match(/\.(mp4|webm|ogg)$/i)) return "video";
  if (url.match(/\.(mp3|wav|ogg)$/i)) return "audio";
  return "unknown";
}

// Component: Smart content renderer
function SmartModalContent({ content }) {
  if (isGcsLink(content)) {
    const fileType = getFileType(content);
    if (fileType === "pdf") {
      return (
        <iframe
          src={content}
          title="PDF Preview"
          width="100%"
          height={600}
          style={{ border: "none" }}
        />
      );
    }
    if (fileType === "image") {
      return (
        <img
          src={content}
          alt="GCS File"
          style={{ display: "block", maxWidth: "100%", maxHeight: 500, margin: "0 auto" }}
        />
      );
    }
    if (fileType === "video") {
      return (
        <video controls width="100%" style={{ maxHeight: 500 }}>
          <source src={content} />
          Your browser does not support the video tag.
        </video>
      );
    }
    if (fileType === "audio") {
      return (
        <audio controls>
          <source src={content} />
          Your browser does not support the audio tag.
        </audio>
      );
    }
    // fallback for unknown file type
    return (
      <a href={content} target="_blank" rel="noopener noreferrer">
        Open file
      </a>
    );
  }

  // fallback: treat as HTML
  return <div dangerouslySetInnerHTML={{ __html: content }} />;
}

const AIAgentList = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedPhone, setCopiedPhone] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        if (!AI_NPC_AGENT_LIST_URL) {
          throw new Error('AI NPC agent list URL is not configured.');
        }
        const res = await fetch(AI_NPC_AGENT_LIST_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Filter for LEVEL_RELAY agents and sort them by difficulty level in ascending order
        const sortedAgents = data.filter(a => a.name?.includes('LEVEL_RELAY'))
                                 .sort((a, b) => a.difficulty - b.difficulty);
        setAgents(sortedAgents);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);

  const handleCopy = (phone) => {
    if (!phone) return;
    navigator.clipboard.writeText(phone);
    setCopiedPhone(phone);
    setTimeout(() => {
      setCopiedPhone(null);
    }, 2000);
  };  

  const DifficultyBadge = ({ level }) => {
    const levelMap = {
      1: { color: 'success', label: 'Level 1', tag: 'green' },
      2: { color: 'primary', label: 'Level 2', tag: 'blue' },
      3: { color: 'default', label: 'Level 3', tag: 'geekblue' },
      4: { color: 'warning', label: 'Level 4', tag: 'orange' },
      5: { color: 'danger', label: 'Level 5 (Hardest)', tag: 'red' },
    };
    const { color, label } = levelMap[level] || { color: 'default', label: `Level ${level}` };
    if (isMobile) {
      return <AMTag color={color}>{label}</AMTag>;
    }
    return <AntTag color={levelMap[level]?.tag || 'default'}>{label}</AntTag>;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        <AntSpin />
      </div>
    );
  }
  if (error) {
    return <AntAlert type="error" message="Failed to load data" description={error} showIcon />;
  }

  const infoBlock = (
    <AntAlert
      type="info"
      showIcon
      message="Getting Started"
      description={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            Before beginning a mock call, you will be expected to provide your{' '}
            <strong>National Producer Number (NPN)</strong> to the AI agent for verification.
          </div>
          <div style={{ fontWeight: 700 }}>Difficulty Level Guide</div>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            <li>Level 1-2: Beginner (compliant and straightforward)</li>
            <li>Level 3: Intermediate (common objections/questions)</li>
            <li>Level 4-5: Advanced (challenging, complex scenarios)</li>
          </ul>
        </div>
      }
    />
  );

  if (isMobile) {
    return (
      <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
        <AMCard>
          <AMCard.Body>
            <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Getting Started</div>
              <div style={{ fontSize: 13, color: '#667085' }}>
                Before beginning a mock call, you will be expected to provide your{' '}
                <strong>National Producer Number (NPN)</strong> to the AI agent for verification.
              </div>
              <div style={{ fontWeight: 700, marginTop: 8 }}>Difficulty Level Guide</div>
              <div style={{ fontSize: 13, color: '#667085' }}>Level 1-2: Beginner</div>
              <div style={{ fontSize: 13, color: '#667085' }}>Level 3: Intermediate</div>
              <div style={{ fontSize: 13, color: '#667085' }}>Level 4-5: Advanced</div>
            </AMSpace>
          </AMCard.Body>
        </AMCard>
        <AMList mode="card">
          {agents.map((agent) => (
            <AMList.Item
              key={agent.phone || agent.name}
              description={<DifficultyBadge level={agent.difficulty} />}
              extra={
                agent.phone ? (
                  <AMButton
                    size="mini"
                    fill="none"
                    onClick={() => handleCopy(agent.phone)}
                  >
                    {copiedPhone === agent.phone ? 'Copied' : 'Copy'}
                  </AMButton>
                ) : null
              }
            >
              <span style={{ fontFamily: 'monospace' }}>{agent.phone ?? '—'}</span>
            </AMList.Item>
          ))}
        </AMList>
      </AMSpace>
    );
  }

  const columns = [
    {
      title: 'Phone Number',
      dataIndex: 'phone',
      key: 'phone',
      render: (value, record) => (
        <AntSpace>
          <span style={{ fontFamily: 'monospace' }}>{value ?? '—'}</span>
          {value ? (
            <AntButton size="small" onClick={() => handleCopy(value)}>
              {copiedPhone === value ? 'Copied' : 'Copy'}
            </AntButton>
          ) : null}
        </AntSpace>
      ),
    },
    {
      title: 'Difficulty Level',
      dataIndex: 'difficulty',
      key: 'difficulty',
      render: (value) => <DifficultyBadge level={value} />,
    },
  ];

  return (
    <AntSpace direction="vertical" style={{ width: '100%' }}>
      {infoBlock}
      <AntTable
        columns={columns}
        dataSource={agents.map((agent) => ({ ...agent, key: agent.phone || agent.name }))}
        pagination={false}
      />
    </AntSpace>
  );
};

const AppModal = () => {
  const { isOpen, isLoading, title, content, closeModal } = useModal();
  const isMobile = useIsMobile();

  const renderedContent = isLoading ? (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
      <AntSpin />
    </div>
  ) : typeof content === 'string' ? (
    <SmartModalContent content={content} />
  ) : typeof content === 'function' || React.isValidElement(content) ? (
    typeof content === 'function' ? React.createElement(content) : content
  ) : null;

  if (isMobile) {
    return (
      <AMPopup
        visible={isOpen}
        onMaskClick={closeModal}
        bodyStyle={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
      >
        <div style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
          <AMButton fill="none" onClick={closeModal} style={{ padding: 0 }}>
            <CloseIcon fontSize="small" />
          </AMButton>
        </div>
        <div style={{ padding: 16, paddingTop: 0 }}>{renderedContent}</div>
        <AMSafeArea position="bottom" />
      </AMPopup>
    );
  }

  return (
    <AntModal open={isOpen} onCancel={closeModal} footer={null} width={840} title={title}>
      {renderedContent}
    </AntModal>
  );
};

// --- REUSABLE & LAYOUT COMPONENTS ---

const ApiAccordion = ({ label, actionValue, defaultExpanded = false }) => {
  const isMobile = useIsMobile();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const handleFetch = async () => {
    if (hasFetched) return;
    setLoading(true);
    setHasFetched(true);
    try {
      const html = await api.toggleForm(actionValue);
      setContent(html);
    } catch (error) {
      console.error(`Error fetching content for ${actionValue}:`, error);
      setContent('<p style="color: red;">Error loading content.</p>');
    } finally {
      setLoading(false);
    }
  };

  const body = loading ? (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
      <AntSpin size="small" />
    </div>
  ) : (
    <div dangerouslySetInnerHTML={{ __html: content }} />
  );

  if (isMobile) {
    return (
      <AMCollapse
        defaultActiveKey={defaultExpanded ? ['1'] : []}
        onChange={(keys) => {
          const hasOpen = Array.isArray(keys) ? keys.length > 0 : Boolean(keys);
          if (hasOpen) handleFetch();
        }}
      >
        <AMCollapse.Panel key="1" title={label}>
          {body}
        </AMCollapse.Panel>
      </AMCollapse>
    );
  }

  return (
    <AntCollapse
      defaultActiveKey={defaultExpanded ? ['1'] : []}
      onChange={(keys) => {
        const hasOpen = Array.isArray(keys) ? keys.length > 0 : Boolean(keys);
        if (hasOpen) handleFetch();
      }}
      items={[
        {
          key: '1',
          label: <span style={{ fontWeight: 600 }}>{label}</span>,
          children: body,
        },
      ]}
    />
  );
};

const handleResourceClick = async ({ type, actionValue, user, openModal, label }) => {
  // Modal forms are handled by the main React app's modal context
  if (type === 'api-content') {
    if (actionValue === 'ai-npc-agents-list-relay') {
      openModal(label, <AIAgentList />);
      return;
    }
    openModal(label, actionValue);
    return;
  }
  // Simple links can be opened directly
  if (type === 'link') {
    window.open(actionValue, '_blank');
    return;
  }

  // Handle secure redirects that require a token or maskId from the API
  if (type === 'secure-redirect') {
    try {
      if (!API_BASE_URL) {
        throw new Error('Missing API base URL.');
      }
      const linkData = await api.secureRedirect(actionValue, user.email, label);
      window.open(`${API_BASE_URL}/secure-portal/view/${linkData.maskId}`, '_blank');
    } catch (error) {
      console.error(`Error for secure-redirect action '${actionValue}':`, error);
    }
    return;
  }
  if (type === 'secure-redirect-app') {
    try {
      if (!API_GATE_TOKEN) {
        throw new Error('Missing API gate token.');
      }
      const userEmail = user.email.toLowerCase().trim();
      const apiToken = API_GATE_TOKEN;

      // Extract base URL (could be anything like /complianceguard-portal)
      const baseUrl = new URL(actionValue);

      // Append required query params
      baseUrl.searchParams.set('api_token', apiToken);
      baseUrl.searchParams.set('activeUserEmail', userEmail);

      // Open the final URL in a new tab
      window.open(baseUrl.toString(), '_blank');
    } catch (error) {
      console.error(`Error for secure-redirect-app action '${actionValue}':`, error);
    }
    return;
  }

  // Handle all other API calls that fetch HTML/content to be displayed in a new window
  try {
    let data;
    switch (type) {
      case 'api':
        data = await api.viewResource(actionValue, user.email);
        break;
      case 'api-unsecure':
        data = await api.viewResourceUnsecure(actionValue, user.email);
        break;
      case 'api-media':
        data = await api.viewResourceMedia(actionValue, user.email);
        break;
      default:
        // Exit if the type is unknown to prevent further errors
        return;
    }

    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      // This can happen if a popup blocker is active
      alert('A popup blocker may be preventing a new window from opening. Please allow popups for this site.');
      return;
    }
    
    // Write the initial HTML content from the API into the new window
    newWindow.document.write(data);

    // --- SCRIPT INJECTION TO FIX LEGACY ONCLICK ERRORS ---
    // This script is created and then injected into the new window's <head>.
    // It defines the globally-scoped functions that the legacy HTML is expecting to find.
    const script = newWindow.document.createElement('script');
    script.textContent = `
      // This entire script runs inside the newly created popup window.
      // We define constants here using values from the main React app.
      const API_BASE_URL = '${API_BASE_URL}';
      const activeUserEmail = '${user.email}';

      // A helper function to make API calls from within the new window
      async function makeApiCall(endpoint, body) {
          const response = await fetch(API_BASE_URL + endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body)
          });
          if (!response.ok) {
              const errorText = await response.text();
              throw new Error(\`API call failed: \${errorText}\`);
          }
          return response.text();
      }

      // Define the missing function: postResourceRequestUnsecure
      window.postResourceRequestUnsecure = async (event) => {
          const buttonId = event.currentTarget.getAttribute('data-button-id');
          if (!buttonId) return;
          try {
              const html = await makeApiCall('/view-resource-unsecure', { button_id: buttonId, activeUserEmail });
              // Replace the entire content of the popup with the new HTML response
              document.body.innerHTML = html;
          } catch (error) {
              console.error('Error in postResourceRequestUnsecure:', error);
              document.body.innerHTML = '<p style="color: red; font-family: sans-serif;">Failed to load resource.</p>';
          }
      };

      // Define the other legacy secure function for completeness
      window.postResourceRequest = async (event) => {
          const buttonId = event.currentTarget.getAttribute('data-button-id');
          if (!buttonId) return;
          try {
              const html = await makeApiCall('/view-resource', { button_id: buttonId, activeUserEmail });
              document.body.innerHTML = html;
          } catch (error) {
              console.error('Error in postResourceRequest:', error);
              document.body.innerHTML = '<p style="color: red; font-family: sans-serif;">Failed to load resource.</p>';
          }
      };
      
      // Define toggleForm for any collapsible sections that might exist in the loaded HTML
      window.toggleForm = async (buttonId) => {
          const container = document.getElementById(buttonId);
          if (!container) return;

          // If the container already has content, just toggle its visibility
          if (container.innerHTML.trim() !== '') {
              container.style.display = container.style.display === 'none' ? 'block' : 'none';
              return;
          }

          // Otherwise, fetch the content for the first time
          try {
              container.innerHTML = '<em style="font-family: sans-serif;">Loading...</em>';
              container.style.display = 'block';
              const html = await makeApiCall('/toggle-form', { button_id: buttonId });
              container.innerHTML = html;
          } catch (error) {
               console.error('Error in toggleForm:', error);
               container.innerHTML = '<p style="color: red; font-family: sans-serif;">Failed to load content.</p>';
          }
      };
    `;
    
    // Append the newly created script to the head of the popup window and close the document stream
    newWindow.document.head.appendChild(script);
    newWindow.document.close();

  } catch (error) {
    console.error(`Error handling resource click for action '${actionValue}':`, error);
    // Optionally, trigger an error modal in the main app to inform the user.
  }
};

// ===================================================================
// =================== NEW LIST ITEM COMPONENT =======================
// ===================================================================
const ResourceListItem = ({ item }) => {
    const { user } = useAuth();
    const { openModal } = useModal();
    const { activeGradient } = useUI();
    const [loading, setLoading] = useState(false);
    const [hovered, setHovered] = useState(false);
    const isMobile = useIsMobile();

    const handleClick = async () => {
        setLoading(true);
        await handleResourceClick({ ...item, user, openModal });
        setLoading(false);
    };

    if (isMobile) {
      return (
        <AMList.Item
          prefix={loading ? <AntSpin size="small" /> : item.icon}
          description={item.description}
          onClick={handleClick}
          clickable
        >
          {item.label}
        </AMList.Item>
      );
    }

    const baseTextColor = hovered ? '#ffffff' : '#111012';
    const descriptionColor = hovered ? 'rgba(255,255,255,0.78)' : 'rgba(17,16,18,0.72)';
    const iconNode = loading ? (
      <AntSpin size="small" />
    ) : (
      <span style={{ color: baseTextColor, display: 'inline-flex', alignItems: 'center' }}>
        {item.icon}
      </span>
    );

    return (
      <>
        <AntList.Item
          onClick={handleClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            cursor: 'pointer',
            background: hovered ? activeGradient : 'transparent',
            transition: 'background 0.2s ease, color 0.2s ease',
            borderRadius: 6,
            paddingLeft: 0.5,
          }}
        >
          <AntList.Item.Meta
            avatar={iconNode}
            title={<span style={{ fontWeight: 600, color: baseTextColor }}>{item.label}</span>}
            description={
              item.description ? <span style={{ color: descriptionColor }}>{item.description}</span> : null
            }
          />
        </AntList.Item>
        <AntDivider style={{ margin: 0 }} />
      </>
    );
};


const ResourceButton = ({ label, tooltip, icon, type, actionValue, pageKey, sx = {} }) => {
  const { user } = useAuth();
  const { openModal } = useModal();
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handleClick = async () => {
    setLoading(true);
    await handleResourceClick({ type, actionValue, user, openModal, label });
    setLoading(false);
  };

  const buttonContent = isMobile ? (
    <AMButton
      block
      onClick={handleClick}
      loading={loading}
      color="primary"
      style={{
        '--border-radius': '12px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        ...sx,
      }}
    >
      <AMSpace direction="vertical" align="center" block style={{ '--gap': '6px' }}>
        {icon}
        <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
      </AMSpace>
    </AMButton>
  ) : (
    <AntButton
      type={pageKey === 'admin-reports' ? 'default' : 'primary'}
      block
      onClick={handleClick}
      loading={loading}
      style={{
        height: '100%',
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 16,
        ...sx,
      }}
    >
      <AntSpace direction="vertical" align="center">
        {icon}
        <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      </AntSpace>
    </AntButton>
  );

  return tooltip ? <Tooltip title={tooltip} placement="top" arrow>{buttonContent}</Tooltip> : buttonContent;
}

const ResourceCard = ({ title, icon, description, items = [], grouped = false, groups = [], display, customStyles = {}, pageKey, color, sidebarIcon }) => {
  const isCarrierPage = color && sidebarIcon;
  const isMobile = useIsMobile();
  const getContrastText = (bg) => {
    if (!bg || typeof bg !== 'string') return '#ffffff';
    const hex = bg.trim();
    const rgbMatch = hex.match(/^rgb\\s*\\(\\s*(\\d+)\\s*,\\s*(\\d+)\\s*,\\s*(\\d+)\\s*\\)$/i);
    let r;
    let g;
    let b;
    if (rgbMatch) {
      r = Number(rgbMatch[1]);
      g = Number(rgbMatch[2]);
      b = Number(rgbMatch[3]);
    } else if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
      const value = hex.slice(1);
      if (value.length === 3) {
        r = parseInt(value[0] + value[0], 16);
        g = parseInt(value[1] + value[1], 16);
        b = parseInt(value[2] + value[2], 16);
      } else {
        r = parseInt(value.slice(0, 2), 16);
        g = parseInt(value.slice(2, 4), 16);
        b = parseInt(value.slice(4, 6), 16);
      }
    }
    if (![r, g, b].every((channel) => Number.isFinite(channel))) {
      return '#ffffff';
    }
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 160 ? '#111012' : '#ffffff';
  };
  const headerTextColor = isCarrierPage ? getContrastText(color) : '#111012';

  const renderGrid = (buttonItems) => {
    if (isMobile) {
      return (
        <AMGrid columns={2} gap={10}>
          {buttonItems.map((item, index) => (
            <AMGrid.Item key={index}>
              <ResourceButton {...item} pageKey={pageKey} />
            </AMGrid.Item>
          ))}
        </AMGrid>
      );
    }

    return (
      <AntRow gutter={[16, 16]}>
        {buttonItems.map((item, index) => (
          <AntCol xs={24} sm={12} md={8} lg={6} key={index}>
            <ResourceButton {...item} pageKey={pageKey} />
          </AntCol>
        ))}
      </AntRow>
    );
  };

  const renderList = (listItems) => {
    if (isMobile) {
      return (
        <AMList mode="card">
          {listItems.map((item, index) => (
            <ResourceListItem key={index} item={item} />
          ))}
        </AMList>
      );
    }

    return (
      <AntList>
        {listItems.map((item, index) => (
          <ResourceListItem key={index} item={item} />
        ))}
      </AntList>
    );
  };

  const headerContent = isCarrierPage ? (
    <div
      style={{
        backgroundColor: color,
        color: headerTextColor,
        padding: 16,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        width: '100%',
      }}
    >
      <span style={{ color: headerTextColor, display: 'inline-flex', alignItems: 'center' }}>{sidebarIcon}</span>
      <span style={{ fontSize: 24, fontWeight: 700 }}>{title}</span>
    </div>
  ) : (
    <div
      style={{
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        backgroundColor: '#ffffff',
        backgroundImage: [
          'linear-gradient(135deg, rgba(17,16,18,0.04) 25%, transparent 25%)',
          'linear-gradient(225deg, rgba(17,16,18,0.04) 25%, transparent 25%)',
          'linear-gradient(45deg, rgba(17,16,18,0.03) 25%, transparent 25%)',
          'linear-gradient(315deg, rgba(17,16,18,0.03) 25%, transparent 25%)',
        ].join(','),
        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
        backgroundSize: '8px 8px',
        width: '100%',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
      <span style={{ fontSize: 24, fontWeight: 700 }}>{title}</span>
    </div>
  );

  if (isMobile) {
    return (
      <AMCard
        style={{
          marginBottom: 16,
          borderRadius: 16,
          overflow: 'hidden',
          ...customStyles,
        }}
      >
        <AMCard.Body>
          <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
            {headerContent}
            {description ? (
              <div style={{ color: '#667085', fontSize: 13 }}>{description}</div>
            ) : null}
            {display === 'list'
              ? renderList(items)
              : grouped
                ? groups.map((group, groupIndex) => (
                    <div key={groupIndex} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{group.title}</div>
                      {renderGrid(group.items)}
                    </div>
                  ))
                : renderGrid(items)}
          </AMSpace>
        </AMCard.Body>
      </AMCard>
    );
  }

  return (
    <AntCard
      id={title.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}
      style={{
        marginBottom: 24,
        borderRadius: 16,
        overflow: 'hidden',
        ...customStyles,
      }}
      title={headerContent}
      bordered={false}
      styles={{
        header: {
          padding: 0,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        },
        body: {
          padding: 4,
        },
      }}
    >
      {description ? (
        <div style={{ color: '#667085', marginBottom: 16 }}>{description}</div>
      ) : null}
      {display === 'list'
        ? renderList(items)
        : grouped
          ? groups.map((group, groupIndex) => (
              <div key={groupIndex} style={{ marginBottom: groupIndex < groups.length - 1 ? 24 : 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{group.title}</div>
                <AntDivider style={{ marginTop: 0 }} />
                {renderGrid(group.items)}
              </div>
            ))
          : renderGrid(items)}
    </AntCard>
  );
}


const ApiContentButton = ({ label, actionValue, icon, gridWidth }) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const colSpan = Math.min(24, (gridWidth || 4) * 2);

  const handleToggle = async () => {
    if (!open && !content) {
      setLoading(true);
      try {
        const html = await api.toggleForm(actionValue);
        setContent(html);
      } catch (error) {
        console.error(`Error fetching content for ${actionValue}:`, error);
        setContent('<p style="color: red;">Error loading content.</p>');
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  if (isMobile) {
    return (
      <AMCard style={{ borderRadius: 12 }}>
        <AMCard.Body>
          <AMSpace direction="vertical" block style={{ '--gap': '8px' }}>
            <AMButton
              block
              fill="outline"
              onClick={handleToggle}
              style={{ '--border-radius': '10px' }}
            >
              <AMSpace align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                <AMSpace align="center">
                  {icon}
                  <span style={{ fontWeight: 600 }}>{label}</span>
                </AMSpace>
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </AMSpace>
            </AMButton>
            {open ? (
              <div style={{ padding: 8 }}>
                {loading ? <AntSpin size="small" /> : <div dangerouslySetInnerHTML={{ __html: content }} />}
              </div>
            ) : null}
          </AMSpace>
        </AMCard.Body>
      </AMCard>
    );
  }

  return (
    <AntCol xs={24} sm={12} md={colSpan} lg={6}>
      <AntCard size="small" style={{ height: '100%' }}>
        <AntButton
          type="text"
          onClick={handleToggle}
          style={{ width: '100%', textAlign: 'left', padding: 0 }}
        >
          <AntSpace direction="vertical" style={{ width: '100%' }}>
            <AntSpace align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
              <AntSpace align="center">
                {icon}
                <span style={{ fontWeight: 600 }}>{label}</span>
              </AntSpace>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </AntSpace>
          </AntSpace>
        </AntButton>
        {open ? (
          <div style={{ paddingTop: 12 }}>
            {loading ? <AntSpin size="small" /> : <div dangerouslySetInnerHTML={{ __html: content }} />}
          </div>
        ) : null}
      </AntCard>
    </AntCol>
  );
};



const drawerWidth = 280;

const normalizeButtonId = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim().toLowerCase();
};

const getItemButtonId = (item = {}) => {
  const candidates = [
    item.button_id,
    item.buttonId,
    item.key,
    item.actionValue,
    item.action_value,
    item.action,
    item.id,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeButtonId(candidate);
    if (normalized) return normalized;
  }

  return '';
};

const SlackAppsMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const buttonId = 'slack-apps-menu';
  const slackOauthUrl = useMemo(() => {
    if (!SLACK_OAUTH_AUTHORIZE_URL) return '';
    try {
      const url = new URL(SLACK_OAUTH_AUTHORIZE_URL);
      if (SLACK_CLIENT_ID) {
        url.searchParams.set('client_id', SLACK_CLIENT_ID);
      }
      url.searchParams.set(
        'scope',
        'assistant:write,chat:write,incoming-webhook,users:read,users:read.email,im:write'
      );
      url.searchParams.set('user_scope', '');
      return url.toString();
    } catch (error) {
      console.error('Invalid Slack OAuth URL:', error);
      return '';
    }
  }, []);

  const handleButtonClick = (event) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title="Slack apps & downloads" placement="bottom">
        <IconButton
          id={buttonId}
          size="small"
          aria-haspopup="true"
          aria-controls={open ? `${buttonId}-flyout` : undefined}
          aria-expanded={open ? 'true' : undefined}
          onClick={handleButtonClick}
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            p: 0,
            backgroundColor: '#fff',
            boxShadow: '0 4px 12px rgba(15,17,30,0.18)',
            '&:hover': {
              backgroundColor: '#fff',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box
            component="img"
            src="https://a.slack-edge.com/38f0e7c/marketing/img/nav/logo.svg"
            alt="Slack"
            sx={{ width: 26, height: 26 }}
          />
        </IconButton>
      </Tooltip>
      <Popover
        id={`${buttonId}-flyout`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          elevation: 0,
          sx: (theme) => ({
            mt: 1,
            p: 1.25,
            borderRadius: 2,
            width: 210,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(23,27,34,0.98)' : '#ffffff',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(17,16,18,0.1)'}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 14px 28px rgba(0,0,0,0.4)'
              : '0 10px 26px rgba(17,16,18,0.14)',
          }),
        }}
      >
        <Box
          component="a"
          href="https://slack.com/interop/gcalapp/install"
          target="_blank"
          rel="noopener noreferrer"
          className="slack_button setup_usage_demo"
          sx={{ display: 'inline-flex', textDecoration: 'none', mb: 0.75, justifyContent: 'center' }}
        >
          <Box
            component="img"
            alt="Add to Slack"
            width={139}
            height={40}
            src="https://a.slack-edge.com/01e32/img/salesforce/add_to_slack.png"
            srcSet="https://storage.googleapis.com/sabal-media-pub/google-calendar-for-slack.png 1x, https://storage.googleapis.com/sabal-media-pub/google-calendar-for-slack.png 2x"
            sx={{ display: 'block' }}
          />
        </Box>
        <Box
          component="a"
          href={slackOauthUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', textDecoration: 'none', mb: 0.75, justifyContent: 'center' }}
        >
          <Box
            component="img"
            alt="Add to Slack"
            width={139}
            height={40}
            src="https://storage.googleapis.com/sabal-media-pub/public-image-assets-sbr/cg-click-to-add-button.png"
            srcSet="https://storage.googleapis.com/sabal-media-pub/public-image-assets-sbr/cg-click-to-add-button.png 1x, https://storage.googleapis.com/sabal-media-pub/public-image-assets-sbr/cg-click-to-add-button.png 2x"
            sx={{ display: 'block' }}
          />
        </Box>
      </Popover>
    </Box>
  );
};
const RadialMenu = () => {
  const { user, agentDirectoryProfile } = useAuth();
  const { activeGradient } = useUI();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loadingLink, setLoadingLink] = useState(null);

  const viciDialerLink = (() => {
    const rawLink = agentDirectoryProfile?.dialer_login_link;
    if (typeof rawLink === 'string') {
      const trimmed = rawLink.trim();
      if (trimmed) return trimmed;
    }
    return null;
  })();

  const quickLinks = [
    { title: 'PaylocityHR', href: 'https://access.paylocity.com/', icon: <RequestQuoteIcon /> },
    { title: 'MARx', href: 'https://portal.cms.gov/portal/', icon: <AssuredWorkloadIcon /> },
    { title: 'CRM', href: 'https://sabal.elaphee.com/', icon: <GroupIcon /> },
    {
      title: 'SunFire',
      type: 'api-secure-link',
      button_id: 'gls1',
      icon: <WhatshotIcon />,
    },
    { title: 'Vici Dialer', href: viciDialerLink, icon: <PhoneIcon /> },
  ];

  const popoverOpen = Boolean(anchorEl);
  const buttonId = 'quick-links-button';

  const handleButtonClick = (event) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDirectLinkClick = (link) => {
    handleClose();
    const target = typeof link.href === 'string' ? link.href.trim() : '';
    if (!target) return;
    try {
      window.open(target, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(`Failed to open ${link.title} link`, err);
    }
  };

  const handleApiLinkClick = async (link) => {
    if (loadingLink || !user?.email) {
      console.error('Cannot fetch secure link: already loading or user not found.');
      return;
    }
    setLoadingLink(link.title);
    try {
      const response = await api.getSecureLink(link.button_id, user.email);
      const secureUrl = response?.content;
      if (secureUrl && secureUrl.startsWith('http')) {
        window.open(secureUrl, '_blank', 'noopener,noreferrer');
        handleClose();
      } else {
        throw new Error('Invalid URL received from the server.');
      }
    } catch (error) {
      console.error(`Failed to fetch secure link for ${link.title}:`, error);
    } finally {
      setLoadingLink(null);
    }
  };

  const renderLinkIcon = (link) => {
    const isLoading = loadingLink === link.title;
    const hasHref = link.type === 'api-secure-link' || (typeof link.href === 'string' && link.href.trim().length > 0);
    const isDisabled = (!hasHref) || (Boolean(loadingLink) && !isLoading);
    const handleClick = () => {
      if (link.type === 'api-secure-link') {
        handleApiLinkClick(link);
      } else {
        handleDirectLinkClick(link);
      }
    };

    return (
      <Tooltip title={link.title} key={link.title} placement="left">
        <span>
          <IconButton
            size="small"
            onClick={handleClick}
            disabled={isDisabled}
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: '50%',
              color: theme.palette.mode === 'dark' ? '#f4f5f7' : theme.palette.primary.main,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(17,16,18,0.06)',
              transition: 'transform 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.14)'
                  : 'rgba(17,16,18,0.12)',
                transform: 'translateY(-1px)',
              },
            })}
          >
            {isLoading ? <CircularProgress size={18} /> : link.icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title="Quick links" placement="bottom">
        <IconButton
          id={buttonId}
          aria-haspopup="true"
          aria-controls={popoverOpen ? `${buttonId}-menu` : undefined}
          aria-expanded={popoverOpen ? 'true' : undefined}
          onClick={handleButtonClick}
          size="small"
          sx={{
            background: activeGradient,
            color: '#fff',
            width: 39,
            height: 39,
            borderRadius: '50%',
            boxShadow: 'none',
            '&:hover': { background: activeGradient, opacity: 0.9 },
          }}
        >
          {popoverOpen ? <CloseIcon fontSize="small" /> : <AppsIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Popover
        id={`${buttonId}-menu`}
        anchorEl={anchorEl}
        open={popoverOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          elevation: 0,
          sx: (theme) => ({
            mt: 1,
            px: 1,
            py: 1.5,
            borderRadius: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(23,27,34,0.98)' : '#ffffff',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(17,16,18,0.08)'}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 18px 40px rgba(0,0,0,0.45)'
              : '0 12px 32px rgba(17,16,18,0.12)',
            backdropFilter: 'blur(8px)',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              left: '50%',
              width: 12,
              height: 12,
              bgcolor: 'background.paper',
              transform: 'translate(-50%, -50%) rotate(45deg)',
              borderLeft: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(17,16,18,0.08)'}`,
              borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(17,16,18,0.08)'}`,
              zIndex: -1,
            },
          }),
        }}
      >
        {quickLinks.map(renderLinkIcon)}
      </Popover>
    </Box>
  );
};

// ===================================================================
// ==================== UPDATED SIDEBAR COMPONENT ====================
// ===================================================================

const DynamicLogo = () => {
  const { logoUrl, isUILoading } = useUI(); // Consume state from the provider

  // Show a spinner placeholder only if the UI is still loading
  if (isUILoading) {
    return <CircularProgress sx={{ color: 'white' }} size={30} />;
  }
  
  // If loading is finished and we have a URL, show the image
  if (logoUrl) {
    return <Box component="img" src={logoUrl} alt="Company Logo" sx={{ maxHeight: '50px', maxWidth: '80%', height: 'auto', width: 'auto' }} />;
  }

  // If loading is finished and there's no URL (error or default), show the fallback icon
  return <RocketLaunchIcon sx={{ fontSize: 50, color: 'primary.main', background: CATCH_GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />;
};


function Sidebar({ mobileOpen, handleDrawerToggle, selectedPage, setSelectedPage }) {
  const {
    lifeInsuranceAccessLevel,
    restrictedPageIds,
    hasExecutiveDashboard,
    hasAdminReports,
    user,
  } = useAuth();
  const restrictedSet = React.useMemo(() => new Set((restrictedPageIds || []).map(k => String(k).toLowerCase())), [restrictedPageIds]);
  const hideRestricted = React.useCallback((list) => list.filter(item => !restrictedSet.has(String(item.key).toLowerCase())), [restrictedSet]);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [acaOpen, setAcaOpen] = useState(false);
  const [ancillaryOpen, setAncillaryOpen] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState(() => {
    const defaultMode = hasExecutiveDashboard ? 'executive' : 'normal';
    if (typeof window === 'undefined') return defaultMode;
    try {
      const stored = window.localStorage.getItem('sabalSidebarMode');
      return stored === 'executive' || stored === 'normal' ? stored : defaultMode;
    } catch (_) {
      return defaultMode;
    }
  });
  const isExecutiveSidebar = hasExecutiveDashboard && sidebarMode === 'executive';

  useEffect(() => {
    if (!hasExecutiveDashboard && sidebarMode === 'executive') {
      setSidebarMode('normal');
    }
  }, [hasExecutiveDashboard, sidebarMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('sabalSidebarMode', sidebarMode);
    } catch (_) {}
  }, [sidebarMode]);

  const handleSidebarModeChange = useCallback((_, value) => {
    if (!value) return;
    setSidebarMode(value);
  }, []);

  const handleComplianceguardClick = useCallback(() => {
    if (!COMPLIANCEGUARD_PORTAL_PATH || !API_GATE_TOKEN) {
      console.error('ComplianceGuard portal URL or API token is missing.');
      return;
    }
    try {
      const userEmail = (user?.email || '').toLowerCase().trim();
      const baseUrl = new URL(COMPLIANCEGUARD_PORTAL_PATH);
      baseUrl.searchParams.set('api_token', API_GATE_TOKEN);
      if (userEmail) {
        baseUrl.searchParams.set('activeUserEmail', userEmail);
      }
      window.open(baseUrl.toString(), '_blank');
    } catch (error) {
      console.error('Failed to open ComplianceGuard AI portal:', error);
    }
  }, [user?.email]);

  const subMenuContainerSx = {
    px: 1.5,
    py: 1,
    mx: 1.5,
    my: 0.5,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)'
  };

  const subMenuButtonSx = {
    pl: 3,
    pr: 2,
    py: 0.6,
    borderRadius: 2,
    minHeight: 34,
    '& .MuiListItemIcon-root': {
      minWidth: 32,
      color: 'rgba(255,255,255,0.65)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
    '& .MuiListItemText-primary': {
      fontSize: '0.8rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(255,255,255,0.16)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.18)',
      },
      '& .MuiListItemText-primary': {
        color: '#fff',
      },
    },
  };

  // --- NAVIGATION ITEMS (REMOTE) ---
  const navItemsContext = useNavItems();
  const fetchedNavItems = navItemsContext?.navItems || {};
  const loadingNavItems = navItemsContext?.loadingNavItems;
  const navItemsError = navItemsContext?.navItemsError;
  const acaItemsRaw = navItemsContext?.acaItems || [];
  const loadingAcaItems = navItemsContext?.loadingAcaItems;
  const acaItemsError = navItemsContext?.acaItemsError;
  const {
    topLevelItems = [],
    allToolItems = [],
    allAncillaryItems = [],
    carrierItems = [],
    trainingItems = [],
  } = fetchedNavItems;

  // --- DYNAMIC FILTERING LOGIC ---
  const ancillaryItemsBase = useMemo(() => {
    if (lifeInsuranceAccessLevel === 'full') {
      return allAncillaryItems;
    }
    if (lifeInsuranceAccessLevel === 'restricted') {
      const allowedKeys = ['hospital-indemnity-resources', 'critical-illness-policy-resources', 'life-insurance'];
      return allAncillaryItems.filter(item => allowedKeys.includes(item.key));
    }
    // 'none' or any other case, hides Life Insurance from the Ancillary menu
    return allAncillaryItems.filter(item => item.key !== 'life-insurance');
  }, [lifeInsuranceAccessLevel, allAncillaryItems]);
  const ancillaryItems = useMemo(() => hideRestricted(ancillaryItemsBase), [ancillaryItemsBase, hideRestricted]);

  // --- UPDATED LOGIC TO CORRECTLY FILTER TOOLS ---
  const toolItemsBase = useMemo(() => {
    if (lifeInsuranceAccessLevel === 'full') {
        // 'full' access sees all tools
        return allToolItems;
    }
    if (lifeInsuranceAccessLevel === 'restricted') {
        // 'restricted' access only sees the quote tool
        return allToolItems.filter(item => item.key === 'life-insurance-quote-li');
    }
    // 'none' access (or any other default) hides the quote tool
    return allToolItems.filter(item => item.key !== 'life-insurance-quote-li');
  }, [lifeInsuranceAccessLevel, allToolItems]);
  const toolItems = useMemo(() => hideRestricted(toolItemsBase), [toolItemsBase, hideRestricted]);

  const visibleAcaItems = useMemo(() => hideRestricted(acaItemsRaw), [acaItemsRaw, hideRestricted]);
  // Apply restrictions to other static groups
  const visibleTopLevelItems = useMemo(() => hideRestricted(topLevelItems), [topLevelItems, hideRestricted]);
  const visibleCarrierItems = useMemo(() => hideRestricted(carrierItems), [carrierItems, hideRestricted]);
  const visibleTrainingItems = useMemo(() => hideRestricted(trainingItems), [trainingItems, hideRestricted]);
  // --- END OF FIX ---

  // --- JSX TO RENDER THE SIDEBAR ---
  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ justifyContent: 'center', py: 2, minHeight: 80 }}>
        <DynamicLogo actionValue="react-logo1" />
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {hasExecutiveDashboard && (
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 0.5 }}>
              Sidebar View
            </Typography>
            <ToggleButtonGroup
              value={isExecutiveSidebar ? 'executive' : 'normal'}
              exclusive
              size="small"
              fullWidth
              onChange={handleSidebarModeChange}
              sx={{
                mt: 1,
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.04)',
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: 'none',
                  color: 'rgba(255,255,255,0.75)',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.16)',
                    color: '#fff',
                  },
                },
              }}
            >
              <ToggleButton value="normal">Normal</ToggleButton>
              <ToggleButton value="executive">Executive</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {isExecutiveSidebar ? (
          <>
            <ListItem sx={{ mt: 1 }}>
              <ListItemText
                primary="Executive Shortcuts"
                primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleComplianceguardClick}>
                <ListItemIcon>
                  <ShieldIcon />
                </ListItemIcon>
                <ListItemText
                  primary="ComplianceGuard AI Portal"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            </ListItem>
            {hasAdminReports && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSelectedPage('admin-reports');
                    if (handleDrawerToggle) handleDrawerToggle();
                  }}
                >
                  <ListItemIcon>
                    <BuildIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Other Applications"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </>
        ) : (
          <>
            {loadingNavItems && (
              <ListItem>
                <ListItemIcon>
                  <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Loading navigation..."
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItem>
            )}
            {navItemsError && !loadingNavItems && (
              <ListItem>
                <ListItemText
                  primary="Navigation failed to load"
                  secondary={navItemsError}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}
                />
              </ListItem>
            )}

            {visibleTopLevelItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={selectedPage === item.key}
                  onClick={() => {
                    setSelectedPage(item.key);
                    if (handleDrawerToggle) handleDrawerToggle();
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem disablePadding>
              <ListItemButton onClick={() => setToolsOpen(!toolsOpen)}>
                <ListItemIcon>
                  <BuildIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Tools"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
                {toolsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={toolsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={subMenuContainerSx}>
                {toolItems.map((item) => {
                  if (item.type === 'link') {
                    return (
                      <ListItemButton
                        component="a"
                        href={item.href}
                        target="_blank"
                        key={item.key}
                        sx={subMenuButtonSx}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
                        />
                      </ListItemButton>
                    );
                  }
                  return (
                    <ListItemButton
                      key={item.key}
                      sx={subMenuButtonSx}
                      selected={selectedPage === item.key}
                      onClick={() => {
                        setSelectedPage(item.key);
                        if (handleDrawerToggle) handleDrawerToggle();
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
            {(loadingAcaItems || visibleAcaItems.length > 0 || acaItemsError) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => setAcaOpen(!acaOpen)}>
                    <ListItemIcon>
                      <HealthAndSafetyIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="ACA Resources"
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                    {acaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={acaOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={subMenuContainerSx}>
                    {loadingAcaItems && (
                      <ListItem>
                        <ListItemIcon>
                          <CircularProgress
                            size={18}
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Loading ACA resources..."
                          primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
                        />
                      </ListItem>
                    )}
                    {!loadingAcaItems && acaItemsError && (
                      <ListItem>
                        <ListItemText
                          primary="Failed to load ACA resources"
                          secondary={acaItemsError}
                          primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
                          secondaryTypographyProps={{
                            fontSize: '0.75rem',
                            color: 'rgba(255,255,255,0.7)',
                          }}
                        />
                      </ListItem>
                    )}
                    {!loadingAcaItems &&
                      visibleAcaItems.map((item) => (
                        <ListItemButton
                          key={item.key}
                          sx={subMenuButtonSx}
                          selected={selectedPage === item.key}
                          onClick={() => {
                            setSelectedPage(item.key);
                            if (handleDrawerToggle) handleDrawerToggle();
                          }}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: '0.8rem',
                              fontWeight: 500,
                            }}
                          />
                        </ListItemButton>
                      ))}
                  </List>
                </Collapse>
              </>
            )}
            <ListItem disablePadding>
              <ListItemButton onClick={() => setAncillaryOpen(!ancillaryOpen)}>
                <ListItemIcon>
                  <BoltIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Supplemental & Ancillary"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
                {ancillaryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={ancillaryOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={subMenuContainerSx}>
                {ancillaryItems.map((item) => (
                  <ListItemButton
                    key={item.key}
                    sx={subMenuButtonSx}
                    selected={selectedPage === item.key}
                    onClick={() => {
                      setSelectedPage(item.key);
                      if (handleDrawerToggle) handleDrawerToggle();
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
            {visibleCarrierItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={selectedPage === item.key}
                  onClick={() => {
                    setSelectedPage(item.key);
                    if (handleDrawerToggle) handleDrawerToggle();
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
            <ListItem disablePadding>
              <ListItemButton onClick={() => setTrainingOpen(!trainingOpen)}>
                <ListItemIcon>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Training & Education"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
                {trainingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={trainingOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={subMenuContainerSx}>
                {visibleTrainingItems.map((item) => (
                  <ListItemButton
                    key={item.key}
                    sx={subMenuButtonSx}
                    selected={selectedPage === item.key}
                    onClick={() => {
                      setSelectedPage(item.key);
                      if (handleDrawerToggle) handleDrawerToggle();
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxsizing: 'border-box', width: drawerWidth } }}>
        {drawerContent}
      </Drawer>
      <Drawer 
        variant="permanent" 
        sx={{ 
          display: { xs: 'none', sm: 'block' }, 
          '& .MuiDrawer-paper': { 
            boxsizing: 'border-box', 
            width: drawerWidth,
            overflowY: 'auto'
          } 
        }} 
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}

// ===================================================================
// ==================== UPDATED HEADER COMPONENT =====================
// ===================================================================
function Header({ handleDrawerToggle, onAdminClick, onRetentionClick, onDashboardClick }) {
  const {
    user,
    logout,
    hasAdminReports,
    hasRetentionAccess,
    openPolarisWindow,
    shouldAnimatePolarisIcon,
    acknowledgePolarisIconAnimation,
    agentDirectoryProfile,
  } = useAuth();
  const { searchTerm, setSearchTerm } = useSearch();
  const { activeGradient } = useUI();
  const [anchorEl, setAnchorEl] = useState(null);
  const [polarisIconHopping, setPolarisIconHopping] = useState(false);
  const [polarisLaunching, setPolarisLaunching] = useState(false);
  const resolvedUserName = useMemo(() => {
    const name = (user?.name || '').trim();
    if (name) return name;
    const directoryName = (agentDirectoryProfile?.agent_name || '').trim();
    if (directoryName) return directoryName;
    const emailLocal = (user?.email || '').split('@')[0] || '';
    return emailLocal ? emailLocal.trim() : 'Agent';
  }, [user?.name, user?.email, agentDirectoryProfile?.agent_name]);
  const avatarInitial = useMemo(() => {
    const source = resolvedUserName || user?.email || 'A';
    return source.trim().charAt(0).toUpperCase() || 'A';
  }, [resolvedUserName, user?.email]);

  useEffect(() => {
    if (shouldAnimatePolarisIcon) {
      setPolarisIconHopping(true);
    } else {
      setPolarisIconHopping(false);
    }
  }, [shouldAnimatePolarisIcon]);

  const handlePolarisAnimationEnd = useCallback(() => {
    setPolarisIconHopping(false);
    if (typeof acknowledgePolarisIconAnimation === 'function') {
      acknowledgePolarisIconAnimation();
    }
  }, [acknowledgePolarisIconAnimation]);

  const handleAdminClick = () => {
    onAdminClick(); // This function is passed down from AuthenticatedApp
    setAnchorEl(null);
  };

  const handleRetentionClick = () => {
    onRetentionClick(); // This function is passed down from AuthenticatedApp
    setAnchorEl(null);
  };
  
  const handleDashboardClick = () => {
    onDashboardClick(); // This function is passed down from AuthenticatedApp
    setAnchorEl(null);
  };

  const handlePolarisClick = useCallback(async () => {
    if (polarisLaunching) return;
    setPolarisLaunching(true);
    try {
      await openPolarisWindow();
    } catch (error) {
      console.error('Failed to launch Polaris:', error);
    } finally {
      setPolarisLaunching(false);
    }
  }, [openPolarisWindow, polarisLaunching]);

  return (
    <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}>
      <Toolbar>
        <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
          <MenuIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, maxWidth: 500 }}>
           <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search all resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              endAdornment: searchTerm ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchTerm('')}><CloseIcon /></IconButton></InputAdornment> : null,
              sx: { borderRadius: '999px', backgroundColor: 'background.default' }
            }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <SlackAppsMenu />
            <Tooltip title={polarisLaunching ? 'Launching Polaris…' : 'Polaris AI Chat'}>
              <span>
                <IconButton
                  onClick={handlePolarisClick}
                  size="small"
                  aria-label="Open Polaris"
                  disabled={polarisLaunching}
                  sx={{ p: 0.5 }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: 37,
                      height: 37,
                    }}
                  >
                    <Box
                      component="img"
                      src={POLARIS_ICON_URL}
                      alt="Polaris"
                      onAnimationEnd={handlePolarisAnimationEnd}
                      sx={{
                        width: '100%',
                        height: '100%',
                        transformOrigin: 'center bottom',
                        opacity: polarisLaunching ? 0.3 : 1,
                        ...(polarisIconHopping
                          ? { animation: `${polarisHopKeyframes} 0.45s ease-in-out 10` }
                          : {}),
                      }}
                    />
                    {polarisLaunching && (
                      <CircularProgress
                        size={28}
                        thickness={5}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    )}
                  </Box>
                </IconButton>
              </span>
            </Tooltip>
            <RadialMenu />
            <Tooltip title="Account">
              <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
                <Avatar sx={{ bgcolor: '#111012' }}>{avatarInitial}</Avatar>
              </IconButton>
            </Tooltip>
            
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      minWidth: 220,
                      '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1, },
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{resolvedUserName}</Typography>
                    <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                </Box>
                <Divider />

                {/* Dashboard Link */}
                <MenuItem onClick={handleDashboardClick}>
                    <ListItemIcon>
                        <DashboardIcon fontSize="small" />
                    </ListItemIcon>
                    Dashboard
                </MenuItem>

                {/* Conditional Admin Chip is shown if the user has admin-reports permissions */}
                {hasAdminReports && (
                    <MenuItem onClick={handleAdminClick}>
                        <ListItemIcon>
                            <AdminPanelSettingsIcon fontSize="small"/>
                        </ListItemIcon>
                        Admin Panel
                    </MenuItem>
                )}
                
                {/* Conditional Retention Chip is shown if the user has retention-resources permissions */}
                {hasRetentionAccess && (
                    <MenuItem onClick={handleRetentionClick}>
                        <ListItemIcon>
                            <AssuredWorkloadIcon fontSize="small" />
                        </ListItemIcon>
                        Retention
                    </MenuItem>
                )}
                
                <MenuItem onClick={logout}>
                    <ListItemIcon>
                        <LoginIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

// --- PAGE CONTENT COMPONENTS ---

function GeneralDocsPage() {
  const { pagesConfig } = usePages();
  const config = pagesConfig?.['general-docx'];
  const isMobile = useIsMobile();
  if (!config) return null;
  const { layout } = config;

  if (isMobile) {
    return (
      <AMCard>
        <AMCard.Body>
          <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
            <AMSpace align="center" style={{ fontSize: 18, fontWeight: 700 }}>
              {config.icon}
              <span>{config.title}</span>
            </AMSpace>
            {layout.accordions.map((acc) => (
              <ApiAccordion key={acc.actionValue} {...acc} />
            ))}
            <AMGrid columns={2} gap={10}>
              {layout.bottomButtons.map((btn, index) => (
                <AMGrid.Item key={index}>
                  <ResourceButton {...btn} />
                </AMGrid.Item>
              ))}
            </AMGrid>
            <ResourceButton {...layout.sidebarButton} />
          </AMSpace>
        </AMCard.Body>
      </AMCard>
    );
  }

  return (
    <AntCard
      title={
        <AntSpace align="center">
          {config.icon}
          <span>{config.title}</span>
        </AntSpace>
      }
      bordered={false}
    >
      <AntRow gutter={[24, 24]}>
        <AntCol xs={24} md={18}>
          <div>
            {layout.accordions.map((acc) => (
              <ApiAccordion key={acc.actionValue} {...acc} />
            ))}
          </div>
          <AntRow gutter={[16, 16]} style={{ marginTop: 16 }}>
            {layout.bottomButtons.map((btn, index) => (
              <AntCol xs={24} sm={12} md={8} key={index}>
                <ResourceButton {...btn} />
              </AntCol>
            ))}
          </AntRow>
        </AntCol>
        <AntCol xs={24} md={6}>
          <ResourceButton
            {...layout.sidebarButton}
            sx={{
              height: '100%',
              minHeight: 200,
              fontSize: '1.1rem',
              fontWeight: 'bold',
            }}
          />
        </AntCol>
      </AntRow>
    </AntCard>
  );
}

const GenericPage = ({ pageKey }) => {
  const { getAdminReportButtons } = useAuth(); // Get the permissions helper
  const { pagesConfig } = usePages();
  const config = pagesConfig?.[pageKey];
  
  if (!config) return <Typography>Page not found: {pageKey}</Typography>;

  // --- NEW LOGIC: Filter items specifically for the admin-reports page ---
  let itemsToRender = config.items || [];
  if (pageKey === 'admin-reports') {
    const allowedButtons = getAdminReportButtons; // This is either 'all' or an array of keys
    
    if (allowedButtons !== 'all') {
      // If it's not 'all', it must be an array of allowed keys. Filter the items.
      itemsToRender = itemsToRender.filter(item => 
        allowedButtons.includes(item.key)
      );
    }
    // If allowedButtons is 'all', itemsToRender remains the full list, which is correct.
  }
  // --- END OF NEW LOGIC ---

  return (
    <ResourceCard
      title={config.title}
      icon={config.icon}
      description={config.description}
      items={itemsToRender} // Use the potentially filtered list of items
      grouped={config.grouped}
      groups={config.groups || []}
      display={config.display}
      pageKey={pageKey}
      color={config.color}
      sidebarIcon={config.sidebarIcon}
    />
  );
};

function MainContent({ page }) {
  const { pagesConfig } = usePages();
  const { hasAccess, dashboardData, dashboardLoading, refreshDashboardData } = useAuth(); // Add dashboard props
  if (!hasAccess(page)) {
    return <Typography sx={{p:3}}>You do not have permission to view this page.</Typography>;
  }

  const config = pagesConfig?.[page];
  if (page === 'life-insurance-quote-li') {
    return <LifeQuoteTool />;
  }
  
  // Update logic to pass props to the Dashboard component
  if (config?.display === 'component') {
    if (page === 'dashboard') {
        // Pass the fetched data and loading state as props
        return <Dashboard data={dashboardData} loading={dashboardLoading} onRefreshDashboard={refreshDashboardData} />;
    }
    // Render other components as before
    return config.component;
  }
  
  return <GenericPage pageKey={page} />;
}


const SearchResults = () => {
  const { searchTerm, searchableData } = useSearch();
  const { pagesConfig } = usePages();
  const { getAdminReportButtons } = useAuth();
  const isMobile = useIsMobile();

  const adminReportAccess = useMemo(() => {
    if (getAdminReportButtons === 'all') {
      return { isAll: true, allowed: new Set() };
    }
    if (Array.isArray(getAdminReportButtons)) {
      return {
        isAll: false,
        allowed: new Set(
          getAdminReportButtons
            .map(normalizeButtonId)
            .filter(Boolean)
        ),
      };
    }
    return { isAll: false, allowed: new Set() };
  }, [getAdminReportButtons]);

  const filtered = useMemo(() => {
    if (!searchTerm) return [];
    const term = searchTerm.toLowerCase();
    
    // Updated filtering to also search within the item's description
    return searchableData.filter(item => {
      const matchesTerm =
        item.label.toLowerCase().includes(term) ||
        (item.description && item.description.toLowerCase().includes(term)) ||
        (item.tooltip && item.tooltip.toLowerCase().includes(term)) ||
        (pagesConfig[item.pageKey]?.title.toLowerCase().includes(term));

      if (!matchesTerm) return false;

      const normalizedPageKey = String(item.pageKey || '').toLowerCase();
      if (normalizedPageKey !== 'admin-reports') return true;
      if (adminReportAccess.isAll) return true;
      const buttonId = getItemButtonId(item);
      return buttonId && adminReportAccess.allowed.has(buttonId);
    });
  }, [searchTerm, searchableData, pagesConfig, adminReportAccess]);

  if (!searchTerm) return null;

  if (isMobile) {
    return (
      <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          Search Results for "{searchTerm}"
        </div>
        {filtered.length > 0 ? (
          <AMList mode="card">
            {filtered.map((item, index) => (
              <ResourceListItem key={`${item.actionValue}-${index}`} item={item} />
            ))}
          </AMList>
        ) : (
          <div style={{ color: '#667085' }}>No results found.</div>
        )}
      </AMSpace>
    );
  }

  return (
    <AntSpace direction="vertical" style={{ width: '100%' }}>
      <AntTypography.Title level={4}>
        Search Results for "{searchTerm}"
      </AntTypography.Title>
      {filtered.length > 0 ? (
        <AntCard>
          <AntList>
            {filtered.map((item, index) => (
              <ResourceListItem key={`${item.actionValue}-${index}`} item={item} />
            ))}
          </AntList>
        </AntCard>
      ) : (
        <AntTypography.Text>No results found.</AntTypography.Text>
      )}
    </AntSpace>
  );
};

const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'background.default', // Uses the theme's default background color
      zIndex: (theme) => theme.zIndex.drawer + 1, // Ensures it's on top of everything
    }}
  >
    <CircularProgress color="primary" />
  </Box>
);

const useIdleTimer = (onIdle, idleTime) => {
  const timeoutId = useRef(null);

  // Function to start or reset the inactivity timer
  const startTimer = useCallback(() => {
    // Clear any existing timer
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    // Set a new timer
    timeoutId.current = setTimeout(onIdle, idleTime);
  }, [onIdle, idleTime]);

  // Function to handle user activity events
  const handleActivity = useCallback(() => {
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    // List of events that indicate user activity
    const activityEvents = [
      'mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'
    ];

    // Add event listeners for each activity type
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Start the initial timer
    startTimer();

    // Cleanup function to remove listeners when the component unmounts
    return () => {
      clearTimeout(timeoutId.current);
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity, startTimer]); // Rerun effect only if these functions change
};

function AuthenticatedApp() {
  const { loading: isAuthLoading, /* needsOnboarding, */ dashboardLoading, user } = useAuth(); // onboarding disabled
  const { isUILoading } = useUI();
  const { loadingPages } = usePages();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState('dashboard'); // Default to dashboard
  const { searchTerm } = useSearch();
  const showDashboardPath = selectedPage !== 'dashboard' && !searchTerm;
  const [showPolarisIntro, setShowPolarisIntro] = useState(false);
  const [polarisIntroPage, setPolarisIntroPage] = useState(1);
  const [dashboardUpdateAcknowledgementChecked, setDashboardUpdateAcknowledgementChecked] = useState(false);
  const [dashboardUpdateState, setDashboardUpdateState] = useState({
    loading: false,
    error: null,
    content: null,
  });
  const normalizeUpdatesPayload = useCallback((payload) => {
    const pickFirst = (value) => {
      if (!value) return null;
      if (Array.isArray(value)) {
        if (value.length === 0) return null;
        if (value.length === 1) return value[0];
        return value[0];
      }
      return value;
    };

    let root = payload;
    if (root && typeof root === 'object') {
      if (root.updates) root = pickFirst(root.updates);
      else if (root.data) root = pickFirst(root.data);
      else if (root.results) root = pickFirst(root.results);
    }

    if (typeof root === 'string') {
      const trimmed = root.trim();
      if (!trimmed) return null;
      return {
        title: null,
        postedDatetime: null,
        page1Content: trimmed,
        page2Content: '',
        includesSecondPage: false,
        includesConfirmCheckbox: false,
        confirmCheckboxLabel: null,
        includesHyperlinkButton: false,
        hyperlinkButtonTarget: '',
        numOfDisplayTimes: 0,
      };
    }

    if (Array.isArray(root)) {
      const first = pickFirst(root);
      return normalizeUpdatesPayload(first ?? '');
    }

    if (!root || typeof root !== 'object') {
      return null;
    }

    const includesSecondPageRaw =
      root.includes_second_page ??
      root.includesSecondPage ??
      root.includeSecondPage ??
      null;
    const includesSecondPage =
      includesSecondPageRaw == null
        ? Boolean(root.modal_page_2_content || root.page2_content || root.page2Content)
        : Boolean(includesSecondPageRaw);
    const includesConfirmCheckbox = Boolean(
      root.includes_confirm_checkbox ?? root.includesConfirmCheckbox ?? false
    );
    const includesHyperlinkButton = Boolean(
      root.includes_hyperlink_button ?? root.includesHyperlinkButton ?? false
    );
    const hyperlinkButtonTarget =
      root.hyperlink_button_target ||
      root.hyperlinkButtonTarget ||
      root.hyperlink_target ||
      root.hyperlinkTarget ||
      '';
    const numOfDisplayTimesRaw =
      root.num_of_display_times ?? root.numOfDisplayTimes ?? root.display_count ?? root.displayCount;
    const hasDisplayLimit = numOfDisplayTimesRaw != null;
    const numOfDisplayTimes = Number.isFinite(Number(numOfDisplayTimesRaw))
      ? Number(numOfDisplayTimesRaw)
      : hasDisplayLimit
        ? 0
        : null;

    const page1Content =
      root.modal_page_1_content ||
      root.page1_content ||
      root.page1Content ||
      root.content ||
      root.message ||
      '';
    const trimmedPage1 = String(page1Content || '').trim();
    if (!trimmedPage1) {
      return null;
    }

    return {
      title: root.title || root.heading || root.subject || null,
      postedDatetime:
        root.posted_datetime ||
        root.postedDatetime ||
        root.updated_at ||
        root.updatedAt ||
        root.date ||
        null,
      page1Content: trimmedPage1,
      page2Content:
        root.modal_page_2_content ||
        root.page2_content ||
        root.page2Content ||
        '',
      includesSecondPage,
      includesConfirmCheckbox,
      confirmCheckboxLabel:
        root.confirm_checkbox_label ||
        root.confirmCheckboxLabel ||
        root.ackLabel ||
        null,
      includesHyperlinkButton: includesHyperlinkButton && Boolean(hyperlinkButtonTarget),
      hyperlinkButtonTarget,
      numOfDisplayTimes,
    };
  }, []);

  const buildUpdateStorageKey = useCallback((payload, email) => {
    const normalizedEmail = (email || '').toLowerCase();
    const stamp = payload?.postedDatetime || '';
    const seed = stamp || String(payload?.page1Content || '').slice(0, 32) || 'default';
    return `updatesModalCount:${POLARIS_INTRO_STORAGE_VERSION}:${normalizedEmail}:${seed}`;
  }, []);

  useEffect(() => {
    if (!user?.email) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;

    const loadUpdates = async () => {
      setDashboardUpdateState({ loading: true, error: null, content: null });
      let normalized = null;
      let errorMessage = null;
      try {
        let updatesUrl = UPDATES_URL;
        let sameOrigin = true;
        if (typeof window !== 'undefined') {
          try {
            const resolved = new URL(UPDATES_URL, window.location.origin);
            updatesUrl = resolved.toString();
            sameOrigin = resolved.origin === window.location.origin;
          } catch (_) {
            sameOrigin = true;
          }
        }
        const response = await fetch(updatesUrl, {
          method: 'GET',
          credentials: sameOrigin ? 'include' : 'omit',
          headers: { accept: 'application/json, text/plain, text/html' },
        });
        const rawText = await response.text();
        if (!response.ok) {
          throw new Error(rawText || `Updates API responded with ${response.status}`);
        }
        let parsed = null;
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          parsed = JSON.parse(rawText);
        } else {
          try {
            parsed = JSON.parse(rawText);
          } catch (_) {
            parsed = rawText;
          }
        }
        normalized = normalizeUpdatesPayload(parsed);
      } catch (error) {
        errorMessage = error?.message || 'Failed to load dashboard update.';
      }

      if (!normalized) {
        if (!cancelled) {
          setDashboardUpdateState({
            loading: false,
            error: errorMessage,
            content: null,
          });
          setShowPolarisIntro(false);
        }
        return;
      }

      const displayLimit = normalized?.numOfDisplayTimes;
      let shouldShow = false;
      if (Number.isFinite(displayLimit)) {
        const maxDisplays = Number(displayLimit);
        if (maxDisplays > 0) {
          try {
            const storageKey = buildUpdateStorageKey(normalized, user.email);
            const raw = window.localStorage.getItem(storageKey);
            let count = raw ? parseInt(raw, 10) : 0;
            if (!Number.isFinite(count)) count = 0;
            if (count < maxDisplays) {
              window.localStorage.setItem(storageKey, String(count + 1));
              shouldShow = true;
            }
          } catch (_) {
            shouldShow = true;
          }
        } else {
          // Treat 0/negative limits as "show once" to avoid suppressing valid updates.
          try {
            const storageKey = buildUpdateStorageKey(normalized, user.email);
            const raw = window.localStorage.getItem(storageKey);
            let count = raw ? parseInt(raw, 10) : 0;
            if (!Number.isFinite(count)) count = 0;
            if (count < 1) {
              window.localStorage.setItem(storageKey, '1');
              shouldShow = true;
            }
          } catch (_) {
            shouldShow = true;
          }
        }
      } else {
        // If no display limit is provided, show once per update seed.
        try {
          const storageKey = buildUpdateStorageKey(normalized, user.email);
          const raw = window.localStorage.getItem(storageKey);
          let count = raw ? parseInt(raw, 10) : 0;
          if (!Number.isFinite(count)) count = 0;
          if (count < 1) {
            window.localStorage.setItem(storageKey, '1');
            shouldShow = true;
          }
        } catch (_) {
          shouldShow = true;
        }
      }

      if (!cancelled) {
        setDashboardUpdateState({
          loading: false,
          error: errorMessage,
          content: normalized,
        });
        setShowPolarisIntro(shouldShow);
      }
    };

    loadUpdates();

    return () => {
      cancelled = true;
    };
  }, [user?.email, normalizeUpdatesPayload, buildUpdateStorageKey]);

  useEffect(() => {
    if (!showPolarisIntro) return;
    setPolarisIntroPage(1);
    setDashboardUpdateAcknowledgementChecked(false);
  }, [showPolarisIntro]);

  const renderUpdateInline = useCallback((text = '') => {
    const parts = String(text).split(/(\\*\\*[^*]+\\*\\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`u-bold-${idx}`}>{part.slice(2, -2)}</strong>;
      }
      return <span key={`u-text-${idx}`}>{part}</span>;
    });
  }, []);

  const renderUpdateParagraphs = useCallback((text = '') => {
    const lines = String(text)
      .split(/\\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (!lines.length) return null;
    const bulletLines = lines.filter((line) => /^[-*•]\\s+/.test(line));
    const numberedLines = lines.filter((line) => /^\\d+\\.\\s+/.test(line));
    if (bulletLines.length === lines.length) {
      return (
        <Box component="ul" sx={{ pl: 3, mb: 0 }}>
          {lines.map((line, idx) => (
            <li key={`u-bullet-${idx}`}>{renderUpdateInline(line.replace(/^[-*•]\\s+/, ''))}</li>
          ))}
        </Box>
      );
    }
    if (numberedLines.length === lines.length) {
      return (
        <Box component="ol" sx={{ pl: 3, mb: 0 }}>
          {lines.map((line, idx) => (
            <li key={`u-num-${idx}`}>{renderUpdateInline(line.replace(/^\\d+\\.\\s+/, ''))}</li>
          ))}
        </Box>
      );
    }
    return lines.map((line, idx) => (
      <Typography key={`u-para-${idx}`}>{renderUpdateInline(line)}</Typography>
    ));
  }, [renderUpdateInline]);

  const renderUpdateContent = useCallback(
    (text = '') => {
      const content = String(text || '').trim();
      if (!content) return null;
      if (/<[^>]+>/.test(content)) {
        return (
          <Box
            sx={{
              '& h1, & h2, & h3': { fontWeight: 700, mt: 2, mb: 1 },
              '& p': { mb: 1.5 },
              '& ul, & ol': { pl: 3, mb: 1.5 },
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        );
      }
      return renderUpdateParagraphs(content);
    },
    [renderUpdateParagraphs]
  );

  const effectiveDashboardUpdate = dashboardUpdateState.content;
  const dashboardUpdateTitle = effectiveDashboardUpdate?.title || null;
  const normalizePostedLabel = (value) => {
    if (value == null) return null;
    const raw = typeof value === 'string' ? value.trim() : value;
    if (raw === '') return null;
    let parsed;
    if (typeof raw === 'number') {
      parsed = new Date(raw);
    } else if (typeof raw === 'string' && /^\d+$/.test(raw)) {
      parsed = new Date(Number(raw));
    } else {
      parsed = new Date(raw);
    }
    if (!Number.isFinite(parsed.getTime())) return null;
    return parsed.toLocaleString();
  };
  const dashboardUpdatePostedLabel = normalizePostedLabel(effectiveDashboardUpdate?.postedDatetime);

  const updateHasSecondPage = Boolean(effectiveDashboardUpdate?.includesSecondPage);
  const updateTotalPages = updateHasSecondPage ? 2 : 1;
  const updatePage = Math.min(polarisIntroPage, updateTotalPages);
  const updatePageContent =
    updateHasSecondPage && updatePage === 2
      ? effectiveDashboardUpdate?.page2Content
      : effectiveDashboardUpdate?.page1Content;
  const requiresConfirmation = Boolean(effectiveDashboardUpdate?.includesConfirmCheckbox);
  const updateConfirmLabel = requiresConfirmation
    ? (effectiveDashboardUpdate?.confirmCheckboxLabel || 'I understand these updates')
    : '';
  const showConfirmationCheckbox = requiresConfirmation && updatePage === updateTotalPages;
  const canCloseUpdate =
    updatePage === updateTotalPages &&
    (!requiresConfirmation || dashboardUpdateAcknowledgementChecked);
  const showHyperlinkButton =
    Boolean(effectiveDashboardUpdate?.includesHyperlinkButton) &&
    Boolean(effectiveDashboardUpdate?.hyperlinkButtonTarget);
  const handleUpdateClose = useCallback((event, reason) => {
    if (!canCloseUpdate) return;
    setShowPolarisIntro(false);
  }, [canCloseUpdate]);
  const handleUpdateNext = useCallback(() => {
    if (updatePage < updateTotalPages) {
      setPolarisIntroPage((prev) => Math.min(prev + 1, updateTotalPages));
    }
  }, [updatePage, updateTotalPages]);
  const handleUpdateBack = useCallback(() => {
    if (updatePage > 1) {
      setPolarisIntroPage((prev) => Math.max(prev - 1, 1));
    }
  }, [updatePage]);

  // --- START: INACTIVITY TIMER ---
  // Define the function that will be executed when the user is idle.
  const handleIdle = () => {
    console.log("User has been idle for 15 minutes. Reloading page.");
  };

  // Set up the idle timer. 900000 milliseconds = 15 minutes.
  useIdleTimer(handleIdle, 900000);
  // --- END: INACTIVITY TIMER ---


  // Onboarding redirect disabled
  // useEffect(() => {
  //   if (!isAuthLoading && !isUILoading && !dashboardLoading && needsOnboarding) {
  //     window.location.href = "https://app.sabal-vega-app.com/onboarding";
  //   }
  // }, [isAuthLoading, isUILoading, dashboardLoading, needsOnboarding]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Update loading condition to include dashboardLoading
  if (isAuthLoading || isUILoading || dashboardLoading || loadingPages /* || needsOnboarding */) {
    return <LoadingScreen />;
  }
   
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} selectedPage={selectedPage} setSelectedPage={setSelectedPage} />
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          display: 'flex',
          flexDirection: 'column',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
         <Header 
            handleDrawerToggle={handleDrawerToggle} 
            onAdminClick={() => setSelectedPage('admin-reports')} 
            onRetentionClick={() => setSelectedPage('retention-resources')}
            onDashboardClick={() => setSelectedPage('dashboard')}
         />
         <Dialog
           open={showPolarisIntro}
           onClose={handleUpdateClose}
           maxWidth="sm"
           fullWidth
           disableEscapeKeyDown={!canCloseUpdate}
         >
           {(dashboardUpdateTitle || dashboardUpdatePostedLabel) && (
             <DialogTitle>
               {dashboardUpdateTitle || dashboardUpdatePostedLabel}
             </DialogTitle>
           )}
           <DialogContent
             dividers
             sx={{ maxHeight: { xs: '75vh', sm: '65vh' }, overflowY: 'auto' }}
           >
             <Stack spacing={2}>
               {updateTotalPages > 1 && (
                 <Typography variant="overline" color="text.secondary">
                   Page {updatePage} of {updateTotalPages}
                 </Typography>
               )}
               {dashboardUpdateState.loading ? (
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <CircularProgress size={18} />
                   <Typography variant="body2" color="text.secondary">
                     Loading update…
                   </Typography>
                 </Box>
               ) : dashboardUpdateState.error ? (
                 <Alert severity="warning">
                   Unable to load the update: {dashboardUpdateState.error}
                 </Alert>
               ) : (
                 <>
                   {renderUpdateContent(updatePageContent)}
                   {dashboardUpdatePostedLabel ? (
                     <Typography variant="caption" color="text.secondary">
                       {dashboardUpdatePostedLabel}
                     </Typography>
                   ) : null}
                   {showConfirmationCheckbox && updateConfirmLabel && (
                     <FormControlLabel
                       sx={{ alignItems: 'center', m: 0 }}
                       control={
                         <Checkbox
                           checked={dashboardUpdateAcknowledgementChecked}
                           onChange={(event) =>
                             setDashboardUpdateAcknowledgementChecked(event.target.checked)
                           }
                         />
                       }
                       label={<Typography variant="body2">{updateConfirmLabel}</Typography>}
                     />
                   )}
                 </>
               )}
             </Stack>
           </DialogContent>
           <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
             <Typography variant="caption" color="text.secondary">
               {updateTotalPages > 1 ? `Page ${updatePage} of ${updateTotalPages}` : ''}
             </Typography>
             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
               {updatePage > 1 && (
                 <Button color="inherit" onClick={handleUpdateBack}>
                   Back
                 </Button>
               )}
               {updatePage < updateTotalPages && (
                 <Button variant="contained" onClick={handleUpdateNext}>
                   Next
                 </Button>
               )}
               {showHyperlinkButton && (
                 <Button
                   component="a"
                   href={effectiveDashboardUpdate?.hyperlinkButtonTarget}
                   target="_blank"
                   rel="noopener noreferrer"
                   variant="outlined"
                 >
                   Open Link
                 </Button>
               )}
               <Button
                 onClick={handleUpdateClose}
                 disabled={!canCloseUpdate}
                 color="inherit"
               >
                 Close
               </Button>
             </Box>
           </DialogActions>
         </Dialog>
         <Toolbar />
         
         <Box
           sx={{
             flexGrow: 1,
             overflowY: 'auto',
             p: { xs: 2, sm: 3 },
           }}
         >
           {showDashboardPath && (
             <Button
               variant="text"
               size="small"
               startIcon={<HomeIcon />}
               onClick={() => setSelectedPage('dashboard')}
               sx={{
                 textTransform: 'none',
                 fontWeight: 600,
                 mt: -2,
                 mb: 0.5,
                 px: 0.5,
                 py: 0,
                 minHeight: 28,
                }}
              >
                Back to Dashboard
             </Button>
           )}
           {searchTerm ? <SearchResults /> : <MainContent page={selectedPage} />}
         </Box>
      </Box>
    </Box>
  );
}

const SearchInitializer = ({ children }) => {
  const { pagesConfig } = usePages();
  const { getAdminReportButtons } = useAuth();

  const adminReportAccess = useMemo(() => {
    if (getAdminReportButtons === 'all') {
      return { isAll: true, allowed: new Set() };
    }
    if (Array.isArray(getAdminReportButtons)) {
      return {
        isAll: false,
        allowed: new Set(
          getAdminReportButtons
            .map(normalizeButtonId)
            .filter(Boolean)
        ),
      };
    }
    return { isAll: false, allowed: new Set() };
  }, [getAdminReportButtons]);

  const searchableData = useMemo(() => {
    const allItems = [];
    if (!pagesConfig) return allItems;

    const shouldIndexItem = (item, pageKey) => {
      const normalizedPageKey = String(pageKey || '').toLowerCase();
      if (normalizedPageKey !== 'admin-reports') return true;
      if (adminReportAccess.isAll) return true;
      const buttonId = getItemButtonId(item);
      return buttonId && adminReportAccess.allowed.has(buttonId);
    };

    for (const pageKey in pagesConfig) {
      const config = pagesConfig[pageKey];
      if (!config) continue;
      if (config.grouped) {
        (config.groups || []).forEach(group => {
          (group.items || []).forEach(item => {
            if (shouldIndexItem(item, pageKey)) {
              allItems.push({ ...item, pageKey, description: item.description || '' });
            }
          });
        });
      } else {
        const items = config.items || [];
        items.forEach(item => {
          if (shouldIndexItem(item, pageKey)) {
            allItems.push({ ...item, pageKey, description: item.description || '' });
          }
        });
      }
    }
    return allItems;
  }, [pagesConfig, adminReportAccess]);

  return (
    <SearchProvider searchableData={searchableData}>{children}</SearchProvider>
  );
};

function App() {
  return (
    // Corrected Provider Hierarchy: AuthProvider wraps UIProvider
    <AuthProvider>
      <UIProvider>
        <NavItemsProvider>
          <PagesProvider>
            <CssBaseline />
            <SearchInitializer>
              <ModalProvider>
                <AuthenticatedApp />
                <AppModal />
              </ModalProvider>
            </SearchInitializer>
          </PagesProvider>
        </NavItemsProvider>
      </UIProvider>
    </AuthProvider>
  );
}

export default App;
